import React, { useState, useEffect, useCallback } from 'react'; // v2.9: added useCallback for new sub-components
import CopilotWidget from '../components/CopilotWidget';
import { apiClient } from '../services/api';
import { aiApi, type AdvisoryResult, type PestReport, type PestStats } from '../services/aiApi';
import { qrApi } from '../services/qrApi';
import QRCode from '../components/QRCode';
import FarmPolygonViewer from '../components/FarmPolygonViewer';
import MappingEvidenceReviewMap from '../components/MappingEvidenceReviewMap';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import FleetMap from '../components/FleetMap';
import IntelligentRoutingPanel from '../components/IntelligentRoutingPanel'; // v3.11.47: IROL panel
import OfflineFleetHealthPanel from '../components/OfflineFleetHealthPanel';
import EudrWorldMap from '../components/EudrWorldMap'; // v3.10.2: EUDR compliance map
import {
  BrandMark, Card, SectionHeader, MetricTile, SmartStatus, StatusPill,
  Spinner, EmptyState, Alert, IconButton, SegmentedTabs, Sheet, Avatar, ProgressBar,
  Pagination, usePagination,
} from '../components/ui';

// ─── Inline enterprise icon set ───
const Icon = {
  Users: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 11-8 0 4 4 0 018 0zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>),
  Package: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>),
  Truck: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>),
  MapPin: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>),
  Dollar: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Search: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>),
  Wallet: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 12V7H5a2 2 0 010-4h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4z" /></svg>),
  X: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 6L6 18M6 6l12 12" /></svg>),
  Send: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 6L9 17l-5-5" /></svg>),
  Clock: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>),
  Award: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="8" r="6" /><path d="M15.5 13.5L17 22l-5-3-5 3 1.5-8.5" /></svg>),
  Key: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>),
  Card: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="1" y="4" width="22" height="16" rx="2" /><path d="M1 10h22" /></svg>),
  Settings: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>),
  FileText: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>),
  Database: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>),
  Activity: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>),
  TrendingUp: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 6l-9.5 9.5-5-5L1 18M17 6h6v6" /></svg>),
  Shield: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
  Phone: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" /></svg>),
  Plus: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 5v14M5 12h14" /></svg>),
  Download: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>),
  BarChart3: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 3v18h18M7 16V8M12 16v-5M17 16v-3" /></svg>),
  Sparkles: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3l1.5 5L19 9.5 13.5 11 12 16l-1.5-5L5 9.5 10.5 8 12 3z" /></svg>),
  Leaf: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19.5 2c-.5 4.5-.5 7.5-3 12-2 3.5-7 4-7 4zM2 21c0-3 1.85-5.36 5.08-6" /></svg>),
  Alert: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" /></svg>),
  Map: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 6v15l7-3 8 3 7-3V3l-7 3-8-3-7 3z" /><path d="M8 3v15M16 6v15" /></svg>),
  Layers: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>),
  User: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>),
  // v2.1.1 (A4 fix): Bell icon for the new Harvest Notifications tab
  Bell: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" /></svg>),
  // v2.1.1 (A3 fix): Cpu icon for the new AI Governance tab
  Cpu: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" /></svg>),
  // v4.4: Additional icons for AI Agent binding UI
  Edit: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>),
  BadgeCheck: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M9 12l2 2 4-4" /><path d="M21 12c0 5-3.5 7.5-8.5 9.5C7.5 19.5 4 17 4 12V6l8.5-3.5L21 6v6z" /></svg>),
  RefreshCw: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  AlertCircle: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>),
};

interface Batch {
  id: string; farmer_id: string; farmer_name: string; status: string;
  est_weight: number; actual_weight: number; quality_grade: string;
  price_paid: number; price_per_kg: number; calculated_amount: number;
  payment_status: string; delivered_at: string; collected_at: string; blockchain_hash: string;
  eudr_compliant?: boolean; dds_reference?: string;
}
interface Farmer {
  id: string; name: string; phone: string; village: string; kyc_verified: boolean;
  total_batches: number; total_earnings: number; available_balance: number;
  // v2.1.1 fix: optional fields surfaced by /api/farmers list endpoint (added in
  // migration 026 + 028). Marked optional so the interface stays backward-compatible.
  certification_status?: string;
  avg_quality_score?: number;
  carbon_points?: number;
  cooperative?: string | null;
  gender?: string;
  primary_language?: string;
  sustainability_tier?: string;
}
interface Withdrawal {
  id: string; farmer_id: string; farmer_name: string; amount: number;
  method: string; status: string; carrier: string; phone: string;
  reference: string; requested_at: string; processed_at: string;
}
interface Transaction {
  id: string; batch_id: string; farmer_id: string; type: string; amount: number;
  carrier: string; transaction_id: string; status: string; created_at: string;
  farmer_name: string; farmer_phone: string;
}
interface TruckInfo {
  id: string; registration_number: string; capacity_kg: number; status: string;
  driver_name: string; driver_id: string; assigned_driver_id: string;
  fuel_level_pct: number; odometer_km: number;
}
interface DriverInfo { id: string; name: string; email: string; phone: string; registration_number?: string; }
interface DispatchInfo {
  id: string; batch_id: string; truck_id: string; driver_id: string; status: string;
  assigned_at: string; batch_farmer_name: string; truck_registration: string;
  truck_reg?: string; driver_name: string;
}
interface AdvisoryItem { id: string; title: string; content: string; category: string; language: string; created_at: string; }
interface AuditEntry { id: string; table_name: string; action: string; user_id: string; created_at: string; }

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileRange, setMobileRange] = useState('7d');

  const handleMobileLogout = useCallback(async () => {
    try {
      await logout();
    } finally {
      window.location.href = '/login';
    }
  }, [logout]);

  /* v4.0.3 — Mobile enterprise normalization.
     The desktop tables remain the authoritative data views. On mobile we add
     non-invasive metadata so the same rows can render as readable data cards,
     and mark the document body so legacy global sidebar controls are suppressed
     only while the Admin workspace is mounted. */
  useEffect(() => {
    document.body.classList.add('admin-workspace-mounted');

    const annotateTables = () => {
      const root = document.querySelector('.admin-dashboard-root');
      if (!root) return;

      root.querySelectorAll<HTMLTableElement>('.admin-main-content table').forEach((table) => {
        if (table.closest('.admin-mobile-overview')) return;
        // Farmers has a purpose-built compact mobile LIST. Keep its desktop table
        // authoritative but do not transform that table into generic mobile cards.
        if (table.classList.contains('farmers-desktop-table')) return;

        const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th'))
          .map((th) => (th.textContent || '').trim().replace(/\s+/g, ' '));

        table.classList.add('admin-mobile-data-table');
        table.querySelectorAll<HTMLTableRowElement>('tbody tr').forEach((row) => {
          Array.from(row.children).forEach((cell, index) => {
            if (!(cell instanceof HTMLTableCellElement)) return;
            const label = headers[index] || cell.getAttribute('aria-label') || '';
            if (label) cell.dataset.mobileLabel = label;
          });
        });
      });
    };

    const frame = window.requestAnimationFrame(annotateTables);
    const observer = new MutationObserver(() => window.requestAnimationFrame(annotateTables));
    const root = document.querySelector('.admin-dashboard-root');
    if (root) observer.observe(root, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      document.body.classList.remove('admin-workspace-mounted');
    };
  }, [activeTab, loading]);

  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [trucks, setTrucks] = useState<TruckInfo[]>([]);
  const [advisory, setAdvisory] = useState<AdvisoryItem[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [pricePerKg, setPricePerKg] = useState<number>(2500);
  const [dashboardStats, setDashboardStats] = useState<any>(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [showPriceModal, setShowPriceModal] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [priceStatus, setPriceStatus] = useState<string | null>(null);

  const [showAdvisoryModal, setShowAdvisoryModal] = useState(false);
  const [advisoryForm, setAdvisoryForm] = useState({ title: '', content: '', category: 'general', language: 'en' });
  const [advisoryStatus, setAdvisoryStatus] = useState<string | null>(null);

  const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState<TruckInfo | null>(null);
  const [drivers, setDrivers] = useState<DriverInfo[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [assignStatus, setAssignStatus] = useState<string | null>(null);

  // Add-truck modal state
  const [showAddTruckModal, setShowAddTruckModal] = useState(false);
  const [addTruckForm, setAddTruckForm] = useState({ registration_number: '', capacity_kg: '', driver_id: '' });
  const [addTruckStatus, setAddTruckStatus] = useState<string | null>(null);
  const [addTruckLoading, setAddTruckLoading] = useState(false);

  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({ batch_id: '', truck_id: '', driver_id: '' });
  const [dispatchStatus, setDispatchStatus] = useState<string | null>(null);
  const [dispatches, setDispatches] = useState<DispatchInfo[]>([]);

  // v2.1.1 (A4 fix): Harvest Notifications state — surfaces the dead (window as any).__harvestStats data
  const [harvestNotifs, setHarvestNotifs] = useState<any[]>([]); // v2.1.1 (A4 fix)
  const [harvestNotifStats, setHarvestNotifStats] = useState<any>(null); // v2.1.1 (A4 fix)
  const [harvestNotifLoading, setHarvestNotifLoading] = useState(false); // v2.1.1 (A4 fix)
  const [harvestNotifError, setHarvestNotifError] = useState<string | null>(null); // v2.1.1 (A4 fix)
  const [harvestNotifFilter, setHarvestNotifFilter] = useState<string>('ALL'); // v2.1.1 (A4 fix) — status filter (ALL/PENDING/INSPECTED/BATCHED/CANCELLED)
  const [harvestNotifFetched, setHarvestNotifFetched] = useState(false); // v2.1.1 (A4 fix) — guards first-activation fetch
  const [harvestNotifActionMsg, setHarvestNotifActionMsg] = useState<{ type: 'success' | 'danger'; text: string } | null>(null); // v2.1.1 (A4 fix)
  const [harvestNotifActionLoading, setHarvestNotifActionLoading] = useState<string | null>(null); // v2.1.1 (A4 fix) — notification id currently being mutated

  // v2.1.1 (A3 fix): AI Governance state — prompt versioning + inference log
  const [aiGovStats, setAiGovStats] = useState<any>(null); // v2.1.1 (A3 fix)
  const [aiGovPrompts, setAiGovPrompts] = useState<any[]>([]); // v2.1.1 (A3 fix)
  const [aiGovInferences, setAiGovInferences] = useState<any[]>([]); // v2.1.1 (A3 fix)
  const [aiGovLoading, setAiGovLoading] = useState(false); // v2.1.1 (A3 fix)
  const [aiGovError, setAiGovError] = useState<string | null>(null); // v2.1.1 (A3 fix)
  const [aiGovSuccess, setAiGovSuccess] = useState<string | null>(null); // v2.1.1 (A3 fix)
  const [showAiGovCreateModal, setShowAiGovCreateModal] = useState(false); // v2.1.1 (A3 fix)
  const [aiGovSubmitting, setAiGovSubmitting] = useState(false); // v2.1.1 (A3 fix)
  const [aiGovForm, setAiGovForm] = useState({ prompt_key: '', content: '', description: '' }); // v2.1.1 (A3 fix)
  const aiGovFetchedRef = React.useRef(false); // v2.1.1 (A3 fix) — guards first-activation fetch to avoid refetch spam

  // v2.2.0: Data Sources tab state — Volume XXXI external data source catalog (DS001-DS029)
  const [dataSources, setDataSources] = useState<any[]>([]); // v2.2.0
  const [dataSourcesStats, setDataSourcesStats] = useState<any>(null); // v2.2.0
  const [dataSourcesLoading, setDataSourcesLoading] = useState(false); // v2.2.0
  const [dataSourcesError, setDataSourcesError] = useState<string | null>(null); // v2.2.0
  const [dataSourcesSyncing, setDataSourcesSyncing] = useState<Set<string>>(new Set()); // v2.2.0 — source codes currently syncing
  const [dataSourcesSyncResult, setDataSourcesSyncResult] = useState<Record<string, any>>({}); // v2.2.0 — last sync result per source
  const [dataSourcesFilter, setDataSourcesFilter] = useState<string>('ALL'); // v2.2.0 — category filter
  const dataSourcesFetchedRef = React.useRef(false); // v2.2.0 — guards first-activation fetch

  // v2.2.0: AI Agents tab state — 5 agents (Disease, Compliance, Market, Fertilizer, Yield)
  const [aiAgents, setAiAgents] = useState<any[]>([]); // v2.2.0
  const [aiAgentsModels, setAiAgentsModels] = useState<any>(null); // v2.2.0 — available_models map
  const [aiAgentsLoading, setAiAgentsLoading] = useState(false); // v2.2.0
  const [aiAgentsError, setAiAgentsError] = useState<string | null>(null); // v2.2.0
  const [aiAgentRunning, setAiAgentRunning] = useState<string | null>(null); // v2.2.0 — agent code currently running
  const [aiAgentResult, setAiAgentResult] = useState<Record<string, any>>({}); // v2.2.0 — last run result per agent
  const [aiAgentInput, setAiAgentInput] = useState<Record<string, any>>({}); // v2.2.0 — per-agent input form state
  const [aiAgentRunSheet, setAiAgentRunSheet] = useState<string | null>(null); // v2.2.0 — agent code of currently open run sheet
  const [aiAgentRecentRuns, setAiAgentRecentRuns] = useState<any[]>([]); // v2.2.0 — recent runs across all agents
  const aiAgentsFetchedRef = React.useRef(false); // v2.2.0 — guards first-activation fetch
  // v4.4: DB-driven AI bindings — system_default_model, providers, task_types, models
  const [aiAgentsSystemDefault, setAiAgentsSystemDefault] = useState<string>('gemini-3.1-flash-lite-preview');
  const [aiAgentsProviders, setAiAgentsProviders] = useState<any[]>([]);
  const [aiAgentsTaskTypes, setAiAgentsTaskTypes] = useState<any[]>([]);
  const [aiAgentsAllModels, setAiAgentsAllModels] = useState<any[]>([]);
  const [aiAgentBindingSheet, setAiAgentBindingSheet] = useState<string | null>(null); // v4.4 — agent code of currently open binding edit sheet
  const [aiAgentBindingSaving, setAiAgentBindingSaving] = useState(false); // v4.4
  const [aiAgentVerifyingAll, setAiAgentVerifyingAll] = useState(false); // v4.4
  const [aiAgentVerifyResult, setAiAgentVerifyResult] = useState<any>(null); // v4.4 — show verify-all result banner

  // v2.2.0: Knowledge Base tab state — search/documents/papers/graph
  const [knowledgeQuery, setKnowledgeQuery] = useState(''); // v2.2.0
  const [knowledgeResults, setKnowledgeResults] = useState<any[]>([]); // v2.2.0
  const [knowledgeLoading, setKnowledgeLoading] = useState(false); // v2.2.0
  const [knowledgeError, setKnowledgeError] = useState<string | null>(null); // v2.2.0
  const [knowledgeTab, setKnowledgeTab] = useState<'search' | 'ask-ai' | 'documents' | 'papers' | 'graph'>('search'); // v2.2.0 + v4.4.6 (ask-ai)
  const [knowledgeDocuments, setKnowledgeDocuments] = useState<any[]>([]); // v2.2.0
  const [knowledgePapers, setKnowledgePapers] = useState<any[]>([]); // v2.2.0
  const [knowledgeGraph, setKnowledgeGraph] = useState<any>(null); // v2.2.0 — { nodes, edges }
  const knowledgeFetchedRef = React.useRef(false); // v2.2.0 — guards first-activation fetch

  // v2.3.1: Knowledge tab — RAG Pipeline + Entity Extraction + Hybrid Search + Graph visualization enhancements
  const [knowledgeSearchMode, setKnowledgeSearchMode] = useState<'keyword' | 'vector' | 'hybrid'>('keyword'); // v2.3.1 — search mode toggle (keyword/vector/hybrid)
  const [knowledgeProcessing, setKnowledgeProcessing] = useState<Set<string>>(new Set()); // v2.3.1 — doc IDs currently being RAG-processed
  const [knowledgeProcessingResult, setKnowledgeProcessingResult] = useState<Record<string, any>>({}); // v2.3.1 — last RAG result per docId
  const [knowledgeExtracting, setKnowledgeExtracting] = useState<Set<string>>(new Set()); // v2.3.1 — doc IDs currently being entity-extracted
  const [knowledgeExtractResult, setKnowledgeExtractResult] = useState<Record<string, any>>({}); // v2.3.1 — last extraction result per docId
  const [knowledgeGraphStats, setKnowledgeGraphStats] = useState<any>(null); // v2.3.1 — graph stats { total_nodes, total_edges, by_type: [] }
  const [knowledgeGraphSearch, setKnowledgeGraphSearch] = useState(''); // v2.3.1 — entity search query input
  const [knowledgeGraphSearchResult, setKnowledgeGraphSearchResult] = useState<any>(null); // v2.3.1 — result of entity search { node, related, count }
  // v4.4.6: Knowledge Q&A — ask the KNOWLEDGE agent (SVC-024) questions against the KB
  const [knowledgeQaQuestion, setKnowledgeQaQuestion] = useState('');
  const [knowledgeQaAnswer, setKnowledgeQaAnswer] = useState<any>(null);
  const [knowledgeQaLoading, setKnowledgeQaLoading] = useState(false);

  // v2.5: ─── Pipeline tab state — Cognitive Pipeline monitoring (stats + runs + stages + execute form)
  const [pipelineStats, setPipelineStats] = useState<any>(null); // v2.5
  const [pipelineRuns, setPipelineRuns] = useState<any[]>([]); // v2.5
  const [pipelineStages, setPipelineStages] = useState<any[]>([]); // v2.5
  const [pipelineLoading, setPipelineLoading] = useState(false); // v2.5
  const [pipelineError, setPipelineError] = useState<string | null>(null); // v2.5
  const [pipelineExecuting, setPipelineExecuting] = useState(false); // v2.5
  const [pipelineExecuteResult, setPipelineExecuteResult] = useState<any>(null); // v2.5
  const [pipelineExecForm, setPipelineExecForm] = useState<{ agent_code: string; input: string }>({ agent_code: '', input: '{}' }); // v2.5
  const pipelineFetchedRef = React.useRef(false); // v2.5

  // v2.5: ─── AI Gov V2 tab state — Extended AI Governance (9 KPIs + provenance + policy + benchmarks)
  const [aiGovV2Metrics, setAiGovV2Metrics] = useState<any>(null); // v2.5
  const [aiGovV2Provenance, setAiGovV2Provenance] = useState<any[]>([]); // v2.5
  const [aiGovV2Rules, setAiGovV2Rules] = useState<any[]>([]); // v2.5
  const [aiGovV2Violations, setAiGovV2Violations] = useState<any[]>([]); // v2.5
  const [aiGovV2Benchmarks, setAiGovV2Benchmarks] = useState<any[]>([]); // v2.5
  const [aiGovV2Loading, setAiGovV2Loading] = useState(false); // v2.5
  const [aiGovV2Error, setAiGovV2Error] = useState<string | null>(null); // v2.5
  const [aiGovV2Success, setAiGovV2Success] = useState<string | null>(null); // v2.5
  const [aiGovV2Resolving, setAiGovV2Resolving] = useState<string | null>(null); // v2.5 — violation id being resolved
  const [aiGovV2RunningBench, setAiGovV2RunningBench] = useState<string | null>(null); // v2.5 — benchmark id currently running
  const [aiGovV2BenchModelInput, setAiGovV2BenchModelInput] = useState<Record<string, string>>({}); // v2.5 — per-benchmark model_code input
  const aiGovV2FetchedRef = React.useRef(false); // v2.5

  // v2.5: ─── ECOS tab state — Strategic Capabilities (agents + skills + evolution + costs)
  const [ecosAgents, setEcosAgents] = useState<any[]>([]); // v2.5
  const [ecosHealth, setEcosHealth] = useState<any>(null); // v2.5
  const [ecosSkills, setEcosSkills] = useState<any[]>([]); // v2.5
  const [ecosFindings, setEcosFindings] = useState<any[]>([]); // v2.5
  const [ecosCosts, setEcosCosts] = useState<any>(null); // v2.5
  const [ecosCostRecs, setEcosCostRecs] = useState<any[]>([]); // v2.5
  const [ecosLoading, setEcosLoading] = useState(false); // v2.5
  const [ecosError, setEcosError] = useState<string | null>(null); // v2.5
  const [ecosSuccess, setEcosSuccess] = useState<string | null>(null); // v2.5
  const [ecosExecutingSkill, setEcosExecutingSkill] = useState<string | null>(null); // v2.5
  const [ecosSkillInput, setEcosSkillInput] = useState<Record<string, string>>({}); // v2.5 — per-skill JSON input
  const [ecosRunningEvolution, setEcosRunningEvolution] = useState(false); // v2.5
  const ecosFetchedRef = React.useRef(false); // v2.5

  // v2.5: ─── Durability tab state — Durable Execution + Circuit Breakers + Approvals + Saga
  const [durabilityBreakers, setDurabilityBreakers] = useState<any[]>([]); // v2.5
  const [durabilityApprovals, setDurabilityApprovals] = useState<any[]>([]); // v2.5
  const [durabilityCompensations, setDurabilityCompensations] = useState<any>(null); // v2.5
  const [durabilityCrashed, setDurabilityCrashed] = useState<any[]>([]); // v2.5
  const [durabilityLoading, setDurabilityLoading] = useState(false); // v2.5
  const [durabilityError, setDurabilityError] = useState<string | null>(null); // v2.5
  const [durabilitySuccess, setDurabilitySuccess] = useState<string | null>(null); // v2.5
  const [durabilityResetting, setDurabilityResetting] = useState<string | null>(null); // v2.5 — service code currently resetting
  const [durabilityResolvingApproval, setDurabilityResolvingApproval] = useState<string | null>(null); // v2.5
  const [durabilityAutoRecovering, setDurabilityAutoRecovering] = useState(false); // v2.5
  const durabilityFetchedRef = React.useRef(false); // v2.5

  // v2.5: ─── Regulatory V2 tab state — Regulatory API Gateway (tokens + logs)
  const [regV2Tokens, setRegV2Tokens] = useState<any[]>([]); // v2.5
  const [regV2Logs, setRegV2Logs] = useState<any[]>([]); // v2.5
  const [regV2Loading, setRegV2Loading] = useState(false); // v2.5
  const [regV2Error, setRegV2Error] = useState<string | null>(null); // v2.5
  const [regV2Success, setRegV2Success] = useState<string | null>(null); // v2.5
  const [regV2ShowCreate, setRegV2ShowCreate] = useState(false); // v2.5
  const [regV2NewToken, setRegV2NewToken] = useState<string | null>(null); // v2.5
  const [regV2Form, setRegV2Form] = useState<{ authority: string; scope: string }>({ authority: 'MINADER', scope: 'soil,quality,export' }); // v2.5
  const regV2FetchedRef = React.useRef(false); // v2.5

  // v2.5: ─── SMS tab state — SMS Management (templates + send + history)
  const [smsTemplates, setSmsTemplates] = useState<any[]>([]); // v2.5
  const [smsHistory, setSmsHistory] = useState<any[]>([]); // v2.5
  const [smsLoading, setSmsLoading] = useState(false); // v2.5
  const [smsError, setSmsError] = useState<string | null>(null); // v2.5
  const [smsSuccess, setSmsSuccess] = useState<string | null>(null); // v2.5
  const [smsSending, setSmsSending] = useState(false); // v2.5
  const [smsSendForm, setSmsSendForm] = useState<{ phone: string; template_id: string }>({ phone: '', template_id: '' }); // v2.5
  const smsFetchedRef = React.useRef(false); // v2.5

  // v2.7: ─── Dependency Graph tab state — layered nodes + edges tables + "What if?" impact analysis
  const [depGraphNodes, setDepGraphNodes] = useState<any[]>([]); // v2.7
  const [depGraphEdges, setDepGraphEdges] = useState<any[]>([]); // v2.7
  const [depGraphLoading, setDepGraphLoading] = useState(false); // v2.7
  const [depGraphError, setDepGraphError] = useState<string | null>(null); // v2.7
  const [depGraphFilter, setDepGraphFilter] = useState<string>('ALL'); // v2.7 — node-type filter (ALL | DATA_SOURCE | AI_AGENT | ERP_MODULE | DASHBOARD | STORAGE | ALERT)
  const [depGraphImpactKey, setDepGraphImpactKey] = useState(''); // v2.7 — node key input for "What if?" impact analysis
  const [depGraphImpactResult, setDepGraphImpactResult] = useState<any>(null); // v2.7 — result of GET /dependency-graph/impact/:key
  const [depGraphImpactLoading, setDepGraphImpactLoading] = useState(false); // v2.7
  const [depGraphImpactError, setDepGraphImpactError] = useState<string | null>(null); // v2.7
  const depGraphFetchedRef = React.useRef(false); // v2.7

  // ─── Filtered lists (memoized for pagination) ───
  const filteredFarmers = farmers.filter(f => !searchTerm || f.name?.toLowerCase().includes(searchTerm.toLowerCase()) || f.phone?.includes(searchTerm));
  const filteredBatches = batches.filter((b: any) => !searchTerm || b.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredWithdrawals = withdrawals.filter((w: any) => !searchTerm || w.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredTransactions = transactions.filter((t: any) => !searchTerm || t.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredTrucks = trucks.filter((t: any) => !searchTerm || t.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredDispatches = dispatches.filter((d: any) => !searchTerm || d.batch_farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || d.truck_registration?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredAdvisory = advisory.filter((a: any) => !searchTerm || a.title?.toLowerCase().includes(searchTerm.toLowerCase()));

  // ─── Pagination hooks (must be called unconditionally) ───
  const farmersPag = usePagination(filteredFarmers, 15);
  const batchesPag = usePagination(filteredBatches, 15);
  const withdrawalsPag = usePagination(filteredWithdrawals, 15);
  const transactionsPag = usePagination(filteredTransactions, 20);
  const trucksPag = usePagination(filteredTrucks, 15);
  const dispatchesPag = usePagination(filteredDispatches, 15);
  const auditPag = usePagination(auditEntries, 20);
  const advisoryPag = usePagination(filteredAdvisory, 10);

  const fetchData = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const failures: string[] = [];
      const safeGet = (url: string, fallback: any) =>
        apiClient.get(url).catch((err: any) => {
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`);
          return { data: fallback };
        });

      const [farmersRes, batchesRes, withdrawalsRes, transactionsRes, trucksRes, advisoryRes, auditRes, statsRes, priceRes, dispatchesRes] = await Promise.all([
        safeGet('/farmers', []),
        safeGet('/batches', []),
        safeGet('/withdrawals', []),
        safeGet('/transactions', []),
        safeGet('/truck-portal/trucks', { trucks: [] }),
        safeGet('/advisory', []),
        safeGet('/audit-log?limit=20', { entries: [] }),
        safeGet('/mel/dashboard', null),
        safeGet('/settings/price-per-kg', { price_per_kg: 2500 }),
        safeGet('/dispatches', []),
      ]);
      // v1.6.7: also fetch chain stats + harvest stats in parallel (non-blocking)
      Promise.all([
        apiClient.get('/chain/stats').catch(() => ({ data: null })),
        apiClient.get('/harvest-notifications/stats').catch(() => ({ data: null })),
      ]).then(([chainRes, harvestRes]) => {
        if (chainRes.data?.stats) (window as any).__chainStats = chainRes.data.stats;
        if (harvestRes.data?.stats) (window as any).__harvestStats = harvestRes.data.stats;
      });
      let batchesData = Array.isArray(batchesRes.data) ? batchesRes.data : [];
      if (batchesRes.data && Array.isArray(batchesRes.data.batches)) batchesData = batchesRes.data.batches;
      setFarmers(Array.isArray(farmersRes.data) ? farmersRes.data : []);
      setBatches(batchesData);
      setWithdrawals(Array.isArray(withdrawalsRes.data) ? withdrawalsRes.data : []);
      setTransactions(Array.isArray(transactionsRes.data) ? transactionsRes.data : []);
      setDispatches(Array.isArray(dispatchesRes.data) ? dispatchesRes.data : []);
      setTrucks(trucksRes.data.trucks || []);
      setAdvisory(Array.isArray(advisoryRes.data) ? advisoryRes.data : []);
      setAuditEntries(auditRes.data.entries || []);
      setDashboardStats(statsRes.data);
      setPricePerKg(priceRes.data.price_per_kg || 2500);

      if (failures.length > 0) {
        console.warn('⚠️ Some admin endpoints failed:', failures);
        setError(`Some sections failed to load: ${failures.join(' | ')}`);
      }
    } catch (err) {
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleBatchPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setPaymentStatus(null);
    try {
      await apiClient.patch(`/batches/${selectedBatch?.id}/pay`, {});
      setPaymentStatus('Payment sent to farmer balance successfully!');
      setTimeout(() => { setShowPaymentModal(false); setPaymentStatus(null); setSelectedBatch(null); fetchData(); }, 2000);
    } catch (err) {
      setPaymentStatus('Payment failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const handleProcessWithdrawal = async (withdrawalId: string, status: string) => {
    try {
      await apiClient.patch(`/withdrawals/${withdrawalId}/process`, { status, notes: `Approved by admin` });
      fetchData();
    } catch (err) { alert('Failed to process withdrawal'); }
  };

  const handleUpdatePrice = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setPriceStatus(null);
    try {
      await apiClient.put('/settings/price-per-kg', { price_per_kg: parseFloat(newPrice) });
      setPriceStatus('Price updated! All pending batches recalculated.');
      setPricePerKg(parseFloat(newPrice));
      setTimeout(() => { setShowPriceModal(false); setPriceStatus(null); fetchData(); }, 2000);
    } catch (err) {
      setPriceStatus('Failed to update price.');
    } finally { setSubmitting(false); }
  };

  const handleCreateAdvisory = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setAdvisoryStatus(null);
    try {
      await apiClient.post('/advisory', advisoryForm);
      setAdvisoryStatus('Advisory content published!');
      setAdvisoryForm({ title: '', content: '', category: 'general', language: 'en' });
      setTimeout(() => { setShowAdvisoryModal(false); setAdvisoryStatus(null); fetchData(); }, 2000);
    } catch (err) {
      setAdvisoryStatus('Failed to publish advisory.');
    } finally { setSubmitting(false); }
  };

  const handleOpenAssignDriver = async (truck: TruckInfo) => {
    setSelectedTruck(truck);
    setSelectedDriverId(truck.assigned_driver_id || truck.driver_id || '');
    setAssignStatus(null);
    try {
      const res = await apiClient.get('/fleet/drivers');
      setDrivers(res.data.drivers || []);
    } catch { /* ignore */ }
    setShowAssignDriverModal(true);
  };

  const handleAssignDriver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTruck || !selectedDriverId) return;
    // v1.6.8: confirmation dialog before assigning driver (ISSUE-009)
    const driverName = drivers.find(d => d.id === selectedDriverId)?.name || 'this driver';
    if (!confirm(`Assign ${driverName} to truck ${selectedTruck.registration_number}?`)) return;
    setSubmitting(true); setAssignStatus(null);
    try {
      await apiClient.post(`/truck-portal/trucks/${selectedTruck.id}/assign`, { driverId: selectedDriverId });
      setAssignStatus('Driver assigned successfully!');
      setTimeout(() => { setShowAssignDriverModal(false); setAssignStatus(null); setSelectedTruck(null); fetchData(); }, 2000);
    } catch (err: any) {
      setAssignStatus(err.response?.data?.error || 'Failed to assign driver.');
    } finally { setSubmitting(false); }
  };

  const handleOpenAddTruck = async () => {
    setAddTruckForm({ registration_number: '', capacity_kg: '', driver_id: '' });
    setAddTruckStatus(null);
    try {
      const res = await apiClient.get('/drivers');
      setDrivers(res.data.drivers || []);
    } catch { /* ignore */ }
    setShowAddTruckModal(true);
  };

  const handleAddTruck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addTruckForm.registration_number || !addTruckForm.capacity_kg) {
      setAddTruckStatus('Registration number and capacity are required.');
      return;
    }
    setAddTruckLoading(true); setAddTruckStatus(null);
    try {
      await apiClient.post('/trucks', {
        registration_number: addTruckForm.registration_number,
        capacity_kg: parseFloat(addTruckForm.capacity_kg),
        driver_id: addTruckForm.driver_id || undefined,
      });
      setAddTruckStatus('Truck created successfully!');
      setTimeout(() => { setShowAddTruckModal(false); setAddTruckStatus(null); setAddTruckForm({ registration_number: '', capacity_kg: '', driver_id: '' }); fetchData(); }, 1500);
    } catch (err: any) {
      setAddTruckStatus(err.response?.data?.error || 'Failed to create truck.');
    } finally { setAddTruckLoading(false); }
  };

  const handleCreateDispatch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setDispatchStatus(null);
    try {
      await apiClient.post('/dispatches', {
        batch_id: dispatchForm.batch_id, truck_id: dispatchForm.truck_id, driver_id: dispatchForm.driver_id,
      });
      setDispatchStatus('Dispatch created! The driver will see it in their portal.');
      setTimeout(() => { setShowDispatchModal(false); setDispatchStatus(null); setDispatchForm({ batch_id: '', truck_id: '', driver_id: '' }); fetchData(); }, 2000);
    } catch (err: any) {
      setDispatchStatus(err.response?.data?.error || 'Failed to create dispatch.');
    } finally { setSubmitting(false); }
  };

  // v2.1.1 (A4 fix): ─── Harvest Notifications fetch + actions ───
  // v2.1.1 (A4 fix): pull list + stats from /api/harvest-notifications (mount prefix /api is added by apiClient baseURL)
  const fetchHarvestNotifs = async (filterOverride?: string) => { // v2.1.1 (A4 fix)
    const f = filterOverride ?? harvestNotifFilter; // v2.1.1 (A4 fix)
    setHarvestNotifLoading(true); // v2.1.1 (A4 fix)
    setHarvestNotifError(null); // v2.1.1 (A4 fix)
    try {
      const statusParam = f && f !== 'ALL' ? `?status=${f}` : ''; // v2.1.1 (A4 fix)
      const [listRes, statsRes] = await Promise.all([ // v2.1.1 (A4 fix)
        apiClient.get(`/harvest-notifications/${statusParam}`).catch((e: any) => { // v2.1.1 (A4 fix)
          setHarvestNotifError(e.response?.data?.error || `Failed to load harvest notifications (status ${e.response?.status || '?'})`); // v2.1.1 (A4 fix)
          return { data: { notifications: [] } }; // v2.1.1 (A4 fix)
        }),
        apiClient.get('/harvest-notifications/stats').catch(() => ({ data: null })), // v2.1.1 (A4 fix)
      ]);
      const notifs = listRes.data?.notifications || []; // v2.1.1 (A4 fix)
      setHarvestNotifs(notifs); // v2.1.1 (A4 fix)
      if (statsRes.data?.stats) { // v2.1.1 (A4 fix)
        setHarvestNotifStats(statsRes.data.stats); // v2.1.1 (A4 fix)
        (window as any).__harvestStats = statsRes.data.stats; // v2.1.1 (A4 fix) — keep legacy global in sync for the Overview tile
      }
    } finally {
      setHarvestNotifLoading(false); // v2.1.1 (A4 fix)
      setHarvestNotifFetched(true); // v2.1.1 (A4 fix)
    }
  };

  // v2.1.1 (A4 fix): mark a notification as INSPECTED (backend status). UI button label is "Mark Inspecting" per spec.
  const handleHarvestNotifInspect = async (notifId: string) => { // v2.1.1 (A4 fix)
    setHarvestNotifActionLoading(notifId); // v2.1.1 (A4 fix)
    setHarvestNotifActionMsg(null); // v2.1.1 (A4 fix)
    try {
      await apiClient.patch(`/harvest-notifications/${notifId}/inspect`, {}); // v2.1.1 (A4 fix)
      setHarvestNotifActionMsg({ type: 'success', text: `Notification ${notifId.slice(0, 8)}… marked as inspecting.` }); // v2.1.1 (A4 fix)
      await fetchHarvestNotifs(); // v2.1.1 (A4 fix)
    } catch (err: any) {
      setHarvestNotifActionMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to mark as inspecting.' }); // v2.1.1 (A4 fix)
    } finally {
      setHarvestNotifActionLoading(null); // v2.1.1 (A4 fix)
    }
  };

  // v2.1.1 (A4 fix): convert a notification into a Batch. The backend POST /:id/convert links an existing batch_id;
  // v2.1.1 (A4 fix): we first create a PENDING batch from the notification's expected weight, then link it.
  const handleHarvestNotifConvert = async (notif: any) => { // v2.1.1 (A4 fix)
    if (!notif?.id) return; // v2.1.1 (A4 fix)
    if (!confirm(`Convert notification ${notif.id.slice(0, 8)}… from ${notif.farmer_name || 'this farmer'} into a PENDING batch?`)) return; // v2.1.1 (A4 fix)
    setHarvestNotifActionLoading(notif.id); // v2.1.1 (A4 fix)
    setHarvestNotifActionMsg(null); // v2.1.1 (A4 fix)
    try {
      // v2.1.1 (A4 fix): step 1 — create a PENDING batch seeded from the notification's expected values
      const createRes = await apiClient.post('/batches', { // v2.1.1 (A4 fix)
        farmer_id: notif.farmer_id, // v2.1.1 (A4 fix)
        farm_id: notif.farm_id || undefined, // v2.1.1 (A4 fix)
        est_weight: Number(notif.expected_weight_kg || 0), // v2.1.1 (A4 fix)
        harvest_notification_id: notif.id, // v2.1.1 (A4 fix)
      });
      const batchId = createRes.data?.batch?.id || createRes.data?.id; // v2.1.1 (A4 fix)
      if (!batchId) throw new Error('Batch creation returned no batch id'); // v2.1.1 (A4 fix)
      // v2.1.1 (A4 fix): step 2 — link the new batch to the notification (status -> BATCHED)
      await apiClient.post(`/harvest-notifications/${notif.id}/convert`, { batch_id: batchId }); // v2.1.1 (A4 fix)
      setHarvestNotifActionMsg({ type: 'success', text: `Converted to batch ${String(batchId).slice(0, 8)}… (PENDING, ready for dispatch).` }); // v2.1.1 (A4 fix)
      await fetchHarvestNotifs(); // v2.1.1 (A4 fix)
    } catch (err: any) {
      // v2.1.1 (A4 fix): fall back to calling convert directly (some deployments auto-create the batch server-side)
      try {
        await apiClient.post(`/harvest-notifications/${notif.id}/convert`, {}); // v2.1.1 (A4 fix)
        setHarvestNotifActionMsg({ type: 'success', text: `Notification ${notif.id.slice(0, 8)}… converted.` }); // v2.1.1 (A4 fix)
        await fetchHarvestNotifs(); // v2.1.1 (A4 fix)
      } catch (err2: any) {
        setHarvestNotifActionMsg({ type: 'danger', text: err2.response?.data?.error || err.response?.data?.error || 'Failed to convert notification into a batch.' }); // v2.1.1 (A4 fix)
      }
    } finally {
      setHarvestNotifActionLoading(null); // v2.1.1 (A4 fix)
    }
  };

  // v2.1.1 (A4 fix): cancel a notification (only PENDING/INSPECTED are cancellable on the backend)
  const handleHarvestNotifCancel = async (notifId: string) => { // v2.1.1 (A4 fix)
    const reason = prompt(`Cancel notification ${notifId.slice(0, 8)}… — enter a reason (optional):`); // v2.1.1 (A4 fix)
    if (reason === null) return; // v2.1.1 (A4 fix) — user clicked Cancel on the prompt
    if (!confirm(`Confirm: cancel notification ${notifId.slice(0, 8)}…?`)) return; // v2.1.1 (A4 fix)
    setHarvestNotifActionLoading(notifId); // v2.1.1 (A4 fix)
    setHarvestNotifActionMsg(null); // v2.1.1 (A4 fix)
    try {
      await apiClient.patch(`/harvest-notifications/${notifId}/cancel`, { cancel_reason: reason || undefined }); // v2.1.1 (A4 fix)
      setHarvestNotifActionMsg({ type: 'success', text: `Notification ${notifId.slice(0, 8)}… cancelled.` }); // v2.1.1 (A4 fix)
      await fetchHarvestNotifs(); // v2.1.1 (A4 fix)
    } catch (err: any) {
      setHarvestNotifActionMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to cancel notification.' }); // v2.1.1 (A4 fix)
    } finally {
      setHarvestNotifActionLoading(null); // v2.1.1 (A4 fix)
    }
  };

  // v2.1.1 (A4 fix): fetch on first tab activation (and whenever the filter changes after the first fetch)
  useEffect(() => { // v2.1.1 (A4 fix)
    if (activeTab !== 'harvest-notif') return; // v2.1.1 (A4 fix)
    if (!harvestNotifFetched) { fetchHarvestNotifs(); return; } // v2.1.1 (A4 fix) — first activation: initial load
    fetchHarvestNotifs(); // v2.1.1 (A4 fix) — subsequent activations: refresh on filter change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, harvestNotifFilter]); // v2.1.1 (A4 fix)

  // v2.1.1 (A4 fix): map backend status -> { label, tone } for StatusPill display
  const harvestNotifStatusMeta = (status: string): { label: string; tone: 'warning' | 'info' | 'success' | 'danger' | 'neutral' } => { // v2.1.1 (A4 fix)
    switch (status) { // v2.1.1 (A4 fix)
      case 'PENDING':   return { label: 'Pending',    tone: 'warning' }; // v2.1.1 (A4 fix)
      case 'INSPECTED': return { label: 'Inspecting', tone: 'info' };    // v2.1.1 (A4 fix)
      case 'BATCHED':   return { label: 'Converted',  tone: 'success' }; // v2.1.1 (A4 fix)
      case 'CANCELLED': return { label: 'Cancelled',  tone: 'danger' };  // v2.1.1 (A4 fix)
      default:          return { label: status || '—', tone: 'neutral' }; // v2.1.1 (A4 fix)
    }
  };

  // v2.1.1 (A3 fix): ─── AI Governance fetch + actions ───
  // v2.1.1 (A3 fix): pull stats + prompts + recent inference log from /api/ai-governance/*
  const fetchAiGovernance = async () => { // v2.1.1 (A3 fix)
    setAiGovLoading(true); // v2.1.1 (A3 fix)
    setAiGovError(null); // v2.1.1 (A3 fix)
    try {
      const failures: string[] = []; // v2.1.1 (A3 fix)
      const safeGet = (url: string, fallback: any) => // v2.1.1 (A3 fix)
        apiClient.get(url).catch((err: any) => { // v2.1.1 (A3 fix)
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.1.1 (A3 fix)
          return { data: fallback }; // v2.1.1 (A3 fix)
        });
      const [statsRes, promptsRes, logsRes] = await Promise.all([ // v2.1.1 (A3 fix)
        safeGet('/ai-governance/stats', { stats: null }), // v2.1.1 (A3 fix)
        safeGet('/ai-governance/prompts', { prompts: [] }), // v2.1.1 (A3 fix)
        safeGet('/ai-governance/?limit=50', { logs: [] }), // v2.1.1 (A3 fix)
      ]);
      setAiGovStats(statsRes.data?.stats || null); // v2.1.1 (A3 fix)
      setAiGovPrompts(Array.isArray(promptsRes.data?.prompts) ? promptsRes.data.prompts : []); // v2.1.1 (A3 fix)
      setAiGovInferences(Array.isArray(logsRes.data?.logs) ? logsRes.data.logs : []); // v2.1.1 (A3 fix)
      if (failures.length > 0) { // v2.1.1 (A3 fix)
        setAiGovError(`Some AI governance endpoints failed: ${failures.join(' | ')}`); // v2.1.1 (A3 fix)
      }
    } catch (err: any) { // v2.1.1 (A3 fix)
      setAiGovError(err.response?.data?.error || 'Failed to load AI governance data'); // v2.1.1 (A3 fix)
    } finally {
      setAiGovLoading(false); // v2.1.1 (A3 fix)
    }
  };

  // v2.1.1 (A3 fix): fetch AI governance data on first tab activation
  useEffect(() => { // v2.1.1 (A3 fix)
    if (activeTab !== 'ai-governance') return; // v2.1.1 (A3 fix)
    if (aiGovFetchedRef.current) return; // v2.1.1 (A3 fix) — already fetched, avoid refetch spam
    aiGovFetchedRef.current = true; // v2.1.1 (A3 fix)
    fetchAiGovernance(); // v2.1.1 (A3 fix)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.1.1 (A3 fix)

  // v2.2.0: ─── Data Sources fetch + sync ───
  // v2.2.0: pull catalog + summary stats from /api/data-sources/*
  const fetchDataSources = async () => { // v2.2.0
    setDataSourcesLoading(true); // v2.2.0
    setDataSourcesError(null); // v2.2.0
    try {
      const failures: string[] = []; // v2.2.0
      const safeGet = (url: string, fallback: any) => // v2.2.0
        apiClient.get(url).catch((err: any) => { // v2.2.0
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.2.0
          return { data: fallback }; // v2.2.0
        });
      const [listRes, statsRes] = await Promise.all([ // v2.2.0
        safeGet('/data-sources', { sources: [] }), // v2.2.0
        safeGet('/data-sources/stats/summary', { totals: {}, by_category: [] }), // v2.2.0
      ]);
      setDataSources(Array.isArray(listRes.data?.sources) ? listRes.data.sources : []); // v2.2.0
      setDataSourcesStats(statsRes.data || null); // v2.2.0
      if (failures.length > 0) { // v2.2.0
        setDataSourcesError(`Some data source endpoints failed: ${failures.join(' | ')}`); // v2.2.0
      }
    } catch (err: any) { // v2.2.0
      setDataSourcesError(err.response?.data?.error || 'Failed to load data sources'); // v2.2.0
    } finally { // v2.2.0
      setDataSourcesLoading(false); // v2.2.0
    }
  };

  // v2.2.0: trigger a sync on a single data source (admin only)
  const handleDataSourceSync = async (sourceCode: string) => { // v2.2.0
    setDataSourcesSyncing(prev => new Set(prev).add(sourceCode)); // v2.2.0
    setDataSourcesSyncResult(prev => ({ ...prev, [sourceCode]: null })); // v2.2.0
    try {
      const res = await apiClient.post(`/data-sources/${sourceCode}/sync`, {}); // v2.2.0
      // v4.2: Check result.error — the server now returns HTTP 422 for errors,
      // but also check the result body for backwards compat
      const result = res.data?.result;
      if (result?.error) {
        setDataSourcesSyncResult(prev => ({ ...prev, [sourceCode]: { ok: false, error: result.error, result } }));
      } else {
        setDataSourcesSyncResult(prev => ({ ...prev, [sourceCode]: { ok: true, result, source_code: sourceCode } }));
      }
      fetchDataSources(); // v2.2.0 — refresh list to surface updated last_synced_at
    } catch (err: any) { // v2.2.0
      // v4.2: HTTP 422 errors land here — extract the error message
      const errorMsg = err.response?.data?.error || err.response?.data?.result?.error || 'Sync failed';
      setDataSourcesSyncResult(prev => ({ ...prev, [sourceCode]: { ok: false, error: errorMsg } }));
    } finally { // v2.2.0
      setDataSourcesSyncing(prev => { const n = new Set(prev); n.delete(sourceCode); return n; }); // v2.2.0
    }
  };

  // v4.2: Data source API key management
  const [dsKeySheetOpen, setDsKeySheetOpen] = useState(false);
  const [dsKeySheetData, setDsKeySheetData] = useState<any>(null);
  const [dsKeyValue, setDsKeyValue] = useState('');
  const [dsKeySaving, setDsKeySaving] = useState(false);
  const [dsKeyStatus, setDsKeyStatus] = useState<Record<string, any>>({});
  const [dsKeyType, setDsKeyType] = useState<'auto' | 'jwt' | 'client_id_secret' | 'token'>('auto'); // v4.3.2

  const openDataSourceKeySheet = async (source: any) => {
    setDsKeySheetData(source);
    setDsKeyValue('');
    setDsKeySheetOpen(true);
    setDsKeyType('auto');
    // Fetch current key status
    try {
      const res = await apiClient.get(`/data-sources/${source.source_code}/api-key-status`);
      setDsKeyStatus(prev => ({ ...prev, [source.source_code]: res.data }));
    } catch {}
  };

  const handleSaveDataSourceKey = async () => {
    if (!dsKeySheetData || !dsKeyValue || dsKeyValue.trim().length < 5) return;
    setDsKeySaving(true);
    try {
      await apiClient.post(`/data-sources/${dsKeySheetData.source_code}/api-key`, { api_key: dsKeyValue.trim() });
      setDataSourcesError(null);
      setDsKeySheetOpen(false);
      setDsKeyValue('');
      // Refresh key status
      const res = await apiClient.get(`/data-sources/${dsKeySheetData.source_code}/api-key-status`);
      setDsKeyStatus(prev => ({ ...prev, [dsKeySheetData.source_code]: res.data }));
    } catch (err: any) {
      setDataSourcesError(err.response?.data?.error || 'Failed to save API key');
    } finally {
      setDsKeySaving(false);
    }
  };

  // v4.2: View All API Keys
  const [allKeysSheetOpen, setAllKeysSheetOpen] = useState(false);
  const [allKeys, setAllKeys] = useState<any[]>([]);
  const [allKeysLoading, setAllKeysLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fetchAllKeys = async () => {
    setAllKeysLoading(true);
    setAllKeysSheetOpen(true);
    try {
      const res = await apiClient.get('/data-sources/all-keys/list');
      setAllKeys(res.data?.keys || []);
    } catch (err: any) {
      setDataSourcesError(err.response?.data?.error || 'Failed to load API keys');
    } finally {
      setAllKeysLoading(false);
    }
  };

  const copyKey = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  // v2.2.0: fetch data sources on first tab activation (guarded by ref)
  useEffect(() => { // v2.2.0
    if (activeTab !== 'data-sources') return; // v2.2.0
    if (dataSourcesFetchedRef.current) return; // v2.2.0
    dataSourcesFetchedRef.current = true; // v2.2.0
    fetchDataSources(); // v2.2.0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.2.0

  // v2.2.0: ─── AI Agents fetch + run ───
  // v2.2.0: pull agent registry + model availability + recent runs
  // v4.4: now also captures system_default_model, providers, task_types, models
  // (all 100% DB-driven — no hardcoded lists in the client).
  const fetchAiAgents = async () => { // v2.2.0
    setAiAgentsLoading(true); // v2.2.0
    setAiAgentsError(null); // v2.2.0
    try {
      const failures: string[] = []; // v2.2.0
      const safeGet = (url: string, fallback: any) => // v2.2.0
        apiClient.get(url).catch((err: any) => { // v2.2.0
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.2.0
          return { data: fallback }; // v2.2.0
        });
      const [agentsRes, runsRes] = await Promise.all([ // v2.2.0
        safeGet('/ai-agents', { agents: [], available_models: {} }), // v2.2.0
        safeGet('/ai-agents/runs/recent', { runs: [] }), // v2.2.0
      ]);
      setAiAgents(Array.isArray(agentsRes.data?.agents) ? agentsRes.data.agents : []); // v2.2.0
      setAiAgentsModels(agentsRes.data?.available_models || null); // v2.2.0
      // v4.4: capture DB-driven bindings + providers + task_types + models
      setAiAgentsSystemDefault(agentsRes.data?.system_default_model || 'gemini-3.1-flash-lite-preview');
      setAiAgentsProviders(Array.isArray(agentsRes.data?.providers) ? agentsRes.data.providers : []);
      setAiAgentsTaskTypes(Array.isArray(agentsRes.data?.task_types) ? agentsRes.data.task_types : []);
      setAiAgentsAllModels(Array.isArray(agentsRes.data?.models) ? agentsRes.data.models : []);
      setAiAgentRecentRuns(Array.isArray(runsRes.data?.runs) ? runsRes.data.runs : []); // v2.2.0
      if (failures.length > 0) { // v2.2.0
        setAiAgentsError(`Some AI agent endpoints failed: ${failures.join(' | ')}`); // v2.2.0
      }
    } catch (err: any) { // v2.2.0
      setAiAgentsError(err.response?.data?.error || 'Failed to load AI agents'); // v2.2.0
    } finally { // v2.2.0
      setAiAgentsLoading(false); // v2.2.0
    }
  };

  // v4.4: Save an agent's binding (primary_model / fallback_model / task_type / trust_threshold)
  const handleSaveAgentBinding = async (agentCode: string, payload: any) => {
    setAiAgentBindingSaving(true);
    setAiAgentsError(null);
    try {
      await apiClient.put(`/ai-agents/${agentCode}/binding`, payload);
      setAiAgentBindingSheet(null);
      // Refresh agents so the card reflects the new binding
      await fetchAiAgents();
    } catch (err: any) {
      setAiAgentsError(err.response?.data?.error || 'Failed to save agent binding');
    } finally {
      setAiAgentBindingSaving(false);
    }
  };

  // v4.4: Orchestrator verify-all — runs the verifier on every agent
  const handleVerifyAllAgents = async () => {
    setAiAgentVerifyingAll(true);
    setAiAgentsError(null);
    try {
      const res = await apiClient.post('/ai-agents/verify-all');
      setAiAgentVerifyResult(res.data);
      // Refresh agents so cards reflect new orchestrator_verified flags
      await fetchAiAgents();
    } catch (err: any) {
      setAiAgentsError(err.response?.data?.error || 'Failed to verify agent bindings');
    } finally {
      setAiAgentVerifyingAll(false);
    }
  };

  // v4.4: Orchestrator verify single agent
  const handleVerifyAgent = async (agentCode: string) => {
    setAiAgentsError(null);
    try {
      await apiClient.post(`/ai-agents/${agentCode}/verify`);
      await fetchAiAgents();
    } catch (err: any) {
      setAiAgentsError(err.response?.data?.error || 'Failed to verify agent');
    }
  };

  // v2.2.0: run an AI agent with the current input form state
  const handleAiAgentRun = async (agentCode: string) => { // v2.2.0
    setAiAgentRunning(agentCode); // v2.2.0
    setAiAgentsError(null); // v2.2.0
    try {
      const payload = aiAgentInput[agentCode] || {}; // v2.2.0
      const res = await apiClient.post(`/ai-agents/${agentCode}/run`, payload); // v2.2.0
      setAiAgentResult(prev => ({ ...prev, [agentCode]: res.data })); // v2.2.0
    } catch (err: any) { // v2.2.0
      // v4.4: surface the full error body (with hint + agent_binding) so the
      // card's "Last Run Result" panel shows the actionable hint — e.g. which
      // model was tried, whether its API key is configured, and a link to the
      // API Keys tab.
      const errBody = err.response?.data || { success: false, error: err.response?.data?.error || 'Agent run failed' }; // v2.2.0
      setAiAgentResult(prev => ({ ...prev, [agentCode]: errBody })); // v2.2.0
      // v4.4: show error + hint inline (instead of just "AI agent failed")
      const errMsg = errBody.error || 'Agent run failed';
      const hint = errBody.hint || errBody.message || '';
      setAiAgentsError(hint ? `${errMsg} — ${hint}` : errMsg); // v2.2.0
    } finally { // v2.2.0
      setAiAgentRunning(null); // v2.2.0
      // v2.2.0: refresh recent runs after a short delay (backend write is async)
      setTimeout(() => { // v2.2.0
        apiClient.get('/ai-agents/runs/recent').then(r => setAiAgentRecentRuns(Array.isArray(r.data?.runs) ? r.data.runs : [])).catch(() => {}); // v2.2.0
      }, 500); // v2.2.0
    }
  };

  // v2.2.0: fetch AI agents on first tab activation (guarded by ref)
  useEffect(() => { // v2.2.0
    if (activeTab !== 'ai-agents') return; // v2.2.0
    if (aiAgentsFetchedRef.current) return; // v2.2.0
    aiAgentsFetchedRef.current = true; // v2.2.0
    fetchAiAgents(); // v2.2.0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.2.0

  // v2.2.0: ─── Knowledge Base fetch + search ───
  // v2.2.0: pull documents + papers + graph on first activation; search is on-demand
  const fetchKnowledge = async () => { // v2.2.0
    setKnowledgeLoading(true); // v2.2.0
    setKnowledgeError(null); // v2.2.0
    try {
      const failures: string[] = []; // v2.2.0
      const safeGet = (url: string, fallback: any) => // v2.2.0
        apiClient.get(url).catch((err: any) => { // v2.2.0
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.2.0
          return { data: fallback }; // v2.2.0
        });
      const [docsRes, papersRes, graphRes] = await Promise.all([ // v2.2.0
        safeGet('/knowledge/documents', { documents: [] }), // v2.2.0
        safeGet('/knowledge/papers', { papers: [] }), // v2.2.0
        safeGet('/knowledge/graph', { nodes: [], edges: [] }), // v2.2.0
      ]);
      setKnowledgeDocuments(Array.isArray(docsRes.data?.documents) ? docsRes.data.documents : []); // v2.2.0
      setKnowledgePapers(Array.isArray(papersRes.data?.papers) ? papersRes.data.papers : []); // v2.2.0
      setKnowledgeGraph(graphRes.data || null); // v2.2.0
      if (failures.length > 0) { // v2.2.0
        setKnowledgeError(`Some knowledge endpoints failed: ${failures.join(' | ')}`); // v2.2.0
      }
    } catch (err: any) { // v2.2.0
      setKnowledgeError(err.response?.data?.error || 'Failed to load knowledge base'); // v2.2.0
    } finally { // v2.2.0
      setKnowledgeLoading(false); // v2.2.0
    }
  };

  // v2.2.0: cross-domain search across documents, papers, and chunks
  const handleKnowledgeSearch = async (modeOverride?: any) => { // v2.2.0 (signature extended v2.3.1 — optional mode override; typed as any so it can be used directly as onClick handler without breaking existing button)
    if (!knowledgeQuery.trim()) return; // v2.2.0
    setKnowledgeLoading(true); // v2.2.0
    setKnowledgeError(null); // v2.2.0
    try {
      // v2.3.1: mode-aware URL — keyword (default), vector (AI similarity), hybrid (best of both)
      const searchMode = typeof modeOverride === 'string' ? modeOverride : knowledgeSearchMode; // v2.3.1 — explicit override avoids stale closure right after setKnowledgeSearchMode
      const searchUrl = searchMode === 'vector' ? `/knowledge/search/vector?q=${encodeURIComponent(knowledgeQuery)}` // v2.3.1
        : searchMode === 'hybrid' ? `/knowledge/search/hybrid?q=${encodeURIComponent(knowledgeQuery)}` // v2.3.1
        : `/knowledge/search?q=${encodeURIComponent(knowledgeQuery)}`; // v2.2.0 — keyword (default)
      const res = await apiClient.get(searchUrl); // v2.2.0 (URL mode-aware v2.3.1)
      setKnowledgeResults(Array.isArray(res.data?.results) ? res.data.results : []); // v2.2.0
    } catch (err: any) { // v2.2.0
      setKnowledgeError(err.response?.data?.error || 'Search failed'); // v2.2.0
      setKnowledgeResults([]); // v2.2.0
    } finally { // v2.2.0
      setKnowledgeLoading(false); // v2.2.0
    }
  };

  // v2.2.0: fetch knowledge base on first tab activation (guarded by ref)
  useEffect(() => { // v2.2.0
    if (activeTab !== 'knowledge') return; // v2.2.0
    if (knowledgeFetchedRef.current) return; // v2.2.0
    knowledgeFetchedRef.current = true; // v2.2.0
    fetchKnowledge(); // v2.2.0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.2.0

  // v2.3.1: ─── Knowledge tab — RAG + Entity Extraction + Graph search handlers ───
  // v2.3.1: switch search mode — re-runs search if a query is already present
  const handleKnowledgeSearchModeChange = (mode: 'keyword' | 'vector' | 'hybrid') => { // v2.3.1
    setKnowledgeSearchMode(mode); // v2.3.1
    if (knowledgeQuery.trim()) { // v2.3.1 — re-run search with new mode if query exists
      handleKnowledgeSearch(mode); // v2.3.1 — pass mode explicitly to avoid stale closure
    }
  }; // v2.3.1

  // v2.3.1: trigger RAG pipeline on a document (POST /knowledge/documents/:id/process)
  // v4.4.3: surface PARTIAL status + error message from the pipeline (e.g. "no embedding API key configured")
  // v4.4.5: use apiClient.post() with a per-request timeout override (180s) — preserves cookie auth.
  const handleRagProcess = async (documentId: string) => { // v2.3.1
    setKnowledgeProcessing(prev => new Set(prev).add(documentId)); // v2.3.1
    setKnowledgeProcessingResult(prev => ({ ...prev, [documentId]: null })); // v2.3.1 — clear previous result
    setKnowledgeError(null); // v2.3.1
    try { // v2.3.1
      // v4.4.5: pass `{ timeout: 180000 }` as the 3rd arg — overrides default 120s for THIS request only.
      const res = await apiClient.post(`/knowledge/documents/${documentId}/process`, {}, { timeout: 180000 });
      const data = res.data || {};
      // v4.4.3: PARTIAL status means chunks were created but embeddings/entities failed
      const isPartial = data.status === 'PARTIAL';
      const isFailed = data.status === 'FAILED';
      setKnowledgeProcessingResult(prev => ({ ...prev, [documentId]: { ok: !isFailed, data, partial: isPartial } }));
      if (isFailed) {
        setKnowledgeError(data.error || 'RAG processing failed');
      } else if (isPartial) {
        // Don't show the big red banner for PARTIAL — the per-document result panel shows the detail.
        // Just log to console for visibility.
        console.warn(`[RAG] Document ${documentId} partial:`, data.error);
      }
      fetchKnowledge(); // v2.3.1 — refresh document list to surface new chunks + status
    } catch (err: any) { // v2.3.1
      const errBody = err.response?.data || {};
      const isAuth = err.response?.status === 401;
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      const msg = isAuth ? 'Authentication required — please log in again.'
                : isTimeout ? 'RAG processing timed out (180s). The AI provider may be rate-limited. Try again in 60 seconds.'
                : (errBody.error || 'RAG processing failed');
      setKnowledgeProcessingResult(prev => ({ ...prev, [documentId]: { ok: false, error: msg } })); // v2.3.1
      if (!isAuth) {
        setKnowledgeError(msg); // v2.3.1
      }
    } finally { // v2.3.1
      setKnowledgeProcessing(prev => { const n = new Set(prev); n.delete(documentId); return n; }); // v2.3.1
    }
  }; // v2.3.1

  // v2.3.1: trigger entity extraction on a document (POST /knowledge/graph/extract/:documentId)
  // v4.4.5: use apiClient.post() with a per-request timeout override (180s).
  // The default apiClient timeout is 120s which is too short when Gemini is rate-limited.
  // Using apiClient (not raw axios) preserves cookie-based auth + interceptors.
  const handleEntityExtract = async (documentId: string) => { // v2.3.1
    setKnowledgeExtracting(prev => new Set(prev).add(documentId)); // v2.3.1
    setKnowledgeExtractResult(prev => ({ ...prev, [documentId]: null })); // v2.3.1 — clear previous result
    setKnowledgeError(null); // v2.3.1
    try { // v2.3.1
      // v4.4.5: pass `{ timeout: 180000 }` as the 3rd arg — axios merges this into the request config,
      // overriding the default 120s timeout for THIS request only. Cookie auth is preserved.
      const res = await apiClient.post(`/knowledge/graph/extract/${documentId}`, {}, { timeout: 180000 });
      setKnowledgeExtractResult(prev => ({ ...prev, [documentId]: { ok: true, data: res.data } })); // v2.3.1
      fetchKnowledge(); // v2.3.1 — refresh graph + document list to surface new nodes/edges
      // v2.3.1: refresh graph stats after extraction (best-effort, fire-and-forget)
      apiClient.get('/knowledge/graph/stats').then(r => setKnowledgeGraphStats(r.data)).catch(() => {}); // v2.3.1
    } catch (err: any) { // v2.3.1
      const errMsg = err.response?.data?.error || err.message || 'Entity extraction failed';
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      const isAuth = err.response?.status === 401;
      setKnowledgeExtractResult(prev => ({ ...prev, [documentId]: { ok: false, error: isAuth ? 'Authentication required — please log in again.' : isTimeout ? 'Extraction timed out (180s). The AI provider may be rate-limited. Try again in 60 seconds, or configure additional free API keys (Groq, OpenRouter) in Admin → API Keys to spread load.' : errMsg } })); // v2.3.1
      if (!isAuth) {
        setKnowledgeError(isTimeout ? 'Extraction timed out — AI provider rate-limited. Wait 60s and try again, or add more API keys.' : errMsg); // v2.3.1
      }
    } finally { // v2.3.1
      setKnowledgeExtracting(prev => { const n = new Set(prev); n.delete(documentId); return n; }); // v2.3.1
    }
  }; // v2.3.1

  // v2.3.1: find related entities for a given entity name (GET /knowledge/graph/search?entity=...)
  const handleGraphEntitySearch = async () => { // v2.3.1
    if (!knowledgeGraphSearch.trim()) return; // v2.3.1
    setKnowledgeLoading(true); // v2.3.1
    setKnowledgeError(null); // v2.3.1
    try { // v2.3.1
      const res = await apiClient.get(`/knowledge/graph/search?entity=${encodeURIComponent(knowledgeGraphSearch)}`); // v2.3.1
      setKnowledgeGraphSearchResult(res.data || null); // v2.3.1
    } catch (err: any) { // v2.3.1
      setKnowledgeError(err.response?.data?.error || 'Entity search failed'); // v2.3.1
      setKnowledgeGraphSearchResult(null); // v2.3.1
    } finally { // v2.3.1
      setKnowledgeLoading(false); // v2.3.1
    }
  }; // v2.3.1

  // v2.3.1: fetch graph stats when graph sub-tab is opened (separate effect to avoid disturbing existing fetchKnowledge)
  useEffect(() => { // v2.3.1
    if (activeTab !== 'knowledge') return; // v2.3.1
    if (knowledgeTab !== 'graph') return; // v2.3.1
    apiClient.get('/knowledge/graph/stats').then(r => setKnowledgeGraphStats(r.data)).catch(() => {}); // v2.3.1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, knowledgeTab]); // v2.3.1

  // v4.4.6: Knowledge Q&A — ask the KNOWLEDGE agent (SVC-024) a question.
  // The agent uses hybridSearch (vector + keyword + graph) to retrieve relevant
  // chunks, then calls callModel('long_reasoning') which walks the ranked_models
  // chain (DeepSeek → Grok → Groq → ... → Gemini default). Returns answer +
  // sources + confidence + retrieved_sources with citations.
  const handleKnowledgeQa = async () => {
    if (!knowledgeQaQuestion.trim()) return;
    setKnowledgeQaLoading(true);
    setKnowledgeError(null);
    try {
      // v4.4.6: 180s timeout — the agent does hybrid search + LLM call
      const res = await apiClient.post('/ai-agents/KNOWLEDGE/run', { question: knowledgeQaQuestion }, { timeout: 180000 });
      setKnowledgeQaAnswer(res.data);
    } catch (err: any) {
      const isAuth = err.response?.status === 401;
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      const msg = isAuth ? 'Authentication required — please log in again.'
                : isTimeout ? 'Knowledge Q&A timed out (180s). The AI provider may be rate-limited. Try again in 60 seconds.'
                : (err.response?.data?.error || err.response?.data?.message || 'Knowledge Q&A failed');
      setKnowledgeQaAnswer({ success: false, error: msg });
      if (!isAuth) setKnowledgeError(msg);
    } finally {
      setKnowledgeQaLoading(false);
    }
  };

  // v2.1.1 (A3 fix): create a new prompt version (DRAFT status)
  const handleCreateAiGovPrompt = async () => { // v2.1.1 (A3 fix)
    if (!aiGovForm.prompt_key || !aiGovForm.content) { // v2.1.1 (A3 fix)
      setAiGovError('Prompt key and content are required.'); // v2.1.1 (A3 fix)
      return; // v2.1.1 (A3 fix)
    }
    setAiGovSubmitting(true); setAiGovError(null); setAiGovSuccess(null); // v2.1.1 (A3 fix)
    try {
      await apiClient.post('/ai-governance/prompts', { // v2.1.1 (A3 fix)
        prompt_key: aiGovForm.prompt_key, // v2.1.1 (A3 fix)
        content: aiGovForm.content, // v2.1.1 (A3 fix)
        description: aiGovForm.description || undefined, // v2.1.1 (A3 fix)
      });
      setAiGovSuccess(`Prompt version created for key "${aiGovForm.prompt_key}" (DRAFT). Approve to activate.`); // v2.1.1 (A3 fix)
      setAiGovForm({ prompt_key: '', content: '', description: '' }); // v2.1.1 (A3 fix)
      setTimeout(() => { setShowAiGovCreateModal(false); setAiGovSuccess(null); }, 1500); // v2.1.1 (A3 fix)
      fetchAiGovernance(); // v2.1.1 (A3 fix) — refresh table
    } catch (err: any) { // v2.1.1 (A3 fix)
      setAiGovError(err.response?.data?.error || 'Failed to create prompt version'); // v2.1.1 (A3 fix)
    } finally { setAiGovSubmitting(false); } // v2.1.1 (A3 fix)
  };

  // v2.1.1 (A3 fix): approve a prompt version (promotes to ACTIVE, retires previous ACTIVE for same key)
  const handleApproveAiGovPrompt = async (promptId: string) => { // v2.1.1 (A3 fix)
    if (!confirm('Approve this prompt version? It will become ACTIVE and the previous ACTIVE version for this key will be RETIRED.')) return; // v2.1.1 (A3 fix)
    setAiGovError(null); setAiGovSuccess(null); // v2.1.1 (A3 fix)
    try {
      await apiClient.post(`/ai-governance/prompts/${promptId}/approve`); // v2.1.1 (A3 fix)
      setAiGovSuccess('Prompt approved and activated.'); // v2.1.1 (A3 fix)
      setTimeout(() => setAiGovSuccess(null), 2500); // v2.1.1 (A3 fix)
      fetchAiGovernance(); // v2.1.1 (A3 fix)
    } catch (err: any) { // v2.1.1 (A3 fix)
      setAiGovError(err.response?.data?.error || 'Failed to approve prompt'); // v2.1.1 (A3 fix)
    }
  };

  // v2.1.1 (A3 fix): retire a prompt version (no longer used for new inferences)
  const handleRetireAiGovPrompt = async (promptId: string) => { // v2.1.1 (A3 fix)
    if (!confirm('Retire this prompt version? It will no longer be used for new inferences.')) return; // v2.1.1 (A3 fix)
    setAiGovError(null); setAiGovSuccess(null); // v2.1.1 (A3 fix)
    try {
      await apiClient.post(`/ai-governance/prompts/${promptId}/retire`); // v2.1.1 (A3 fix)
      setAiGovSuccess('Prompt retired.'); // v2.1.1 (A3 fix)
      setTimeout(() => setAiGovSuccess(null), 2500); // v2.1.1 (A3 fix)
      fetchAiGovernance(); // v2.1.1 (A3 fix)
    } catch (err: any) { // v2.1.1 (A3 fix)
      setAiGovError(err.response?.data?.error || 'Failed to retire prompt'); // v2.1.1 (A3 fix)
    }
  };

  // v2.5: ═══════════ Pipeline tab fetch + execute ═══════════
  // v2.5: pull pipeline stats + recent runs + all 30 stages reference table
  const fetchPipeline = async () => { // v2.5
    setPipelineLoading(true); // v2.5
    setPipelineError(null); // v2.5
    try { // v2.5
      const failures: string[] = []; // v2.5
      const safeGet = (url: string, fallback: any) => // v2.5
        apiClient.get(url).catch((err: any) => { // v2.5
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.5
          return { data: fallback }; // v2.5
        });
      const [statsRes, runsRes, stagesRes] = await Promise.all([ // v2.5
        safeGet('/pipeline/stats', { stats: null }), // v2.5
        safeGet('/pipeline/runs?limit=20', { runs: [] }), // v2.5
        safeGet('/pipeline/stages', { stages: [] }), // v2.5
      ]);
      setPipelineStats(statsRes.data?.stats || statsRes.data || null); // v2.5
      setPipelineRuns(Array.isArray(runsRes.data?.runs) ? runsRes.data.runs : Array.isArray(runsRes.data) ? runsRes.data : []); // v2.5
      setPipelineStages(Array.isArray(stagesRes.data?.stages) ? stagesRes.data.stages : Array.isArray(stagesRes.data) ? stagesRes.data : []); // v2.5
      if (failures.length > 0) { // v2.5
        setPipelineError(`Some pipeline endpoints failed: ${failures.join(' | ')}`); // v2.5
      }
    } catch (err: any) { // v2.5
      setPipelineError(err.response?.data?.error || 'Failed to load pipeline data'); // v2.5
    } finally { // v2.5
      setPipelineLoading(false); // v2.5
    }
  }; // v2.5

  // v2.5: trigger a manual pipeline execution (POST /api/pipeline/execute with agent_code + input JSON)
  const handlePipelineExecute = async () => { // v2.5
    if (!pipelineExecForm.agent_code) { // v2.5
      setPipelineError('Agent code is required to execute a pipeline.'); // v2.5
      return; // v2.5
    }
    let parsedInput: any = {}; // v2.5
    try { // v2.5
      parsedInput = JSON.parse(pipelineExecForm.input || '{}'); // v2.5
    } catch { // v2.5
      setPipelineError('Input must be valid JSON.'); // v2.5
      return; // v2.5
    }
    setPipelineExecuting(true); // v2.5
    setPipelineError(null); // v2.5
    setPipelineExecuteResult(null); // v2.5
    try { // v2.5
      const res = await apiClient.post('/pipeline/execute', { agent_code: pipelineExecForm.agent_code, input: parsedInput }); // v2.5
      setPipelineExecuteResult({ ok: true, data: res.data }); // v2.5
      fetchPipeline(); // v2.5 — refresh stats + runs
    } catch (err: any) { // v2.5
      setPipelineExecuteResult({ ok: false, error: err.response?.data?.error || 'Pipeline execution failed' }); // v2.5
      setPipelineError(err.response?.data?.error || 'Pipeline execution failed'); // v2.5
    } finally { // v2.5
      setPipelineExecuting(false); // v2.5
    }
  }; // v2.5

  // v2.5: fetch pipeline on first tab activation (guarded by ref)
  useEffect(() => { // v2.5
    if (activeTab !== 'pipeline') return; // v2.5
    if (pipelineFetchedRef.current) return; // v2.5
    pipelineFetchedRef.current = true; // v2.5
    fetchPipeline(); // v2.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.5

  // v2.5: ═══════════ AI Gov V2 tab fetch + resolve violation + run benchmark ═══════════
  // v2.5: pull extended metrics + provenance + policy rules + violations + benchmarks
  const fetchAiGovV2 = async () => { // v2.5
    setAiGovV2Loading(true); // v2.5
    setAiGovV2Error(null); // v2.5
    try { // v2.5
      const failures: string[] = []; // v2.5
      const safeGet = (url: string, fallback: any) => // v2.5
        apiClient.get(url).catch((err: any) => { // v2.5
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.5
          return { data: fallback }; // v2.5
        });
      const [metricsRes, provenanceRes, rulesRes, violationsRes, benchmarksRes] = await Promise.all([ // v2.5
        safeGet('/ai-governance-v2/metrics/extended', { metrics: null }), // v2.5
        safeGet('/ai-governance-v2/provenance?limit=20', { provenance: [] }), // v2.5
        safeGet('/ai-governance-v2/policy/rules', { rules: [] }), // v2.5
        safeGet('/ai-governance-v2/policy/violations?limit=20', { violations: [] }), // v2.5
        safeGet('/ai-governance-v2/benchmarks', { benchmarks: [] }), // v2.5
      ]);
      setAiGovV2Metrics(metricsRes.data?.metrics || metricsRes.data || null); // v2.5
      setAiGovV2Provenance(Array.isArray(provenanceRes.data?.provenance) ? provenanceRes.data.provenance : Array.isArray(provenanceRes.data) ? provenanceRes.data : []); // v2.5
      setAiGovV2Rules(Array.isArray(rulesRes.data?.rules) ? rulesRes.data.rules : Array.isArray(rulesRes.data) ? rulesRes.data : []); // v2.5
      setAiGovV2Violations(Array.isArray(violationsRes.data?.violations) ? violationsRes.data.violations : Array.isArray(violationsRes.data) ? violationsRes.data : []); // v2.5
      setAiGovV2Benchmarks(Array.isArray(benchmarksRes.data?.benchmarks) ? benchmarksRes.data.benchmarks : Array.isArray(benchmarksRes.data) ? benchmarksRes.data : []); // v2.5
      if (failures.length > 0) { // v2.5
        setAiGovV2Error(`Some AI Governance V2 endpoints failed: ${failures.join(' | ')}`); // v2.5
      }
    } catch (err: any) { // v2.5
      setAiGovV2Error(err.response?.data?.error || 'Failed to load AI Governance V2 data'); // v2.5
    } finally { // v2.5
      setAiGovV2Loading(false); // v2.5
    }
  }; // v2.5

  // v2.5: resolve a policy violation (POST /ai-governance-v2/policy/violations/:id/resolve)
  const handleAiGovV2ResolveViolation = async (violationId: string) => { // v2.5
    if (!confirm(`Resolve policy violation ${String(violationId).slice(0, 8)}…?`)) return; // v2.5
    setAiGovV2Resolving(violationId); // v2.5
    setAiGovV2Error(null); setAiGovV2Success(null); // v2.5
    try { // v2.5
      await apiClient.post(`/ai-governance-v2/policy/violations/${violationId}/resolve`, {}); // v2.5
      setAiGovV2Success(`Violation ${String(violationId).slice(0, 8)}… resolved.`); // v2.5
      setTimeout(() => setAiGovV2Success(null), 2500); // v2.5
      fetchAiGovV2(); // v2.5
    } catch (err: any) { // v2.5
      setAiGovV2Error(err.response?.data?.error || 'Failed to resolve violation'); // v2.5
    } finally { // v2.5
      setAiGovV2Resolving(null); // v2.5
    }
  }; // v2.5

  // v2.5: run a benchmark suite (POST /ai-governance-v2/benchmarks/:id/run with model_code)
  const handleAiGovV2RunBenchmark = async (benchmarkId: string) => { // v2.5
    const modelCode = aiGovV2BenchModelInput[benchmarkId] || ''; // v2.5
    if (!modelCode) { // v2.5
      setAiGovV2Error('Enter a model_code before running the benchmark.'); // v2.5
      return; // v2.5
    }
    if (!confirm(`Run benchmark ${String(benchmarkId).slice(0, 8)}… with model "${modelCode}"?`)) return; // v2.5
    setAiGovV2RunningBench(benchmarkId); // v2.5
    setAiGovV2Error(null); setAiGovV2Success(null); // v2.5
    try { // v2.5
      const res = await apiClient.post(`/ai-governance-v2/benchmarks/${benchmarkId}/run`, { model_code: modelCode }); // v2.5
      setAiGovV2Success(`Benchmark "${res.data?.name || benchmarkId}" started.`); // v2.5
      setTimeout(() => setAiGovV2Success(null), 3000); // v2.5
      fetchAiGovV2(); // v2.5
    } catch (err: any) { // v2.5
      setAiGovV2Error(err.response?.data?.error || 'Failed to run benchmark'); // v2.5
    } finally { // v2.5
      setAiGovV2RunningBench(null); // v2.5
    }
  }; // v2.5

  // v2.5: fetch AI Gov V2 on first tab activation (guarded by ref)
  useEffect(() => { // v2.5
    if (activeTab !== 'ai-gov-v2') return; // v2.5
    if (aiGovV2FetchedRef.current) return; // v2.5
    aiGovV2FetchedRef.current = true; // v2.5
    fetchAiGovV2(); // v2.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.5

  // v2.5: ═══════════ ECOS tab fetch + execute skill + run evolution ═══════════
  // v2.5: pull agents + health summary + skills + evolution findings + costs summary + recommendations
  const fetchEcos = async () => { // v2.5
    setEcosLoading(true); // v2.5
    setEcosError(null); // v2.5
    try { // v2.5
      const failures: string[] = []; // v2.5
      const safeGet = (url: string, fallback: any) => // v2.5
        apiClient.get(url).catch((err: any) => { // v2.5
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.5
          return { data: fallback }; // v2.5
        });
      const [agentsRes, healthRes, skillsRes, findingsRes, costsRes, recsRes] = await Promise.all([ // v2.5
        safeGet('/ecos/agents', { agents: [] }), // v2.5
        safeGet('/ecos/agents/health/summary', { summary: null }), // v2.5
        safeGet('/ecos/skills', { skills: [] }), // v2.5
        safeGet('/ecos/evolution/findings?limit=20', { findings: [] }), // v2.5
        safeGet('/ecos/costs/summary?days=7', { summary: null }), // v2.5
        safeGet('/ecos/costs/recommendations', { recommendations: [] }), // v2.5
      ]);
      setEcosAgents(Array.isArray(agentsRes.data?.agents) ? agentsRes.data.agents : Array.isArray(agentsRes.data) ? agentsRes.data : []); // v2.5
      setEcosHealth(healthRes.data?.summary || healthRes.data || null); // v2.5
      setEcosSkills(Array.isArray(skillsRes.data?.skills) ? skillsRes.data.skills : Array.isArray(skillsRes.data) ? skillsRes.data : []); // v2.5
      setEcosFindings(Array.isArray(findingsRes.data?.findings) ? findingsRes.data.findings : Array.isArray(findingsRes.data) ? findingsRes.data : []); // v2.5
      setEcosCosts(costsRes.data?.summary || costsRes.data || null); // v2.5
      setEcosCostRecs(Array.isArray(recsRes.data?.recommendations) ? recsRes.data.recommendations : Array.isArray(recsRes.data) ? recsRes.data : []); // v2.5
      if (failures.length > 0) { // v2.5
        setEcosError(`Some ECOS endpoints failed: ${failures.join(' | ')}`); // v2.5
      }
    } catch (err: any) { // v2.5
      setEcosError(err.response?.data?.error || 'Failed to load ECOS data'); // v2.5
    } finally { // v2.5
      setEcosLoading(false); // v2.5
    }
  }; // v2.5

  // v2.5: execute a skill (POST /ecos/skills/:id/execute with input JSON)
  const handleEcosExecuteSkill = async (skillId: string) => { // v2.5
    let parsedInput: any = {}; // v2.5
    try { // v2.5
      parsedInput = JSON.parse(ecosSkillInput[skillId] || '{}'); // v2.5
    } catch { // v2.5
      setEcosError(`Invalid JSON input for skill ${String(skillId).slice(0, 8)}…`); // v2.5
      return; // v2.5
    }
    setEcosExecutingSkill(skillId); // v2.5
    setEcosError(null); setEcosSuccess(null); // v2.5
    try { // v2.5
      const res = await apiClient.post(`/ecos/skills/${skillId}/execute`, { input: parsedInput }); // v2.5
      setEcosSuccess(`Skill ${String(skillId).slice(0, 8)}… executed successfully.`); // v2.5
      setTimeout(() => setEcosSuccess(null), 3000); // v2.5
      fetchEcos(); // v2.5 — refresh skill stats
      // v2.5: stash result on window for debugging
      (window as any).__lastEcosSkillResult = res.data; // v2.5
    } catch (err: any) { // v2.5
      setEcosError(err.response?.data?.error || 'Skill execution failed'); // v2.5
    } finally { // v2.5
      setEcosExecutingSkill(null); // v2.5
    }
  }; // v2.5

  // v2.5: run knowledge evolution processes (POST /ecos/evolution/run)
  const handleEcosRunEvolution = async () => { // v2.5
    if (!confirm('Run knowledge evolution processes? This will analyze recent activity and emit new findings.')) return; // v2.5
    setEcosRunningEvolution(true); // v2.5
    setEcosError(null); setEcosSuccess(null); // v2.5
    try { // v2.5
      const res = await apiClient.post('/ecos/evolution/run', {}); // v2.5
      setEcosSuccess(`Evolution complete: ${res.data?.findings_emitted ?? res.data?.findings ?? 0} new findings.`); // v2.5
      setTimeout(() => setEcosSuccess(null), 3500); // v2.5
      fetchEcos(); // v2.5 — refresh findings
    } catch (err: any) { // v2.5
      setEcosError(err.response?.data?.error || 'Evolution run failed'); // v2.5
    } finally { // v2.5
      setEcosRunningEvolution(false); // v2.5
    }
  }; // v2.5

  // v2.5: fetch ECOS on first tab activation (guarded by ref)
  useEffect(() => { // v2.5
    if (activeTab !== 'ecos') return; // v2.5
    if (ecosFetchedRef.current) return; // v2.5
    ecosFetchedRef.current = true; // v2.5
    fetchEcos(); // v2.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.5

  // v2.5: ═══════════ Durability tab fetch + reset breaker + resolve approval + auto-recover ═══════════
  // v2.5: pull circuit breakers + pending approvals + compensation stats + crashed workflows
  const fetchDurability = async () => { // v2.5
    setDurabilityLoading(true); // v2.5
    setDurabilityError(null); // v2.5
    try { // v2.5
      const failures: string[] = []; // v2.5
      const safeGet = (url: string, fallback: any) => // v2.5
        apiClient.get(url).catch((err: any) => { // v2.5
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.5
          return { data: fallback }; // v2.5
        });
      const [breakersRes, approvalsRes, compRes, crashedRes] = await Promise.all([ // v2.5
        safeGet('/durability/circuit-breakers', { breakers: [] }), // v2.5
        safeGet('/durability/approvals/pending', { approvals: [] }), // v2.5
        safeGet('/durability/compensations/stats/summary', { summary: null }), // v2.5
        safeGet('/durability/crashed', { crashed: [] }), // v2.5
      ]);
      setDurabilityBreakers(Array.isArray(breakersRes.data?.breakers) ? breakersRes.data.breakers : Array.isArray(breakersRes.data) ? breakersRes.data : []); // v2.5
      setDurabilityApprovals(Array.isArray(approvalsRes.data?.approvals) ? approvalsRes.data.approvals : Array.isArray(approvalsRes.data) ? approvalsRes.data : []); // v2.5
      setDurabilityCompensations(compRes.data?.summary || compRes.data || null); // v2.5
      setDurabilityCrashed(Array.isArray(crashedRes.data?.crashed) ? crashedRes.data.crashed : Array.isArray(crashedRes.data) ? crashedRes.data : []); // v2.5
      if (failures.length > 0) { // v2.5
        setDurabilityError(`Some durability endpoints failed: ${failures.join(' | ')}`); // v2.5
      }
    } catch (err: any) { // v2.5
      setDurabilityError(err.response?.data?.error || 'Failed to load durability data'); // v2.5
    } finally { // v2.5
      setDurabilityLoading(false); // v2.5
    }
  }; // v2.5

  // v2.5: reset a circuit breaker (POST /durability/circuit-breakers/:serviceCode/reset)
  const handleDurabilityResetBreaker = async (serviceCode: string) => { // v2.5
    if (!confirm(`Reset circuit breaker for service "${serviceCode}"? This will close the breaker and allow traffic again.`)) return; // v2.5
    setDurabilityResetting(serviceCode); // v2.5
    setDurabilityError(null); setDurabilitySuccess(null); // v2.5
    try { // v2.5
      await apiClient.post(`/durability/circuit-breakers/${serviceCode}/reset`, {}); // v2.5
      setDurabilitySuccess(`Circuit breaker for "${serviceCode}" reset to CLOSED.`); // v2.5
      setTimeout(() => setDurabilitySuccess(null), 2500); // v2.5
      fetchDurability(); // v2.5
    } catch (err: any) { // v2.5
      setDurabilityError(err.response?.data?.error || 'Failed to reset circuit breaker'); // v2.5
    } finally { // v2.5
      setDurabilityResetting(null); // v2.5
    }
  }; // v2.5

  // v2.5: resolve a pending approval (POST /durability/approvals/:id/resolve with decision)
  const handleDurabilityResolveApproval = async (approvalId: string, decision: 'APPROVED' | 'REJECTED') => { // v2.5
    if (!confirm(`${decision === 'APPROVED' ? 'Approve' : 'Reject'} this request?`)) return; // v2.5
    setDurabilityResolvingApproval(approvalId); // v2.5
    setDurabilityError(null); setDurabilitySuccess(null); // v2.5
    try { // v2.5
      await apiClient.post(`/durability/approvals/${approvalId}/resolve`, { decision }); // v2.5
      setDurabilitySuccess(`Approval ${String(approvalId).slice(0, 8)}… ${decision.toLowerCase()}.`); // v2.5
      setTimeout(() => setDurabilitySuccess(null), 2500); // v2.5
      fetchDurability(); // v2.5
    } catch (err: any) { // v2.5
      setDurabilityError(err.response?.data?.error || 'Failed to resolve approval'); // v2.5
    } finally { // v2.5
      setDurabilityResolvingApproval(null); // v2.5
    }
  }; // v2.5

  // v2.5: auto-recover crashed workflows (POST /durability/auto-recover)
  const handleDurabilityAutoRecover = async () => { // v2.5
    if (!confirm('Trigger auto-recovery for all crashed workflows? This will resume or compensate each one.')) return; // v2.5
    setDurabilityAutoRecovering(true); // v2.5
    setDurabilityError(null); setDurabilitySuccess(null); // v2.5
    try { // v2.5
      const res = await apiClient.post('/durability/auto-recover', {}); // v2.5
      setDurabilitySuccess(`Auto-recovery complete: ${res.data?.recovered ?? 0} recovered, ${res.data?.compensated ?? 0} compensated.`); // v2.5
      setTimeout(() => setDurabilitySuccess(null), 4000); // v2.5
      fetchDurability(); // v2.5
    } catch (err: any) { // v2.5
      setDurabilityError(err.response?.data?.error || 'Auto-recovery failed'); // v2.5
    } finally { // v2.5
      setDurabilityAutoRecovering(false); // v2.5
    }
  }; // v2.5

  // v2.5: fetch Durability on first tab activation (guarded by ref)
  useEffect(() => { // v2.5
    if (activeTab !== 'durability') return; // v2.5
    if (durabilityFetchedRef.current) return; // v2.5
    durabilityFetchedRef.current = true; // v2.5
    fetchDurability(); // v2.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.5

  // v2.5: ═══════════ Regulatory V2 tab fetch + create token ═══════════
  // v2.5: pull tokens + access logs from /api/regulatory/*
  const fetchRegV2 = async () => { // v2.5
    setRegV2Loading(true); // v2.5
    setRegV2Error(null); // v2.5
    try { // v2.5
      const failures: string[] = []; // v2.5
      const safeGet = (url: string, fallback: any) => // v2.5
        apiClient.get(url).catch((err: any) => { // v2.5
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.5
          return { data: fallback }; // v2.5
        });
      const [tokensRes, logsRes] = await Promise.all([ // v2.5
        safeGet('/regulatory/tokens', { tokens: [] }), // v2.5
        safeGet('/regulatory/logs?limit=50', { logs: [] }), // v2.5
      ]);
      setRegV2Tokens(Array.isArray(tokensRes.data?.tokens) ? tokensRes.data.tokens : Array.isArray(tokensRes.data) ? tokensRes.data : []); // v2.5
      setRegV2Logs(Array.isArray(logsRes.data?.logs) ? logsRes.data.logs : Array.isArray(logsRes.data) ? logsRes.data : []); // v2.5
      if (failures.length > 0) { // v2.5
        setRegV2Error(`Some regulatory endpoints failed: ${failures.join(' | ')}`); // v2.5
      }
    } catch (err: any) { // v2.5
      setRegV2Error(err.response?.data?.error || 'Failed to load regulatory data'); // v2.5
    } finally { // v2.5
      setRegV2Loading(false); // v2.5
    }
  }; // v2.5

  // v2.5: create a new regulatory token (POST /regulatory/tokens with authority + scope)
  const handleRegV2CreateToken = async () => { // v2.5
    if (!regV2Form.authority) { // v2.5
      setRegV2Error('Authority is required to create a token.'); // v2.5
      return; // v2.5
    }
    setRegV2Loading(true); // v2.5
    setRegV2Error(null); setRegV2Success(null); // v2.5
    try { // v2.5
      const res = await apiClient.post('/regulatory/tokens', { authority: regV2Form.authority, scope: regV2Form.scope }); // v2.5
      setRegV2NewToken(res.data?.raw_token || res.data?.token || null); // v2.5
      setRegV2Success(`Token created for ${regV2Form.authority}.`); // v2.5
      setTimeout(() => setRegV2Success(null), 4000); // v2.5
      setRegV2ShowCreate(false); // v2.5
      fetchRegV2(); // v2.5 — refresh token list
    } catch (err: any) { // v2.5
      setRegV2Error(err.response?.data?.error || 'Failed to create token'); // v2.5
    } finally { // v2.5
      setRegV2Loading(false); // v2.5
    }
  }; // v2.5

  // v2.5: fetch Regulatory V2 on first tab activation (guarded by ref)
  useEffect(() => { // v2.5
    if (activeTab !== 'regulatory-v2') return; // v2.5
    if (regV2FetchedRef.current) return; // v2.5
    regV2FetchedRef.current = true; // v2.5
    fetchRegV2(); // v2.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.5

  // v2.5: ═══════════ SMS tab fetch + send ═══════════
  // v2.5: pull templates + history from /api/sms/*
  const fetchSms = async () => { // v2.5
    setSmsLoading(true); // v2.5
    setSmsError(null); // v2.5
    try { // v2.5
      const failures: string[] = []; // v2.5
      const safeGet = (url: string, fallback: any) => // v2.5
        apiClient.get(url).catch((err: any) => { // v2.5
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.5
          return { data: fallback }; // v2.5
        });
      const [templatesRes, historyRes] = await Promise.all([ // v2.5
        safeGet('/sms/templates', { templates: [] }), // v2.5
        safeGet('/sms/history?limit=50', { messages: [] }), // v2.5
      ]);
      setSmsTemplates(Array.isArray(templatesRes.data?.templates) ? templatesRes.data.templates : Array.isArray(templatesRes.data) ? templatesRes.data : []); // v2.5
      setSmsHistory(Array.isArray(historyRes.data?.messages) ? historyRes.data.messages : Array.isArray(historyRes.data?.history) ? historyRes.data.history : Array.isArray(historyRes.data) ? historyRes.data : []); // v2.5
      if (failures.length > 0) { // v2.5
        setSmsError(`Some SMS endpoints failed: ${failures.join(' | ')}`); // v2.5
      }
    } catch (err: any) { // v2.5
      setSmsError(err.response?.data?.error || 'Failed to load SMS data'); // v2.5
    } finally { // v2.5
      setSmsLoading(false); // v2.5
    }
  }; // v2.5

  // v2.5: send a test SMS (POST /sms/send with phone + template_id)
  const handleSmsSend = async () => { // v2.5
    if (!smsSendForm.phone || !smsSendForm.template_id) { // v2.5
      setSmsError('Phone number and template are both required.'); // v2.5
      return; // v2.5
    }
    setSmsSending(true); // v2.5
    setSmsError(null); setSmsSuccess(null); // v2.5
    try { // v2.5
      const res = await apiClient.post('/sms/send', { phone: smsSendForm.phone, template_id: smsSendForm.template_id }); // v2.5
      setSmsSuccess(`SMS sent! Cost: $${res.data?.cost_usd ?? '0.0585'}`); // v2.5
      setTimeout(() => setSmsSuccess(null), 4000); // v2.5
      setSmsSendForm({ phone: '', template_id: '' }); // v2.5
      fetchSms(); // v2.5 — refresh history
    } catch (err: any) { // v2.5
      setSmsError(err.response?.data?.error || 'Failed to send SMS'); // v2.5
    } finally { // v2.5
      setSmsSending(false); // v2.5
    }
  }; // v2.5

  // v2.5: fetch SMS on first tab activation (guarded by ref)
  useEffect(() => { // v2.5
    if (activeTab !== 'sms') return; // v2.5
    if (smsFetchedRef.current) return; // v2.5
    smsFetchedRef.current = true; // v2.5
    fetchSms(); // v2.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // v2.5

  // v2.7: fetch dependency graph (nodes + edges) on first tab activation (guarded by ref)
  const fetchDepGraph = async () => { // v2.7
    setDepGraphLoading(true); // v2.7
    setDepGraphError(null); // v2.7
    try { // v2.7
      const failures: string[] = []; // v2.7
      const safeGet = (url: string, fallback: any) => // v2.7
        apiClient.get(url).catch((err: any) => { // v2.7
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`); // v2.7
          return { data: fallback }; // v2.7
        }); // v2.7
      // GET /dependency-graph returns { nodes: [], edges: [] } (migration 055) // v2.7
      const res = await safeGet('/dependency-graph', { nodes: [], edges: [] }); // v2.7
      const nodesRaw = res.data?.nodes ?? res.data?.data?.nodes ?? (Array.isArray(res.data) ? res.data : []); // v2.7
      const edgesRaw = res.data?.edges ?? res.data?.data?.edges ?? []; // v2.7
      setDepGraphNodes(Array.isArray(nodesRaw) ? nodesRaw : []); // v2.7
      setDepGraphEdges(Array.isArray(edgesRaw) ? edgesRaw : []); // v2.7
      if (failures.length > 0) { // v2.7
        setDepGraphError(`Some dependency-graph endpoints failed: ${failures.join(' | ')}`); // v2.7
      }
    } catch (err: any) { // v2.7
      setDepGraphError(err.response?.data?.error || 'Failed to load dependency graph'); // v2.7
    } finally { // v2.7
      setDepGraphLoading(false); // v2.7
    }
  }; // v2.7

  // v2.7: "What if?" impact analysis — type a node key → GET /dependency-graph/impact/:key → list affected nodes
  const handleDepGraphImpact = async () => { // v2.7
    const key = depGraphImpactKey.trim(); // v2.7
    if (!key) { // v2.7
      setDepGraphImpactError('Enter a node key first.'); // v2.7
      return; // v2.7
    }
    setDepGraphImpactLoading(true); // v2.7
    setDepGraphImpactError(null); // v2.7
    setDepGraphImpactResult(null); // v2.7
    try { // v2.7
      const res = await apiClient.get(`/dependency-graph/impact/${encodeURIComponent(key)}`).catch((err: any) => { // v2.7
        throw new Error(err.response?.data?.error || `No impact data for key "${key}"`); // v2.7
      }); // v2.7
      const data = res.data ?? res; // v2.7
      setDepGraphImpactResult(data); // v2.7
    } catch (err: any) { // v2.7
      setDepGraphImpactError(err.message || 'Impact analysis failed'); // v2.7
    } finally { // v2.7
      setDepGraphImpactLoading(false); // v2.7
    }
  }; // v2.7

  // v2.7: fetch dependency graph on first tab activation (guarded by ref)
  useEffect(() => { // v2.7
    if (activeTab !== 'dep-graph') return; // v2.7
    if (depGraphFetchedRef.current) return; // v2.7
    depGraphFetchedRef.current = true; // v2.7
    fetchDepGraph(); // v2.7
    // eslint-disable-next-line react-hooks/exhaustive-deps // v2.7
  }, [activeTab]); // v2.7

  const tabs = [
    { id: 'overview',    label: 'Overview',     icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'farmers',     label: 'Farmers',      icon: <Icon.Users className="w-4 h-4" />, badge: farmers.length || undefined },
    { id: 'batches',     label: 'Batches',      icon: <Icon.Package className="w-4 h-4" />, badge: batches.length || undefined },
    { id: 'dispatches',  label: 'Dispatches',   icon: <Icon.Send className="w-4 h-4" /> },
    { id: 'withdrawals', label: 'Withdrawals',  icon: <Icon.Wallet className="w-4 h-4" /> },
    { id: 'transactions',label: 'Transactions', icon: <Icon.Dollar className="w-4 h-4" /> },
    { id: 'villages',    label: 'Villages',     icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'agents',      label: 'Agents',       icon: <Icon.Users className="w-4 h-4" /> },
    { id: 'drivers',     label: 'Drivers',      icon: <Icon.Truck className="w-4 h-4" /> },
    { id: 'users',       label: 'Users',        icon: <Icon.Users className="w-4 h-4" /> },
    { id: 'trucks',      label: 'Trucks',       icon: <Icon.Truck className="w-4 h-4" /> },
    { id: 'fleet',       label: 'Fleet Map',    icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'farms',       label: 'Farms',        icon: <Icon.Map className="w-4 h-4" /> },
    { id: 'eudr',        label: 'EUDR',         icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'carbon',      label: 'Carbon',       icon: <Icon.Award className="w-4 h-4" /> },
    { id: 'advisory',    label: 'Advisory',     icon: <Icon.FileText className="w-4 h-4" /> },
    { id: 'audit',       label: 'Audit Log',    icon: <Icon.Database className="w-4 h-4" /> },
    { id: 'spatial',     label: 'Spatial OS',   icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'mel',         label: 'MEL Analytics',icon: <Icon.BarChart3 className="w-4 h-4" /> },
    { id: 'ai-advisory', label: 'AI Advisory',  icon: <Icon.Sparkles className="w-4 h-4" /> },
    { id: 'pest-reports',label: 'Pest Reports', icon: <Icon.Leaf className="w-4 h-4" /> },
    { id: 'qr-codes',     label: 'QR Codes',    icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'smart-advisory', label: 'Smart Advisory', icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'products',    label: 'Products',     icon: <Icon.Shield className="w-4 h-4" /> },
    // v1.6.0: Chain of Custody tab — full farm-to-warehouse traceability
    { id: 'chain',       label: 'Chain of Custody', icon: <Icon.Shield className="w-4 h-4" /> },
    // v1.7.9: Yield forecasting (anti-fraud) + MRL lab tests + cash float tracker
    { id: 'yield',       label: 'Yield Forecast', icon: <Icon.TrendingUp className="w-4 h-4" /> },
    { id: 'lab-tests',   label: 'Lab Tests (MRL)', icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'cash-floats', label: 'Cash Floats',  icon: <Icon.Wallet className="w-4 h-4" /> },
    // v1.8.0: EUDR DDS Generator + Export Documents Hub (Phase 2)
    { id: 'dds',         label: 'EUDR DDS',      icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'export-docs', label: 'Export Docs',   icon: <Icon.FileText className="w-4 h-4" /> },
    // v1.8.1: Phase 3 — Organoleptic + Farm Inputs + Stock Reconciliation
    { id: 'organoleptic', label: 'Sensory QC',   icon: <Icon.Leaf className="w-4 h-4" /> },
    { id: 'farm-inputs',  label: 'Input Credit',  icon: <Icon.Package className="w-4 h-4" /> },
    { id: 'stock-counts', label: 'Stock Counts',  icon: <Icon.Database className="w-4 h-4" /> },
    // v1.8.2: Post-harvest infrastructure
    { id: 'post-harvest', label: 'Post-Harvest',  icon: <Icon.Map className="w-4 h-4" /> },
    // v1.9.2: Admin points config + MEL overview + quality correlation
    { id: 'points-rules', label: 'Points Rules',  icon: <Icon.Award className="w-4 h-4" /> },
    { id: 'mel-overview', label: 'MEL Overview',  icon: <Icon.Database className="w-4 h-4" /> },
    { id: 'quality-corr', label: 'Quality Corr.', icon: <Icon.Shield className="w-4 h-4" /> },
    // v2.0.0: Regulatory API Gateway (National Authority Data Node)
    { id: 'regulatory',  label: 'Regulatory API', icon: <Icon.Shield className="w-4 h-4" /> },
    // v2.1.1 (A4 fix): Harvest Notifications tab — surfaces the dead (window as any).__harvestStats data into a full management UI
    { id: 'harvest-notif', label: 'Harvest Notif.', icon: <Icon.Bell className="w-4 h-4" />, badge: harvestNotifStats?.pending || undefined },
    // v2.1.1 (A3 fix): AI Governance tab — prompt versioning + inference logging (DPO/admin only)
    { id: 'ai-governance', label: 'AI Governance', icon: <Icon.Cpu className="w-4 h-4" /> },
    // v2.2.0: Volume XXXI — Data Sources, AI Agents, Knowledge Base tabs (inserted before profile)
    { id: 'data-sources', label: 'Data Sources', icon: <Icon.Database className="w-4 h-4" /> }, // v2.2.0
    { id: 'ai-agents',   label: 'AI Agents',    icon: <Icon.Cpu className="w-4 h-4" /> }, // v2.2.0
    { id: 'satellite',   label: 'Satellite',    icon: <Icon.MapPin className="w-4 h-4" /> }, // v3.2: new satellite imagery tab
    { id: 'knowledge',   label: 'Knowledge',    icon: <Icon.FileText className="w-4 h-4" /> }, // v2.2.0
    // v2.5: 6 NEW admin tabs wiring remaining backend endpoints (Pipeline, AI Gov V2, ECOS, Durability, Regulatory V2, SMS)
    { id: 'pipeline',      label: 'Pipeline',      icon: <Icon.Activity className="w-4 h-4" /> }, // v2.5
    { id: 'ai-gov-v2',     label: 'AI Gov V2',     icon: <Icon.Cpu className="w-4 h-4" /> }, // v2.5
    { id: 'ecos',          label: 'ECOS',          icon: <Icon.Sparkles className="w-4 h-4" /> }, // v2.5
    { id: 'durability',    label: 'Durability',    icon: <Icon.Shield className="w-4 h-4" /> }, // v2.5
    { id: 'regulatory-v2', label: 'Regulatory V2', icon: <Icon.Shield className="w-4 h-4" /> }, // v2.5
    { id: 'sms',           label: 'SMS',           icon: <Icon.Phone className="w-4 h-4" /> }, // v2.5
    // v2.7: Dependency Graph — layered nodes + edges tables + "What if?" impact analysis
    { id: 'dep-graph',     label: 'Dependency Graph', icon: <Icon.Activity className="w-4 h-4" /> }, // v2.7
    // v2.8: 5 NEW tabs — API Key Management (GAP 3) + 4 Business Module tabs (GAP 5)
    { id: 'households',  label: 'Households',   icon: <Icon.Users className="w-4 h-4" /> }, // v2.8
    { id: 'nursery',     label: 'Nursery',      icon: <Icon.Leaf className="w-4 h-4" /> }, // v2.8
    { id: 'plantation',  label: 'Plantation',   icon: <Icon.Map className="w-4 h-4" /> }, // v2.8
    { id: 'factory',     label: 'Factory',      icon: <Icon.Package className="w-4 h-4" /> }, // v2.8
    { id: 'producers',   label: 'Producers',    icon: <Icon.Users className="w-4 h-4" /> }, // v3.2.8: Producer Management
    { id: 'api-keys',    label: 'API Keys',     icon: <Icon.Settings className="w-4 h-4" /> }, // v2.8
    // v2.9: 2 NEW Intelligence tabs — Cognitive Memory + EAOE Orchestrator
    { id: 'cognitive-memory', label: 'Cognitive Memory', icon: <Icon.Database className="w-4 h-4" /> }, // v2.9
    { id: 'orchestrator',     label: 'Orchestrator',     icon: <Icon.Cpu className="w-4 h-4" /> }, // v2.9
    { id: 'profile',     label: 'My Profile',   icon: <Icon.User className="w-4 h-4" /> },
    // v1.6.5: warehouse management link
    { id: 'warehouse',   label: 'Warehouse',    icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'settings',    label: 'Settings',     icon: <Icon.Settings className="w-4 h-4" /> },
    // v3.10.2: New tabs for recent backend upgrades
    { id: 'eudr-map',          label: 'EUDR Map',          icon: <Icon.Map className="w-4 h-4" /> }, // v3.10.2: EUDR compliance world map
    { id: 'traceability',      label: 'Traceability',      icon: <Icon.Send className="w-4 h-4" /> }, // v3.9.0: CNCC waybills + checkpoints + warehouse bins
    { id: 'market-prices',     label: 'Market Prices',     icon: <Icon.Dollar className="w-4 h-4" /> }, // v3.7.0: ONCC cocoa price scraper
    { id: 'carbon-credits',    label: 'Carbon Credits',    icon: <Icon.Award className="w-4 h-4" /> }, // v3.10.0: Real carbon measurement + monetization
    { id: 'biomass',           label: 'Biomass Waste',     icon: <Icon.Leaf className="w-4 h-4" /> }, // v3.10.0: Biomass waste ledger + fraud detection
      // v3.11.47: IROL Intelligent Routing panel
    { id: 'intelligent-routing', label: 'Intelligent Routing', icon: <Icon.Cpu className="w-4 h-4" /> },
];

  // Single source of truth for Admin navigation. Desktop and mobile intentionally
  // consume the same groups and tab IDs so a mobile selection can never diverge
  // from the desktop Admin view. Every unique tab is represented exactly once.
  const adminNavGroups = [
    { group: 'Ops', ids: ['overview','farmers','batches','dispatches','yield','cash-floats','households','nursery','plantation','factory','producers'] },
    { group: 'Finance', ids: ['withdrawals','transactions','carbon','farm-inputs'] },
    { group: 'Logistics', ids: ['trucks','fleet','drivers','warehouse'] },
    { group: 'Compliance', ids: ['eudr','farms','villages','agents','advisory','audit','lab-tests','dds','export-docs','organoleptic','post-harvest','points-rules','quality-corr','regulatory','harvest-notif'] },
    { group: 'Intelligence', ids: ['spatial','mel','ai-advisory','pest-reports','qr-codes','smart-advisory','products','chain','stock-counts','mel-overview','ai-governance','data-sources','ai-agents','satellite','knowledge','pipeline','ai-gov-v2','ecos','cognitive-memory','orchestrator'] },
    { group: 'System', ids: ['users','durability','regulatory-v2','sms','dep-graph','api-keys','profile','settings','eudr-map','traceability','market-prices','carbon-credits','biomass','intelligent-routing'] },
  ] as const;

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="admin" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading workspace…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-root min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.6.8: Skip-to-content link for accessibility (WCAG 2.2 AA) */}
      <a href="#admin-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>

      {/* ═════════════════════════════════════════════════════════════════
          Mobile Command-Center shell
          Desktop markup below remains untouched; this shell is mobile-only.
         ═════════════════════════════════════════════════════════════════ */}
      <div className="admin-mobile-shell">
        <header className="admin-mobile-appbar">
          <button
            type="button"
            className="admin-mobile-workspace"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open workspace menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="admin-mobile-eyebrow">Workspace</span>
            <span className="admin-mobile-workspace-name">
              Admin workspace
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
            </span>
          </button>
          <div className="admin-mobile-appbar-spacer" />
          <button
            type="button"
            className="admin-mobile-icon-btn"
            onClick={() => { window.location.href = '/command-center'; }}
            aria-label="Open Command Center"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            <span className="admin-mobile-notification-dot" />
          </button>
          <button
            type="button"
            className="admin-mobile-avatar"
            onClick={() => setActiveTab('profile')}
            aria-label="Open profile"
          >
            {(user?.name || 'A').trim().charAt(0).toUpperCase()}
            <span>ad</span>
          </button>
        </header>

        <div
          className={`admin-mobile-scrim${mobileMenuOpen ? ' open' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden={!mobileMenuOpen}
        />
        <aside
          className={`admin-mobile-drawer${mobileMenuOpen ? ' open' : ''}`}
          aria-hidden={!mobileMenuOpen}
          aria-label="Admin workspace navigation"
        >
          <div className="admin-mobile-drawer-head">
            <div className="admin-mobile-workspace admin-mobile-workspace-static">
              <span className="admin-mobile-eyebrow">Workspace</span>
              <span className="admin-mobile-workspace-name">Admin workspace</span>
            </div>
            <button type="button" className="admin-mobile-drawer-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close workspace menu">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="admin-mobile-drawer-body">
            {adminNavGroups.map(({ group, ids }) => (
              <div className="admin-mobile-drawer-group" key={group}>
                <div className="admin-mobile-drawer-label">{group}</div>
                {ids.map(id => {
                  const tab = tabs.find(t => t.id === id);
                  if (!tab) return null;
                  return (
                    <button
                      type="button"
                      key={tab.id}
                      className={`admin-mobile-drawer-item${activeTab === tab.id ? ' active' : ''}`}
                      onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    >
                      <span className="admin-mobile-drawer-dot" />
                      <span>{tab.label}</span>
                      {tab.badge ? <span className="admin-mobile-drawer-count">{tab.badge > 99 ? '99+' : tab.badge}</span> : null}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="admin-mobile-drawer-user">
            <div className="admin-mobile-avatar admin-mobile-avatar-small">{(user?.name || 'A').trim().charAt(0).toUpperCase()}<span>ad</span></div>
            <div className="admin-mobile-drawer-user-copy">
              <strong>{user?.name || 'Admin User'}</strong>
              <small>{user?.role || 'admin'}</small>
            </div>
            <button
              type="button"
              className="admin-mobile-logout"
              onClick={handleMobileLogout}
              aria-label="Sign out"
              title="Sign out"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <nav className="admin-mobile-bottomnav" aria-label="Primary mobile navigation">
          <button type="button" className={activeTab === 'overview' ? 'on' : ''} onClick={() => setActiveTab('overview')}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg></span>
            <span>Overview</span>
          </button>
          <button type="button" className={['farmers','batches','dispatches','drivers','trucks','fleet','warehouse'].includes(activeTab) ? 'on' : ''} onClick={() => setActiveTab('farmers')}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21V10l9-6 9 6v11"/><path d="M9 21v-6h6v6"/></svg></span>
            <span>Ops</span>
          </button>
          <button type="button" className="fab" onClick={() => { window.location.href = '/command-center'; }} aria-label="Open Command Center">
            <span className="ic">+</span>
          </button>
          <button type="button" className={['mel','mel-overview','cognitive-memory','orchestrator','ai-agents','knowledge','pipeline'].includes(activeTab) ? 'on' : ''} onClick={() => setActiveTab('mel-overview')}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21V3"/><path d="M7 14l4-4 3 3 5-6"/></svg></span>
            <span>Analytics</span>
          </button>
          <button type="button" className={['audit','harvest-notif','traceability'].includes(activeTab) ? 'on' : ''} onClick={() => setActiveTab('audit')}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></span>
            <span>Activity</span>
          </button>
        </nav>
      </div>

      {/* ─── Top bar ─── */}
      <header className="topbar admin-desktop-topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="admin" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">Admin workspace</p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{user?.name} · {user?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={fetchData}
              disabled={refreshing}
              className="btn-secondary text-xs hide-mobile"
            >
              <Icon.Refresh className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing…' : 'Refresh'}</span>
            </button>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing} className="md:hidden">
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            {/* v2.7: link to National Command Center (top-level /command-center route) */}
            <a href="/command-center" className="btn-secondary text-xs hide-mobile" title="National Command Center"> {/* v2.7 */}
              <Icon.Activity className="w-3.5 h-3.5" /> {/* v2.7 */}
              <span>Command Center</span> {/* v2.7 */}
            </a>
            <ProfileButton accent="admin" />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="admin-main-content" data-admin-tab={activeTab} className="admin-main-content max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6">
        {error && (
          <div className="mb-6"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>
        )}

        {/* v1.6.8: Tab navigation with ARIA roles + grouped sections */}
        {/* Desktop and mobile use the same authoritative adminNavGroups registry. */}
        <div className="hidden md:flex flex-wrap gap-1 p-1.5 bg-ink-100 dark:bg-ink-900 rounded-xl mb-6" role="tablist" aria-label="Admin navigation">
          {adminNavGroups.map(({ group, ids }) => (
            <React.Fragment key={group}>
              <span className="text-2xs font-medium uppercase tracking-micro text-ink-400 dark:text-ink-500 self-center px-1.5">{group}</span>
              {ids.map(id => {
                const t = tabs.find(tab => tab.id === id);
                if (!t) return null;
                return (
                  <button key={t.id} role="tab" aria-selected={activeTab === t.id} onClick={() => setActiveTab(t.id)}
                    className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all duration-150 ease-app ${
                      activeTab === t.id ? 'bg-ink-0 dark:bg-ink-800 text-ink-900 dark:text-ink-50 shadow-e1' : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-200 hover:bg-ink-0/50 dark:hover:bg-ink-800/50'
                    }`}>
                    {t.icon}<span>{t.label}</span>
                    {t.badge ? <span className={`min-w-4 h-4 px-1 rounded-full text-2xs font-semibold flex items-center justify-center nums ${activeTab === t.id ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-200 dark:bg-ink-700 text-ink-600 dark:text-ink-300'}`}>{t.badge > 99 ? '99+' : t.badge}</span> : null}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {activeTab !== 'overview' && (
          <div className="admin-mobile-tab-header">
            <div className="admin-mobile-tab-header-eyebrow">Admin / {adminNavGroups.find(group => (group.ids as readonly string[]).includes(activeTab))?.group || 'Workspace'}</div>
            <div className="admin-mobile-tab-header-row">
              <div>
                <h1>{tabs.find(tab => tab.id === activeTab)?.label || 'Admin'}</h1>
                <p>Enterprise workspace view</p>
              </div>
              <span className="admin-mobile-tab-header-status">LIVE</span>
            </div>
          </div>
        )}

        {/* ─── Search ─── */}
        {/* v2.8: exclude the 5 new sub-component tabs (households/nursery/plantation/factory/api-keys) — they render their own internal search where applicable */}
        {/* v2.9: also exclude cognitive-memory + orchestrator (sub-component tabs with their own action buttons) */}
        {activeTab !== 'overview' && activeTab !== 'settings' && !['households', 'nursery', 'plantation', 'factory', 'api-keys', 'cognitive-memory', 'orchestrator'].includes(activeTab) && ( // v2.8 // v2.9
          <div className="mb-5 admin-mobile-search-panel">
            <div className="relative max-w-md">
              <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}…`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
        )}

        {/* ═════════════════════════════════════════════════════════════════
            MOBILE OVERVIEW — faithful React implementation of the supplied
            AgriOps mobile reference. Data remains sourced from the existing
            dashboard state; no hard-coded business values are introduced.
           ═════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && dashboardStats && (
          <div className="admin-mobile-overview">
            <section className="admin-mobile-hero">
              <div className="admin-mobile-crumb"><strong>Workspaces / Admin</strong> · Overview</div>
              <h1>Overview</h1>
              <p>Real-time operations across all modules.</p>
              <div className="admin-mobile-segmented" role="tablist" aria-label="Overview range">
                {['Today','7d','30d','90d','All'].map(label => {
                  const value = label === 'Today' ? '1d' : label.toLowerCase();
                  return (
                    <button type="button" key={label} className={mobileRange === value ? 'on' : ''} onClick={() => setMobileRange(value)}>{label}</button>
                  );
                })}
              </div>
            </section>

            <div className="admin-mobile-kpi-carousel" aria-label="Primary KPIs">
              {[
                ['Farmers', dashboardStats.total_farmers || 0, 'No change vs. last week', 'admin'],
                ['Batches', dashboardStats.total_delivered || 0, 'Delivered batches in window', 'info'],
                ['Payouts (XAF)', Number(dashboardStats.total_payout || 0).toLocaleString(), 'Total recorded payouts', 'violet'],
                ['Zero waste', Number(dashboardStats.avg_zero_waste_score || 0).toFixed(1), 'Tracking baseline', 'accent'],
                ['Pending', dashboardStats.total_pending || 0, 'Items awaiting delivery', 'neutral'],
                ['Harvest alerts', (window as any).__harvestStats?.pending || 0, 'Current pending alerts', 'accent'],
              ].map(([label, value, foot, tone], index) => (
                <article className="admin-mobile-kcard" key={String(label)}>
                  <div className="admin-mobile-kcard-label"><span>{label}</span><span className={`admin-mobile-kcard-icon tone-${tone}`}>{['●','▤','₣','⌗','◷','⚑'][index]}</span></div>
                  <div className="admin-mobile-kcard-value"><strong>{value}</strong><span className="admin-mobile-kcard-pill">0%</span></div>
                  <svg className="admin-mobile-spark" viewBox="0 0 200 36" preserveAspectRatio="none" aria-hidden="true">
                    <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={index % 2 === 0 ? '0,24 30,25 60,20 90,18 120,19 150,13 180,15 200,10' : '0,18 30,16 60,20 90,22 120,18 150,24 180,28 200,30'} />
                  </svg>
                  <div className="admin-mobile-kcard-foot">{foot}</div>
                </article>
              ))}
            </div>

            <div className="admin-mobile-matrix">
              {[
                ['Pending', dashboardStats.total_pending || 0, 'items'],
                ['Avg price / kg', Math.round(Number(dashboardStats.avg_price_per_kg || 0)).toLocaleString(), 'XAF'],
                ['Harvest alerts', (window as any).__harvestStats?.pending || 0, 'alerts'],
                ['Farmer verified', (window as any).__chainStats?.farmer_verified_batches || 0, 'batches'],
                ['Export ready', (window as any).__chainStats?.export_batches || 0, 'batches'],
                ['Variance alerts', (window as any).__chainStats?.pending_variance_investigations || 0, 'open'],
              ].map(([label, value, unit]) => (
                <div className="admin-mobile-tile" key={String(label)}>
                  <div className="admin-mobile-tile-label">{label}</div>
                  <div className="admin-mobile-tile-value"><strong>{value}</strong><span>{unit}</span></div>
                </div>
              ))}
            </div>

            <section className="admin-mobile-panel">
              <header><h3>Pending batches <span>— Awaiting delivery</span></h3><b>A</b></header>
              <div className="admin-mobile-panel-body">
                <div className="admin-mobile-legend"><span><i className="received" />Received</span><span><i className="transit" />In transit</span><span><i className="delivered" />Delivered</span></div>
                <div className="admin-mobile-empty">
                  <strong>{dashboardStats.total_pending ? `${dashboardStats.total_pending} pending batches` : 'No pending batches this week'}</strong>
                  {dashboardStats.total_pending ? 'Batches currently awaiting delivery.' : 'Accepted batches will appear here as they move through Received → In transit → Delivered.'}
                </div>
              </div>
            </section>

            <section className="admin-mobile-panel">
              <header><h3>Avg price / kg <span>— XAF per kilogram</span></h3><b>B</b></header>
              <div className="admin-mobile-panel-body">
                <div className="admin-mobile-price-head"><strong>{Math.round(Number(dashboardStats.avg_price_per_kg || 0)).toLocaleString()}</strong><span>XAF</span></div>
                <svg className="admin-mobile-chart" viewBox="0 0 320 120" preserveAspectRatio="none" aria-hidden="true">
                  <g className="grid"><line x1="0" y1="20" x2="320" y2="20" /><line x1="0" y1="50" x2="320" y2="50" /><line x1="0" y1="80" x2="320" y2="80" /><line x1="0" y1="110" x2="320" y2="110" /></g>
                  <path className="area" d="M0,80 L40,70 L80,75 L120,60 L160,68 L200,40 L240,45 L280,30 L320,22 L320,110 L0,110 Z" />
                  <path className="line" d="M0,80 L40,70 L80,75 L120,60 L160,68 L200,40 L240,45 L280,30 L320,22" />
                  <circle cx="200" cy="40" r="3.2" />
                </svg>
              </div>
            </section>

            <section className="admin-mobile-panel">
              <header><h3>Price per kg <span>— Current buy price</span></h3><b>C</b></header>
              <div className="admin-mobile-panel-body">
                <div className="admin-mobile-buy-head"><div><strong>{pricePerKg.toLocaleString()}</strong> <span>XAF</span><small>Current configured buy price</small></div></div>
                <svg className="admin-mobile-trend" viewBox="0 0 320 60" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M0,42 L40,38 L80,42 L120,28 L160,32 L200,22 L240,24 L280,14 L320,18 L320,60 L0,60 Z" />
                  <polyline fill="none" points="0,42 40,38 80,42 120,28 160,32 200,22 240,24 280,14 320,18" />
                </svg>
                <div className="admin-mobile-price-tiers">
                  <div><small>Floor</small><strong>{Math.round(pricePerKg * 0.97).toLocaleString()} <em>XAF</em></strong></div>
                  <div><small>Target</small><strong>{Math.round(pricePerKg).toLocaleString()} <em>XAF</em></strong></div>
                  <div><small>Ceiling</small><strong>{Math.round(pricePerKg * 1.08).toLocaleString()} <em>XAF</em></strong></div>
                </div>
                <button type="button" className="admin-mobile-primary-btn" onClick={() => { setNewPrice(pricePerKg.toString()); setShowPriceModal(true); }}>Update price</button>
              </div>
            </section>

            <section className="admin-mobile-panel">
              <header><h3>Recent activity</h3></header>
              <div className="admin-mobile-panel-body">
                <div className="admin-mobile-filter-row">
                  {['All','Delivered','In transit','Pending','Verified'].map((label, i) => <span key={label} className={`admin-mobile-chip${i === 0 ? ' on' : ''}`}>{label}</span>)}
                </div>
                <div className="admin-mobile-recent">
                  {batches.slice(0, 5).map((b: any) => (
                    <article className="admin-mobile-activity-card" key={b.id}>
                      <div className="top-row">
                        <div><span className="bid">B-{String(b.id || '').slice(0, 10) || '—'}</span><span className="status">{b.payment_status || b.status || 'Pending'}</span></div>
                        <time>{b.created_at ? new Date(b.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Recent'}</time>
                      </div>
                      <div className="meta-row"><div>Farmer<strong>{b.farmer_name || 'Unknown'}</strong></div><div>Village<strong>{b.village || '—'}</strong></div></div>
                      <div className="stats"><div><small>Weight</small><strong>{b.actual_weight || b.est_weight || 0}<em>kg</em></strong></div><div><small>Price</small><strong>{Math.round(Number(pricePerKg || dashboardStats.avg_price_per_kg || 0)).toLocaleString()}<em>XAF</em></strong></div><div><small>Total</small><strong>{Number(b.calculated_amount || ((b.actual_weight || b.est_weight || 0) * pricePerKg) || 0).toLocaleString()}<em>XAF</em></strong></div></div>
                    </article>
                  ))}
                  {batches.length === 0 && <div className="admin-mobile-empty"><strong>No recent activity</strong>New activity will appear here as batches are recorded.</div>}
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ═══ OVERVIEW — desktop/tablet implementation retained unchanged ═══ */}
        {activeTab === 'overview' && dashboardStats && (
          <div className="admin-desktop-overview space-y-6">
            {/* v1.6.7: Core KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Farmers"      value={dashboardStats.total_farmers || 0}            icon={<Icon.Users className="w-4 h-4" />} accent="admin" />
              <MetricTile label="Batches"      value={dashboardStats.total_delivered || 0}         icon={<Icon.Package className="w-4 h-4" />} />
              <MetricTile label="Payouts (XAF)"value={Number(dashboardStats.total_payout||0).toLocaleString()} icon={<Icon.Dollar className="w-4 h-4" />} />
              <MetricTile label="Zero-Waste"   value={Number(dashboardStats.avg_zero_waste_score||0).toFixed(1)} icon={<Icon.Award className="w-4 h-4" />} />
            </div>

            {/* v1.6.7: Chain of Custody + Harvest stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <MetricTile label="Pending"      value={dashboardStats.total_pending || 0} icon={<Icon.Clock className="w-4 h-4" />} />
              <MetricTile label="Avg Price/kg" value={`${Math.round(Number(dashboardStats.avg_price_per_kg || 0)).toLocaleString()}`} />
              <MetricTile label="Harvest Alerts" value={(window as any).__harvestStats?.pending || 0} icon={<Icon.Alert className="w-4 h-4" />} />
              <MetricTile label="Farmer Verified" value={(window as any).__chainStats?.farmer_verified_batches || 0} />
              <MetricTile label="Export Ready" value={(window as any).__chainStats?.export_batches || 0} />
              <MetricTile label="Variance Alerts" value={(window as any).__chainStats?.pending_variance_investigations || 0} icon={<Icon.Alert className="w-4 h-4" />} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card padding="lg">
                <SectionHeader title="Pending batches" icon={<Icon.Clock className="w-4 h-4" />} subtitle="Awaiting delivery" />
                <p className="mt-3 text-3xl font-semibold nums tracking-tight text-warning-700 dark:text-warning-500">{dashboardStats.total_pending || 0}</p>
                <ProgressBar className="mt-3" value={Math.min(100, (dashboardStats.total_pending / Math.max(1, dashboardStats.total_delivered + dashboardStats.total_pending)) * 100)} tone="warning" />
              </Card>
              <Card padding="lg">
                <SectionHeader title="Avg price / kg" icon={<Icon.TrendingUp className="w-4 h-4" />} subtitle="XAF per kilogram" />
                <p className="mt-3 text-3xl font-semibold nums tracking-tight text-ink-900 dark:text-ink-50">{Math.round(Number(dashboardStats.avg_price_per_kg || 0)).toLocaleString()}</p>
                <p className="text-xs text-ink-500 dark:text-ink-400 mt-1">XAF / kg</p>
              </Card>
              <Card padding="lg">
                <SectionHeader title="Price per kg" icon={<Icon.Settings className="w-4 h-4" />} subtitle="Current buy price" />
                <div className="mt-3 flex items-center justify-between gap-2">
                  <p className="text-3xl font-semibold nums tracking-tight text-ink-900 dark:text-ink-50">{pricePerKg.toLocaleString()} <span className="text-base text-ink-500 dark:text-ink-400">XAF</span></p>
                  <button onClick={() => { setNewPrice(pricePerKg.toString()); setShowPriceModal(true); }} className="btn-secondary text-xs">
                    <Icon.Settings className="w-3.5 h-3.5" /> Update
                  </button>
                </div>
              </Card>
            </div>

            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow="Recent" title="Latest activity" icon={<Icon.Activity className="w-4 h-4" />} />
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Type</th><th>Detail</th><th>Status</th></tr></thead>
                  <tbody>
                    {batches.slice(0, 5).map(b => (
                      <tr key={b.id}>
                        <td><span className="pill-neutral">Batch</span></td>
                        <td>
                          <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{b.farmer_name}</p>
                          <p className="text-2xs text-ink-500 dark:text-ink-400 nums">{b.actual_weight || b.est_weight} kg · #{b.id?.slice(0, 8)}</p>
                        </td>
                        <td><SmartStatus status={b.payment_status || b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ FARMERS ═══ */}
        {activeTab === 'farmers' && (
          <Card padding="none" className="overflow-hidden farmers-tab-panel">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmers-tab-summary">
              <span className="text-sm text-ink-500 dark:text-ink-400">{farmersPag.total} farmers registered</span>
            </div>

            {/* Mobile: enterprise compact LIST (not cards). Desktop data table remains unchanged below. */}
            <div className="farmers-mobile-list" role="list" aria-label="Registered farmers">
              {farmersPag.paginatedItems.length === 0 ? (
                <div className="farmers-mobile-empty">No farmers match the current search.</div>
              ) : farmersPag.paginatedItems.map((farmer) => (
                <div key={farmer.id} className="farmers-mobile-row" role="listitem">
                  <div className="farmers-mobile-identity">
                    <Avatar name={farmer.name} size="xs" />
                    <div className="farmers-mobile-primary">
                      <p className="farmers-mobile-name">{farmer.name || 'Unnamed farmer'}</p>
                      <p className="farmers-mobile-phone nums">{farmer.phone || 'No phone'}</p>
                    </div>
                  </div>
                  <div className="farmers-mobile-meta">
                    <span className="farmers-mobile-village">{farmer.village || 'Village not set'}</span>
                    {farmer.kyc_verified
                      ? <StatusPill tone="success" dot>Verified</StatusPill>
                      : <StatusPill tone="warning" dot>Pending KYC</StatusPill>}
                  </div>
                  <div className="farmers-mobile-stats" aria-label={`Operational summary for ${farmer.name || 'farmer'}`}>
                    <span><small>Batches</small><b className="nums">{farmer.total_batches || 0}</b></span>
                    <span><small>Score</small><b className="nums">{farmer.avg_quality_score ? Number(farmer.avg_quality_score).toFixed(1) : '—'}</b></span>
                    <span><small>Balance</small><b className="nums">{farmer.available_balance ? `${Number(farmer.available_balance).toLocaleString()} XAF` : '0 XAF'}</b></span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: preserve the original full-detail authoritative table 100%. */}
            <div className="overflow-x-auto farmers-desktop-table-wrap">
              <table className="data-table farmers-desktop-table">
                <thead><tr><th>Farmer</th><th>Village</th><th>KYC</th><th>Cert.</th><th>Batches</th><th>Avg Score</th><th>Earnings</th><th>Balance</th><th>Carbon</th></tr></thead>
                <tbody>
                  {farmersPag.paginatedItems.map((farmer) => (
                    <tr key={farmer.id}>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <Avatar name={farmer.name} size="xs" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink-900 dark:text-ink-50 truncate">{farmer.name}</p>
                            <p className="text-2xs text-ink-500 dark:text-ink-400 nums">{farmer.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400">{farmer.village}</td>
                      <td>{farmer.kyc_verified ? <StatusPill tone="success" dot>Verified</StatusPill> : <StatusPill tone="warning" dot>Pending</StatusPill>}</td>
                      <td className="text-2xs">{farmer.certification_status && farmer.certification_status !== 'none' ? <StatusPill tone="info">{farmer.certification_status}</StatusPill> : '—'}</td>
                      <td className="nums">{farmer.total_batches || 0}</td>
                      <td className="nums">{farmer.avg_quality_score ? Number(farmer.avg_quality_score).toFixed(1) : '—'}</td>
                      <td className="nums font-medium text-ink-900 dark:text-ink-50">{farmer.total_earnings ? `${Number(farmer.total_earnings).toLocaleString()} XAF` : '0 XAF'}</td>
                      <td className="nums font-medium text-ink-900 dark:text-ink-50">{farmer.available_balance ? `${Number(farmer.available_balance).toLocaleString()} XAF` : '0 XAF'}</td>
                      <td className="nums">{farmer.carbon_points ? Number(farmer.carbon_points).toFixed(0) : '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800 farmers-pagination">
              <Pagination
                page={farmersPag.page}
                pageSize={farmersPag.pageSize}
                total={farmersPag.total}
                onPageChange={farmersPag.setPage}
                onPageSizeChange={farmersPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ═══ BATCHES ═══ */}
        {activeTab === 'batches' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
              <span className="text-sm text-ink-500 dark:text-ink-400">{batchesPag.total} batches</span>
              <span className="text-2xs text-ink-400 nums">Price: {pricePerKg.toLocaleString()} XAF / kg</span>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Batch</th><th>Farmer</th><th>Weight</th><th>Grade</th><th>Score</th><th>Verified</th><th>Decision</th><th>Amount</th><th>Payment</th><th>Action</th></tr></thead>
                <tbody>
                  {batchesPag.total === 0 && (
                    <tr className="admin-empty-table-row">
                      <td colSpan={10}>
                        <span className="admin-empty-table-title">No batches yet</span>
                        <span className="admin-empty-table-copy">Batches will appear here when cocoa intake records are available.</span>
                      </td>
                    </tr>
                  )}
                  {batchesPag.paginatedItems.map((batch: any) => (
                    <tr key={batch.id}>
                      <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.id?.slice(0, 8)}…</td>
                      <td className="text-sm text-ink-700 dark:text-ink-200">{batch.farmer_name || '—'}</td>
                      <td className="nums">{batch.actual_weight || batch.est_weight || '—'} kg</td>
                      <td><StatusPill tone={batch.quality_grade === 'A' ? 'success' : batch.quality_grade === 'B' ? 'warning' : 'danger'} dot>{batch.quality_grade || '—'}</StatusPill></td>
                      <td className="nums">{batch.quality_score ? Number(batch.quality_score).toFixed(0) : '—'}</td>
                      <td>{batch.farmer_verified ? <span className="text-success-600 dark:text-success-500 text-xs">✓</span> : <span className="text-ink-400 text-xs">—</span>}</td>
                      <td>{batch.batch_decision ? <StatusPill tone={batch.batch_decision === 'EXPORT' ? 'success' : batch.batch_decision === 'REJECT' ? 'danger' : 'info'}>{batch.batch_decision}</StatusPill> : <span className="text-ink-400 text-xs">—</span>}</td>
                      <td className="nums font-medium text-ink-900 dark:text-ink-50">{batch.calculated_amount ? `${Number(batch.calculated_amount).toLocaleString()} XAF` : '—'}</td>
                      <td><SmartStatus status={batch.payment_status || 'PENDING'} /></td>
                      <td>
                        {/* v1.6.8: Payment approval is in Finance portal only — admin sees status, not Pay button */}
                        {batch.payment_status === 'PAID' ? (
                          <span className="inline-flex items-center gap-1 text-2xs text-success-600 dark:text-success-500"><Icon.Check className="w-3.5 h-3.5" /> Paid</span>
                        ) : batch.intaked_at ? (
                          <a href="/finance" className="btn-secondary text-xs">→ Finance</a>
                        ) : (
                          <span className="text-2xs text-ink-400">Awaiting intake</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={batchesPag.page}
                pageSize={batchesPag.pageSize}
                total={batchesPag.total}
                onPageChange={batchesPag.setPage}
                onPageSizeChange={batchesPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ═══ DISPATCHES ═══ */}
        {activeTab === 'dispatches' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setShowDispatchModal(true)} className="btn-primary">
                <Icon.Plus className="w-4 h-4" /> New dispatch
              </button>
            </div>
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <span className="text-sm text-ink-500 dark:text-ink-400">{dispatchesPag.total} dispatches</span>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Dispatch</th><th>Farmer</th><th>Truck</th><th>Driver</th><th>Status</th><th>Assigned</th></tr></thead>
                  <tbody>
                    {dispatchesPag.total === 0 && (
                      <tr className="admin-empty-table-row">
                        <td colSpan={6}>
                          <span className="admin-empty-table-title">No dispatches yet</span>
                          <span className="admin-empty-table-copy">Create a dispatch when a batch is ready to be assigned to a truck and driver.</span>
                        </td>
                      </tr>
                    )}
                    {dispatchesPag.paginatedItems.map((d: any) => (
                      <tr key={d.id}>
                        <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{d.id?.slice(0, 8)}…</td>
                        <td className="text-sm text-ink-700 dark:text-ink-200">{d.batch_farmer_name || '—'}</td>
                        <td className="text-sm text-ink-700 dark:text-ink-200">{d.truck_registration || d.truck_reg || '—'}</td>
                        <td className="text-sm text-ink-700 dark:text-ink-200">{d.driver_name || 'Unassigned'}</td>
                        <td><SmartStatus status={d.status === 'ASSIGNED' ? 'PENDING' : d.status === 'DELIVERED' ? 'COMPLETED' : 'PROCESSING'} /></td>
                        <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{d.assigned_at ? new Date(d.assigned_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-ink-200 dark:border-ink-800">
                <Pagination
                  page={dispatchesPag.page}
                  pageSize={dispatchesPag.pageSize}
                  total={dispatchesPag.total}
                  onPageChange={dispatchesPag.setPage}
                  onPageSizeChange={dispatchesPag.setPageSize}
                  showPageSize
                />
              </div>
            </Card>
          </div>
        )}

        {/* ═══ WITHDRAWALS ═══ */}
        {activeTab === 'withdrawals' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800"><span className="text-sm text-ink-500 dark:text-ink-400">{withdrawalsPag.total} withdrawal requests</span></div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Farmer</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {withdrawalsPag.total === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-xs text-ink-500 dark:text-ink-400">No withdrawal requests</td></tr>
                  )}
                  {withdrawalsPag.paginatedItems.map((w: any) => (
                    <tr key={w.id}>
                      <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{w.farmer_name}</td>
                      <td className="nums font-medium text-ink-900 dark:text-ink-50">{Number(w.amount).toLocaleString()} XAF</td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400">{w.method}{w.carrier ? ` · ${w.carrier}` : ''}</td>
                      <td><SmartStatus status={w.status} /></td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{w.requested_at ? new Date(w.requested_at).toLocaleDateString() : '—'}</td>
                      <td>
                        {(w.status === 'PENDING' || w.status === 'PROCESSING') ? (
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => handleProcessWithdrawal(w.id, 'COMPLETED')} className="btn-secondary text-xs px-2 py-1 text-success-700 dark:text-success-500">Approve</button>
                            <button onClick={() => handleProcessWithdrawal(w.id, 'REJECTED')} className="btn-secondary text-xs px-2 py-1 text-danger-600 dark:text-danger-500">Reject</button>
                          </div>
                        ) : w.status === 'COMPLETED' ? (
                          <span className="text-2xs text-success-600 dark:text-success-500">Processed</span>
                        ) : <span className="text-2xs text-danger-600 dark:text-danger-500">Rejected</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={withdrawalsPag.page}
                pageSize={withdrawalsPag.pageSize}
                total={withdrawalsPag.total}
                onPageChange={withdrawalsPag.setPage}
                onPageSizeChange={withdrawalsPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ═══ TRANSACTIONS ═══ */}
        {activeTab === 'transactions' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800"><span className="text-sm text-ink-500 dark:text-ink-400">{transactionsPag.total} transactions</span></div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>TX ID</th><th>Farmer</th><th>Type</th><th>Amount</th><th>Carrier</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {transactionsPag.total === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-xs text-ink-500 dark:text-ink-400">No transactions yet</td></tr>
                  )}
                  {transactionsPag.paginatedItems.map((t: any) => (
                    <tr key={t.id}>
                      <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{t.transaction_id?.slice(0, 16)}…</td>
                      <td className="text-sm text-ink-700 dark:text-ink-200">{t.farmer_name || '—'}</td>
                      <td><StatusPill tone="info">{t.type}</StatusPill></td>
                      <td className="nums font-medium text-ink-900 dark:text-ink-50">{Number(t.amount).toLocaleString()} XAF</td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400">{t.carrier || '—'}</td>
                      <td><SmartStatus status={t.status} /></td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(t.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={transactionsPag.page}
                pageSize={transactionsPag.pageSize}
                total={transactionsPag.total}
                onPageChange={transactionsPag.setPage}
                onPageSizeChange={transactionsPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ═══ TRUCKS ═══ */}
        {activeTab === 'trucks' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between gap-3">
              <span className="text-sm text-ink-500 dark:text-ink-400">{trucksPag.total} trucks</span>
              <button onClick={handleOpenAddTruck} className="btn-accent brand-surface-cargo text-xs">
                <Icon.Plus className="w-3.5 h-3.5" /> Add truck
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Registration</th><th>Capacity</th><th>Driver</th><th>Fuel</th><th>Odometer</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {trucksPag.total === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-xs text-ink-500 dark:text-ink-400">No trucks yet</td></tr>
                  )}
                  {trucksPag.paginatedItems.map((t: any) => (
                    <tr key={t.id}>
                      <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{t.registration_number}</td>
                      <td className="nums text-2xs">{t.capacity_kg} kg</td>
                      <td className="text-sm text-ink-700 dark:text-ink-200">{t.driver_name || 'Unassigned'}</td>
                      <td>
                        <span className={`nums font-medium ${t.fuel_level_pct < 20 ? 'text-danger-600 dark:text-danger-500' : 'text-ink-700 dark:text-ink-200'}`}>{t.fuel_level_pct}%</span>
                      </td>
                      <td className="nums text-2xs">{Number(t.odometer_km || 0).toLocaleString()} km</td>
                      <td><SmartStatus status={t.status} /></td>
                      <td>
                        <button onClick={() => handleOpenAssignDriver(t)} className="btn-secondary text-xs">
                          <Icon.Users className="w-3.5 h-3.5" /> Assign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={trucksPag.page}
                pageSize={trucksPag.pageSize}
                total={trucksPag.total}
                onPageChange={trucksPag.setPage}
                onPageSizeChange={trucksPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ═══ FLEET MAP ═══ */}
        {activeTab === 'fleet' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader
                eyebrow="Telemetry"
                title="Live fleet map"
                subtitle="Real-time truck locations with driver names. Auto-refreshes every 15 seconds."
                icon={<Icon.MapPin className="w-4 h-4" />}
              />
            </div>
            <FleetMap height="600px" />
          </Card>
        )}

        {/* ═══ FARMS (multi-farm per farmer) ═══ */}
        {activeTab === 'farms' && <FarmsTab />}

        {/* ═══ VILLAGES ═══ */}
        {activeTab === 'villages' && <VillagesTab />}

        {/* ═══ AGENTS ═══ */}
        {activeTab === 'agents' && <AgentsTab />}

        {/* ═══ DRIVERS ═══ */}
        {activeTab === 'drivers' && <DriversTab />}

        {/* ═══ USERS ═══ */}
        {activeTab === 'users' && <UsersTab />}

        {/* ═══ PROFILE ═══ */}
        {activeTab === 'profile' && <ProfileTab />}

        {/* ═══ SMART ADVISORY (Phase 29 — Timeline Performance + Predicted Events) ═══ */}
        {activeTab === 'smart-advisory' && <SmartAdvisoryTab />}

        {/* ═══ PRODUCTS (CC-DTIP v4.0 Phase 19.6 — Agro-Chemical Registry) ═══ */}
        {activeTab === 'products' && <ProductsTab />}
        {/* v1.6.0: Chain of Custody tab */}
        {activeTab === 'chain' && <ChainOfCustodyTab />}
        {/* v2.1.1 (A4 fix): Harvest Notifications tab — surfaces dead harvest-stats data + full management UI */}
        {activeTab === 'harvest-notif' && (
          <div className="space-y-4">
            {/* v2.1.1 (A4 fix): Header */}
            {/* v2.1.1 (A4 fix) */}
            <SectionHeader
              eyebrow="Harvest Signals"
              title="Harvest Notifications — Farmer harvest-ready signals"
              subtitle="Farmers flag cocoa ready for pickup. Inspect, convert into batches, or cancel."
              icon={<Icon.Bell className="w-4 h-4" />}
            />

            {/* v2.1.1 (A4 fix): Stats grid — 4 KPI tiles pulled from /api/harvest-notifications/stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3"> {/* v2.1.1 (A4 fix) */}
              <MetricTile label="Pending"     value={Number(harvestNotifStats?.pending || 0)}    icon={<Icon.Clock className="w-4 h-4" />} accent="admin" /> {/* v2.1.1 (A4 fix) */}
              <MetricTile label="Inspecting"  value={Number(harvestNotifStats?.inspected || 0)} icon={<Icon.Search className="w-4 h-4" />} /> {/* v2.1.1 (A4 fix) */}
              <MetricTile label="Converted"   value={Number(harvestNotifStats?.batched || 0)}   icon={<Icon.Check className="w-4 h-4" />} /> {/* v2.1.1 (A4 fix) */}
              <MetricTile label="Cancelled"   value={Number(harvestNotifStats?.cancelled || 0)} icon={<Icon.X className="w-4 h-4" />} /> {/* v2.1.1 (A4 fix) */}
            </div>

            {/* v2.1.1 (A4 fix): Action result alert (success/danger after inspect/convert/cancel) */}
            {/* v2.1.1 (A4 fix) */}
            {harvestNotifActionMsg && (
              <Alert tone={harvestNotifActionMsg.type === 'success' ? 'success' : 'danger'} onClose={() => setHarvestNotifActionMsg(null)}>
                {harvestNotifActionMsg.text}
              </Alert>
            )}
            {/* v2.1.1 (A4 fix): List-level error alert */}
            {/* v2.1.1 (A4 fix) */}
            {harvestNotifError && (
              <Alert tone="danger" onClose={() => setHarvestNotifError(null)}>{harvestNotifError}</Alert>
            )}

            {/* v2.1.1 (A4 fix): Filter buttons — All / Pending / Inspecting / Converted / Cancelled */}
            <Card padding="lg"> {/* v2.1.1 (A4 fix) */}
              <div className="flex flex-wrap items-center gap-2"> {/* v2.1.1 (A4 fix) */}
                <span className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mr-1">Filter:</span> {/* v2.1.1 (A4 fix) */}
                {/* v2.1.1 (A4 fix) */}
                {([
                  { label: 'All',         value: 'ALL' },
                  { label: 'Pending',     value: 'PENDING' },
                  { label: 'Inspecting',  value: 'INSPECTED' }, /* v2.1.1 (A4 fix) — backend status INSPECTED maps to UI "Inspecting" */
                  { label: 'Converted',   value: 'BATCHED' }, /* v2.1.1 (A4 fix) — backend status BATCHED maps to UI "Converted" */
                  { label: 'Cancelled',   value: 'CANCELLED' },
                ] as { label: string; value: string }[]).map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setHarvestNotifFilter(opt.value)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                      harvestNotifFilter === opt.value
                        ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900'
                        : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                <div className="ml-auto"> {/* v2.1.1 (A4 fix) */}
                  <button onClick={() => fetchHarvestNotifs()} disabled={harvestNotifLoading} className="btn-secondary text-xs"> {/* v2.1.1 (A4 fix) */}
                    <Icon.Refresh className={`w-3.5 h-3.5 ${harvestNotifLoading ? 'animate-spin' : ''}`} /> {/* v2.1.1 (A4 fix) */}
                    <span>Refresh</span> {/* v2.1.1 (A4 fix) */}
                  </button>
                </div>
              </div>
            </Card>

            {/* v2.1.1 (A4 fix): Notifications table */}
            <Card padding="none" className="overflow-hidden"> {/* v2.1.1 (A4 fix) */}
              {harvestNotifLoading && harvestNotifs.length === 0 ? ( /* v2.1.1 (A4 fix) */
                <div className="p-8 flex justify-center"><Spinner /></div> /* v2.1.1 (A4 fix) */
              ) : harvestNotifs.length === 0 ? ( /* v2.1.1 (A4 fix) */
                <div className="p-8"> {/* v2.1.1 (A4 fix) */}
                  <EmptyState /* v2.1.1 (A4 fix) */
                    icon={<Icon.Bell className="w-5 h-5" />} /* v2.1.1 (A4 fix) */
                    title="No harvest notifications" /* v2.1.1 (A4 fix) */
                    description="Farmers have not signalled any harvest-ready cocoa matching this filter." /* v2.1.1 (A4 fix) */
                  />
                </div>
              ) : ( /* v2.1.1 (A4 fix) */
                <div className="overflow-x-auto"> {/* v2.1.1 (A4 fix) */}
                  <table className="data-table"> {/* v2.1.1 (A4 fix) */}
                    <thead> {/* v2.1.1 (A4 fix) */}
                      <tr> {/* v2.1.1 (A4 fix) */}
                        <th>Notification #</th> {/* v2.1.1 (A4 fix) */}
                        <th>Farmer</th> {/* v2.1.1 (A4 fix) */}
                        <th>Village</th> {/* v2.1.1 (A4 fix) */}
                        <th>Expected Bags</th> {/* v2.1.1 (A4 fix) */}
                        <th>Expected Weight (kg)</th> {/* v2.1.1 (A4 fix) */}
                        <th>Status</th> {/* v2.1.1 (A4 fix) */}
                        <th>Created At</th> {/* v2.1.1 (A4 fix) */}
                        <th>Actions</th> {/* v2.1.1 (A4 fix) */}
                      </tr>
                    </thead>
                    <tbody> {/* v2.1.1 (A4 fix) */}
                      {harvestNotifs.map((n: any) => { /* v2.1.1 (A4 fix) */
                        const meta = harvestNotifStatusMeta(n.status); /* v2.1.1 (A4 fix) */
                        const rowLoading = harvestNotifActionLoading === n.id; /* v2.1.1 (A4 fix) */
                        return ( /* v2.1.1 (A4 fix) */
                          <tr key={n.id} className={rowLoading ? 'opacity-60' : ''}> {/* v2.1.1 (A4 fix) */}
                            <td className="text-xs font-mono">{n.id ? `${n.id.slice(0, 8)}…` : '—'}</td> {/* v2.1.1 (A4 fix) */}
                            <td className="text-sm">{n.farmer_name || n.farmer_id || '—'}</td> {/* v2.1.1 (A4 fix) */}
                            <td className="text-sm">{n.village || '—'}</td> {/* v2.1.1 (A4 fix) */}
                            <td className="text-sm nums">{Number(n.expected_bags || 0)}</td> {/* v2.1.1 (A4 fix) */}
                            <td className="text-sm nums">{Number(n.expected_weight_kg || 0).toFixed(1)}</td> {/* v2.1.1 (A4 fix) */}
                            <td><StatusPill tone={meta.tone} dot>{meta.label}</StatusPill></td> {/* v2.1.1 (A4 fix) */}
                            <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{n.created_at ? new Date(n.created_at).toLocaleString() : '—'}</td> {/* v2.1.1 (A4 fix) */}
                            <td> {/* v2.1.1 (A4 fix) */}
                              <div className="flex flex-wrap items-center gap-1.5"> {/* v2.1.1 (A4 fix) */}
                                {n.status === 'PENDING' && ( /* v2.1.1 (A4 fix) */
                                  <button /* v2.1.1 (A4 fix) */
                                    onClick={() => handleHarvestNotifInspect(n.id)} /* v2.1.1 (A4 fix) */
                                    disabled={!!harvestNotifActionLoading} /* v2.1.1 (A4 fix) */
                                    className="btn-secondary text-xs px-2 py-1" /* v2.1.1 (A4 fix) */
                                    title="Mark as inspecting (PATCH /:id/inspect)" /* v2.1.1 (A4 fix) */
                                  >
                                    <Icon.Search className="w-3 h-3" /> Mark Inspecting {/* v2.1.1 (A4 fix) */}
                                  </button>
                                )}
                                {n.status === 'INSPECTED' && ( /* v2.1.1 (A4 fix) */
                                  <button /* v2.1.1 (A4 fix) */
                                    onClick={() => handleHarvestNotifConvert(n)} /* v2.1.1 (A4 fix) */
                                    disabled={!!harvestNotifActionLoading} /* v2.1.1 (A4 fix) */
                                    className="btn-primary text-xs px-2 py-1" /* v2.1.1 (A4 fix) */
                                    title="Convert into a PENDING batch (POST /:id/convert)" /* v2.1.1 (A4 fix) */
                                  >
                                    <Icon.Package className="w-3 h-3" /> Convert to Batch {/* v2.1.1 (A4 fix) */}
                                  </button>
                                )}
                                {(n.status === 'PENDING' || n.status === 'INSPECTED') && ( /* v2.1.1 (A4 fix) */
                                  <button /* v2.1.1 (A4 fix) */
                                    onClick={() => handleHarvestNotifCancel(n.id)} /* v2.1.1 (A4 fix) */
                                    disabled={!!harvestNotifActionLoading} /* v2.1.1 (A4 fix) */
                                    className="btn-secondary text-xs px-2 py-1 text-danger-600 dark:text-danger-500" /* v2.1.1 (A4 fix) */
                                    title="Cancel notification (PATCH /:id/cancel)" /* v2.1.1 (A4 fix) */
                                  >
                                    <Icon.X className="w-3 h-3" /> Cancel {/* v2.1.1 (A4 fix) */}
                                  </button>
                                )}
                                {n.status === 'BATCHED' && n.batch_id && ( /* v2.1.1 (A4 fix) */
                                  <span className="text-2xs text-success-600 dark:text-success-500 font-mono" title="Linked batch id">batch {String(n.batch_id).slice(0, 8)}…</span> /* v2.1.1 (A4 fix) */
                                )}
                                {rowLoading && <Spinner size="sm" />} {/* v2.1.1 (A4 fix) */}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
        {/* v1.7.9: Yield forecasting + MRL lab tests + cash float tracker */}
        {activeTab === 'yield' && <YieldForecastTab />}
        {activeTab === 'lab-tests' && <LabTestsTab />}
        {activeTab === 'cash-floats' && <CashFloatsTab />}
        {/* v1.8.0: EUDR DDS Generator + Export Documents Hub */}
        {activeTab === 'dds' && <EudrDDSTab />}
        {activeTab === 'export-docs' && <ExportDocsTab />}
        {/* v1.8.1: Phase 3 — Organoleptic + Farm Inputs + Stock Reconciliation */}
        {activeTab === 'organoleptic' && <OrganolepticTab />}
        {activeTab === 'farm-inputs' && <FarmInputsTab />}
        {activeTab === 'stock-counts' && <StockCountsTab />}
        {/* v1.8.2: Post-harvest infrastructure */}
        {activeTab === 'post-harvest' && <PostHarvestTab />}
        {/* v1.9.2: Admin points config + MEL overview + quality correlation */}
        {activeTab === 'points-rules' && <PointsRulesTab />}
        {activeTab === 'mel-overview' && <MELOverviewTab />}
        {activeTab === 'quality-corr' && <QualityCorrelationTab />}
        {/* v2.0.0: Regulatory API Gateway */}
        {activeTab === 'regulatory' && <RegulatoryApiTab />}

        {/* ═══ v2.1.1 (A3 fix): AI GOVERNANCE — Prompt Versioning & Inference Logging ═══ */}
        {activeTab === 'ai-governance' && (
          <div className="space-y-4">
            {/* v2.1.1 (A3 fix): error + success banners */}
            {aiGovError && <Alert tone="danger" onClose={() => setAiGovError(null)}>{aiGovError}</Alert>}
            {aiGovSuccess && <Alert tone="success" onClose={() => setAiGovSuccess(null)}>{aiGovSuccess}</Alert>}

            {/* v2.1.1 (A3 fix): AI Governance header */}
            <SectionHeader
              eyebrow="AI Governance"
              title="AI Governance — Prompt Versioning & Inference Logging"
              subtitle="Every Gemini inference is persisted with prompt version, confidence, and user feedback. Approve, retire, or audit prompts here. (DPO/admin only)"
              icon={<Icon.Cpu className="w-4 h-4" />}
            />

            {/* v2.1.1 (A3 fix): KPI tiles — Total Inferences, Avg Confidence, Rejection Rate, Active Prompts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total Inferences" value={Number(aiGovStats?.total_inferences ?? 0).toLocaleString()} hint="Last 30 days" icon={<Icon.Activity className="w-4 h-4" />} accent="admin" />
              <MetricTile
                label="Avg Confidence"
                value={`${(Number(Array.isArray(aiGovStats?.by_prompt) && aiGovStats.by_prompt.length > 0 ? aiGovStats.by_prompt.reduce((s: number, p: any) => s + Number(p.avg_confidence || 0), 0) / aiGovStats.by_prompt.length : 0) * 100).toFixed(1)}%`}
                hint="Across all prompts"
                icon={<Icon.TrendingUp className="w-4 h-4" />}
              />
              <MetricTile label="Rejection Rate" value={`${(Number(aiGovStats?.rejected_rate ?? 0) * 100).toFixed(1)}%`} hint="User-feedback REJECTED" icon={<Icon.Alert className="w-4 h-4" />} />
              <MetricTile label="Active Prompts" value={aiGovPrompts.filter((p: any) => p.status === 'ACTIVE').length} hint={`${aiGovPrompts.length} versions total`} icon={<Icon.Sparkles className="w-4 h-4" />} />
            </div>

            {/* v2.1.1 (A3 fix): loading + side-by-side Prompt Versions + Inference Log */}
            {aiGovLoading ? (
              <Card padding="lg"><div className="flex justify-center items-center py-8"><Spinner /> <span className="ml-3 text-sm text-ink-500 dark:text-ink-400">Loading AI governance data…</span></div></Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* v2.1.1 (A3 fix): Prompt Versions table — Key | Version | Status | Created | Actions */}
                <Card padding="none" className="overflow-hidden">
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
                    <SectionHeader eyebrow="Prompt Versioning" title="Prompt Versions" subtitle="DRAFT → ACTIVE → RETIRED lifecycle" icon={<Icon.Sparkles className="w-4 h-4" />} />
                    <button onClick={() => setShowAiGovCreateModal(true)} className="btn-primary text-xs"><Icon.Plus className="w-4 h-4" /> Add Version</button>
                  </div>
                  {aiGovPrompts.length === 0 ? (
                    <div className="p-8"><EmptyState icon={<Icon.Sparkles className="w-5 h-5" />} title="No prompt versions" description="Create your first prompt version to begin auditing AI inferences." /></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead><tr><th>Key</th><th>Version</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                        <tbody>
                          {aiGovPrompts.map((p: any) => (
                            <tr key={p.id}>
                              <td className="text-sm font-medium text-ink-900 dark:text-ink-50 font-mono">{p.prompt_key}</td>
                              <td className="nums">v{p.version}</td>
                              <td><StatusPill tone={p.status === 'ACTIVE' ? 'success' : p.status === 'DRAFT' ? 'warning' : 'neutral'} dot>{p.status}</StatusPill></td>
                              <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                              <td>
                                <div className="flex gap-1.5">
                                  {p.status !== 'ACTIVE' && (
                                    <button onClick={() => handleApproveAiGovPrompt(p.id)} className="btn-secondary text-2xs" title="Promote to ACTIVE">Approve</button>
                                  )}
                                  {p.status !== 'RETIRED' && (
                                    <button onClick={() => handleRetireAiGovPrompt(p.id)} className="btn-ghost text-2xs" title="Retire this version">Retire</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.1.1 (A3 fix): Recent Inference Log table — Timestamp | Prompt Key | Version | Confidence | Outcome */}
                <Card padding="none" className="overflow-hidden">
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                    <SectionHeader eyebrow="Audit Trail" title="Recent Inference Log" subtitle="Last 50 Gemini calls with outcome" icon={<Icon.Database className="w-4 h-4" />} />
                  </div>
                  {aiGovInferences.length === 0 ? (
                    <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No inferences logged" description="Inference records will appear here as users interact with AI features (advisory, grading, smart advisory)." /></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead><tr><th>Timestamp</th><th>Prompt Key</th><th>Version</th><th>Confidence</th><th>Outcome</th></tr></thead>
                        <tbody>
                          {aiGovInferences.map((l: any) => {
                            // v2.1.1 (A3 fix): derive outcome from error_message / user_feedback / hallucination flag
                            const outcome = l.error_message
                              ? { tone: 'danger' as const, label: 'ERROR' }
                              : l.user_feedback === 'REJECTED'
                                ? { tone: 'danger' as const, label: 'REJECTED' }
                                : l.user_feedback === 'CONFIRMED'
                                  ? { tone: 'success' as const, label: 'ACCEPTED' }
                                  : l.is_hallucination_suspected
                                    ? { tone: 'warning' as const, label: 'REVIEW' }
                                    : { tone: 'info' as const, label: 'REVIEW' };
                            return (
                              <tr key={l.id}>
                                <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                                <td className="text-sm font-medium text-ink-900 dark:text-ink-50 font-mono">{l.prompt_key}</td>
                                <td className="nums">{l.prompt_version ? `v${l.prompt_version}` : '—'}</td>
                                <td className="nums">{l.confidence_score != null ? `${(Number(l.confidence_score) * 100).toFixed(0)}%` : '—'}</td>
                                <td><StatusPill tone={outcome.tone} dot>{outcome.label}</StatusPill></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ═══ v2.2.0: DATA SOURCES — Volume XXXI External Data Source Catalog ═══ */}
        {activeTab === 'data-sources' && ( // v2.2.0
          <div className="space-y-4"> {/* v2.2.0 */}
            {/* v2.2.0: header */}
            <SectionHeader
              eyebrow="Volume XXXI"
              title="Volume XXXI — External Data Sources"
              subtitle="Catalog of 29 external data sources (DS001-DS029) with connector status, sync history, and trust levels."
              icon={<Icon.Database className="w-4 h-4" />}
              actions={
                <button onClick={fetchAllKeys} className="btn-secondary text-xs inline-flex items-center gap-1.5">
                  <Icon.Key className="w-3 h-3" /> View All API Keys
                </button>
              }
            />

            {/* v2.2.0: KPI tiles pulled from /api/data-sources/stats/summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"> {/* v2.2.0 */}
              <MetricTile label="Total Sources"          value={dataSources.length}                                                    icon={<Icon.Database className="w-4 h-4" />} accent="admin" /> {/* v2.2.0 */}
              <MetricTile label="Documents Ingested"     value={Number(dataSourcesStats?.totals?.documents ?? 0).toLocaleString()}             icon={<Icon.FileText className="w-4 h-4" />} /> {/* v2.2.0 */}
              <MetricTile label="Research Papers"        value={Number(dataSourcesStats?.totals?.papers ?? 0).toLocaleString()}                icon={<Icon.FileText className="w-4 h-4" />} /> {/* v2.2.0 */}
              <MetricTile label="Weather Observations"   value={Number(dataSourcesStats?.totals?.weather ?? 0).toLocaleString()}               icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.2.0 */}
              <MetricTile label="Deforestation Alerts"   value={Number(dataSourcesStats?.totals?.deforestation_alerts ?? 0).toLocaleString()}   icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.2.0 */}
              <MetricTile label="Soil Samples"           value={Number(dataSourcesStats?.totals?.soil_samples ?? 0).toLocaleString()}          icon={<Icon.MapPin className="w-4 h-4" />} /> {/* v2.2.0 */}
            </div>

            {dataSourcesError && <Alert tone="danger" onClose={() => setDataSourcesError(null)}>{dataSourcesError}</Alert>} {/* v2.2.0 */}

            {/* v2.2.0: filter buttons — All / Government / Research / Satellite / Weather / Soil / Geospatial / AI Model */}
            <div className="flex flex-wrap gap-1.5"> {/* v2.2.0 */}
              {['ALL', 'government', 'research', 'satellite', 'weather', 'soil', 'geospatial', 'ai_model'].map(cat => ( // v4.2: Fixed case — DB stores lowercase
                <button
                  key={cat}
                  onClick={() => setDataSourcesFilter(cat)} // v2.2.0
                  className={`text-2xs font-medium px-3 py-1.5 rounded-lg transition-colors ${dataSourcesFilter === cat ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700'}`} // v2.2.0
                >
                  {cat === 'ALL' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* v2.2.0: sources table */}
            <Card padding="none" className="overflow-hidden"> {/* v2.2.0 */}
              <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.2.0 */}
                <SectionHeader eyebrow="Catalog" title="Data Source Catalog (DS001-DS029)" subtitle="Sync on demand — connectors registered dynamically" icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.2.0 */}
              </div>
              {dataSourcesLoading ? ( // v2.2.0
                <div className="p-8 flex justify-center"><Spinner /></div> // v2.2.0
              ) : dataSources.length === 0 ? ( // v2.2.0
                <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No data sources registered" description="Run the Volume XXXI migration to seed the catalog." /></div> // v2.2.0
              ) : (
                <div className="overflow-x-auto"> {/* v2.2.0 */}
                  <table className="data-table"> {/* v2.2.0 */}
                    <thead> {/* v2.2.0 */}
                      <tr> {/* v2.2.0 */}
                        <th>Code</th><th>Name</th><th>Category</th><th>Update Freq</th><th>Trust</th><th>Auth Required</th><th>Last Sync</th><th>Records</th><th>Status</th><th>Actions</th> {/* v2.2.0 */}
                      </tr>
                    </thead>
                    <tbody> {/* v2.2.0 */}
                      {dataSources
                        .filter((s: any) => dataSourcesFilter === 'ALL' || s.category === dataSourcesFilter) // v2.2.0
                        .map((s: any) => {
                          const isSyncing = dataSourcesSyncing.has(s.source_code); // v2.2.0
                          const result = dataSourcesSyncResult[s.source_code]; // v2.2.0
                          // v2.2.0: map backend last_sync_status -> StatusPill tone
                          const statusMeta = (() => { // v2.2.0
                            const st = (s.last_sync_status || 'NEVER').toUpperCase(); // v2.2.0
                            if (st === 'SUCCESS') return { tone: 'success' as const, label: 'SUCCESS' }; // v2.2.0 — green
                            if (st === 'FAILED' || st === 'ERROR') return { tone: 'danger' as const, label: 'FAILED' }; // v2.2.0 — red
                            if (st === 'PARTIAL') return { tone: 'warning' as const, label: 'PARTIAL' }; // v2.2.0 — yellow
                            return { tone: 'neutral' as const, label: 'NEVER' }; // v2.2.0 — gray
                          })();
                          return (
                            <tr key={s.source_code}> {/* v2.2.0 */}
                              <td className="text-xs font-mono font-semibold">{s.source_code}</td> {/* v2.2.0 */}
                              <td className="text-sm font-medium">
                                <div className="flex flex-col">
                                  <span>{s.name}</span>
                                  {/* v4.2: Auth badge with "Enter Key" button for auth-required sources */}
                                  {s.requires_auth && s.auth_env_var && (
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <span className="text-2xs text-warning-700 dark:text-warning-400">🔑 <code className="font-mono">{s.auth_env_var}</code></span>
                                      <button
                                        onClick={() => openDataSourceKeySheet(s)}
                                        className="text-2xs text-admin-700 dark:text-admin-500 hover:underline"
                                      >
                                        Enter Key →
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td><StatusPill tone="info" dot>{s.category || '—'}</StatusPill></td> {/* v2.2.0 */}
                              <td className="text-2xs">{s.update_frequency || '—'}</td> {/* v2.2.0 */}
                              <td><StatusPill tone={Number(s.trust_level) >= 90 ? 'success' : Number(s.trust_level) >= 70 ? 'warning' : 'neutral'}>{s.trust_level ? `${s.trust_level}%` : '—'}</StatusPill></td> {/* v4.2: Fixed trust_level — DB stores integer, not string */}
                              <td className="text-2xs">{s.requires_auth ? '✓ Yes' : 'No'}</td> {/* v2.2.0 */}
                              <td className="text-2xs nums">{s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : 'Never'}</td> {/* v2.2.0 */}
                              <td className="nums">{Number(s.last_sync_records || 0).toLocaleString()}</td> {/* v2.2.0 */}
                              <td><StatusPill tone={statusMeta.tone} dot>{statusMeta.label}</StatusPill></td> {/* v2.2.0 */}
                              <td> {/* v2.2.0 */}
                                <div className="flex flex-col gap-1"> {/* v2.2.0 */}
                                  <button
                                    onClick={() => handleDataSourceSync(s.source_code)} // v2.2.0
                                    disabled={isSyncing} // v2.2.0
                                    className="btn-secondary text-2xs inline-flex items-center gap-1.5"
                                  >
                                    {isSyncing ? <Spinner size="sm" /> : <Icon.Refresh className="w-3 h-3" />} {/* v2.2.0 */}
                                    {isSyncing ? 'Syncing…' : 'Sync Now'} {/* v2.2.0 */}
                                  </button>
                                  {/* v4.2: show sync result — check result.error too (not just HTTP status) */}
                                  {result && (
                                    <span className={`text-2xs ${result.ok && !result.result?.error ? 'text-success-700 dark:text-success-400' : 'text-danger-700 dark:text-danger-400'}`}>
                                      {result.ok && !result.result?.error
                                        ? `✓ +${result.result?.inserted ?? 0} new / ${result.result?.updated ?? 0} updated`
                                        : `✕ ${result.error || result.result?.error || 'Sync failed'}`}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* v4.3.2: Sheet modal for entering a data source API key — with key type selector */}
            <Sheet
              open={dsKeySheetOpen}
              onClose={() => { setDsKeySheetOpen(false); setDsKeySheetData(null); setDsKeyValue(''); setDsKeyType('auto'); }}
              title={dsKeySheetData ? `API Key — ${dsKeySheetData.name}` : 'Enter API Key'}
              footer={
                <div className="flex gap-2">
                  <button onClick={handleSaveDataSourceKey} disabled={dsKeySaving || dsKeyValue.trim().length < 5} className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5">
                    {dsKeySaving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save & Enable Sync
                  </button>
                  <button onClick={() => { setDsKeySheetOpen(false); setDsKeySheetData(null); setDsKeyValue(''); setDsKeyType('auto'); }} className="btn-ghost">Cancel</button>
                </div>
              }
            >
              {dsKeySheetData && (
                <div className="space-y-3">
                  <div>
                    <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Data Source</p>
                    <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{dsKeySheetData.name}</p>
                    <p className="text-2xs text-ink-500 dark:text-ink-400">{dsKeySheetData.source_code}</p>
                  </div>

                  {/* Current key status */}
                  {dsKeyStatus[dsKeySheetData.source_code]?.configured ? (
                    <div className="p-2 rounded-lg bg-success-50 dark:bg-success-500/10 border border-success-500/20">
                      <p className="text-2xs text-success-700 dark:text-success-400">
                        ✓ Key configured ({dsKeyStatus[dsKeySheetData.source_code]?.masked_value}) — source: {dsKeyStatus[dsKeySheetData.source_code]?.source}
                      </p>
                    </div>
                  ) : (
                    <div className="p-2 rounded-lg bg-warning-50 dark:bg-warning-500/10 border border-warning-500/20">
                      <p className="text-2xs text-warning-700 dark:text-warning-400">⚠ No API key configured. Sync will fail until you enter a key.</p>
                    </div>
                  )}

                  {/* v4.3.2: Key type selector — only for sources that support multiple formats */}
                  {dsKeySheetData.source_code === 'DS010' && (
                    <div>
                      <label className="label">Key Type</label>
                      <select className="input mb-2" value={dsKeyType} onChange={e => setDsKeyType(e.target.value as any)}>
                        <option value="auto">Auto-detect (recommended)</option>
                        <option value="jwt">JWT Access Token (from dashboard — starts with "eyJ")</option>
                        <option value="client_id_secret">Client ID : Client Secret (for OAuth2)</option>
                      </select>
                      {dsKeyType === 'jwt' && (
                        <div className="p-2 rounded-lg bg-info-50 dark:bg-info-500/10 border border-info-500/20 mb-2">
                          <p className="text-2xs text-info-700 dark:text-info-400">
                            <strong>How to get a JWT token:</strong><br/>
                            1. Go to <a href="https://dataspace.copernicus.eu/" target="_blank" className="underline">dataspace.copernicus.eu</a> and log in<br/>
                            2. Open browser DevTools → Network tab<br/>
                            3. Click on any product in the catalog<br/>
                            4. Find the request with <code className="font-mono">Authorization: Bearer eyJ...</code><br/>
                            5. Copy the token (starts with "eyJ")<br/>
                            <strong>⚠ Note:</strong> JWT tokens expire (usually in 1 hour). For production, use Client ID:Secret instead.
                          </p>
                        </div>
                      )}
                      {dsKeyType === 'client_id_secret' && (
                        <div className="p-2 rounded-lg bg-info-50 dark:bg-info-500/10 border border-info-500/20 mb-2">
                          <p className="text-2xs text-info-700 dark:text-info-400">
                            <strong>How to get Client ID + Secret:</strong><br/>
                            1. Go to <a href="https://dataspace.copernicus.eu/" target="_blank" className="underline">dataspace.copernicus.eu</a><br/>
                            2. Register → Create a new application<br/>
                            3. Copy the Client ID and Client Secret<br/>
                            4. Paste as: <code className="font-mono">client_id:client_secret</code><br/>
                            <strong>✓ This doesn't expire — recommended for production.</strong>
                          </p>
                        </div>
                      )}
                      {dsKeyType === 'auto' && (
                        <div className="p-2 rounded-lg bg-info-50 dark:bg-info-500/10 border border-info-500/20 mb-2">
                          <p className="text-2xs text-info-700 dark:text-info-400">
                            Paste either:<br/>
                            • A <strong>JWT access token</strong> (starts with "eyJ") — from the Copernicus dashboard<br/>
                            • A <strong>Client ID:Client Secret</strong> pair — from your Copernicus application<br/>
                            The system will auto-detect the format.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Source-specific instructions for other auth sources */}
                  {dsKeySheetData.source_code !== 'DS010' && dsKeySheetData.auth_env_var && (
                    <div className="p-2 rounded-lg bg-info-50 dark:bg-info-500/10 border border-info-500/20">
                      <p className="text-2xs text-info-700 dark:text-info-400">
                        <strong>Required:</strong> <code className="font-mono">{dsKeySheetData.auth_env_var}</code><br/>
                        {dsKeySheetData.source_code === 'DS019' && 'Get a token from api.globalforestwatch.org → register → copy your API token.'}
                        {dsKeySheetData.source_code === 'DS012' && 'Get a token from urs.earthdata.nasa.gov → register → copy your token.'}
                        {dsKeySheetData.source_code === 'DS013' && 'Get a key from planet.com/nicfi → register for NICFI access.'}
                        {dsKeySheetData.source_code === 'DS014' && 'Get a key from cds.climate.copernicus.eu → register → copy your API key (format: UID:KEY).'}
                        {dsKeySheetData.source_code === 'DS034' && 'Paste your Google Earth Engine service account JSON key.'}
                        {dsKeySheetData.source_code === 'DS042' && 'Get a key from app.tomorrow.io → register → copy your API key.'}
                        {dsKeySheetData.source_code === 'DS043' && 'Get a key from weatherapi.com → register → copy your API key.'}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="label">API Key / Token Value</label>
                    <textarea
                      autoComplete="off"
                      spellCheck={false}
                      value={dsKeyValue}
                      onChange={e => setDsKeyValue(e.target.value)}
                      placeholder={
                        dsKeySheetData.source_code === 'DS010' && dsKeyType === 'client_id_secret'
                          ? 'client_id:client_secret'
                          : dsKeySheetData.source_code === 'DS010' && dsKeyType === 'jwt'
                          ? 'eyJhbGciOiJSUzI1NiIs...'
                          : 'Paste your API key / token here…'
                      }
                      className="input font-mono"
                      rows={3}
                    />
                    <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1.5">
                      Stored in the database as <code className="font-mono">ai_key_{(dsKeySheetData.auth_env_var || 'key').toLowerCase().replace(/[^a-z0-9]/g, '_')}</code>. Takes effect immediately.
                    </p>
                  </div>
                </div>
              )}
            </Sheet>

            {/* v4.2: Sheet modal for viewing all API keys */}
            <Sheet
              open={allKeysSheetOpen}
              onClose={() => setAllKeysSheetOpen(false)}
              title="All API Keys in Database"
              footer={
                <button onClick={() => setAllKeysSheetOpen(false)} className="btn-ghost">Close</button>
              }
            >
              {allKeysLoading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : allKeys.length === 0 ? (
                <p className="text-sm text-ink-500 dark:text-ink-400 text-center py-6">No API keys found in the database.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mb-2">{allKeys.length} key(s) stored in the database. Click "Copy" to copy the full key value.</p>
                  {allKeys.map((k: any) => (
                    <div key={k.key} className="p-2.5 rounded-lg border border-ink-200 dark:border-ink-700 surface-inset">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-2xs text-ink-600 dark:text-ink-300">{k.key}</span>
                            {k.is_configured ? (
                              <span className="text-2xs px-1.5 py-0.5 rounded bg-success-100 dark:bg-success-500/20 text-success-800 dark:text-success-300">✓ Configured</span>
                            ) : (
                              <span className="text-2xs px-1.5 py-0.5 rounded bg-warning-100 dark:bg-warning-500/20 text-warning-800 dark:text-warning-300">⚠ Empty</span>
                            )}
                          </div>
                          {k.description && <p className="text-2xs text-ink-400 dark:text-ink-500 mt-0.5">{k.description}</p>}
                          {k.masked_value && <p className="font-mono text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{k.masked_value}</p>}
                          {k.updated_at && <p className="text-2xs text-ink-400 dark:text-ink-500">Updated: {new Date(k.updated_at).toLocaleString()}</p>}
                        </div>
                        {k.full_value && (
                          <button
                            onClick={() => copyKey(k.key, k.full_value)}
                            className={`btn-secondary text-2xs py-1 px-2 shrink-0 ${copiedKey === k.key ? 'text-success-600 dark:text-success-500' : ''}`}
                          >
                            {copiedKey === k.key ? '✓ Copied!' : 'Copy'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Sheet>
          </div>
        )}

        {/* ═══ v4.4: AI AGENTS — 100% DB-DRIVEN, every card reads from DB ═══ */}
        {activeTab === 'ai-agents' && (
          <div className="space-y-4">
            {/* v4.4: header */}
            <SectionHeader
              eyebrow="Volume XXXI"
              title={`Volume XXXI — AI Agents (${aiAgents.length || 35})`}
              subtitle={`Every agent is bound to its task-type recommended AI (primary) and the system default ${aiAgentsSystemDefault} (fallback). The orchestrator verifies every binding every 5 min. All values are 100% database-driven — edit any binding below and the change takes effect immediately.`}
              icon={<Icon.Cpu className="w-4 h-4" />}
            />

            {aiAgentsError && <Alert tone="danger" onClose={() => setAiAgentsError(null)}>{aiAgentsError}</Alert>}

            {/* v4.4: system default + verify-all banner */}
            <Card padding="md" className="bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-semibold text-ink-900 dark:text-ink-50">System Default AI:</span>
                  <code className="font-mono text-xs bg-white dark:bg-ink-900 px-2 py-0.5 rounded border border-ink-200 dark:border-ink-700">{aiAgentsSystemDefault}</code>
                  <span className="text-ink-500">·</span>
                  <span className="font-semibold">Task Types (DB):</span>
                  <span className="text-2xs">{aiAgentsTaskTypes.length}</span>
                  <span className="text-ink-500">·</span>
                  <span className="font-semibold">Providers (DB):</span>
                  <span className="text-2xs">{aiAgentsProviders.length}</span>
                  <span className="text-ink-500">·</span>
                  <span className="font-semibold">Models (DB):</span>
                  <span className="text-2xs">{aiAgentsAllModels.length}</span>
                </div>
                <button
                  onClick={handleVerifyAllAgents}
                  disabled={aiAgentVerifyingAll}
                  className="btn-primary text-xs inline-flex items-center gap-1.5"
                >
                  {aiAgentVerifyingAll ? <Spinner size="sm" /> : <Icon.RefreshCw className="w-3.5 h-3.5" />}
                  {aiAgentVerifyingAll ? 'Verifying…' : 'Orchestrator: Verify All'}
                </button>
              </div>
            </Card>

            {/* v4.4: verify-all result banner */}
            {aiAgentVerifyResult && (
              <Alert
                tone={aiAgentVerifyResult.unverified === 0 ? 'success' : 'warning'}
                onClose={() => setAiAgentVerifyResult(null)}
              >
                <div className="text-sm">
                  <strong>Orchestrator verification complete:</strong> {aiAgentVerifyResult.verified}/{aiAgentVerifyResult.total} agents verified.
                  {aiAgentVerifyResult.unverified > 0 && (
                    <span className="ml-2">
                      Unverified: {aiAgentVerifyResult.results.filter((r: any) => !r.verified).map((r: any) => r.agent_code).join(', ')}
                    </span>
                  )}
                </div>
              </Alert>
            )}

            {/* v4.4: provider availability banner (DB-driven — no hardcoded list) */}
            <Card padding="md" className="bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-semibold text-ink-900 dark:text-ink-50">Provider Availability (from DB):</span>
                {aiAgentsProviders.length === 0 ? (
                  <span className="text-2xs text-ink-500">No providers in DB — run migration 060.</span>
                ) : (
                  aiAgentsProviders.map((p: any) => (
                    <span key={p.provider_code} className="text-2xs inline-flex items-center gap-1">
                      <span className={p.configured ? 'text-success-600 dark:text-success-400' : 'text-ink-400'}>
                        {p.configured ? '✓' : '✗'}
                      </span>
                      <span className="font-medium">{p.provider_name}</span>
                      {p.is_free && <span className="text-2xs text-success-600 dark:text-success-400">(free)</span>}
                    </span>
                  ))
                )}
              </div>
            </Card>

            {aiAgentsLoading ? (
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>
            ) : aiAgents.length === 0 ? (
              <Card padding="lg"><EmptyState icon={<Icon.Cpu className="w-5 h-5" />} title="No AI agents registered" description="Run migration 062 (agent_model_bindings_v44) to seed the agent registry." /></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiAgents.map((agent: any) => {
                  const isRunning = aiAgentRunning === agent.agent_code;
                  const result = aiAgentResult[agent.agent_code];
                  const verified = !!agent.orchestrator_verified;
                  const matchesRecommended = !!agent.binding_matches_recommended;
                  const primaryConfigured = !!agent.primary_configured;
                  const fallbackConfigured = !!agent.fallback_configured;
                  return (
                    <Card key={agent.agent_code} padding="lg" className="space-y-3">
                      {/* ─── Header: code + name + status ─── */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-2xs text-ink-500 dark:text-ink-400 font-mono uppercase tracking-micro">{agent.agent_code}</p>
                            {agent.svc_code && (
                              <span className="text-2xs pill-info font-mono">{agent.svc_code}</span>
                            )}
                            {agent.domain && (
                              <span className="text-2xs pill-neutral">{agent.domain}</span>
                            )}
                          </div>
                          <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50 truncate">{agent.name}</h3>
                          <p className="text-xs text-ink-600 dark:text-ink-400 mt-1 text-pretty">{agent.description}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <StatusPill tone={agent.is_active ? 'success' : 'neutral'} dot>{agent.is_active ? 'ACTIVE' : 'INACTIVE'}</StatusPill>
                          {verified ? (
                            <span className="inline-flex items-center gap-1 text-2xs text-success-600 dark:text-success-400 font-medium" title={`Verified by ${agent.verified_by || 'orchestrator'} at ${agent.verified_at ? new Date(agent.verified_at).toLocaleString() : '—'}`}>
                              <Icon.BadgeCheck className="w-3.5 h-3.5" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-2xs text-warning-600 dark:text-warning-400 font-medium" title="Primary model does not match the system recommendation, or fallback is not the system default. Click Verify to re-check.">
                              <Icon.AlertCircle className="w-3.5 h-3.5" /> Unverified
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ─── Consumes ─── */}
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Consumes</p>
                        <div className="flex flex-wrap gap-1">
                          {(agent.consumes_sources || []).map((src: string) => <span key={src} className="pill-info text-2xs font-mono">{src}</span>)}
                          {(!agent.consumes_sources || agent.consumes_sources.length === 0) && <span className="text-2xs text-ink-400">—</span>}
                        </div>
                      </div>

                      {/* ─── Produces ─── */}
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Produces</p>
                        <div className="flex flex-wrap gap-1">
                          {(agent.produces_outputs || []).map((out: string) => <span key={out} className="pill-success text-2xs">{out}</span>)}
                          {(!agent.produces_outputs || agent.produces_outputs.length === 0) && <span className="text-2xs text-ink-400">—</span>}
                        </div>
                      </div>

                      {/* ─── v4.4.2: AI Binding — Effective / Static Recommended / Next Best / Fallback / Trust ─── */}
                      <div className="border-t border-ink-200 dark:border-ink-700 pt-2 space-y-1.5">
                        {/* v4.4.2: Effective Primary — the model the runtime will ACTUALLY use (highest-ranked CONFIGURED) */}
                        <div className="flex items-center justify-between text-2xs">
                          <span className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400" title="The model the runtime will actually use — the highest-ranked CONFIGURED model from the ranked_models list">
                            Effective Primary
                          </span>
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-xs bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 px-1.5 py-0.5 rounded border border-success-500/20">
                              {agent.effective_primary || agent.primary_model || '—'}
                            </code>
                            {agent.effective_primary_configured ? (
                              <span className="text-success-600 dark:text-success-400" title="API key for this model is configured">✓</span>
                            ) : (
                              <span className="text-danger-500" title="API key not configured">✗</span>
                            )}
                            {agent.effective_primary_provider && (
                              <span className="text-2xs pill-neutral">{agent.effective_primary_provider}</span>
                            )}
                            {agent.effective_primary_rank <= 3 && (
                              <span className="text-2xs text-success-600 dark:text-success-400" title={`Rank #${agent.effective_primary_rank} (top-3 recommendation)`}>
                                top-{agent.effective_primary_rank}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* v4.4: Static Recommended (the ideal primary, may not be configured) */}
                        <div className="flex items-center justify-between text-2xs">
                          <span className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400" title="The system-recommended ideal primary AI for this task type (may not be configured — effective primary is used instead)">
                            Recommended
                          </span>
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-xs text-ink-600 dark:text-ink-300">{agent.recommended_model || '—'}</code>
                            {agent.recommended_configured ? (
                              <span className="text-success-600 dark:text-success-400" title="Recommended model is configured">✓</span>
                            ) : (
                              <span className="text-warning-600 dark:text-warning-400 text-2xs" title="Recommended model NOT configured — effective primary is used instead">not configured</span>
                            )}
                          </div>
                        </div>

                        {/* v4.4: Primary (admin's chosen — kept for reference) */}
                        <div className="flex items-center justify-between text-2xs">
                          <span className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400" title="The primary model set in the DB (admin can override via Edit Binding)">
                            Primary (DB)
                          </span>
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-xs bg-ink-100 dark:bg-ink-800 px-1.5 py-0.5 rounded">{agent.primary_model || '—'}</code>
                            {primaryConfigured ? (
                              <span className="text-success-600 dark:text-success-400" title="API key for this model is configured">✓</span>
                            ) : (
                              <span className="text-danger-500" title="API key not configured — falls back automatically">✗</span>
                            )}
                            {matchesRecommended ? (
                              <span className="text-success-600 dark:text-success-400 text-2xs" title="Matches the system recommendation">= recommended</span>
                            ) : (
                              <span className="text-warning-600 dark:text-warning-400 text-2xs" title={`Recommended: ${agent.recommended_model || '—'}`}>≠ recommended</span>
                            )}
                          </div>
                        </div>

                        {/* v4.4.2: Next Best — other configured models the runtime will try if effective primary fails */}
                        {Array.isArray(agent.next_best) && agent.next_best.length > 0 && (
                          <div className="text-2xs">
                            <p className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1" title="Other configured models the runtime will try if the effective primary fails — walks the ranked_models list in rank order before falling to the system default">
                              Next Best (configured, in rank order)
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {agent.next_best.slice(0, 6).map((nb: any, i: number) => (
                                <span key={`${nb.model}-${i}`} className="inline-flex items-center gap-1 pill-info text-2xs font-mono" title={`Rank #${nb.rank} — ${nb.note || ''} (${nb.type})`}>
                                  #{nb.rank} {nb.model}
                                  <span className="text-success-600 dark:text-success-400">✓</span>
                                  {nb.type === 'paid' && <span className="text-warning-600 dark:text-warning-400">$</span>}
                                </span>
                              ))}
                              {agent.next_best.length > 6 && (
                                <span className="text-2xs text-ink-400">+{agent.next_best.length - 6} more</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* v4.4.2: Fallback — the system default, always last resort */}
                        <div className="flex items-center justify-between text-2xs">
                          <span className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400" title="The system default AI — always used as the final fallback before 'No AI API key configured'">
                            Fallback (system)
                          </span>
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-xs bg-ink-100 dark:bg-ink-800 px-1.5 py-0.5 rounded">{agent.fallback_model || aiAgentsSystemDefault}</code>
                            {fallbackConfigured ? (
                              <span className="text-success-600 dark:text-success-400" title="API key for fallback is configured">✓</span>
                            ) : (
                              <span className="text-danger-500" title="Fallback API key not configured — agent will fail if no other model works">✗</span>
                            )}
                            {(agent.fallback_model || aiAgentsSystemDefault) === aiAgentsSystemDefault && (
                              <span className="text-success-600 dark:text-success-400 text-2xs" title="Uses the system default AI as fallback">system default</span>
                            )}
                          </div>
                        </div>

                        {/* Task type + Trust threshold */}
                        <div className="flex items-center justify-between text-2xs">
                          <span className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Task Type</span>
                          <code className="font-mono text-xs">{agent.task_type || '—'}</code>
                        </div>
                        <div className="flex items-center justify-between text-2xs">
                          <span className="font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Trust Threshold</span>
                          <span className="nums">{agent.trust_threshold ?? '—'}</span>
                        </div>
                      </div>

                      {/* v4.4.2: Full ranked models list (collapsible) */}
                      {Array.isArray(agent.ranked_models) && agent.ranked_models.length > 0 && (
                        <details className="text-2xs text-ink-600 dark:text-ink-400">
                          <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-300">
                            Full ranked models ({agent.ranked_models.length})
                          </summary>
                          <div className="mt-1 p-2 bg-ink-50 dark:bg-ink-900 rounded space-y-0.5">
                            {agent.ranked_models.map((rm: any, i: number) => (
                              <div key={`${rm.model}-${i}`} className="flex items-center justify-between gap-2 text-2xs">
                                <span className="font-mono">
                                  <span className="text-ink-400">#{rm.rank}</span>{' '}
                                  <span className={rm.configured ? 'text-success-700 dark:text-success-400' : 'text-ink-500'}>
                                    {rm.configured ? '✓' : '✗'}{' '}{rm.model}
                                  </span>
                                </span>
                                <span className="text-ink-400 text-2xs">
                                  {rm.provider}{rm.type === 'paid' ? ' ($)' : ''}{rm.note ? ` — ${rm.note}` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      {/* v4.4: rationale (collapsible) */}
                      {agent.recommended_rationale && (
                        <details className="text-2xs text-ink-600 dark:text-ink-400">
                          <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-300">Why this AI?</summary>
                          <p className="mt-1 p-2 bg-ink-50 dark:bg-ink-900 rounded">{agent.recommended_rationale}</p>
                        </details>
                      )}

                      {/* ─── Actions: Run / Edit Binding / Verify ─── */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button onClick={() => setAiAgentRunSheet(agent.agent_code)} className="btn-primary text-xs inline-flex items-center gap-1.5">
                          <Icon.Sparkles className="w-3.5 h-3.5" /> Run Agent
                        </button>
                        <button
                          onClick={() => setAiAgentBindingSheet(agent.agent_code)}
                          className="btn-secondary text-xs inline-flex items-center gap-1.5"
                          title="Edit the primary/fallback AI models for this agent"
                        >
                          <Icon.Edit className="w-3.5 h-3.5" /> Edit Binding
                        </button>
                        <button
                          onClick={() => handleVerifyAgent(agent.agent_code)}
                          className="btn-secondary text-xs inline-flex items-center gap-1.5"
                          title="Orchestrator: verify this agent's binding"
                        >
                          <Icon.BadgeCheck className="w-3.5 h-3.5" /> Verify
                        </button>
                      </div>

                      {/* last run result (collapsible) */}
                      {result && (
                        <details className="text-xs">
                          <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-300">Last Run Result</summary>
                          <pre className="mt-2 p-3 bg-ink-100 dark:bg-ink-800 rounded-lg overflow-x-auto text-2xs font-mono">
                            {JSON.stringify(result, null, 2)}
                          </pre>
                        </details>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* v4.4: recent runs table — last 10 runs across all agents */}
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow="Audit Trail" title="Recent Agent Runs" subtitle="Last 10 runs across all agents" icon={<Icon.Activity className="w-4 h-4" />} />
              </div>
              {aiAgentRecentRuns.length === 0 ? (
                <div className="p-8"><EmptyState icon={<Icon.Activity className="w-5 h-5" />} title="No agent runs yet" description="Run an agent to see results appear here." /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Time</th><th>Agent</th><th>Status</th><th>Model</th><th>Duration</th><th>Tokens</th></tr></thead>
                    <tbody>
                      {aiAgentRecentRuns.slice(0, 10).map((r: any, i: number) => (
                        <tr key={r.id || i}>
                          <td className="text-2xs nums">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                          <td className="text-sm font-medium font-mono">{r.agent_code || '—'}</td>
                          <td><StatusPill tone={r.success ? 'success' : 'danger'} dot>{r.success ? 'SUCCESS' : 'FAILED'}</StatusPill></td>
                          <td className="text-2xs font-mono">{r.model || '—'}</td>
                          <td className="nums">{Number(r.duration_ms || r.durationMs || 0).toLocaleString()} ms</td>
                          <td className="nums text-2xs">↑ {r.tokens_input ?? r.tokensInput ?? '—'} / ↓ {r.tokens_output ?? r.tokensOutput ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* v4.4: Edit Binding Sheet — DB-driven model selectors */}
            {aiAgentBindingSheet && (() => {
              const agent = aiAgents.find(a => a.agent_code === aiAgentBindingSheet);
              if (!agent) return null;
              const code = agent.agent_code;
              // v4.4: group models by provider for the selectors
              const modelsByProvider = aiAgentsAllModels.reduce((acc: any, m: any) => {
                const key = `${m.provider_name || m.provider_code}${m.provider_configured ? '' : ' (no key)'}`;
                if (!acc[key]) acc[key] = [];
                acc[key].push(m);
                return acc;
              }, {});
              // local state for the form
              const [formState, setFormState] = React.useState({
                primary_model: agent.primary_model || agent.recommended_model || aiAgentsSystemDefault,
                fallback_model: agent.fallback_model || aiAgentsSystemDefault,
                task_type: agent.task_type || 'fast_response',
                trust_threshold: agent.trust_threshold ?? 90,
              });
              const updateField = (field: string, value: any) => setFormState((prev: any) => ({ ...prev, [field]: value }));
              return (
                <Sheet
                  open={true}
                  onClose={() => setAiAgentBindingSheet(null)}
                  title={`Edit AI Binding — ${agent.name}`}
                  footer={
                    <div className="flex gap-2">
                      <button onClick={() => setAiAgentBindingSheet(null)} className="btn-secondary flex-1">Cancel</button>
                      <button
                        onClick={() => handleSaveAgentBinding(code, formState)}
                        disabled={aiAgentBindingSaving}
                        className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5"
                      >
                        {aiAgentBindingSaving ? <Spinner size="sm" /> : <Icon.Check className="w-3.5 h-3.5" />}
                        {aiAgentBindingSaving ? 'Saving…' : 'Save Binding'}
                      </button>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    <Alert tone="info">
                      <div className="text-sm">
                        Agent <strong>{code}</strong> ({agent.svc_code || '—'}) — task type <strong>{agent.task_type || '—'}</strong>.
                        System-recommended primary: <code className="font-mono">{agent.recommended_model || '—'}</code>.
                        System default fallback: <code className="font-mono">{aiAgentsSystemDefault}</code>.
                      </div>
                    </Alert>

                    {/* Task Type selector (DB-driven) */}
                    <div>
                      <label className="label">Task Type (DB: task_type_recommendations)</label>
                      <select
                        className="input"
                        value={formState.task_type}
                        onChange={e => {
                          const newTask = e.target.value;
                          // Auto-update primary to the recommended model for this task type
                          const tt = aiAgentsTaskTypes.find((t: any) => t.task_type === newTask);
                          updateField('task_type', newTask);
                          if (tt) {
                            updateField('primary_model', tt.recommended_model);
                            updateField('fallback_model', tt.fallback_model || aiAgentsSystemDefault);
                          }
                        }}
                      >
                        {aiAgentsTaskTypes.length === 0 ? (
                          <option value="">— no task types in DB —</option>
                        ) : (
                          aiAgentsTaskTypes.map((tt: any) => (
                            <option key={tt.task_type} value={tt.task_type}>
                              {tt.task_type} → {tt.recommended_model}
                            </option>
                          ))
                        )}
                      </select>
                      <p className="text-2xs text-ink-500 mt-1">
                        Selecting a task type auto-fills the recommended primary + fallback.
                      </p>
                    </div>

                    {/* Primary model selector (DB-driven, grouped by provider) */}
                    <div>
                      <label className="label">Primary Model (recommended)</label>
                      <select
                        className="input"
                        value={formState.primary_model}
                        onChange={e => updateField('primary_model', e.target.value)}
                      >
                        {Object.keys(modelsByProvider).length === 0 ? (
                          <option value="">— no models in DB —</option>
                        ) : (
                          Object.entries(modelsByProvider).map(([providerLabel, models]: [string, any]) => (
                            <optgroup key={providerLabel} label={providerLabel}>
                              {models.map((m: any) => (
                                <option key={m.model_code} value={m.model_code}>
                                  {m.model_name} ({m.model_code}){m.is_default ? ' ★' : ''}
                                </option>
                              ))}
                            </optgroup>
                          ))
                        )}
                      </select>
                      <p className="text-2xs text-ink-500 mt-1">
                        {formState.primary_model === agent.recommended_model
                          ? '✓ Matches the system recommendation.'
                          : `⚠ Does not match recommended (${agent.recommended_model || '—'}). Orchestrator will mark as Unverified.`}
                      </p>
                    </div>

                    {/* Fallback model selector (DB-driven) */}
                    <div>
                      <label className="label">Fallback Model (system default)</label>
                      <select
                        className="input"
                        value={formState.fallback_model}
                        onChange={e => updateField('fallback_model', e.target.value)}
                      >
                        <option value={aiAgentsSystemDefault}>{aiAgentsSystemDefault} (system default)</option>
                        {Object.entries(modelsByProvider).map(([providerLabel, models]: [string, any]) => (
                          <optgroup key={providerLabel} label={providerLabel}>
                            {models.map((m: any) => (
                              <option key={m.model_code} value={m.model_code}>
                                {m.model_name} ({m.model_code})
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <p className="text-2xs text-ink-500 mt-1">
                        {formState.fallback_model === aiAgentsSystemDefault
                          ? '✓ Uses the system default.'
                          : '⚠ Not the system default — orchestrator will mark as Unverified.'}
                      </p>
                    </div>

                    {/* Trust threshold */}
                    <div>
                      <label className="label">Trust Threshold (0–100)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="input"
                        value={formState.trust_threshold}
                        onChange={e => updateField('trust_threshold', e.target.value === '' ? '' : Number(e.target.value))}
                      />
                      <p className="text-2xs text-ink-500 mt-1">
                        Minimum trust score for sources this agent consumes.
                      </p>
                    </div>

                    {/* Show recommended rationale */}
                    {agent.recommended_rationale && (
                      <Alert tone="info">
                        <div className="text-xs">
                          <strong>System rationale:</strong> {agent.recommended_rationale}
                        </div>
                      </Alert>
                    )}
                  </div>
                </Sheet>
              );
            })()}

            {/* v2.2.0: Run Agent Sheet — opens with per-agent input form */}
            {aiAgentRunSheet && (() => {
              const agent = aiAgents.find(a => a.agent_code === aiAgentRunSheet);
              if (!agent) return null;
              const code = agent.agent_code;
              const isRunning = aiAgentRunning === code;
              const input = aiAgentInput[code] || {};
              const setField = (field: string, value: any) => setAiAgentInput(prev => ({ ...prev, [code]: { ...(prev[code] || {}), [field]: value } }));
              const renderInputField = (field: string, label: string, type: string = 'text') => (
                <div key={field}>
                  <label className="label">{label}</label>
                  {type === 'textarea' ? (
                    <textarea className="input" rows={3} value={input[field] ?? ''} onChange={e => setField(field, e.target.value)} />
                  ) : (
                    <input type={type} className="input" value={input[field] ?? ''} onChange={e => setField(field, type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} />
                  )}
                </div>
              );
              let fields: React.ReactNode = null;
              if (code === 'DISEASE') {
                fields = (
                  <>
                    {renderInputField('symptoms', 'Symptoms', 'textarea')}
                    {renderInputField('photoUrl', 'Photo URL')}
                    {renderInputField('farmId', 'Farm ID')}
                    {renderInputField('villageName', 'Village Name')}
                  </>
                );
              } else if (code === 'COMPLIANCE') {
                fields = (
                  <>
                    {renderInputField('farmId', 'Farm ID')}
                    {renderInputField('farmerName', 'Farmer Name')}
                  </>
                );
              } else if (code === 'MARKET') {
                fields = (
                  <>
                    {renderInputField('region', 'Region')}
                    {renderInputField('cooperative', 'Cooperative')}
                  </>
                );
              } else if (code === 'FERTILIZER') {
                fields = (
                  <>
                    {renderInputField('farmId', 'Farm ID')}
                    {renderInputField('soilPh', 'Soil pH', 'number')}
                    {renderInputField('soilNitrogen', 'Soil Nitrogen', 'number')}
                    {renderInputField('soilPhosphorus', 'Soil Phosphorus', 'number')}
                    {renderInputField('soilPotassium', 'Soil Potassium', 'number')}
                    {renderInputField('treeAgeYears', 'Tree Age (years)', 'number')}
                    {renderInputField('areaHectares', 'Area (hectares)', 'number')}
                  </>
                );
              } else if (code === 'YIELD') {
                fields = (
                  <>
                    {renderInputField('farmId', 'Farm ID')}
                    {renderInputField('treeAgeYears', 'Tree Age (years)', 'number')}
                    {renderInputField('areaHectares', 'Area (hectares)', 'number')}
                  </>
                );
              } else {
                // v4.4: generic fallback for the other 30 agents
                fields = (
                  <>
                    {renderInputField('message', 'Message', 'textarea')}
                    {renderInputField('farmId', 'Farm ID (optional)')}
                  </>
                );
              }
              return (
                <Sheet
                  open={true}
                  onClose={() => setAiAgentRunSheet(null)}
                  title={`Run ${agent.name}`}
                  footer={
                    <div className="flex gap-2">
                      <button onClick={() => setAiAgentRunSheet(null)} className="btn-secondary flex-1">Cancel</button>
                      <button onClick={() => handleAiAgentRun(code)} disabled={isRunning} className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5">
                        {isRunning ? <Spinner size="sm" /> : <Icon.Sparkles className="w-3.5 h-3.5" />}
                        {isRunning ? 'Running…' : 'Run Agent'}
                      </button>
                    </div>
                  }
                >
                  <div className="space-y-3">
                    <Alert tone="info">
                      <div className="text-sm space-y-1">
                        <div>
                          Agent <strong>{code}</strong> ({agent.svc_code || '—'}) — task type <strong>{agent.task_type || '—'}</strong>
                        </div>
                        <div>
                          <strong>Effective Primary:</strong>{' '}
                          <code className="font-mono">{agent.effective_primary || agent.primary_model || '—'}</code>{' '}
                          {agent.effective_primary_configured ? '✓' : '✗'}
                          {agent.effective_primary_rank <= 3 && agent.effective_primary_rank > 0 && (
                            <span className="text-2xs ml-1 text-success-600 dark:text-success-400">(top-{agent.effective_primary_rank})</span>
                          )}
                        </div>
                        {Array.isArray(agent.next_best) && agent.next_best.length > 0 && (
                          <div className="text-2xs text-ink-600 dark:text-ink-400">
                            <strong>Next best:</strong>{' '}
                            {agent.next_best.slice(0, 4).map((nb: any, i: number) => (
                              <span key={i}>
                                {i > 0 && ' → '}{nb.model} (#{nb.rank}{nb.type === 'paid' ? ', $' : ''})
                              </span>
                            ))}
                            {agent.next_best.length > 4 && <span> +{agent.next_best.length - 4} more</span>}
                          </div>
                        )}
                        <div>
                          <strong>Fallback:</strong>{' '}
                          <code className="font-mono">{agent.fallback_model || aiAgentsSystemDefault}</code>{' '}
                          {agent.fallback_configured ? '✓' : '✗'}
                          <span className="text-2xs text-ink-500 ml-1">(system default)</span>
                        </div>
                      </div>
                    </Alert>
                    {fields}
                  </div>
                </Sheet>
              );
            })()}
          </div>
        )}

        {/* ═══ v3.2: SATELLITE — Gemini Vision satellite imagery analysis ═══ */}
        {activeTab === 'satellite' && (
          <SatelliteTab />
        )}

        {/* ═══ v2.2.0: KNOWLEDGE BASE — Volume XXXI cross-domain search ═══ */}
        {activeTab === 'knowledge' && ( // v2.2.0
          <div className="space-y-4"> {/* v2.2.0 */}
            {/* v2.2.0: header */}
            <SectionHeader
              eyebrow="Volume XXXI"
              title="Volume XXXI — Knowledge Base"
              subtitle="Cross-domain search across ingested documents, research papers, and embedding chunks."
              icon={<Icon.FileText className="w-4 h-4" />}
            />

            {knowledgeError && <Alert tone="danger" onClose={() => setKnowledgeError(null)}>{knowledgeError}</Alert>} {/* v2.2.0 */}

            {/* v2.2.0: sub-tabs — Search | Documents | Research Papers | Graph */}
            <SegmentedTabs
              items={[
                { id: 'search',    label: 'Search',         icon: <Icon.Search className="w-4 h-4" /> }, // v2.2.0
                { id: 'ask-ai',    label: 'Ask AI (SVC-024)', icon: <Icon.Sparkles className="w-4 h-4" /> }, // v4.4.6
                { id: 'documents', label: 'Documents',      icon: <Icon.FileText className="w-4 h-4" /> }, // v2.2.0
                { id: 'papers',    label: 'Research Papers',icon: <Icon.Sparkles className="w-4 h-4" /> }, // v2.2.0
                { id: 'graph',     label: 'Graph',          icon: <Icon.Database className="w-4 h-4" /> }, // v2.2.0
              ]}
              active={knowledgeTab}
              onChange={(id) => setKnowledgeTab(id as any)} // v2.2.0
            />

            {/* v2.3.1: search mode toggle — Keyword / Vector (AI) / Hybrid (Best) */}
            {knowledgeTab === 'search' && ( // v2.3.1
              <div className="flex flex-wrap items-center gap-2"> {/* v2.3.1 */}
                <span className="text-xs font-medium text-ink-500 dark:text-ink-400">Search Mode:</span> {/* v2.3.1 */}
                {(['keyword', 'vector', 'hybrid'] as const).map(m => ( // v2.3.1
                  <button // v2.3.1
                    key={m} // v2.3.1
                    onClick={() => handleKnowledgeSearchModeChange(m)} // v2.3.1
                    className={`text-2xs font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors ${knowledgeSearchMode === m ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700'}`} // v2.3.1
                  >
                    {m === 'keyword' ? <Icon.Search className="w-3 h-3" /> : m === 'vector' ? <Icon.Sparkles className="w-3 h-3" /> : <Icon.Layers className="w-3 h-3" />} {/* v2.3.1 */}
                    {m === 'keyword' ? 'Keyword' : m === 'vector' ? 'Vector (AI)' : 'Hybrid (Best)'} {/* v2.3.1 */}
                  </button>
                ))} {/* v2.3.1 */}
                {knowledgeSearchMode !== 'keyword' && ( // v3.11.33: replaced warning with info
                  <span className="text-2xs text-success-700 dark:text-success-400 ml-2">✓ Vector search active — uses Jina/OpenAI/HuggingFace embeddings + pgvector similarity</span> // v3.11.33
                )} {/* v3.11.33 */}
              </div>
            )} {/* v2.3.1 */}

            {/* v2.2.0: Search sub-tab */}
            {knowledgeTab === 'search' && ( // v2.2.0
              <Card padding="none" className="overflow-hidden"> {/* v2.2.0 */}
                <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex gap-2"> {/* v2.2.0 */}
                  <div className="relative flex-1"> {/* v2.2.0 */}
                    <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" /> {/* v2.2.0 */}
                    <input
                      type="text"
                      placeholder="Search documents, papers, and chunks…" // v2.2.0
                      value={knowledgeQuery}
                      onChange={e => setKnowledgeQuery(e.target.value)} // v2.2.0
                      onKeyDown={e => { if (e.key === 'Enter') handleKnowledgeSearch(); }} // v2.2.0
                      className="input pl-10"
                    />
                  </div>
                  <button onClick={handleKnowledgeSearch} disabled={knowledgeLoading} className="btn-primary inline-flex items-center gap-1.5"> {/* v2.2.0 */}
                    {knowledgeLoading ? <Spinner size="sm" /> : <Icon.Search className="w-4 h-4" />} {/* v2.2.0 */}
                    Search {/* v2.2.0 */}
                  </button>
                </div>
                {knowledgeResults.length === 0 ? ( // v2.2.0
                  <div className="p-8"><EmptyState icon={<Icon.Search className="w-5 h-5" />} title="No results yet" description="Type a query above and hit Enter to search the knowledge base." /></div> // v2.2.0
                ) : (
                  <div className="divide-y divide-ink-100 dark:divide-ink-800"> {/* v2.2.0 */}
                    {knowledgeResults.map((r: any, i: number) => ( // v2.2.0
                      <div key={r.id || i} className="p-4"> {/* v2.2.0 */}
                        <div className="flex items-start gap-3"> {/* v2.2.0 */}
                          <StatusPill tone={r.type === 'paper' ? 'info' : r.type === 'document' ? 'success' : 'neutral'} dot>{r.type}</StatusPill> {/* v2.2.0 */}
                          <div className="flex-1 min-w-0"> {/* v2.2.0 */}
                            <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{r.title || '—'}</p> {/* v2.2.0 */}
                            {r.subtitle && <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{r.subtitle}</p>} {/* v2.2.0 */}
                            {r.snippet && <p className="text-xs text-ink-600 dark:text-ink-300 mt-1 line-clamp-3">{r.snippet}</p>} {/* v2.2.0 */}
                            {r.source_url && ( // v2.2.0
                              <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-2xs text-info-600 dark:text-info-400 hover:underline mt-1 inline-block"> {/* v2.2.0 */}
                                {r.source_url}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* v4.4.6: Ask AI sub-tab — Knowledge Q&A via the KNOWLEDGE agent (SVC-024) */}
            {knowledgeTab === 'ask-ai' && (
              <div className="space-y-4">
                <Card padding="md" className="bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
                  <div className="text-sm space-y-1">
                    <p className="font-semibold text-ink-900 dark:text-ink-50">Knowledge Q&A — SVC-024 Knowledge Intelligence Agent</p>
                    <p className="text-xs text-ink-600 dark:text-ink-400">
                      Ask a question about cocoa agronomy, diseases, pests, regulations, etc. The agent retrieves relevant chunks from the knowledge base using <strong>hybrid search (vector + keyword + graph)</strong>, then answers using the highest-ranked configured AI (DeepSeek → Grok → Groq → ... → Gemini system default). Every answer includes source citations with document titles + relevance scores.
                    </p>
                    <p className="text-2xs text-ink-500">
                      The embeddings generated during RAG processing (BGE-M3 via HuggingFace/OpenRouter) are used here for semantic matching — questions don't need to contain exact keywords from the documents.
                    </p>
                  </div>
                </Card>

                <Card padding="none" className="overflow-hidden">
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex gap-2">
                    <div className="relative flex-1">
                      <Icon.Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                      <input
                        type="text"
                        placeholder="Ask a question — e.g. 'How to treat black pod disease?' or 'What is the recommended pruning schedule for cocoa?'"
                        value={knowledgeQaQuestion}
                        onChange={e => setKnowledgeQaQuestion(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleKnowledgeQa(); }}
                        className="input pl-10"
                      />
                    </div>
                    <button onClick={handleKnowledgeQa} disabled={knowledgeQaLoading || !knowledgeQaQuestion.trim()} className="btn-primary inline-flex items-center gap-1.5">
                      {knowledgeQaLoading ? <Spinner size="sm" /> : <Icon.Sparkles className="w-4 h-4" />}
                      {knowledgeQaLoading ? 'Thinking…' : 'Ask KNOWLEDGE Agent'}
                    </button>
                  </div>

                  {knowledgeQaAnswer && (
                    <div className="p-5 space-y-4">
                      {knowledgeQaAnswer.success === false ? (
                        <Alert tone="danger">{knowledgeQaAnswer.error || 'Knowledge Q&A failed'}</Alert>
                      ) : (
                        <>
                          {/* Answer */}
                          {knowledgeQaAnswer.output?.answer && (
                            <div>
                              <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Answer</p>
                              <p className="text-sm text-ink-800 dark:text-ink-200 whitespace-pre-wrap">{knowledgeQaAnswer.output.answer}</p>
                            </div>
                          )}

                          {/* Confidence + model + search type */}
                          <div className="flex flex-wrap gap-3 text-2xs">
                            {knowledgeQaAnswer.output?.confidence != null && (
                              <span className="pill-info">Confidence: {knowledgeQaAnswer.output.confidence}%</span>
                            )}
                            {knowledgeQaAnswer.model && (
                              <span className="pill-neutral">Model: {knowledgeQaAnswer.model}</span>
                            )}
                            {knowledgeQaAnswer.output?.search_type && (
                              <span className="pill-success">Search: {knowledgeQaAnswer.output.search_type}</span>
                            )}
                            {knowledgeQaAnswer.output?.retrieval_count != null && (
                              <span className="pill-neutral">Chunks retrieved: {knowledgeQaAnswer.output.retrieval_count}</span>
                            )}
                            {knowledgeQaAnswer.durationMs != null && (
                              <span className="pill-neutral">Duration: {(knowledgeQaAnswer.durationMs / 1000).toFixed(1)}s</span>
                            )}
                          </div>

                          {/* Sources */}
                          {Array.isArray(knowledgeQaAnswer.output?.retrieved_sources) && knowledgeQaAnswer.output.retrieved_sources.length > 0 && (
                            <div>
                              <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Sources (citations)</p>
                              <div className="space-y-2">
                                {knowledgeQaAnswer.output.retrieved_sources.map((s: any, i: number) => (
                                  <div key={i} className="p-3 bg-ink-50 dark:bg-ink-900 rounded-lg border border-ink-200 dark:border-ink-700">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <span className="text-2xs font-mono font-medium">[{i + 1}] {s.document_title || 'unknown'}</span>
                                      <div className="flex gap-2 text-2xs">
                                        <span className="text-info-600 dark:text-info-400">combined: {s.combined_score?.toFixed(3) ?? '?'}</span>
                                        {s.vector_score > 0 && <span className="text-success-600 dark:text-success-400">vector: {s.vector_score.toFixed(3)}</span>}
                                        {s.keyword_score > 0 && <span className="text-warning-600 dark:text-warning-400">keyword: {s.keyword_score.toFixed(3)}</span>}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 text-2xs text-ink-500 mb-1">
                                      {s.organization && <span>Org: {s.organization}</span>}
                                      {s.doc_type && <span>Type: {s.doc_type}</span>}
                                      {s.section_title && <span>Section: {s.section_title}</span>}
                                    </div>
                                    <p className="text-xs text-ink-600 dark:text-ink-400 line-clamp-3">{s.chunk_text}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Knowledge gaps */}
                          {Array.isArray(knowledgeQaAnswer.output?.knowledge_gaps) && knowledgeQaAnswer.output.knowledge_gaps.length > 0 && (
                            <div>
                              <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Knowledge Gaps</p>
                              <ul className="text-xs text-ink-600 dark:text-ink-400 list-disc list-inside space-y-0.5">
                                {knowledgeQaAnswer.output.knowledge_gaps.map((g: string, i: number) => <li key={i}>{g}</li>)}
                              </ul>
                            </div>
                          )}

                          {/* Recommended sources */}
                          {Array.isArray(knowledgeQaAnswer.output?.recommended_sources) && knowledgeQaAnswer.output.recommended_sources.length > 0 && (
                            <div>
                              <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Recommended Sources to Ingest</p>
                              <ul className="text-xs text-ink-600 dark:text-ink-400 list-disc list-inside space-y-0.5">
                                {knowledgeQaAnswer.output.recommended_sources.map((r: string, i: number) => <li key={i}>{r}</li>)}
                              </ul>
                            </div>
                          )}

                          {/* Raw JSON (collapsible) */}
                          <details className="text-xs">
                            <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-300">Raw agent output (JSON)</summary>
                            <pre className="mt-2 p-3 bg-ink-100 dark:bg-ink-800 rounded-lg overflow-x-auto text-2xs font-mono">
                              {JSON.stringify(knowledgeQaAnswer, null, 2)}
                            </pre>
                          </details>
                        </>
                      )}
                    </div>
                  )}

                  {!knowledgeQaAnswer && !knowledgeQaLoading && (
                    <div className="p-8">
                      <EmptyState
                        icon={<Icon.Sparkles className="w-5 h-5" />}
                        title="Ask the KNOWLEDGE agent"
                        description="Type a question above and press Enter. The agent will search the knowledge base (vector + keyword + graph) and answer with citations."
                      />
                    </div>
                  )}
                </Card>
              </div>
            )}

            {/* v3.2.21: Knowledge Tools Health panel — shows pgvector status,
                embedding coverage, and provides a "Generate missing embeddings"
                button. This addresses the orchestrator's knowledge_tools_health
                finding that reported "no embeddings generated" + "pgvector not installed". */}
            <KnowledgeToolsHealthPanel />

            {/* v2.2.0: Documents sub-tab */}
            {knowledgeTab === 'documents' && ( // v2.2.0
              <Card padding="none" className="overflow-hidden"> {/* v2.2.0 */}
                <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.2.0 */}
                  <SectionHeader eyebrow="Ingested" title="Documents" subtitle="Manually uploaded + connector-ingested documents" icon={<Icon.FileText className="w-4 h-4" />} /> {/* v2.2.0 */}
                </div>
                {knowledgeLoading ? ( // v2.2.0
                  <div className="p-8 flex justify-center"><Spinner /></div> // v2.2.0
                ) : knowledgeDocuments.length === 0 ? ( // v2.2.0
                  <div className="p-8"><EmptyState icon={<Icon.FileText className="w-5 h-5" />} title="No documents ingested" description="Upload documents via the API or trigger a data-source sync." /></div> // v2.2.0
                ) : (
                  <div className="overflow-x-auto"> {/* v2.2.0 */}
                    <table className="data-table"> {/* v2.2.0 */}
                      <thead><tr><th>Title</th><th>Source</th><th>Type</th><th>Org</th><th>Status</th><th>URL</th><th>Chunks</th><th>RAG</th><th>Entities</th></tr></thead> {/* v2.2.0 (cols extended v2.3.1) */}
                      <tbody> {/* v2.2.0 */}
                        {knowledgeDocuments.map((d: any, i: number) => ( // v2.2.0
                          <tr key={d.id || i}> {/* v2.2.0 */}
                            <td className="text-sm font-medium">{d.title || '—'}</td> {/* v2.2.0 */}
                            <td className="text-2xs">{d.source_code || d.source || '—'}</td> {/* v2.2.0 */}
                            <td><StatusPill tone="info">{d.doc_type || d.type || '—'}</StatusPill></td> {/* v2.2.0 */}
                            <td className="text-2xs">{d.organization || '—'}</td> {/* v2.2.0 */}
                            <td><StatusPill tone={d.ingest_status === 'COMPLETED' ? 'success' : d.ingest_status === 'FAILED' ? 'danger' : 'warning'} dot>{d.ingest_status || d.status || '—'}</StatusPill></td> {/* v2.2.0 */}
                            <td className="text-2xs">{d.source_url ? <a href={d.source_url} target="_blank" rel="noopener noreferrer" className="text-info-600 dark:text-info-400 hover:underline">View ↗</a> : '—'}</td> {/* v2.2.0 */}
                            {/* v2.3.1: chunk count column (from document metadata.chunk_count or top-level chunk_count) */}
                            <td className="nums text-2xs">{ // v2.3.1
                              (d.metadata?.chunk_count ?? d.chunk_count) != null // v2.3.1
                                ? Number(d.metadata?.chunk_count ?? d.chunk_count).toLocaleString() // v2.3.1
                                : '—' // v2.3.1
                            }</td> {/* v2.3.1 */}
                            {/* v2.3.1: RAG process action column — green button + spinner + result */}
                            <td> {/* v2.3.1 */}
                              <div className="flex flex-col gap-1"> {/* v2.3.1 */}
                                <button // v2.3.1
                                  onClick={() => handleRagProcess(d.id)} // v2.3.1
                                  disabled={knowledgeProcessing.has(d.id)} // v2.3.1
                                  className="text-2xs font-medium px-2.5 py-1 rounded-lg inline-flex items-center gap-1.5 bg-success-500 text-white hover:bg-success-600 disabled:opacity-60 transition-colors" // v2.3.1 — green
                                >
                                  {knowledgeProcessing.has(d.id) ? <Spinner size="sm" /> : <Icon.Refresh className="w-3 h-3" />} {/* v2.3.1 */}
                                  {knowledgeProcessing.has(d.id) ? 'Processing…' : 'Process'} {/* v2.3.1 */}
                                </button>
                                {knowledgeProcessingResult[d.id] && ( // v2.3.1 — show result alert
                                  <span className={`text-2xs ${
                                    knowledgeProcessingResult[d.id].ok
                                      ? (knowledgeProcessingResult[d.id].partial ? 'text-warning-700 dark:text-warning-400' : 'text-success-700 dark:text-success-400')
                                      : 'text-danger-700 dark:text-danger-400'
                                  }`} title={knowledgeProcessingResult[d.id].partial ? knowledgeProcessingResult[d.id].data?.error : ''}>
                                    {knowledgeProcessingResult[d.id].ok // v2.3.1
                                      ? (knowledgeProcessingResult[d.id].partial
                                          ? `⚠ ${knowledgeProcessingResult[d.id].data?.chunksCreated ?? 0} chunks / ${knowledgeProcessingResult[d.id].data?.embeddingsGenerated ?? 0} embeddings — ${knowledgeProcessingResult[d.id].data?.error || 'partial'}`
                                          : `✓ ${knowledgeProcessingResult[d.id].data?.chunksCreated ?? 0} chunks / ${knowledgeProcessingResult[d.id].data?.embeddingsGenerated ?? 0} embeddings`)
                                      : `✕ ${knowledgeProcessingResult[d.id].error}`} {/* v2.3.1 */}
                                  </span>
                                )}
                              </div>
                            </td>
                            {/* v2.3.1: entity extraction action column — blue button + spinner + result */}
                            <td> {/* v2.3.1 */}
                              <div className="flex flex-col gap-1"> {/* v2.3.1 */}
                                <button // v2.3.1
                                  onClick={() => handleEntityExtract(d.id)} // v2.3.1
                                  disabled={knowledgeExtracting.has(d.id)} // v2.3.1
                                  className="text-2xs font-medium px-2.5 py-1 rounded-lg inline-flex items-center gap-1.5 bg-info-500 text-white hover:bg-info-600 disabled:opacity-60 transition-colors" // v2.3.1 — blue
                                >
                                  {knowledgeExtracting.has(d.id) ? <Spinner size="sm" /> : <Icon.Database className="w-3 h-3" />} {/* v2.3.1 */}
                                  {knowledgeExtracting.has(d.id) ? 'Extracting…' : 'Extract'} {/* v2.3.1 */}
                                </button>
                                {knowledgeExtractResult[d.id] && ( // v2.3.1 — show result alert
                                  <span className={`text-2xs ${
                                    knowledgeExtractResult[d.id].ok
                                      ? (knowledgeExtractResult[d.id].data?.status === 'PARTIAL' ? 'text-warning-700 dark:text-warning-400' : 'text-success-700 dark:text-success-400')
                                      : 'text-danger-700 dark:text-danger-400'
                                  }`} title={knowledgeExtractResult[d.id].data?.error || knowledgeExtractResult[d.id].error || ''}>
                                    {knowledgeExtractResult[d.id].ok // v2.3.1
                                      ? (knowledgeExtractResult[d.id].data?.status === 'PARTIAL'
                                          ? `⚠ ${knowledgeExtractResult[d.id].data?.entitiesExtracted ?? 0} entities — ${knowledgeExtractResult[d.id].data?.error || 'partial'}`
                                          : `✓ ${knowledgeExtractResult[d.id].data?.entitiesExtracted ?? 0} entities / ${knowledgeExtractResult[d.id].data?.newNodesCreated ?? 0} nodes`)
                                      : `✕ ${(knowledgeExtractResult[d.id].error || '').slice(0, 80)}`} {/* v2.3.1 */}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}

            {/* v2.2.0: Research Papers sub-tab */}
            {knowledgeTab === 'papers' && ( // v2.2.0
              <Card padding="none" className="overflow-hidden"> {/* v2.2.0 */}
                <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.2.0 */}
                  <SectionHeader eyebrow="Research" title="Research Papers" subtitle="Peer-reviewed cocoa agronomy research ingested from open archives" icon={<Icon.Sparkles className="w-4 h-4" />} /> {/* v2.2.0 */}
                </div>
                {knowledgeLoading ? ( // v2.2.0
                  <div className="p-8 flex justify-center"><Spinner /></div> // v2.2.0
                ) : knowledgePapers.length === 0 ? ( // v2.2.0
                  <div className="p-8"><EmptyState icon={<Icon.Sparkles className="w-5 h-5" />} title="No papers ingested" description="Trigger a Research data-source sync to ingest papers." /></div> // v2.2.0
                ) : (
                  <div className="overflow-x-auto"> {/* v2.2.0 */}
                    <table className="data-table"> {/* v2.2.0 */}
                      <thead><tr><th>Title</th><th>Journal</th><th>Date</th><th>DOI</th><th>Citations</th><th>Open Access URL</th></tr></thead> {/* v2.2.0 */}
                      <tbody> {/* v2.2.0 */}
                        {knowledgePapers.map((p: any, i: number) => ( // v2.2.0
                          <tr key={p.id || i}> {/* v2.2.0 */}
                            <td className="text-sm font-medium max-w-md">{p.title || '—'}</td> {/* v2.2.0 */}
                            <td className="text-2xs">{p.journal || p.publisher || '—'}</td> {/* v2.2.0 */}
                            <td className="text-2xs nums">{p.published_date || p.publication_date || '—'}</td> {/* v2.2.0 */}
                            <td className="text-2xs font-mono">{p.doi || '—'}</td> {/* v2.2.0 */}
                            <td className="nums">{Number(p.citation_count || 0).toLocaleString()}</td> {/* v2.2.0 */}
                            <td className="text-2xs">{p.open_access_url ? <a href={p.open_access_url} target="_blank" rel="noopener noreferrer" className="text-info-600 dark:text-info-400 hover:underline">PDF ↗</a> : '—'}</td> {/* v2.2.0 */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}

            {/* v2.2.0: Graph sub-tab — nodes + edges as tables (no visual graph rendering) */}
            {knowledgeTab === 'graph' && ( // v2.2.0
              <div className="space-y-4"> {/* v2.3.1 — graph sub-tab wrapper (KPIs + by-type + search + existing tables) */}
                {/* v2.3.1: 3 KPI tiles — Total Nodes, Total Edges, Entity Types count */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3"> {/* v2.3.1 */}
                  <MetricTile label="Total Nodes" value={Number(knowledgeGraphStats?.total_nodes ?? knowledgeGraph?.nodes?.length ?? 0).toLocaleString()} icon={<Icon.Database className="w-4 h-4" />} accent="admin" /> {/* v2.3.1 */}
                  <MetricTile label="Total Edges" value={Number(knowledgeGraphStats?.total_edges ?? knowledgeGraph?.edges?.length ?? 0).toLocaleString()} icon={<Icon.Activity className="w-4 h-4" />} accent="admin" /> {/* v2.3.1 */}
                  <MetricTile label="Entity Types" value={Number(knowledgeGraphStats?.by_type?.length ?? 0).toLocaleString()} hint="distinct node types" icon={<Icon.Layers className="w-4 h-4" />} accent="admin" /> {/* v2.3.1 */}
                </div>
                {/* v2.3.1: "by type" breakdown table */}
                <Card padding="none" className="overflow-hidden"> {/* v2.3.1 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.3.1 */}
                    <SectionHeader eyebrow="Distribution" title="Nodes by Type" subtitle="Entity type breakdown across the knowledge graph" icon={<Icon.Layers className="w-4 h-4" />} /> {/* v2.3.1 */}
                  </div>
                  {(knowledgeGraphStats?.by_type || []).length === 0 ? ( // v2.3.1
                    <div className="p-8"><EmptyState icon={<Icon.Layers className="w-5 h-5" />} title="No type stats available" description="Run entity extraction on a document to populate the graph." /></div> // v2.3.1
                  ) : ( // v2.3.1
                    <div className="overflow-x-auto"> {/* v2.3.1 */}
                      <table className="data-table"> {/* v2.3.1 */}
                        <thead><tr><th>Entity Type</th><th>Count</th></tr></thead> {/* v2.3.1 */}
                        <tbody> {/* v2.3.1 */}
                          {(knowledgeGraphStats?.by_type || []).map((row: any, i: number) => ( // v2.3.1
                            <tr key={row.node_type || i}> {/* v2.3.1 */}
                              <td><StatusPill tone="info" dot>{row.node_type || '—'}</StatusPill></td> {/* v2.3.1 */}
                              <td className="nums">{Number(row.count || 0).toLocaleString()}</td> {/* v2.3.1 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
                {/* v2.3.1: entity search box + results */}
                <Card padding="none" className="overflow-hidden"> {/* v2.3.1 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800 space-y-3"> {/* v2.3.1 */}
                    <SectionHeader eyebrow="Traversal" title="Find Related Entities" subtitle="Search for an entity and explore its graph neighbors" icon={<Icon.Search className="w-4 h-4" />} /> {/* v2.3.1 */}
                    <div className="flex gap-2"> {/* v2.3.1 */}
                      <div className="relative flex-1"> {/* v2.3.1 */}
                        <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" /> {/* v2.3.1 */}
                        <input // v2.3.1
                          type="text" // v2.3.1
                          placeholder="Search by entity name (e.g. cocoa, swollen shoot virus)…" // v2.3.1
                          value={knowledgeGraphSearch} // v2.3.1
                          onChange={e => setKnowledgeGraphSearch(e.target.value)} // v2.3.1
                          onKeyDown={e => { if (e.key === 'Enter') handleGraphEntitySearch(); }} // v2.3.1
                          className="input pl-10"
                        />
                      </div>
                      <button onClick={handleGraphEntitySearch} disabled={knowledgeLoading} className="btn-primary inline-flex items-center gap-1.5"> {/* v2.3.1 */}
                        {knowledgeLoading ? <Spinner size="sm" /> : <Icon.Search className="w-4 h-4" />} {/* v2.3.1 */}
                        Find Related {/* v2.3.1 */}
                      </button>
                    </div>
                  </div>
                  {/* v2.3.1: search results — node details + related entities table */}
                  {knowledgeGraphSearchResult == null ? ( // v2.3.1 — no search yet
                    <div className="p-8"><EmptyState icon={<Icon.Search className="w-5 h-5" />} title="No search yet" description="Enter an entity name above to discover related nodes." /></div> // v2.3.1
                  ) : !knowledgeGraphSearchResult.node ? ( // v2.3.1 — no match found
                    <div className="p-8"><EmptyState icon={<Icon.Search className="w-5 h-5" />} title="Entity not found" description={`No graph node matches "${knowledgeGraphSearch}".`} /></div> // v2.3.1
                  ) : ( // v2.3.1 — show node + related
                    <div className="divide-y divide-ink-100 dark:divide-ink-800"> {/* v2.3.1 */}
                      {/* v2.3.1: found node details */}
                      <div className="p-4"> {/* v2.3.1 */}
                        <div className="flex items-start gap-3"> {/* v2.3.1 */}
                          <StatusPill tone="info" dot>{knowledgeGraphSearchResult.node.type || knowledgeGraphSearchResult.node.node_type || 'ENTITY'}</StatusPill> {/* v2.3.1 */}
                          <div className="flex-1 min-w-0"> {/* v2.3.1 */}
                            <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{knowledgeGraphSearchResult.node.name || knowledgeGraphSearchResult.node.label || '—'}</p> {/* v2.3.1 */}
                            {knowledgeGraphSearchResult.node.description && <p className="text-xs text-ink-600 dark:text-ink-300 mt-1">{knowledgeGraphSearchResult.node.description}</p>} {/* v2.3.1 */}
                            <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">{knowledgeGraphSearchResult.count ?? 0} related entities</p> {/* v2.3.1 */}
                          </div>
                        </div>
                      </div>
                      {/* v2.3.1: related entities table */}
                      <div className="overflow-x-auto"> {/* v2.3.1 */}
                        <table className="data-table"> {/* v2.3.1 */}
                          <thead><tr><th>Connected Entity</th><th>Type</th><th>Relationship</th><th>Direction</th><th>Weight</th></tr></thead> {/* v2.3.1 */}
                          <tbody> {/* v2.3.1 */}
                            {(Array.isArray(knowledgeGraphSearchResult.related) ? knowledgeGraphSearchResult.related : []).map((rel: any, i: number) => ( // v2.3.1
                              <tr key={i}> {/* v2.3.1 */}
                                <td className="text-sm font-medium">{rel.name || rel.label || '—'}</td> {/* v2.3.1 */}
                                <td><StatusPill tone="info">{rel.type || rel.node_type || '—'}</StatusPill></td> {/* v2.3.1 */}
                                <td><StatusPill tone="success">{rel.relation || rel.relationship || rel.label || '—'}</StatusPill></td> {/* v2.3.1 */}
                                <td className="text-2xs">{rel.direction || '—'}</td> {/* v2.3.1 */}
                                <td className="nums text-2xs">{rel.weight != null ? Number(rel.weight).toFixed(2) : '—'}</td> {/* v2.3.1 */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Card>
                {/* v2.3.1: existing v2.2.0 graph tables — preserved below */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"> {/* v2.2.0 */}
                <Card padding="none" className="overflow-hidden"> {/* v2.2.0 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.2.0 */}
                    <SectionHeader eyebrow="Nodes" title="Knowledge Graph Nodes" subtitle={`${knowledgeGraph?.nodes?.length ?? 0} nodes`} icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.2.0 */}
                  </div>
                  {(knowledgeGraph?.nodes || []).length === 0 ? ( // v2.2.0
                    <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No nodes" /></div> // v2.2.0
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.2.0 */}
                      <table className="data-table"> {/* v2.2.0 */}
                        <thead><tr><th>ID</th><th>Label</th><th>Type</th></tr></thead> {/* v2.2.0 */}
                        <tbody> {/* v2.2.0 */}
                          {(knowledgeGraph?.nodes || []).slice(0, 50).map((n: any, i: number) => ( // v2.2.0
                            <tr key={n.id || i}> {/* v2.2.0 */}
                              <td className="text-2xs font-mono">{n.id || '—'}</td> {/* v2.2.0 */}
                              <td className="text-sm">{n.label || n.name || '—'}</td> {/* v2.2.0 */}
                              <td><StatusPill tone="info">{n.type || n.node_type || '—'}</StatusPill></td> {/* v2.2.0 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
                <Card padding="none" className="overflow-hidden"> {/* v2.2.0 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.2.0 */}
                    <SectionHeader eyebrow="Edges" title="Knowledge Graph Edges" subtitle={`${knowledgeGraph?.edges?.length ?? 0} edges`} icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.2.0 */}
                  </div>
                  {(knowledgeGraph?.edges || []).length === 0 ? ( // v2.2.0
                    <div className="p-8"><EmptyState icon={<Icon.Activity className="w-5 h-5" />} title="No edges" /></div> // v2.2.0
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.2.0 */}
                      <table className="data-table"> {/* v2.2.0 */}
                        <thead><tr><th>Source</th><th>Relation</th><th>Target</th></tr></thead> {/* v2.2.0 */}
                        <tbody> {/* v2.2.0 */}
                          {(knowledgeGraph?.edges || []).slice(0, 50).map((e: any, i: number) => ( // v2.2.0
                            <tr key={i}> {/* v2.2.0 */}
                              <td className="text-2xs font-mono">{e.source || e.from || '—'}</td> {/* v2.2.0 */}
                              <td><StatusPill tone="success">{e.relation || e.label || e.type || '—'}</StatusPill></td> {/* v2.2.0 */}
                              <td className="text-2xs font-mono">{e.target || e.to || '—'}</td> {/* v2.2.0 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div> {/* v2.2.0 — closes nodes/edges grid */}
              {/* v2.3.1: close graph sub-tab wrapper */}
              </div>
            )}
          </div>
        )}

        {/* ═══ v2.5: PIPELINE — Cognitive Pipeline monitoring ═══ */}
        {activeTab === 'pipeline' && ( // v2.5
          <div className="space-y-4"> {/* v2.5 */}
            {/* v2.5: header */}
            <SectionHeader // v2.5
              eyebrow="Cognitive Pipeline" // v2.5
              title="Cognitive Pipeline — 30-Stage Orchestration" // v2.5
              subtitle="Monitor pipeline runs, inspect the 30-stage reference table, and trigger manual executions." // v2.5
              icon={<Icon.Activity className="w-4 h-4" />} // v2.5
            /> {/* v2.5 */}

            {/* v2.5: KPI tiles pulled from /api/pipeline/stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3"> {/* v2.5 */}
              <MetricTile label="Total Runs"    value={Number(pipelineStats?.total ?? pipelineRuns.length ?? 0).toLocaleString()}             icon={<Icon.Activity className="w-4 h-4" />} accent="admin" /> {/* v2.5 */}
              <MetricTile label="Completed"     value={Number(pipelineStats?.completed ?? 0).toLocaleString()}             icon={<Icon.Check className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Failed"        value={Number(pipelineStats?.failed ?? 0).toLocaleString()}                icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Avg Duration"  value={`${Number(pipelineStats?.avg_duration_ms ?? pipelineStats?.avg_duration ?? 0).toFixed(0)} ms`} icon={<Icon.Clock className="w-4 h-4" />} /> {/* v2.5 */}
            </div>

            {pipelineError && <Alert tone="danger" onClose={() => setPipelineError(null)}>{pipelineError}</Alert>} {/* v2.5 */}

            {/* v2.5: manual pipeline execution form */}
            <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
              <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                <SectionHeader eyebrow="Manual Execution" title="Trigger Pipeline Run" subtitle="Select an agent and provide input JSON" icon={<Icon.Sparkles className="w-4 h-4" />} /> {/* v2.5 */}
              </div>
              <div className="p-5 space-y-3"> {/* v2.5 */}
                <div> {/* v2.5 */}
                  <label className="label">Agent Code *</label> {/* v2.5 */}
                  <select className="input" value={pipelineExecForm.agent_code} onChange={e => setPipelineExecForm(prev => ({ ...prev, agent_code: e.target.value }))}> {/* v2.5 */}
                    <option value="">— Select agent —</option> {/* v2.5 */}
                    {/* v2.5: reuse the AI Agents registry if loaded, otherwise show the 5 known agent codes */}
                    {aiAgents.length > 0 // v2.5
                      ? aiAgents.map((a: any) => <option key={a.agent_code} value={a.agent_code}>{a.agent_code} — {a.name}</option>) // v2.5
                      : ['DISEASE', 'COMPLIANCE', 'MARKET', 'FERTILIZER', 'YIELD'].map(c => <option key={c} value={c}>{c}</option>)} {/* v2.5 */}
                  </select>
                </div>
                <div> {/* v2.5 */}
                  <label className="label">Input JSON</label> {/* v2.5 */}
                  <textarea className="input font-mono text-xs" rows={5} value={pipelineExecForm.input} onChange={e => setPipelineExecForm(prev => ({ ...prev, input: e.target.value }))} placeholder='{"farm_id":"...","village_name":"..."}' /> {/* v2.5 */}
                </div>
                <button onClick={handlePipelineExecute} disabled={pipelineExecuting} className="btn-primary inline-flex items-center gap-1.5"> {/* v2.5 */}
                  {pipelineExecuting ? <Spinner size="sm" /> : <Icon.Sparkles className="w-4 h-4" />} {/* v2.5 */}
                  {pipelineExecuting ? 'Executing…' : 'Execute Pipeline'} {/* v2.5 */}
                </button>
                {pipelineExecuteResult && ( // v2.5
                  <Alert tone={pipelineExecuteResult.ok ? 'success' : 'danger'} onClose={() => setPipelineExecuteResult(null)}> {/* v2.5 */}
                    {pipelineExecuteResult.ok // v2.5
                      ? `✓ Pipeline started — workflow_id: ${pipelineExecuteResult.data?.workflow_id ?? pipelineExecuteResult.data?.run?.workflow_id ?? '—'}` // v2.5
                      : `✕ ${pipelineExecuteResult.error}`} {/* v2.5 */}
                  </Alert>
                )}
              </div>
            </Card>

            {/* v2.5: recent runs table */}
            <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
              <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                <SectionHeader eyebrow="Recent Activity" title="Recent Pipeline Runs" subtitle="Last 20 runs across all agents" icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.5 */}
              </div>
              {pipelineLoading ? ( // v2.5
                <div className="p-8 flex justify-center"><Spinner /></div> // v2.5
              ) : pipelineRuns.length === 0 ? ( // v2.5
                <div className="p-8"><EmptyState icon={<Icon.Activity className="w-5 h-5" />} title="No pipeline runs yet" description="Trigger a manual run above to see results here." /></div> // v2.5
              ) : (
                <div className="overflow-x-auto"> {/* v2.5 */}
                  <table className="data-table"> {/* v2.5 */}
                    <thead><tr><th>Workflow ID</th><th>Agent</th><th>State</th><th>Phase</th><th>Stage</th><th>Duration</th><th>Error</th></tr></thead> {/* v2.5 */}
                    <tbody> {/* v2.5 */}
                      {pipelineRuns.slice(0, 20).map((r: any, i: number) => ( // v2.5
                        <tr key={r.id || r.workflow_id || i}> {/* v2.5 */}
                          <td className="text-2xs font-mono">{r.workflow_id || r.id?.slice(0, 8) || '—'}</td> {/* v2.5 */}
                          <td className="text-sm font-medium font-mono">{r.agent_code || r.agent || '—'}</td> {/* v2.5 */}
                          <td><StatusPill tone={r.state === 'COMPLETED' ? 'success' : r.state === 'FAILED' ? 'danger' : r.state === 'RUNNING' ? 'info' : 'warning'} dot>{r.state || '—'}</StatusPill></td> {/* v2.5 */}
                          <td className="text-2xs">{r.phase || '—'}</td> {/* v2.5 */}
                          <td className="text-2xs">{r.stage || r.current_stage || '—'}</td> {/* v2.5 */}
                          <td className="nums text-2xs">{Number(r.duration_ms ?? r.duration ?? 0).toLocaleString()} ms</td> {/* v2.5 */}
                          <td className="text-2xs text-danger-600 dark:text-danger-400 max-w-xs truncate" title={r.error || r.error_message || ''}>{r.error || r.error_message || '—'}</td> {/* v2.5 */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* v2.5: all 30 stages reference table */}
            <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
              <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                <SectionHeader eyebrow="Reference" title="Pipeline Stages (30)" subtitle="Full reference of all cognitive pipeline stages" icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.5 */}
              </div>
              {pipelineStages.length === 0 ? ( // v2.5
                <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No stages registered" description="Stages will be listed here once the pipeline service is seeded." /></div> // v2.5
              ) : (
                <div className="overflow-x-auto"> {/* v2.5 */}
                  <table className="data-table"> {/* v2.5 */}
                    <thead><tr><th>#</th><th>Code</th><th>Name</th><th>Phase</th><th>Description</th></tr></thead> {/* v2.5 */}
                    <tbody> {/* v2.5 */}
                      {pipelineStages.map((s: any, i: number) => ( // v2.5
                        <tr key={s.code || s.stage_code || s.id || i}> {/* v2.5 */}
                          <td className="nums text-2xs">{s.order ?? s.stage_order ?? i + 1}</td> {/* v2.5 */}
                          <td className="text-xs font-mono font-semibold">{s.code || s.stage_code || '—'}</td> {/* v2.5 */}
                          <td className="text-sm font-medium">{s.name || s.stage_name || '—'}</td> {/* v2.5 */}
                          <td><StatusPill tone="info">{s.phase || '—'}</StatusPill></td> {/* v2.5 */}
                          <td className="text-2xs text-ink-500 dark:text-ink-400">{s.description || '—'}</td> {/* v2.5 */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ═══ v2.5: AI GOV V2 — Extended AI Governance ═══ */}
        {activeTab === 'ai-gov-v2' && ( // v2.5
          <div className="space-y-4"> {/* v2.5 */}
            {/* v2.5: header */}
            <SectionHeader // v2.5
              eyebrow="AI Governance V2" // v2.5
              title="Extended AI Governance — KPIs · Provenance · Policy · Benchmarks" // v2.5
              subtitle="9 governance KPIs, provenance records, policy rules + violations, and benchmark suites." // v2.5
              icon={<Icon.Cpu className="w-4 h-4" />} // v2.5
            /> {/* v2.5 */}

            {aiGovV2Error && <Alert tone="danger" onClose={() => setAiGovV2Error(null)}>{aiGovV2Error}</Alert>} {/* v2.5 */}
            {aiGovV2Success && <Alert tone="success" onClose={() => setAiGovV2Success(null)}>{aiGovV2Success}</Alert>} {/* v2.5 */}

            {/* v2.5: 9 KPI tiles — AI latency, token usage, retrieval quality, citation coverage, hallucination rate, model routing, user feedback, safety failures, provenance records */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3"> {/* v2.5 */}
              <MetricTile label="AI Latency"          value={`${Number(aiGovV2Metrics?.ai_latency_ms ?? aiGovV2Metrics?.avg_latency_ms ?? 0).toFixed(0)} ms`}        icon={<Icon.Clock className="w-4 h-4" />} accent="admin" /> {/* v2.5 */}
              <MetricTile label="Token Usage"         value={Number(aiGovV2Metrics?.token_usage ?? aiGovV2Metrics?.total_tokens ?? 0).toLocaleString()}                 icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Retrieval Quality"   value={`${Number(aiGovV2Metrics?.retrieval_quality ?? aiGovV2Metrics?.avg_retrieval_score ?? 0).toFixed(2)}`}      icon={<Icon.TrendingUp className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Citation Coverage"   value={`${Number(aiGovV2Metrics?.citation_coverage ?? 0).toFixed(1)}%`}                                          icon={<Icon.FileText className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Hallucination Rate"  value={`${Number(aiGovV2Metrics?.hallucination_rate ?? 0).toFixed(2)}%`}                                       icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Model Routing"       value={aiGovV2Metrics?.model_routing ?? aiGovV2Metrics?.primary_model ?? 'Gemini 3.1 Flash Lite'}                       icon={<Icon.Cpu className="w-4 h-4" />} /> {/* v3.4 */}
              <MetricTile label="User Feedback"       value={`${Number(aiGovV2Metrics?.user_feedback_rate ?? aiGovV2Metrics?.feedback_rate ?? 0).toFixed(1)}%`}         icon={<Icon.Sparkles className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Safety Failures"     value={Number(aiGovV2Metrics?.safety_failures ?? 0).toLocaleString()}                                          icon={<Icon.Shield className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Provenance Records"  value={Number(aiGovV2Metrics?.provenance_records ?? aiGovV2Provenance.length ?? 0).toLocaleString()}             icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.5 */}
            </div>

            {aiGovV2Loading ? ( // v2.5
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card> // v2.5
            ) : (
              <div className="space-y-4"> {/* v2.5 */}
                {/* v2.5: Provenance records table — time | agent | model | confidence | blocked | feedback */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Audit Trail" title="Provenance Records" subtitle="Last 20 AI inferences with provenance metadata" icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {aiGovV2Provenance.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No provenance records" description="AI inference provenance will be recorded as users interact with AI features." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Time</th><th>Agent</th><th>Model</th><th>Confidence</th><th>Blocked</th><th>Feedback</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {aiGovV2Provenance.map((p: any, i: number) => ( // v2.5
                            <tr key={p.id || i}> {/* v2.5 */}
                              <td className="text-2xs nums">{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td className="text-sm font-medium font-mono">{p.agent_code || p.agent || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs font-mono">{p.model || p.model_code || '—'}</td> {/* v2.5 */}
                              <td className="nums">{p.confidence != null ? `${(Number(p.confidence) * 100).toFixed(0)}%` : '—'}</td> {/* v2.5 */}
                              <td>{p.blocked ? <StatusPill tone="danger" dot>Blocked</StatusPill> : <StatusPill tone="success" dot>Allowed</StatusPill>}</td> {/* v2.5 */}
                              <td><StatusPill tone={p.user_feedback === 'CONFIRMED' ? 'success' : p.user_feedback === 'REJECTED' ? 'danger' : 'neutral'}>{p.user_feedback || '—'}</StatusPill></td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Policy rules table — code | name | type | action | priority | active */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Policy" title="Policy Rules" subtitle="Active governance policy rules" icon={<Icon.Shield className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {aiGovV2Rules.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No policy rules" description="Policy rules will appear here once configured." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Action</th><th>Priority</th><th>Active</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {aiGovV2Rules.map((r: any, i: number) => ( // v2.5
                            <tr key={r.id || r.code || i}> {/* v2.5 */}
                              <td className="text-xs font-mono font-semibold">{r.code || r.rule_code || '—'}</td> {/* v2.5 */}
                              <td className="text-sm font-medium">{r.name || r.rule_name || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone="info">{r.type || r.rule_type || '—'}</StatusPill></td> {/* v2.5 */}
                              <td><StatusPill tone={r.action === 'BLOCK' ? 'danger' : r.action === 'WARN' ? 'warning' : 'neutral'}>{r.action || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="nums">{r.priority ?? '—'}</td> {/* v2.5 */}
                              <td>{r.is_active ? <StatusPill tone="success" dot>Active</StatusPill> : <StatusPill tone="neutral">Inactive</StatusPill>}</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Policy violations table — time | rule | agent | action | detail | resolved (with resolve button) */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Enforcement" title="Policy Violations" subtitle="Last 20 violations — resolve to clear them" icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {aiGovV2Violations.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Alert className="w-5 h-5" />} title="No violations" description="AI policy violations will be tracked here." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Time</th><th>Rule</th><th>Agent</th><th>Action</th><th>Detail</th><th>Resolved</th><th>Action</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {aiGovV2Violations.map((v: any, i: number) => ( // v2.5
                            <tr key={v.id || i}> {/* v2.5 */}
                              <td className="text-2xs nums">{v.created_at ? new Date(v.created_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td className="text-xs font-mono">{v.rule_code || v.rule || '—'}</td> {/* v2.5 */}
                              <td className="text-xs font-mono">{v.agent_code || v.agent || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone={v.action === 'BLOCK' ? 'danger' : 'warning'}>{v.action || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="text-2xs text-ink-500 dark:text-ink-400 max-w-xs truncate" title={v.detail || v.reason || ''}>{v.detail || v.reason || '—'}</td> {/* v2.5 */}
                              <td>{v.resolved ? <StatusPill tone="success" dot>Resolved</StatusPill> : <StatusPill tone="warning" dot>Open</StatusPill>}</td> {/* v2.5 */}
                              <td> {/* v2.5 */}
                                {!v.resolved && ( // v2.5
                                  <button onClick={() => handleAiGovV2ResolveViolation(v.id)} disabled={aiGovV2Resolving === v.id} className="btn-secondary text-2xs"> {/* v2.5 */}
                                    {aiGovV2Resolving === v.id ? <Spinner size="sm" /> : null} {/* v2.5 */}
                                    Resolve {/* v2.5 */}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Benchmarks list with run button + model_code input */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Quality" title="Benchmark Suites" subtitle="Run benchmark suites against specific models" icon={<Icon.Award className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {aiGovV2Benchmarks.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Award className="w-5 h-5" />} title="No benchmarks configured" description="Benchmark suites will appear here once seeded." /></div> // v2.5
                  ) : (
                    <div className="divide-y divide-ink-100 dark:divide-ink-800"> {/* v2.5 */}
                      {aiGovV2Benchmarks.map((b: any, i: number) => ( // v2.5
                        <div key={b.id || i} className="p-4 flex flex-col md:flex-row md:items-center gap-3"> {/* v2.5 */}
                          <div className="flex-1 min-w-0"> {/* v2.5 */}
                            <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{b.name || b.benchmark_name || '—'}</p> {/* v2.5 */}
                            <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{b.description || `${b.test_count ?? b.cases ?? 0} test cases`}</p> {/* v2.5 */}
                            {b.last_run_at && <p className="text-2xs text-ink-400 nums mt-0.5">Last run: {new Date(b.last_run_at).toLocaleString()}</p>} {/* v2.5 */}
                          </div>
                          <div className="flex items-center gap-2 shrink-0"> {/* v2.5 */}
                            <input className="input text-xs w-44" placeholder="model_code" value={aiGovV2BenchModelInput[b.id] || ''} onChange={e => setAiGovV2BenchModelInput(prev => ({ ...prev, [b.id]: e.target.value }))} /> {/* v2.5 */}
                            <button onClick={() => handleAiGovV2RunBenchmark(b.id)} disabled={aiGovV2RunningBench === b.id} className="btn-primary text-xs inline-flex items-center gap-1.5"> {/* v2.5 */}
                              {aiGovV2RunningBench === b.id ? <Spinner size="sm" /> : <Icon.Sparkles className="w-3.5 h-3.5" />} {/* v2.5 */}
                              {aiGovV2RunningBench === b.id ? 'Running…' : 'Run'} {/* v2.5 */}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ═══ v2.5: ECOS — Strategic Capabilities ═══ */}
        {activeTab === 'ecos' && ( // v2.5
          <div className="space-y-4"> {/* v2.5 */}
            {/* v2.5: header */}
            <SectionHeader // v2.5
              eyebrow="Strategic Capabilities" // v2.5
              title="ECOS — Agents · Skills · Evolution · Costs" // v2.5
              subtitle="Monitor agent health, execute skills, evolve knowledge, and track costs." // v2.5
              icon={<Icon.Sparkles className="w-4 h-4" />} // v2.5
            /> {/* v2.5 */}

            {ecosError && <Alert tone="danger" onClose={() => setEcosError(null)}>{ecosError}</Alert>} {/* v2.5 */}
            {ecosSuccess && <Alert tone="success" onClose={() => setEcosSuccess(null)}>{ecosSuccess}</Alert>} {/* v2.5 */}

            {/* v2.5: 3 health KPI tiles pulled from /ecos/agents/health/summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3"> {/* v2.5 */}
              <MetricTile label="Healthy Agents"   value={Number(ecosHealth?.healthy ?? 0).toLocaleString()} icon={<Icon.Check className="w-4 h-4" />} accent="admin" /> {/* v2.5 */}
              <MetricTile label="Degraded Agents"  value={Number(ecosHealth?.degraded ?? 0).toLocaleString()} icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Unhealthy Agents" value={Number(ecosHealth?.unhealthy ?? 0).toLocaleString()} icon={<Icon.Shield className="w-4 h-4" />} /> {/* v2.5 */}
            </div>

            {/* v2.5: cost summary KPIs — total cost, tokens, records */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3"> {/* v2.5 */}
              <MetricTile label="Total Cost (7d)"  value={`$${Number(ecosCosts?.total_cost_usd ?? ecosCosts?.total_cost ?? 0).toFixed(2)}`} icon={<Icon.Dollar className="w-4 h-4" />} accent="admin" /> {/* v2.5 */}
              <MetricTile label="Tokens Used"      value={Number(ecosCosts?.total_tokens ?? 0).toLocaleString()} icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Records"          value={Number(ecosCosts?.total_records ?? 0).toLocaleString()} icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.5 */}
            </div>

            {ecosLoading ? ( // v2.5
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card> // v2.5
            ) : (
              <div className="space-y-4"> {/* v2.5 */}
                {/* v2.5: Agents cards table — code | SVC ID | status | health | circuit | invocations | success rate | avg latency */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Agent Registry" title="Strategic Agents" subtitle="Health, circuit state, and performance metrics" icon={<Icon.Cpu className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {ecosAgents.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Cpu className="w-5 h-5" />} title="No ECOS agents" description="Strategic agents will appear here once registered." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Code</th><th>SVC ID</th><th>Status</th><th>Health</th><th>Circuit</th><th>Invocations</th><th>Success Rate</th><th>Avg Latency</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {ecosAgents.map((a: any, i: number) => ( // v2.5
                            <tr key={a.id || a.code || i}> {/* v2.5 */}
                              <td className="text-xs font-mono font-semibold">{a.code || a.agent_code || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs font-mono">{a.svc_id || a.service_id || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone={a.status === 'ACTIVE' ? 'success' : a.status === 'INACTIVE' ? 'neutral' : 'warning'} dot>{a.status || '—'}</StatusPill></td> {/* v2.5 */}
                              <td><StatusPill tone={a.health === 'HEALTHY' ? 'success' : a.health === 'DEGRADED' ? 'warning' : a.health === 'UNHEALTHY' ? 'danger' : 'neutral'} dot>{a.health || '—'}</StatusPill></td> {/* v2.5 */}
                              <td><StatusPill tone={a.circuit_state === 'CLOSED' ? 'success' : a.circuit_state === 'OPEN' ? 'danger' : 'warning'} dot>{a.circuit_state || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="nums">{Number(a.invocations ?? 0).toLocaleString()}</td> {/* v2.5 */}
                              <td className="nums">{a.success_rate != null ? `${(Number(a.success_rate) * 100).toFixed(1)}%` : '—'}</td> {/* v2.5 */}
                              <td className="nums text-2xs">{Number(a.avg_latency_ms ?? 0).toLocaleString()} ms</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Skills table — skill_id | name | category | composable | executions | avg latency (with execute button + input JSON) */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Capabilities" title="Skills" subtitle="Composable skills with on-demand execution" icon={<Icon.Sparkles className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {ecosSkills.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Sparkles className="w-5 h-5" />} title="No skills registered" description="Skills will appear here once registered." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Skill ID</th><th>Name</th><th>Category</th><th>Composable</th><th>Executions</th><th>Avg Latency</th><th>Input JSON</th><th>Action</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {ecosSkills.map((s: any, i: number) => { // v2.5
                            const skillId = s.id || s.skill_id || ''; // v2.5
                            return ( // v2.5
                              <tr key={skillId || i}> {/* v2.5 */}
                                <td className="text-2xs font-mono">{skillId.slice(0, 8)}…</td> {/* v2.5 */}
                                <td className="text-sm font-medium">{s.name || s.skill_name || '—'}</td> {/* v2.5 */}
                                <td><StatusPill tone="info">{s.category || '—'}</StatusPill></td> {/* v2.5 */}
                                <td>{s.composable ? <StatusPill tone="success" dot>Yes</StatusPill> : <StatusPill tone="neutral">No</StatusPill>}</td> {/* v2.5 */}
                                <td className="nums">{Number(s.executions ?? 0).toLocaleString()}</td> {/* v2.5 */}
                                <td className="nums text-2xs">{Number(s.avg_latency_ms ?? 0).toLocaleString()} ms</td> {/* v2.5 */}
                                <td> {/* v2.5 */}
                                  <input className="input text-2xs font-mono w-40" placeholder='{"key":"value"}' value={ecosSkillInput[skillId] || ''} onChange={e => setEcosSkillInput(prev => ({ ...prev, [skillId]: e.target.value }))} /> {/* v2.5 */}
                                </td>
                                <td> {/* v2.5 */}
                                  <button onClick={() => handleEcosExecuteSkill(skillId)} disabled={ecosExecutingSkill === skillId} className="btn-primary text-2xs inline-flex items-center gap-1"> {/* v2.5 */}
                                    {ecosExecutingSkill === skillId ? <Spinner size="sm" /> : <Icon.Sparkles className="w-3 h-3" />} {/* v2.5 */}
                                    Execute {/* v2.5 */}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Evolution findings table — type | detail | severity | status (with run evolution button) */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center"> {/* v2.5 */}
                    <SectionHeader eyebrow="Knowledge Evolution" title="Evolution Findings" subtitle="Last 20 findings emitted by knowledge evolution" icon={<Icon.TrendingUp className="w-4 h-4" />} /> {/* v2.5 */}
                    <button onClick={handleEcosRunEvolution} disabled={ecosRunningEvolution} className="btn-primary text-xs inline-flex items-center gap-1.5"> {/* v2.5 */}
                      {ecosRunningEvolution ? <Spinner size="sm" /> : <Icon.Refresh className="w-3.5 h-3.5" />} {/* v2.5 */}
                      {ecosRunningEvolution ? 'Running…' : 'Run Evolution'} {/* v2.5 */}
                    </button>
                  </div>
                  {ecosFindings.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.TrendingUp className="w-5 h-5" />} title="No findings yet" description="Run knowledge evolution to emit new findings." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Type</th><th>Detail</th><th>Severity</th><th>Status</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {ecosFindings.map((f: any, i: number) => ( // v2.5
                            <tr key={f.id || i}> {/* v2.5 */}
                              <td><StatusPill tone="info">{f.type || f.finding_type || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="text-sm text-ink-600 dark:text-ink-300 max-w-md">{f.detail || f.description || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone={f.severity === 'CRITICAL' ? 'danger' : f.severity === 'HIGH' ? 'warning' : 'neutral'} dot>{f.severity || '—'}</StatusPill></td> {/* v2.5 */}
                              <td><StatusPill tone={f.status === 'APPLIED' ? 'success' : f.status === 'PENDING' ? 'warning' : 'neutral'} dot>{f.status || '—'}</StatusPill></td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Cost optimization recommendations */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Optimization" title="Cost Recommendations" subtitle="AI-generated cost reduction recommendations" icon={<Icon.Dollar className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {ecosCostRecs.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Dollar className="w-5 h-5" />} title="No recommendations" description="Cost optimization recommendations will appear here." /></div> // v2.5
                  ) : (
                    <div className="divide-y divide-ink-100 dark:divide-ink-800"> {/* v2.5 */}
                      {ecosCostRecs.map((rec: any, i: number) => ( // v2.5
                        <div key={rec.id || i} className="p-4"> {/* v2.5 */}
                          <div className="flex items-start gap-3"> {/* v2.5 */}
                            <StatusPill tone={rec.priority === 'HIGH' ? 'danger' : rec.priority === 'MEDIUM' ? 'warning' : 'neutral'} dot>{rec.priority || 'LOW'}</StatusPill> {/* v2.5 */}
                            <div className="flex-1 min-w-0"> {/* v2.5 */}
                              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{rec.title || rec.recommendation || '—'}</p> {/* v2.5 */}
                              {rec.detail && <p className="text-xs text-ink-600 dark:text-ink-300 mt-1">{rec.detail}</p>} {/* v2.5 */}
                              {rec.estimated_savings_usd != null && <p className="text-2xs text-success-700 dark:text-success-400 mt-1 nums">Est. savings: ${Number(rec.estimated_savings_usd).toFixed(2)}/mo</p>} {/* v2.5 */}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ═══ v2.5: DURABILITY — Durable Execution + Circuit Breakers + Approvals + Saga ═══ */}
        {activeTab === 'durability' && ( // v2.5
          <div className="space-y-4"> {/* v2.5 */}
            {/* v2.5: header */}
            <SectionHeader // v2.5
              eyebrow="Durable Execution" // v2.5
              title="Durability — Circuit Breakers · Approvals · Saga · Auto-Recovery" // v2.5
              subtitle="Monitor and operate the durable execution layer that protects every workflow." // v2.5
              icon={<Icon.Shield className="w-4 h-4" />} // v2.5
              actions={ // v2.5
                <button onClick={handleDurabilityAutoRecover} disabled={durabilityAutoRecovering} className="btn-primary text-xs inline-flex items-center gap-1.5"> {/* v2.5 */}
                  {durabilityAutoRecovering ? <Spinner size="sm" /> : <Icon.Refresh className="w-3.5 h-3.5" />} {/* v2.5 */}
                  {durabilityAutoRecovering ? 'Recovering…' : 'Auto-Recover'} {/* v2.5 */}
                </button>
              }
            /> {/* v2.5 */}

            {durabilityError && <Alert tone="danger" onClose={() => setDurabilityError(null)}>{durabilityError}</Alert>} {/* v2.5 */}
            {durabilitySuccess && <Alert tone="success" onClose={() => setDurabilitySuccess(null)}>{durabilitySuccess}</Alert>} {/* v2.5 */}

            {/* v2.5: Saga compensation stats KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3"> {/* v2.5 */}
              <MetricTile label="Total Compensations" value={Number(durabilityCompensations?.total ?? 0).toLocaleString()} icon={<Icon.Refresh className="w-4 h-4" />} accent="admin" /> {/* v2.5 */}
              <MetricTile label="Succeeded"            value={Number(durabilityCompensations?.succeeded ?? 0).toLocaleString()} icon={<Icon.Check className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Failed"               value={Number(durabilityCompensations?.failed ?? 0).toLocaleString()} icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
              <MetricTile label="Crashed Workflows"    value={durabilityCrashed.length} icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
            </div>

            {durabilityLoading ? ( // v2.5
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card> // v2.5
            ) : (
              <div className="space-y-4"> {/* v2.5 */}
                {/* v2.5: Circuit breakers table — service | state | failures | successes | avg latency | last failure (with reset button) */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Resilience" title="Circuit Breakers" subtitle="Per-service circuit breaker state + failure counters" icon={<Icon.Shield className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {durabilityBreakers.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No circuit breakers" description="Breakers will appear once services register them." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Service</th><th>State</th><th>Failures</th><th>Successes</th><th>Avg Latency</th><th>Last Failure</th><th>Action</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {durabilityBreakers.map((b: any, i: number) => ( // v2.5
                            <tr key={b.service_code || b.service || i}> {/* v2.5 */}
                              <td className="text-sm font-medium font-mono">{b.service_code || b.service || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone={b.state === 'CLOSED' ? 'success' : b.state === 'OPEN' ? 'danger' : 'warning'} dot>{b.state || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="nums">{Number(b.failures ?? b.failure_count ?? 0).toLocaleString()}</td> {/* v2.5 */}
                              <td className="nums">{Number(b.successes ?? b.success_count ?? 0).toLocaleString()}</td> {/* v2.5 */}
                              <td className="nums text-2xs">{Number(b.avg_latency_ms ?? 0).toLocaleString()} ms</td> {/* v2.5 */}
                              <td className="text-2xs nums">{b.last_failure_at ? new Date(b.last_failure_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td> {/* v2.5 */}
                                <button onClick={() => handleDurabilityResetBreaker(b.service_code || b.service)} disabled={durabilityResetting === (b.service_code || b.service)} className="btn-secondary text-2xs inline-flex items-center gap-1"> {/* v2.5 */}
                                  {durabilityResetting === (b.service_code || b.service) ? <Spinner size="sm" /> : <Icon.Refresh className="w-3 h-3" />} {/* v2.5 */}
                                  Reset {/* v2.5 */}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Pending approvals table — time | agent | type | urgency | reason | decision summary (with approve/reject buttons) */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Human-in-the-Loop" title="Pending Approvals" subtitle="Awaiting an admin decision" icon={<Icon.Clock className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {durabilityApprovals.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Clock className="w-5 h-5" />} title="No pending approvals" description="Approvals requiring admin action will appear here." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Time</th><th>Agent</th><th>Type</th><th>Urgency</th><th>Reason</th><th>Decision</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {durabilityApprovals.map((a: any, i: number) => ( // v2.5
                            <tr key={a.id || i}> {/* v2.5 */}
                              <td className="text-2xs nums">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td className="text-xs font-mono">{a.agent_code || a.agent || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone="info">{a.type || a.approval_type || '—'}</StatusPill></td> {/* v2.5 */}
                              <td><StatusPill tone={a.urgency === 'CRITICAL' ? 'danger' : a.urgency === 'HIGH' ? 'warning' : 'neutral'} dot>{a.urgency || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="text-2xs text-ink-500 dark:text-ink-400 max-w-xs truncate" title={a.reason || a.summary || ''}>{a.reason || a.summary || '—'}</td> {/* v2.5 */}
                              <td> {/* v2.5 */}
                                <div className="flex gap-1.5"> {/* v2.5 */}
                                  <button onClick={() => handleDurabilityResolveApproval(a.id, 'APPROVED')} disabled={durabilityResolvingApproval === a.id} className="btn-primary text-2xs inline-flex items-center gap-1"> {/* v2.5 */}
                                    {durabilityResolvingApproval === a.id ? <Spinner size="sm" /> : <Icon.Check className="w-3 h-3" />} {/* v2.5 */}
                                    Approve {/* v2.5 */}
                                  </button>
                                  <button onClick={() => handleDurabilityResolveApproval(a.id, 'REJECTED')} disabled={durabilityResolvingApproval === a.id} className="btn-secondary text-2xs inline-flex items-center gap-1"> {/* v2.5 */}
                                    <Icon.X className="w-3 h-3" /> {/* v2.5 */}
                                    Reject {/* v2.5 */}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Crashed workflows table */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Recovery" title="Crashed Workflows" subtitle="Workflows that failed mid-execution and need recovery" icon={<Icon.Alert className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {durabilityCrashed.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Check className="w-5 h-5" />} title="No crashed workflows" description="All workflows are healthy." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Workflow ID</th><th>Agent</th><th>Crashed At</th><th>Stage</th><th>Error</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {durabilityCrashed.map((c: any, i: number) => ( // v2.5
                            <tr key={c.id || c.workflow_id || i}> {/* v2.5 */}
                              <td className="text-2xs font-mono">{c.workflow_id || c.id?.slice(0, 8) || '—'}</td> {/* v2.5 */}
                              <td className="text-xs font-mono">{c.agent_code || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs nums">{c.crashed_at ? new Date(c.crashed_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td className="text-2xs">{c.stage || c.current_stage || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs text-danger-600 dark:text-danger-400 max-w-xs truncate" title={c.error || ''}>{c.error || '—'}</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ═══ v2.5: REGULATORY V2 — Regulatory API Gateway (tokens + logs) ═══ */}
        {activeTab === 'regulatory-v2' && ( // v2.5
          <div className="space-y-4"> {/* v2.5 */}
            {/* v2.5: header */}
            <SectionHeader // v2.5
              eyebrow="Regulatory API Gateway" // v2.5
              title="Regulatory V2 — Tokens & Access Logs" // v2.5
              subtitle="Issue API tokens to government authorities and inspect access logs." // v2.5
              icon={<Icon.Shield className="w-4 h-4" />} // v2.5
              actions={ // v2.5
                <button onClick={() => setRegV2ShowCreate(true)} className="btn-primary text-xs inline-flex items-center gap-1.5"> {/* v2.5 */}
                  <Icon.Plus className="w-3.5 h-3.5" /> Issue Token {/* v2.5 */}
                </button>
              }
            /> {/* v2.5 */}

            {regV2Error && <Alert tone="danger" onClose={() => setRegV2Error(null)}>{regV2Error}</Alert>} {/* v2.5 */}
            {regV2Success && <Alert tone="success" onClose={() => setRegV2Success(null)}>{regV2Success}</Alert>} {/* v2.5 */}

            {/* v2.5: newly-created token banner */}
            {regV2NewToken && ( // v2.5
              <Card padding="lg" className="border-2 border-success-300 bg-success-50 dark:bg-success-500/10"> {/* v2.5 */}
                <p className="text-sm font-bold text-success-700 dark:text-success-400 mb-2">✅ Token Created — Copy Now!</p> {/* v2.5 */}
                <p className="text-xs text-ink-600 dark:text-ink-400 mb-2">This token will NOT be shown again. Share it securely with the authority.</p> {/* v2.5 */}
                <code className="block p-3 bg-ink-100 dark:bg-ink-800 rounded-lg text-xs font-mono break-all">{regV2NewToken}</code> {/* v2.5 */}
                <div className="flex gap-2 mt-2"> {/* v2.5 */}
                  <button onClick={() => { navigator.clipboard.writeText(regV2NewToken); setRegV2Success('Token copied to clipboard!'); }} className="btn-primary text-xs">Copy Token</button> {/* v2.5 */}
                  <button onClick={() => setRegV2NewToken(null)} className="btn-secondary text-xs">Dismiss</button> {/* v2.5 */}
                </div>
              </Card>
            )}

            {regV2Loading ? ( // v2.5
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card> // v2.5
            ) : (
              <div className="space-y-4"> {/* v2.5 */}
                {/* v2.5: Tokens table */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Access Management" title="API Tokens" subtitle="Tokens issued to government authorities" icon={<Icon.Shield className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {regV2Tokens.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No tokens issued" description="Issue a token to grant API access to an authority." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Authority</th><th>Scope</th><th>Created</th><th>Last Access</th><th>Status</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {regV2Tokens.map((t: any, i: number) => ( // v2.5
                            <tr key={t.id || i}> {/* v2.5 */}
                              <td className="text-sm font-medium">{t.authority || t.agency_name || t.agency_type || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs">{t.scope || t.scopes || 'all'}</td> {/* v2.5 */}
                              <td className="text-2xs nums">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td> {/* v2.5 */}
                              <td className="text-2xs nums">{t.last_access_at ? new Date(t.last_access_at).toLocaleString() : 'Never'}</td> {/* v2.5 */}
                              <td>{t.is_active !== false ? <StatusPill tone="success" dot>Active</StatusPill> : <StatusPill tone="danger" dot>Revoked</StatusPill>}</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: Access logs table — time | authority | endpoint | status | response_time */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Audit Trail" title="API Access Logs" subtitle="Last 50 API calls made with regulatory tokens" icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {regV2Logs.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No access logs" description="API calls will be logged here once authorities start using their tokens." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Time</th><th>Authority</th><th>Endpoint</th><th>Status</th><th>Response Time</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {regV2Logs.map((l: any, i: number) => ( // v2.5
                            <tr key={l.id || i}> {/* v2.5 */}
                              <td className="text-2xs nums">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td className="text-xs font-medium">{l.authority || l.agency_type || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs font-mono">{l.endpoint || l.path || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone={Number(l.status_code ?? l.status) >= 200 && Number(l.status_code ?? l.status) < 300 ? 'success' : Number(l.status_code ?? l.status) >= 400 && Number(l.status_code ?? l.status) < 500 ? 'warning' : 'danger'} dot>{l.status_code || l.status || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="nums text-2xs">{Number(l.response_time_ms ?? l.response_time ?? 0).toLocaleString()} ms</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            )}

            {/* v2.5: Create token Sheet */}
            {regV2ShowCreate && ( // v2.5
              <Sheet // v2.5
                open={regV2ShowCreate} // v2.5
                onClose={() => setRegV2ShowCreate(false)} // v2.5
                title="Issue Regulatory API Token" // v2.5
                footer={ // v2.5
                  <div className="flex gap-2"> {/* v2.5 */}
                    <button onClick={() => setRegV2ShowCreate(false)} className="btn-secondary flex-1">Cancel</button> {/* v2.5 */}
                    <button onClick={handleRegV2CreateToken} disabled={regV2Loading || !regV2Form.authority} className="btn-primary flex-1"> {/* v2.5 */}
                      {regV2Loading ? <Spinner size="sm" /> : null} {/* v2.5 */}
                      Create Token {/* v2.5 */}
                    </button>
                  </div>
                }
              >
                <div className="space-y-3"> {/* v2.5 */}
                  <div> {/* v2.5 */}
                    <label className="label">Authority *</label> {/* v2.5 */}
                    <select className="input" value={regV2Form.authority} onChange={e => setRegV2Form(prev => ({ ...prev, authority: e.target.value }))}> {/* v2.5 */}
                      <option value="MINADER">MINADER — Ministry of Agriculture</option> {/* v2.5 */}
                      <option value="NCCB">NCCB — National Cocoa & Coffee Board</option> {/* v2.5 */}
                      <option value="MINEE">MINEE — Ministry of Energy & Water</option> {/* v2.5 */}
                      <option value="MINCOMMERCE">MINCOMMERCE — Ministry of Commerce</option> {/* v2.5 */}
                      <option value="MINAS">MINAS — Ministry of Social Affairs</option> {/* v2.5 */}
                      <option value="MINTSS">MINTSS — Ministry of Labour</option> {/* v2.5 */}
                      <option value="MINT">MINT — Ministry of Transport</option> {/* v2.5 */}
                      <option value="IRAD">IRAD — Agricultural Research Institute</option> {/* v2.5 */}
                    </select>
                  </div>
                  <div> {/* v2.5 */}
                    <label className="label">Scopes (comma-separated)</label> {/* v2.5 */}
                    <input className="input" value={regV2Form.scope} onChange={e => setRegV2Form(prev => ({ ...prev, scope: e.target.value }))} placeholder="soil,quality,export" /> {/* v2.5 */}
                  </div>
                  <Alert tone="info">The token grants read-only access to the specified data scopes. The authority must include it as the <code>X-Regulatory-Token</code> header in all API requests.</Alert> {/* v2.5 */}
                </div>
              </Sheet>
            )}
          </div>
        )}

        {/* ═══ v2.5: SMS — SMS Management ═══ */}
        {activeTab === 'sms' && ( // v2.5
          <div className="space-y-4"> {/* v2.5 */}
            {/* v2.5: header */}
            <SectionHeader // v2.5
              eyebrow="SMS Management" // v2.5
              title="SMS — Templates · Send · History" // v2.5
              subtitle="Send test SMS using registered templates and inspect delivery history." // v2.5
              icon={<Icon.Phone className="w-4 h-4" />} // v2.5
            /> {/* v2.5 */}

            {smsError && <Alert tone="danger" onClose={() => setSmsError(null)}>{smsError}</Alert>} {/* v2.5 */}
            {smsSuccess && <Alert tone="success" onClose={() => setSmsSuccess(null)}>{smsSuccess}</Alert>} {/* v2.5 */}

            {/* v2.5: Send test SMS form — phone input + template select + send button */}
            <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
              <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                <SectionHeader eyebrow="Compose" title="Send Test SMS" subtitle="Send a single SMS using a registered template" icon={<Icon.Send className="w-4 h-4" />} /> {/* v2.5 */}
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3"> {/* v2.5 */}
                <div> {/* v2.5 */}
                  <label className="label">Phone Number *</label> {/* v2.5 */}
                  <input className="input" type="tel" value={smsSendForm.phone} onChange={e => setSmsSendForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="+2376XXXXXXXX" /> {/* v2.5 */}
                </div>
                <div> {/* v2.5 */}
                  <label className="label">Template *</label> {/* v2.5 */}
                  <select className="input" value={smsSendForm.template_id} onChange={e => setSmsSendForm(prev => ({ ...prev, template_id: e.target.value }))}> {/* v2.5 */}
                    <option value="">— Select template —</option> {/* v2.5 */}
                    {smsTemplates.map((t: any, i: number) => <option key={t.id || i} value={t.id || t.template_id}>{t.name || t.template_name || t.id}</option>)} {/* v2.5 */}
                  </select>
                </div>
                <div className="flex items-end"> {/* v2.5 */}
                  <button onClick={handleSmsSend} disabled={smsSending} className="btn-primary w-full inline-flex items-center justify-center gap-1.5"> {/* v2.5 */}
                    {smsSending ? <Spinner size="sm" /> : <Icon.Send className="w-4 h-4" />} {/* v2.5 */}
                    {smsSending ? 'Sending…' : 'Send SMS'} {/* v2.5 */}
                  </button>
                </div>
              </div>
            </Card>

            {smsLoading ? ( // v2.5
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card> // v2.5
            ) : (
              <div className="space-y-4"> {/* v2.5 */}
                {/* v2.5: Templates table */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Catalog" title="SMS Templates" subtitle="Pre-registered message templates" icon={<Icon.FileText className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {smsTemplates.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.FileText className="w-5 h-5" />} title="No templates registered" description="Templates will appear here once seeded." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>ID</th><th>Name</th><th>Language</th><th>Body</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {smsTemplates.map((t: any, i: number) => ( // v2.5
                            <tr key={t.id || i}> {/* v2.5 */}
                              <td className="text-2xs font-mono">{String(t.id || t.template_id || '').slice(0, 8)}…</td> {/* v2.5 */}
                              <td className="text-sm font-medium">{t.name || t.template_name || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone="info">{t.language || 'en'}</StatusPill></td> {/* v2.5 */}
                              <td className="text-2xs text-ink-500 dark:text-ink-400 max-w-md truncate" title={t.body || t.content || ''}>{t.body || t.content || '—'}</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* v2.5: History table — time | to | template | status | message */}
                <Card padding="none" className="overflow-hidden"> {/* v2.5 */}
                  <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.5 */}
                    <SectionHeader eyebrow="Audit Trail" title="SMS History" subtitle="Last 50 SMS messages sent" icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.5 */}
                  </div>
                  {smsHistory.length === 0 ? ( // v2.5
                    <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No SMS history" description="Sent messages will appear here." /></div> // v2.5
                  ) : (
                    <div className="overflow-x-auto"> {/* v2.5 */}
                      <table className="data-table"> {/* v2.5 */}
                        <thead><tr><th>Time</th><th>To</th><th>Template</th><th>Status</th><th>Message</th></tr></thead> {/* v2.5 */}
                        <tbody> {/* v2.5 */}
                          {smsHistory.map((m: any, i: number) => ( // v2.5
                            <tr key={m.id || i}> {/* v2.5 */}
                              <td className="text-2xs nums">{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</td> {/* v2.5 */}
                              <td className="text-xs font-mono">{m.to || m.phone || m.recipient || '—'}</td> {/* v2.5 */}
                              <td className="text-2xs">{m.template_id ? String(m.template_id).slice(0, 8) + '…' : m.template_name || '—'}</td> {/* v2.5 */}
                              <td><StatusPill tone={m.status === 'DELIVERED' ? 'success' : m.status === 'FAILED' ? 'danger' : m.status === 'QUEUED' ? 'warning' : 'info'} dot>{m.status || '—'}</StatusPill></td> {/* v2.5 */}
                              <td className="text-2xs text-ink-500 dark:text-ink-400 max-w-md truncate" title={m.message || m.body || ''}>{m.message || m.body || '—'}</td> {/* v2.5 */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ═══ v2.7: DEPENDENCY GRAPH ═══ */}
        {activeTab === 'dep-graph' && ( // v2.7
          <div className="space-y-4"> {/* v2.7 */}
            <SectionHeader // v2.7
              eyebrow="System" // v2.7
              title="Dependency Graph" // v2.7
              subtitle="Layered view of all CoConnect business-module nodes + edges (migration 055)" // v2.7
              icon={<Icon.Activity className="w-4 h-4" />} // v2.7
            />

            {depGraphLoading && ( // v2.7
              <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card> // v2.7
            )}
            {depGraphError && ( // v2.7
              <Alert tone="warning" onClose={() => setDepGraphError(null)}>{depGraphError}</Alert> // v2.7
            )}

            {/* ─── "What if?" impact analysis ─── */} {/* v2.7 */}
            <Card padding="md"> {/* v2.7 */}
              <SectionHeader // v2.7
                eyebrow="Impact Analysis" // v2.7
                title='"What if?" — Simulate Node Failure' // v2.7
                subtitle="Type a node key → see every dependent node that would be affected" // v2.7
                icon={<Icon.Alert className="w-4 h-4" />} // v2.7
                className="mb-3" // v2.7
              />
              <div className="flex flex-col md:flex-row gap-2"> {/* v2.7 */}
                <input // v2.7
                  type="text" // v2.7
                  className="input flex-1" // v2.7
                  placeholder="e.g. data_source_open_meteo or ai_agent_disease" // v2.7
                  value={depGraphImpactKey} // v2.7
                  onChange={e => setDepGraphImpactKey(e.target.value)} // v2.7
                  onKeyDown={e => { if (e.key === 'Enter') handleDepGraphImpact(); }} // v2.7
                />
                <button // v2.7
                  onClick={handleDepGraphImpact} // v2.7
                  disabled={depGraphImpactLoading || !depGraphImpactKey.trim()} // v2.7
                  className="btn-primary text-xs" // v2.7
                >
                  {depGraphImpactLoading ? <Spinner size="sm" /> : <Icon.Activity className="w-3.5 h-3.5" />} {/* v2.7 */}
                  <span>{depGraphImpactLoading ? 'Analyzing…' : 'Run Impact Analysis'}</span> {/* v2.7 */}
                </button>
              </div>
              {depGraphImpactError && ( // v2.7
                <div className="mt-3"><Alert tone="danger" onClose={() => setDepGraphImpactError(null)}>{depGraphImpactError}</Alert></div> // v2.7
              )}
              {depGraphImpactResult && ( // v2.7
                <div className="mt-3 space-y-3"> {/* v2.7 */}
                  <Alert tone="info"> {/* v2.7 */}
                    <span className="font-medium">Node:</span> {depGraphImpactResult.node?.node_key || depGraphImpactResult.node?.key || depGraphImpactResult.key || depGraphImpactKey} {/* v2.7 */}
                    {' · '} {/* v2.7 */}
                    <span className="font-medium">Affected:</span> { // v2.7
                      Array.isArray(depGraphImpactResult.affected) ? depGraphImpactResult.affected.length // v2.7
                      : Array.isArray(depGraphImpactResult.impacted) ? depGraphImpactResult.impacted.length // v2.7
                      : Array.isArray(depGraphImpactResult.dependencies) ? depGraphImpactResult.dependencies.length // v2.7
                      : (depGraphImpactResult.affected_count ?? depGraphImpactResult.total ?? '—') // v2.7
                    } {/* v2.7 */}
                  </Alert>
                  {/* List every affected node (defensive — handles affected | impacted | dependencies) */} {/* v2.7 */}
                  {(() => { // v2.7
                    const list = Array.isArray(depGraphImpactResult.affected) ? depGraphImpactResult.affected // v2.7
                      : Array.isArray(depGraphImpactResult.impacted) ? depGraphImpactResult.impacted // v2.7
                      : Array.isArray(depGraphImpactResult.dependencies) ? depGraphImpactResult.dependencies // v2.7
                      : Array.isArray(depGraphImpactResult.nodes) ? depGraphImpactResult.nodes // v2.7
                      : []; // v2.7
                    if (list.length === 0) { // v2.7
                      return <EmptyState icon={<Icon.Check className="w-5 h-5" />} title="No downstream dependencies" description="This node has no affected dependents." />; // v2.7
                    }
                    return ( // v2.7
                      <div className="overflow-x-auto"> {/* v2.7 */}
                        <table className="data-table"> {/* v2.7 */}
                          <thead><tr><th>Type</th><th>Key</th><th>Name</th><th>Critical?</th></tr></thead> {/* v2.7 */}
                          <tbody> {/* v2.7 */}
                            {list.map((n: any, i: number) => ( // v2.7
                              <tr key={n.node_key || n.key || i}> {/* v2.7 */}
                                <td className="text-2xs">{n.node_type || n.type || '—'}</td> {/* v2.7 */}
                                <td className="text-xs font-mono">{n.node_key || n.key || '—'}</td> {/* v2.7 */}
                                <td className="text-sm">{n.node_name || n.name || '—'}</td> {/* v2.7 */}
                                <td>{n.is_critical ? <StatusPill tone="danger" dot>Critical</StatusPill> : <StatusPill tone="neutral">No</StatusPill>}</td> {/* v2.7 */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              )}
            </Card>

            {/* ─── Filter by node type ─── */} {/* v2.7 */}
            <Card padding="md"> {/* v2.7 */}
              <SectionHeader // v2.7
                eyebrow="Filter" // v2.7
                title="Nodes" // v2.7
                subtitle={`${depGraphNodes.length} nodes · ${depGraphEdges.length} edges`} // v2.7
                icon={<Icon.Database className="w-4 h-4" />} // v2.7
                className="mb-3" // v2.7
              />
              <div className="flex flex-wrap gap-1.5 mb-4"> {/* v2.7 */}
                {[ // v2.7
                  { id: 'ALL',          label: 'All' }, // v2.7
                  { id: 'DATA_SOURCE',  label: 'Data Sources' }, // v2.7
                  { id: 'AI_AGENT',     label: 'AI Agents' }, // v2.7
                  { id: 'ERP_MODULE',   label: 'ERP Modules' }, // v2.7
                  { id: 'DASHBOARD',    label: 'Dashboards' }, // v2.7
                  { id: 'STORAGE',      label: 'Storage' }, // v2.7
                  { id: 'ALERT',        label: 'Alerts' }, // v2.7
                ].map(f => ( // v2.7
                  <button // v2.7
                    key={f.id} // v2.7
                    onClick={() => setDepGraphFilter(f.id)} // v2.7
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${ // v2.7
                      depGraphFilter === f.id // v2.7
                        ? 'bg-ink-900 dark:bg-ink-50 text-ink-50 dark:text-ink-900 shadow-e1' // v2.7
                        : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700' // v2.7
                    }`} // v2.7
                  >
                    {f.label} {/* v2.7 */}
                  </button>
                ))}
              </div>

              {/* Table 1: Nodes [Type | Key | Name | Description | Critical?] */} {/* v2.7 */}
              <div className="overflow-x-auto mb-5"> {/* v2.7 */}
                <table className="data-table"> {/* v2.7 */}
                  <thead><tr><th>Type</th><th>Key</th><th>Name</th><th>Description</th><th>Critical?</th></tr></thead> {/* v2.7 */}
                  <tbody> {/* v2.7 */}
                    {depGraphNodes // v2.7
                      .filter(n => depGraphFilter === 'ALL' || (n.node_type || n.type || '').toUpperCase() === depGraphFilter) // v2.7
                      .map((n: any, i: number) => ( // v2.7
                      <tr key={n.node_key || n.key || i}> {/* v2.7 */}
                        <td className="text-2xs">{n.node_type || n.type || '—'}</td> {/* v2.7 */}
                        <td className="text-xs font-mono">{n.node_key || n.key || '—'}</td> {/* v2.7 */}
                        <td className="text-sm">{n.node_name || n.name || '—'}</td> {/* v2.7 */}
                        <td className="text-2xs text-ink-500 dark:text-ink-400 max-w-md truncate" title={n.node_description || n.description || ''}>{n.node_description || n.description || '—'}</td> {/* v2.7 */}
                        <td>{n.is_critical ? <StatusPill tone="danger" dot>Critical</StatusPill> : <StatusPill tone="neutral">No</StatusPill>}</td> {/* v2.7 */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Table 2: Edges [Source | Target | Type | Description] */} {/* v2.7 */}
              <SectionHeader // v2.7
                eyebrow="Layer 2" // v2.7
                title="Edges" // v2.7
                icon={<Icon.Activity className="w-4 h-4" />} // v2.7
                className="mb-3" // v2.7
              />
              {depGraphEdges.length === 0 ? ( // v2.7
                <EmptyState icon={<Icon.Activity className="w-5 h-5" />} title="No edges" description="Dependency edges will appear here once the graph is populated." /> // v2.7
              ) : ( // v2.7
                <div className="overflow-x-auto"> {/* v2.7 */}
                  <table className="data-table"> {/* v2.7 */}
                    <thead><tr><th>Source</th><th>Target</th><th>Type</th><th>Description</th></tr></thead> {/* v2.7 */}
                    <tbody> {/* v2.7 */}
                      {depGraphEdges.map((e: any, i: number) => ( // v2.7
                        <tr key={i}> {/* v2.7 */}
                          <td className="text-xs font-mono">{e.source_node_key || e.source || '—'}</td> {/* v2.7 */}
                          <td className="text-xs font-mono">{e.target_node_key || e.target || '—'}</td> {/* v2.7 */}
                          <td className="text-2xs">{e.edge_type || e.type || '—'}</td> {/* v2.7 */}
                          <td className="text-2xs text-ink-500 dark:text-ink-400 max-w-md truncate" title={e.edge_description || e.description || ''}>{e.edge_description || e.description || '—'}</td> {/* v2.7 */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ═══ v2.8: API KEYS (GAP 3) ═══ */}
        {activeTab === 'api-keys' && <ApiKeysTab />} {/* v2.8 */}

        {/* ═══ v2.8: HOUSEHOLDS (GAP 5a) ═══ */}
        {activeTab === 'households' && <HouseholdsTab />} {/* v2.8 */}

        {/* ═══ v2.8: NURSERY (GAP 5b) ═══ */}
        {activeTab === 'nursery' && <NurseryTab />} {/* v2.8 */}

        {/* ═══ v2.8: PLANTATION (GAP 5c) ═══ */}
        {activeTab === 'plantation' && <PlantationTab />} {/* v2.8 */}

        {/* ═══ v2.8: FACTORY (GAP 5d) ═══ */}
        {activeTab === 'factory' && <FactoryTab />} {/* v2.8 */}

        {/* ═══ v3.2.8: PRODUCER MANAGEMENT ═══ */}
        {activeTab === 'producers' && <ProducerManagementTab />} {/* v3.2.8 */}

        {/* ═══ v2.9: COGNITIVE MEMORY (Intelligence) ═══ */}
        {activeTab === 'cognitive-memory' && <CognitiveMemoryTab />} {/* v2.9 */}

        {/* ═══ v2.9: ORCHESTRATOR (Intelligence — EAOE) ═══ */}
        {activeTab === 'orchestrator' && <OrchestratorTab />} {/* v2.9 */}

        {/* ═══ EUDR ═══ */}
        {activeTab === 'eudr' && <EudrTab />}

        {/* ═══ CARBON ═══ */}
        {activeTab === 'carbon' && <CarbonTab />}

        {/* ═══ ADVISORY ═══ */}
        {activeTab === 'advisory' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setShowAdvisoryModal(true)} className="btn-primary">
                <Icon.Plus className="w-4 h-4" /> New advisory
              </button>
            </div>
            <Card padding="lg">
              {advisoryPag.total === 0 ? (
                <EmptyState icon={<Icon.FileText className="w-5 h-5" />} title="No advisory published" description="Publish tips and recommendations to farmers." />
              ) : (
                <div className="space-y-2.5">
                  {advisoryPag.paginatedItems.map((item: any) => (
                    <div key={item.id} className="p-3.5 surface-inset rounded-lg">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{item.title}</h4>
                        <span className="text-2xs text-ink-500 dark:text-ink-400 nums shrink-0">{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-ink-600 dark:text-ink-300 text-pretty">{item.content}</p>
                      <div className="flex items-center gap-1.5 mt-2">
                        <span className="pill-info capitalize">{item.category}</span>
                        <span className="pill-neutral uppercase">{item.language}</span>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-ink-200 dark:border-ink-800 mt-3 -mx-2">
                    <Pagination
                      page={advisoryPag.page}
                      pageSize={advisoryPag.pageSize}
                      total={advisoryPag.total}
                      onPageChange={advisoryPag.setPage}
                    />
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ═══ AUDIT LOG ═══ */}
        {activeTab === 'audit' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
              <span className="text-sm text-ink-500 dark:text-ink-400">Recent audit entries</span>
              <button onClick={() => window.open('/api/audit-log/export', '_blank')} className="btn-ghost text-xs">
                <Icon.Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Table</th><th>Action</th><th>Timestamp</th></tr></thead>
                <tbody>
                  {auditPag.paginatedItems.map((entry) => (
                    <tr key={entry.id}>
                      <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{entry.table_name}</td>
                      <td>
                        <StatusPill tone={entry.action === 'CREATE' ? 'success' : entry.action === 'UPDATE' ? 'info' : 'warning'} dot>
                          {entry.action}
                        </StatusPill>
                      </td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(entry.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination
                page={auditPag.page}
                pageSize={auditPag.pageSize}
                total={auditPag.total}
                onPageChange={auditPag.setPage}
                onPageSizeChange={auditPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ═══ SPATIAL OS ═══ */}
        {activeTab === 'spatial' && (
          <Card padding="lg" className="max-w-2xl">
            <SectionHeader
              eyebrow="Spatial Intelligence"
              title="Spatial Operating System"
              subtitle="Event-driven spatial intelligence layer — geometry pipeline, temporal versioning, ML feature store, and digital twin profiles."
              icon={<Icon.MapPin className="w-4 h-4" />}
            />
            <div className="mt-5 space-y-3">
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.Activity className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Live event stream</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Real-time GPS, polygon, weather, and sync events via SSE.</p>
                </div>
              </div>
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.MapPin className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Geometry pipeline lab</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">8-stage GPS track processor — denoise, simplify, validate.</p>
                </div>
              </div>
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.Clock className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Temporal versioning</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Replay farm boundary state at any point in time.</p>
                </div>
              </div>
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.Database className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">ML feature store + Digital twin</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">8-level maturity model and feature registry for AI models.</p>
                </div>
              </div>
              <a href="/spatial" className="btn-accent w-full py-3 brand-surface-analytics mt-2">
                Open Spatial Intelligence dashboard
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
            </div>
          </Card>
        )}

        {/* ═══ MEL ANALYTICS ═══ */}
        {activeTab === 'mel' && (
          <Card padding="lg" className="max-w-2xl">
            <SectionHeader
              eyebrow="Analytics"
              title="MEL Dashboard"
              subtitle="Monitoring, Evaluation & Learning — full analytics dashboard with financial, operational, compliance, and sustainability insights."
              icon={<Icon.BarChart3 className="w-4 h-4" />}
            />
            <div className="mt-5 space-y-3">
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.BarChart3 className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">5-tab enterprise analytics</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Overview, Financial, Operations, Compliance, Analytics — all with live data.</p>
                </div>
              </div>
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.TrendingUp className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">6 KPI tiles + trend indicators</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Farmers, batches, payouts, price/kg, zero-waste score.</p>
                </div>
              </div>
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.Shield className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">EUDR compliance + KYC tracking</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Real-time compliance rates, verification status, sustainability scores.</p>
                </div>
              </div>
              <div className="p-4 surface-inset rounded-lg flex items-start gap-3">
                <span className="shrink-0 w-9 h-9 rounded-lg bg-analytics-50 dark:bg-analytics-500/10 text-analytics-700 dark:text-analytics-500 flex items-center justify-center">
                  <Icon.Database className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">CSV export + system health</p>
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Export all metrics. Live API/DB/Redis/Spatial OS telemetry.</p>
                </div>
              </div>
              <a href="/dashboard" className="btn-accent w-full py-3 brand-surface-analytics mt-2">
                Open MEL Dashboard
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
            </div>
          </Card>
        )}

        {/* ═══ AI ADVISORY ═══ */}
        {activeTab === 'ai-advisory' && <AIAdvisoryTab />}

        {/* ═══ PEST REPORTS ═══ */}
        {activeTab === 'pest-reports' && <PestReportsTab />}

        {/* ═══ QR CODES ═══ */}
        {activeTab === 'qr-codes' && <QRCodesTab />}

        {/* ═══ v1.6.5: WAREHOUSE (redirect to warehouse portal) ═══ */}
        {activeTab === 'warehouse' && (
          <Card padding="lg">
            <SectionHeader eyebrow="Warehouse Management" title="Physical Inventory Control"
              subtitle="Manage receiving, inventory, outbound dispatch, and stock levels" />
            <div className="mt-4">
              <a href="/warehouse" className="btn-primary inline-flex">
                <Icon.Shield className="w-4 h-4" /> Open Warehouse Portal →
              </a>
            </div>
            <Alert tone="info" className="mt-4">
              The Warehouse Portal allows you to:
              <ul className="mt-2 list-disc list-inside text-sm space-y-1">
                <li>View stock summary (batches in stock, by grade, by decision, by section)</li>
                <li>See batches awaiting receipt (PENDING status)</li>
                <li>Manage inventory (INTAKED batches with warehouse sections)</li>
                <li>Dispatch batches outbound (to export warehouse, factory, or rejection)</li>
                <li>View outbound dispatch history with container numbers</li>
                <li>Track warehouse section capacity utilization</li>
              </ul>
            </Alert>
          </Card>
        )}

        {/* ═══ SETTINGS ═══ */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <OfflineFleetHealthPanel />
            <Card padding="lg" className="max-w-xl">
              <SectionHeader
                eyebrow="Pricing"
                title="Price per kilogram"
                subtitle="Current buy price for cocoa beans. Updating recalculates all pending batches."
                icon={<Icon.Dollar className="w-4 h-4" />}
              />
              <div className="mt-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-3xl font-semibold nums tracking-tight text-ink-900 dark:text-ink-50">{pricePerKg.toLocaleString()} <span className="text-base text-ink-500 dark:text-ink-400">XAF</span></p>
                  <p className="text-xs text-ink-500 dark:text-ink-400 mt-1">Per kilogram of cocoa beans</p>
                </div>
                <button onClick={() => { setNewPrice(pricePerKg.toString()); setShowPriceModal(true); }} className="btn-primary">
                  <Icon.Settings className="w-4 h-4" /> Update price
                </button>
              </div>
            </Card>

            {/* AI API Key Configuration */}
            <Card padding="lg" className="max-w-xl">
              <SectionHeader
                eyebrow="AI"
                title="AI API key"
                subtitle="Configure your Google Gemini API key for advisory generation, MEL analysis, and pest scanning. Free key from Google AI Studio."
                icon={<Icon.Sparkles className="w-4 h-4" />}
              />
              <div className="mt-5" id="ai-key-section">
                <AIApiKeyConfig />
              </div>
            </Card>

            {/* Weather Provider Configuration */}
            <Card padding="lg" className="max-w-xl">
              <WeatherConfigCard />
            </Card>

            {/* SMS Provider Configuration (Twilio free trial) */}
            <Card padding="lg" className="max-w-xl">
              <SmsConfigCard />
            </Card>

            {/* Map Provider Configuration (Google Maps / OpenStreetMap) */}
            <Card padding="lg" className="max-w-xl">
              <MapConfigCard />
            </Card>
          </div>
        )}

        {/* ═══ v3.10.2: EUDR MAP — full compliance world map ═══ */}
        {activeTab === 'eudr-map' && (
          <div className="space-y-4">
            <SectionHeader eyebrow="EUDR Compliance Map" title="Farm Polygon Compliance Map" subtitle="All farms' polygons with EUDR verification status, cocoa-belt zones, and compliance coloring." icon={<Icon.Map className="w-4 h-4" />} />
            <EudrWorldMap />
          </div>
        )}

        {/* ═══ v3.10.2: TRACEABILITY — CNCC waybills + checkpoints + warehouse bins ═══ */}
        {activeTab === 'traceability' && <TraceabilityTab />}

        {/* ═══ v3.10.2: MARKET PRICES — ONCC cocoa price data ═══ */}
        {activeTab === 'market-prices' && <MarketPricesTab />}

        {/* ═══ v3.10.2: CARBON CREDITS — real carbon measurement + monetization ═══ */}
        {activeTab === 'carbon-credits' && <CarbonCreditsTab />}

        {/* ═══ v3.10.2: BIOMASS — biomass waste ledger + fraud detection ═══ */}
        {activeTab === 'biomass' && <BiomassTab />}
      
        {/* v3.11.47: IROL INTELLIGENT ROUTING */}
        {activeTab === 'intelligent-routing' && <IntelligentRoutingPanel />}
</main>

      {/* ══════ MODALS ══════ */}
      {showPaymentModal && selectedBatch && (
        <ModalShell title="Pay farmer" onClose={() => setShowPaymentModal(false)}>
          <div className="mb-4 p-3.5 rounded-lg surface-inset">
            <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Farmer</p>
            <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{selectedBatch.farmer_name}</p>
            <p className="text-sm font-medium text-ink-900 dark:text-ink-50 nums mt-1">{selectedBatch.calculated_amount?.toLocaleString()} XAF</p>
          </div>
          <form onSubmit={handleBatchPayment}>
            {paymentStatus && (
              <div className="mb-3"><Alert tone={paymentStatus.includes('success') ? 'success' : 'danger'}>{paymentStatus}</Alert></div>
            )}
            <button type="submit" disabled={submitting} className="btn-accent w-full py-3 brand-surface-agrarian">
              {submitting ? <Spinner size="sm" /> : <><Icon.Send className="w-4 h-4" /> Pay {selectedBatch.calculated_amount?.toLocaleString()} XAF</>}
            </button>
          </form>
        </ModalShell>
      )}

      {showPriceModal && (
        <ModalShell title="Update price" onClose={() => setShowPriceModal(false)}>
          <form onSubmit={handleUpdatePrice}>
            <div className="mb-4">
              <label htmlFor="p-price" className="label">Price per kg (XAF)</label>
              <input id="p-price" type="number" required value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className="input" />
              <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">All pending batches will be recalculated.</p>
            </div>
            {priceStatus && <div className="mb-3"><Alert tone={priceStatus.includes('updated') ? 'success' : 'danger'}>{priceStatus}</Alert></div>}
            <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
              {submitting ? <Spinner size="sm" /> : 'Update price'}
            </button>
          </form>
        </ModalShell>
      )}

      {showAdvisoryModal && (
        <ModalShell title="New advisory" onClose={() => setShowAdvisoryModal(false)}>
          <form onSubmit={handleCreateAdvisory}>
            <div className="space-y-3">
              <div>
                <label htmlFor="a-title" className="label">Title</label>
                <input id="a-title" type="text" required value={advisoryForm.title} onChange={(e) => setAdvisoryForm({ ...advisoryForm, title: e.target.value })} className="input" placeholder="e.g. Pest management tip" />
              </div>
              <div>
                <label htmlFor="a-content" className="label">Content</label>
                <textarea id="a-content" required rows={4} value={advisoryForm.content} onChange={(e) => setAdvisoryForm({ ...advisoryForm, content: e.target.value })} className="input resize-none" placeholder="Write your recommendation…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="a-cat" className="label">Category</label>
                  <select id="a-cat" value={advisoryForm.category} onChange={(e) => setAdvisoryForm({ ...advisoryForm, category: e.target.value })} className="input">
                    <option value="general">General</option><option value="pest">Pest Control</option>
                    <option value="weather">Weather</option><option value="harvest">Harvest</option><option value="market">Market</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="a-lang" className="label">Language</label>
                  <select id="a-lang" value={advisoryForm.language} onChange={(e) => setAdvisoryForm({ ...advisoryForm, language: e.target.value })} className="input">
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="pcm">Pidgin</option>
                    <option value="mok">Mokpe</option>
                    <option value="oroko">Oroko (General)</option>
                    <option value="lokundu">Oroko — Lokundu (Bakundu)</option>
                    <option value="lolue">Oroko — Lolue (Balue)</option>
                    <option value="londo">Oroko — Londo (Balondo)</option>
                    <option value="lombonge">Oroko — Lombonge (Mbonge)</option>
                    <option value="lokombe">Oroko — Lokombe (Ekombe)</option>
                    <option value="longolo">Oroko — Longolo (Ngolo)</option>
                    <option value="lokoko">Oroko — Lokoko (Bima)</option>
                    <option value="lotanga">Oroko — Lotanga (Batanga)</option>
                    <option value="loma">Oroko — Loma</option>
                  </select>
                </div>
              </div>
              {advisoryStatus && <div><Alert tone={advisoryStatus.includes('published') ? 'success' : 'danger'}>{advisoryStatus}</Alert></div>}
              <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
                {submitting ? <Spinner size="sm" /> : 'Publish advisory'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {showAssignDriverModal && selectedTruck && (
        <ModalShell title="Assign driver" onClose={() => setShowAssignDriverModal(false)}>
          <div className="mb-4 p-3.5 rounded-lg surface-inset">
            <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Truck</p>
            <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{selectedTruck.registration_number}</p>
            <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">Current: {selectedTruck.driver_name || 'None'}</p>
          </div>
          <form onSubmit={handleAssignDriver}>
            <div className="mb-4">
              <label htmlFor="ad-driver" className="label">Select driver</label>
              <select id="ad-driver" required value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)} className="input">
                <option value="">Choose a driver…</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} ({d.email}){d.registration_number ? ` — On ${d.registration_number}` : ' — Available'}</option>
                ))}
              </select>
              {drivers.length === 0 && (
                <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">No drivers found. Create a driver user first.</p>
              )}
            </div>
            {assignStatus && <div className="mb-3"><Alert tone={assignStatus.includes('success') ? 'success' : 'danger'}>{assignStatus}</Alert></div>}
            <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
              {submitting ? <Spinner size="sm" /> : 'Assign driver'}
            </button>
          </form>
        </ModalShell>
      )}

      {showAddTruckModal && (
        <ModalShell title="Add truck" onClose={() => setShowAddTruckModal(false)}>
          <form onSubmit={handleAddTruck}>
            <div className="space-y-4">
              <div>
                <label htmlFor="at-reg" className="label">Registration number *</label>
                <input id="at-reg" required value={addTruckForm.registration_number}
                  onChange={(e) => setAddTruckForm({ ...addTruckForm, registration_number: e.target.value })}
                  className="input" placeholder="e.g. CE-1234-AB" />
              </div>
              <div>
                <label htmlFor="at-cap" className="label">Capacity (kg) *</label>
                <input id="at-cap" type="number" step="0.1" required value={addTruckForm.capacity_kg}
                  onChange={(e) => setAddTruckForm({ ...addTruckForm, capacity_kg: e.target.value })}
                  className="input" placeholder="e.g. 5000" />
              </div>
              <div>
                <label htmlFor="at-driver" className="label">Assign driver (optional)</label>
                <select id="at-driver" value={addTruckForm.driver_id}
                  onChange={(e) => setAddTruckForm({ ...addTruckForm, driver_id: e.target.value })}
                  className="input">
                  <option value="">— No driver —</option>
                  {drivers.map((d: any) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.email}){d.truck_registration ? ` — Currently on ${d.truck_registration}` : ' — Available'}
                    </option>
                  ))}
                </select>
                {drivers.length === 0 && (
                  <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">No drivers found. Create a driver in the Drivers tab first.</p>
                )}
              </div>
              {addTruckStatus && (
                <div><Alert tone={addTruckStatus.includes('success') ? 'success' : 'danger'}>{addTruckStatus}</Alert></div>
              )}
              <button type="submit" disabled={addTruckLoading} className="btn-accent brand-surface-cargo w-full py-3">
                {addTruckLoading ? <Spinner size="sm" /> : <><Icon.Truck className="w-4 h-4" /> Create truck</>}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {showDispatchModal && (
        <ModalShell title="Create dispatch" onClose={() => setShowDispatchModal(false)}>
          <form onSubmit={handleCreateDispatch}>
            <div className="space-y-4">
              <div>
                <label htmlFor="d-batch" className="label">Batch</label>
                <select id="d-batch" required value={dispatchForm.batch_id}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, batch_id: e.target.value })} className="input">
                  <option value="">Choose a batch…</option>
                  {batches.filter((b: any) => b.status === 'AWAITING_DISPATCH').map((b: any) => (
                    <option key={b.id} value={b.id}>{b.id?.slice(0, 8)}… — {b.farmer_name || 'Unknown'} — {b.est_weight || b.actual_weight || '?'}kg</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="d-truck" className="label">Truck</label>
                <select id="d-truck" required value={dispatchForm.truck_id}
                  onChange={(e) => {
                    const truck = trucks.find(t => t.id === e.target.value);
                    setDispatchForm({ ...dispatchForm, truck_id: e.target.value, driver_id: truck?.assigned_driver_id || truck?.driver_id || '' });
                  }} className="input">
                  <option value="">Choose a truck…</option>
                  {trucks
                    .filter((t) =>
                      t.status === 'AVAILABLE' &&
                      Boolean(t.assigned_driver_id || t.driver_id)
                    )
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.registration_number} ({t.driver_name || 'Assigned driver'})
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="label">Driver</label>
                <input type="text" value={dispatchForm.driver_id} readOnly placeholder="Auto-filled from truck assignment" className="input bg-ink-50 dark:bg-ink-900 text-ink-500 dark:text-ink-400 cursor-not-allowed" />
                <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">Driver is auto-assigned from the truck. Assign a driver to the truck first.</p>
              </div>
              {dispatchStatus && <div><Alert tone={dispatchStatus.includes('created') ? 'success' : 'danger'}>{dispatchStatus}</Alert></div>}
              <button type="submit" disabled={submitting || !dispatchForm.batch_id || !dispatchForm.truck_id} className="btn-primary w-full py-3">
                {submitting ? <Spinner size="sm" /> : 'Create dispatch'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* v2.1.1 (A3 fix): AI Governance — Create New Prompt Version Sheet modal */}
      {showAiGovCreateModal && (
        <Sheet
          open={showAiGovCreateModal}
          onClose={() => setShowAiGovCreateModal(false)}
          title="Create New Prompt Version"
          footer={
            <div className="flex gap-2">
              <button onClick={() => setShowAiGovCreateModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleCreateAiGovPrompt} disabled={!aiGovForm.prompt_key || !aiGovForm.content || aiGovSubmitting} className="btn-primary flex-1">
                {aiGovSubmitting ? <Spinner size="sm" /> : null}
                {aiGovSubmitting ? 'Creating…' : 'Create Version'}
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {/* v2.1.1 (A3 fix): prompt_key — datalist with existing keys + free text for new keys */}
            <div>
              <label className="label">Prompt Key *</label>
              <input
                className="input"
                value={aiGovForm.prompt_key}
                onChange={e => setAiGovForm({ ...aiGovForm, prompt_key: e.target.value })}
                placeholder="e.g. advisory.weather, quality.grading, smart_advisory.generate"
                list="ai-gov-prompt-keys"
              />
              <datalist id="ai-gov-prompt-keys">
                {Array.from(new Set(aiGovPrompts.map((p: any) => p.prompt_key))).map(k => <option key={k} value={k} />)}
              </datalist>
              <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">Choose an existing key to version-bump, or type a new key to register a brand-new prompt family.</p>
            </div>
            {/* v2.1.1 (A3 fix): auto-incremented version suggestion (read-only display) */}
            <div>
              <label className="label">Version (auto-increment suggestion)</label>
              <input
                className="input bg-ink-50 dark:bg-ink-900 text-ink-500 dark:text-ink-400 cursor-not-allowed"
                value={`v${aiGovForm.prompt_key ? Math.max(0, ...aiGovPrompts.filter((p: any) => p.prompt_key === aiGovForm.prompt_key).map((p: any) => Number(p.version) || 0)) + 1 : 1}`}
                readOnly
              />
              <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">The server computes the final version from MAX(existing) + 1 for this key.</p>
            </div>
            {/* v2.1.1 (A3 fix): prompt content textarea */}
            <div>
              <label className="label">Prompt Content *</label>
              <textarea
                className="input"
                rows={6}
                value={aiGovForm.content}
                onChange={e => setAiGovForm({ ...aiGovForm, content: e.target.value })}
                placeholder="You are an expert cocoa agronomist. Given the following farm data, generate…"
              />
            </div>
            {/* v2.1.1 (A3 fix): description input */}
            <div>
              <label className="label">Description</label>
              <input
                className="input"
                value={aiGovForm.description}
                onChange={e => setAiGovForm({ ...aiGovForm, description: e.target.value })}
                placeholder="e.g. Updated weather advisory with rainfall thresholds + pest trigger logic"
              />
            </div>
            {aiGovError && <Alert tone="danger" onClose={() => setAiGovError(null)}>{aiGovError}</Alert>}
            {aiGovSuccess && <Alert tone="success" onClose={() => setAiGovSuccess(null)}>{aiGovSuccess}</Alert>}
            <Alert tone="info">New versions are created in <strong>DRAFT</strong> status. Approve them from the table to activate — the previous ACTIVE version for this key will be automatically RETIRED for audit traceability.</Alert>
          </div>
        </Sheet>
      )}
          <CopilotWidget userRole="admin" />
</div>
  );
};

/* ─── Shared modal shell (bottom sheet on mobile, centered card on desktop) ─── */
const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <>
    <div className="sheet-backdrop hidden md:block" onClick={onClose} />
    <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-6 pointer-events-none">
      <Card padding="lg" className="max-w-md w-full pointer-events-auto animate-scale-in">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
          <IconButton label="Close" onClick={onClose} size="sm"><Icon.X className="w-4 h-4" /></IconButton>
        </div>
        {children}
      </Card>
    </div>
    {/* Mobile bottom sheet */}
    <div className="md:hidden">
      <div className="sheet-backdrop" onClick={onClose} />
      <div className="sheet animate-sheet-up">
        <div className="sheet-handle" />
        <div className="px-5 pb-4 pt-2 flex items-center justify-between">
          <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
          <IconButton label="Close" onClick={onClose} size="sm"><Icon.X className="w-4 h-4" /></IconButton>
        </div>
        <div className="px-5 pb-6">{children}</div>
      </div>
    </div>
  </>
);

/* ═══════════════════════════════════════════════════════════════
 * Farms Tab — admin overview of all farms (multi-farm per farmer)
 * Each farm has its own polygon + EUDR status. Admins can verify a farm's
 * polygon directly from this tab and view the polygon on a map.
 * ═══════════════════════════════════════════════════════════════ */
const EUDR_BADGE: Record<string, { tone: 'success' | 'warning' | 'danger' | 'info'; label: string }> = {
  VERIFIED: { tone: 'success', label: 'Verified' },
  PENDING:  { tone: 'warning', label: 'Pending' },
  UNMAPPED: { tone: 'info',    label: 'Unmapped' },
  REJECTED: { tone: 'danger',  label: 'Rejected' },
  EXPIRED:  { tone: 'danger',  label: 'Expired' },
};

const FarmsTab: React.FC = () => {
  const [farms, setFarms] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedFarm, setSelectedFarm] = useState<any | null>(null);
  const [verifyingFarmId, setVerifyingFarmId] = useState<string | null>(null);
  const [reviewCases, setReviewCases] = useState<any[]>([]);
  const [reviewingCaseId, setReviewingCaseId] = useState<string | null>(null);
  const [selectedReviewEvidence, setSelectedReviewEvidence] = useState<any | null>(null);
  const [loadingReviewEvidence, setLoadingReviewEvidence] = useState<string | null>(null);
  const farmsPag = usePagination(
    farms.filter(f =>
      (!searchTerm ||
        f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.village?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (!filterStatus || f.eudr_status === filterStatus)
    ),
    12
  );

  const refresh = () => {
    setLoading(true);
    Promise.all([apiClient.get('/eudr/farms-report'), apiClient.get('/mapping/reviews')]).then(([res, reviews]) => {
      setFarms(res.data.farms || []);
      setSummary(res.data.summary || null);
      setReviewCases(reviews.data.cases || []);
      setError(null);
    }).catch(err => {
      setError(err.response?.data?.error || 'Failed to load farm evidence registry');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleVerify = async (farmId: string) => {
    setVerifyingFarmId(farmId);
    setError(null);
    try {
      await apiClient.post(`/eudr/verify-farm/${farmId}`, {});
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify farm');
    } finally {
      setVerifyingFarmId(null);
    }
  };

  const inspectReviewEvidence = async (caseId: string) => {
    setLoadingReviewEvidence(caseId); setError(null);
    try { const r = await apiClient.get(`/mapping/reviews/${caseId}/evidence`); setSelectedReviewEvidence(r.data); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to load preserved mapping evidence'); }
    finally { setLoadingReviewEvidence(null); }
  };

  const handleReviewDecision = async (caseId: string, decision: 'APPROVE'|'REQUEST_FIELD_VERIFICATION'|'REJECT') => {
    const reasonCode = decision === 'APPROVE' ? 'ADMIN_EVIDENCE_REVIEW_APPROVED' : decision === 'REJECT' ? 'ADMIN_EVIDENCE_REVIEW_REJECTED' : 'FIELD_REVERIFICATION_REQUIRED';
    setReviewingCaseId(caseId); setError(null);
    try { await apiClient.post(`/mapping/reviews/${caseId}/decision`, { decision, reasonCode, justification: 'Decision recorded from Admin EUDR evidence review workbench.' }); refresh(); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to record review decision'); }
    finally { setReviewingCaseId(null); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {/* ─── Summary KPIs ─── */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="Total farms"     value={summary.total_farms || 0}     icon={<Icon.Layers className="w-4 h-4" />} accent="admin" />
          <MetricTile label="Mapped"          value={summary.mapped || 0}          icon={<Icon.Map className="w-4 h-4" />} />
          <MetricTile label="EUDR verified"   value={summary.verified || 0}        icon={<Icon.Shield className="w-4 h-4" />} />
          <MetricTile label="Total area"      value={`${Number(summary.total_area_ha || 0).toFixed(2)} ha`} icon={<Icon.MapPin className="w-4 h-4" />} />
        </div>
      )}

      {reviewCases.length > 0 && (
        <Card padding="md">
          <SectionHeader eyebrow="Evidence review queue" title={`Mapping cases requiring governance (${reviewCases.length})`} subtitle="Risk is server-derived. Reviewers inspect preserved capture evidence; the original mapper cannot approve their own geometry." icon={<Icon.Shield className="w-4 h-4" />} />
          <div className="mt-4 space-y-3">
            {reviewCases.slice(0, 8).map((c: any) => (
              <div key={c.id} className="rounded-xl border border-ink-200 dark:border-ink-800 p-3 grid gap-3 md:grid-cols-[1fr_auto] items-center">
                <div><div className="flex flex-wrap items-center gap-2"><strong className="text-sm">{c.farmer_name} · {c.farm_name} · {c.plot_code}</strong><StatusPill tone={c.risk_class === 'CRITICAL' || c.risk_class === 'HIGH' ? 'danger' : 'warning'}>{c.risk_class || c.priority}</StatusPill></div><p className="text-xs text-ink-500 mt-1">Risk {Number(c.risk_score || 0).toFixed(1)} · {c.decision} · policy {c.policy_version || '—'} · engine {c.engine_version || '—'} · {Number(c.area_ha || 0).toFixed(2)} ha</p><details className="mt-2 text-xs"><summary className="cursor-pointer font-medium">Decision evidence</summary><pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap text-2xs">{JSON.stringify(c.explanation || {}, null, 2)}</pre></details></div>
                <div className="flex flex-wrap gap-2"><button disabled={loadingReviewEvidence===c.id} className="btn-secondary text-xs" onClick={() => inspectReviewEvidence(c.id)}>{loadingReviewEvidence===c.id?'Loading…':'Inspect evidence'}</button><button disabled={reviewingCaseId===c.id} className="btn-secondary text-xs" onClick={() => handleReviewDecision(c.id,'REQUEST_FIELD_VERIFICATION')}>Field verify</button><button disabled={reviewingCaseId===c.id} className="btn-secondary text-xs" onClick={() => handleReviewDecision(c.id,'REJECT')}>Reject</button><button disabled={reviewingCaseId===c.id} className="btn-primary text-xs" onClick={() => handleReviewDecision(c.id,'APPROVE')}>{reviewingCaseId===c.id?'Saving…':'Approve'}</button></div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {selectedReviewEvidence && (() => {
        const ev = selectedReviewEvidence;
        const raw = ev.rawEvents || []; const rejected = raw.filter((x:any) => !x.accepted_for_boundary);
        let geom:any = null; try { geom = ev.case?.geometry_geojson ? JSON.parse(ev.case.geometry_geojson) : null; } catch {}
        return <div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-ink-950/70 p-0 md:p-6" onClick={() => setSelectedReviewEvidence(null)}>
          <div className="w-full md:max-w-5xl max-h-[94vh] overflow-y-auto bg-ink-0 dark:bg-ink-900 rounded-t-2xl md:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900"><div><p className="section-eyebrow">Preserved evidence</p><h3 className="text-base font-semibold">{ev.case?.farmer_name} · {ev.case?.farm_name} · {ev.case?.plot_code}</h3></div><IconButton label="Close" onClick={() => setSelectedReviewEvidence(null)}><Icon.X className="w-4 h-4" /></IconButton></div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3"><MetricTile label="Raw observations" value={raw.length} icon={<Icon.Activity className="w-4 h-4" />} /><MetricTile label="Accepted vertices" value={(ev.acceptedVertices||[]).length} icon={<Icon.MapPin className="w-4 h-4" />} /><MetricTile label="Rejected fixes" value={rejected.length} icon={<Icon.Alert className="w-4 h-4" />} /><MetricTile label="Risk" value={ev.assessment?.risk_class || '—'} icon={<Icon.Shield className="w-4 h-4" />} /><MetricTile label="Geometry version" value={ev.case?.version || '—'} icon={<Icon.Layers className="w-4 h-4" />} /></div>
              <MappingEvidenceReviewMap geometry={geom} rawEvents={raw} acceptedVertices={ev.acceptedVertices || []} />
              <div className="grid md:grid-cols-2 gap-4"><Card padding="md"><h4 className="text-sm font-semibold mb-2">GNSS quality</h4><div className="space-y-1 text-xs">{['EXCELLENT','GOOD','DEGRADED','POOR','UNKNOWN'].map(q => <div key={q} className="flex justify-between"><span>{q}</span><strong>{raw.filter((x:any)=>x.quality_class===q).length}</strong></div>)}</div></Card><Card padding="md"><h4 className="text-sm font-semibold mb-2">Decision provenance</h4><p className="text-xs">Policy {ev.assessment?.policy_version || '—'} · Engine {ev.assessment?.engine_version || '—'}</p><p className="text-xs mt-1">Risk score {ev.assessment?.risk_score ?? '—'} · {ev.assessment?.decision || '—'}</p><p className="text-2xs text-ink-500 mt-2 break-all">Geometry hash: {ev.case?.geometry_hash || '—'}</p></Card></div>
              <div className="grid md:grid-cols-2 gap-4"><Card padding="md"><h4 className="text-sm font-semibold mb-2">Risk signals</h4>{(ev.signals||[]).length ? <div className="space-y-2">{ev.signals.map((x:any)=><div key={x.id} className="text-xs"><strong>{x.signal_code}</strong><p className="text-ink-500">{x.severity} · {x.description}</p></div>)}</div>:<p className="text-xs text-ink-500">No risk signals.</p>}</Card><Card padding="md"><h4 className="text-sm font-semibold mb-2">Capture actions & transitions</h4><p className="text-xs">Capture actions: {(ev.captureEvents||[]).length} · State transitions: {(ev.transitions||[]).length}</p><details className="mt-2 text-xs"><summary className="cursor-pointer">View timeline</summary><pre className="text-2xs max-h-48 overflow-auto mt-2 whitespace-pre-wrap">{JSON.stringify({captureEvents:ev.captureEvents,transitions:ev.transitions},null,2)}</pre></details></Card></div>
              <Card padding="md"><h4 className="text-sm font-semibold mb-2">Geometry history</h4><div className="space-y-2">{(ev.geometryVersions||[]).map((g:any)=><div key={g.id} className="flex flex-wrap justify-between gap-2 text-xs border-b border-ink-100 dark:border-ink-800 pb-2"><span>v{g.version} · {g.source} · {g.status}</span><span>{Number(g.area_ha||0).toFixed(2)} ha · {g.reason_code || '—'}</span></div>)}</div></Card>
            </div>
          </div>
        </div>;
      })()}

      {/* ─── Filters ─── */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input
              className="input pl-10"
              placeholder="Search by farm name, farmer, village…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="input md:w-56"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">All EUDR statuses</option>
            <option value="VERIFIED">Verified</option>
            <option value="PENDING">Pending</option>
            <option value="UNMAPPED">Unmapped</option>
            <option value="REJECTED">Rejected</option>
            <option value="EXPIRED">Expired</option>
          </select>
          <button onClick={refresh} className="btn-secondary text-sm">
            <Icon.Refresh className="w-4 h-4" /> Refresh
          </button>
        </div>
      </Card>

      {/* ─── Farms grid ─── */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader
            eyebrow="Multi-farm registry"
            title={`All farms (${farmsPag.total})`}
            subtitle="Farm polygons become authoritative only through preserved mapping evidence, server validation, risk triage and governed approval."
            icon={<Icon.Layers className="w-4 h-4" />}
          />
        </div>
        {farmsPag.total === 0 ? (
          <EmptyState icon={<Icon.Map className="w-5 h-5" />} title="No farms found" description="No farms match your filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Farm</th><th>Farmer</th><th>Village</th><th>Area</th><th>EUDR</th><th>Batch#</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {farmsPag.paginatedItems.map((farm: any) => {
                  const badge = EUDR_BADGE[farm.eudr_status] || EUDR_BADGE.UNMAPPED;
                  return (
                    <tr key={farm.id}>
                      <td className="text-sm font-medium text-ink-900 dark:text-ink-50">
                        {farm.name}
                        {farm.is_primary && (
                          <span className="ml-1 text-2xs px-1.5 py-0.5 rounded bg-admin-50 dark:bg-admin-500/10 text-admin-700 dark:text-admin-500">Primary</span>
                        )}
                      </td>
                      <td className="text-sm">{farm.farmer_name || '—'}</td>
                      <td className="text-sm">{farm.village || '—'}</td>
                      <td className="text-sm nums">{Number(farm.polygon_area_hectares || 0).toFixed(2)} ha</td>
                      <td><StatusPill tone={badge.tone} dot>{badge.label}</StatusPill></td>
                      <td className="text-sm nums">{farm.batch_count || 0}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedFarm(farm)}
                            className="text-xs text-admin-700 dark:text-admin-500 hover:underline"
                          >
                            View polygon
                          </button>
                          {farm.eudr_status !== 'VERIFIED' && farm.gps_polygon_geojson && (
                            <>
                              <span className="text-ink-300 dark:text-ink-700">·</span>
                              <button
                                onClick={() => handleVerify(farm.id)}
                                disabled={verifyingFarmId === farm.id}
                                className="text-xs text-success-700 dark:text-success-500 hover:underline disabled:opacity-50"
                              >
                                {verifyingFarmId === farm.id ? 'Verifying…' : 'Verify'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination
            page={farmsPag.page}
            pageSize={farmsPag.pageSize}
            total={farmsPag.total}
            onPageChange={farmsPag.setPage}
            onPageSizeChange={farmsPag.setPageSize}
            showPageSize
          />
        </div>
      </Card>

      {/* ─── Farm polygon viewer sheet ─── */}
      {selectedFarm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink-900/60 backdrop-blur-sm safe-top safe-bottom" onClick={() => setSelectedFarm(null)}>
          <div className="w-full md:max-w-3xl max-h-[90vh] overflow-y-auto bg-ink-0 dark:bg-ink-900 md:rounded-2xl rounded-t-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <div>
                <p className="section-eyebrow">Farm polygon</p>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{selectedFarm.name}</h3>
              </div>
              <IconButton label="Close" onClick={() => setSelectedFarm(null)}><Icon.X className="w-4 h-4" /></IconButton>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><p className="text-ink-500 dark:text-ink-400">Farmer</p><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{selectedFarm.farmer_name}</p></div>
                <div><p className="text-ink-500 dark:text-ink-400">Village</p><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{selectedFarm.village || '—'}</p></div>
                <div><p className="text-ink-500 dark:text-ink-400">Area</p><p className="text-sm font-semibold nums text-ink-900 dark:text-ink-50">{Number(selectedFarm.polygon_area_hectares || 0).toFixed(2)} ha</p></div>
                <div><p className="text-ink-500 dark:text-ink-400">EUDR</p><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{selectedFarm.eudr_status}</p></div>
              </div>
              {selectedFarm.gps_polygon_geojson ? (
                <FarmPolygonViewer
                  polygon={JSON.parse(selectedFarm.gps_polygon_geojson)}
                  centroid={selectedFarm.centroid_lat && selectedFarm.centroid_lng ? { lat: selectedFarm.centroid_lat, lng: selectedFarm.centroid_lng } : null}
                  areaHa={selectedFarm.polygon_area_hectares}
                  height="380px"
                  label={selectedFarm.name}
                />
              ) : (
                <EmptyState icon={<Icon.MapPin className="w-5 h-5" />} title="No polygon mapped" description="This farm has no boundary yet." />
              )}
              {selectedFarm.eudr_status !== 'VERIFIED' && selectedFarm.gps_polygon_geojson && (
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { handleVerify(selectedFarm.id); setSelectedFarm(null); }}
                    disabled={verifyingFarmId === selectedFarm.id}
                    className="btn-accent brand-surface-admin text-sm"
                  >
                    <Icon.Check className="w-4 h-4" /> Verify for EUDR
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── EUDR Tab ─── */
const EudrTab: React.FC = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState<any[]>([]);
  const batchesPag = usePagination(batches, 15);

  useEffect(() => {
    Promise.all([
      apiClient.get('/eudr/compliance-report').catch(() => ({ data: null })),
      apiClient.get('/batches').catch(() => ({ data: [] })),
    ]).then(([reportRes, batchesRes]) => {
      setReport(reportRes.data);
      setBatches(Array.isArray(batchesRes.data) ? batchesRes.data : []);
      setLoading(false);
    });
  }, []);

  const handleGenerateDDS = async (batchId: string) => {
    try {
      await apiClient.post(`/eudr/dds/${batchId}`);
      alert('DDS generated successfully! Batch is now EUDR compliant.');
      Promise.all([
        apiClient.get('/eudr/compliance-report'),
        apiClient.get('/batches'),
      ]).then(([reportRes, batchesRes]) => {
        setReport(reportRes.data);
        setBatches(Array.isArray(batchesRes.data) ? batchesRes.data : []);
      });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to generate DDS');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricTile label="EUDR compliant"   value={report.stats?.compliant_batches || 0}     icon={<Icon.Shield className="w-4 h-4" />} accent="admin" />
            <MetricTile label="Non-compliant"    value={report.stats?.non_compliant_batches || 0} icon={<Icon.Clock className="w-4 h-4" />} />
            <MetricTile label="Compliance rate"  value={`${report.stats?.compliance_rate || 0}%`} icon={<Icon.TrendingUp className="w-4 h-4" />} />
            <MetricTile label="Total batches"    value={report.stats?.total_batches || 0}         icon={<Icon.Database className="w-4 h-4" />} />
          </div>

          {/* Multi-farm EUDR summary */}
          {report.farm_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total farms"     value={report.farm_summary.total_farms || 0}     icon={<Icon.Layers className="w-4 h-4" />} accent="admin" />
              <MetricTile label="Farms verified"  value={report.farm_summary.verified_farms || 0}  icon={<Icon.Map className="w-4 h-4" />} />
              <MetricTile label="Farms pending"   value={report.farm_summary.pending_farms || 0}   icon={<Icon.Clock className="w-4 h-4" />} />
              <MetricTile label="Total farm area" value={`${Number(report.farm_summary.total_area_ha || 0).toFixed(2)} ha`} icon={<Icon.MapPin className="w-4 h-4" />} />
            </div>
          )}
        </>
      )}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader title="EUDR compliance per batch" eyebrow="Verification" subtitle="Generate a Due Diligence Statement (DDS) for any batch — the polygon from the batch's farm is used automatically." />
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Batch</th><th>Farmer</th><th>Farm</th><th>EUDR</th><th>DDS reference</th><th>Action</th></tr></thead>
            <tbody>
              {batchesPag.paginatedItems.map((batch: any) => (
                <tr key={batch.id}>
                  <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.id?.slice(0, 8)}…</td>
                  <td className="text-sm">{batch.farmer_name || '—'}</td>
                  <td className="text-sm">{batch.farm_name || '—'}{batch.farm_eudr_status ? ` (${batch.farm_eudr_status})` : ''}</td>
                  <td>{batch.eudr_compliant ? <StatusPill tone="success" dot>Compliant</StatusPill> : <StatusPill tone="warning" dot>Pending</StatusPill>}</td>
                  <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.dds_reference || '—'}</td>
                  <td>
                    {!batch.eudr_compliant && (
                      <button onClick={() => handleGenerateDDS(batch.id)} className="btn-secondary text-xs">
                        Generate DDS
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination
            page={batchesPag.page}
            pageSize={batchesPag.pageSize}
            total={batchesPag.total}
            onPageChange={batchesPag.setPage}
            onPageSizeChange={batchesPag.setPageSize}
            showPageSize
          />
        </div>
      </Card>
    </div>
  );
};

/* ─── Carbon Tab ─── */
const CarbonTab: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [rules, setRules] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ carbon_point_to_xaf_rate: '', carbon_credit_min_redemption: '' });
  const [submitting, setSubmitting] = useState(false);
  const leaderboardPag = usePagination(leaderboard, 15);

  useEffect(() => {
    Promise.all([
      apiClient.get('/eudr/leaderboard').catch(() => ({ data: { leaderboard: [] } })),
      apiClient.get('/eudr/rules').catch(() => ({ data: { rules: [] } })),
      apiClient.get('/eudr/settings').catch(() => ({ data: { settings: {} } })),
    ]).then(([lbRes, rulesRes, settingsRes]) => {
      setLeaderboard(lbRes.data.leaderboard || []);
      setRules(rulesRes.data.rules || []);
      setSettings(settingsRes.data.settings || {});
      setSettingsForm({
        carbon_point_to_xaf_rate: settingsRes.data.settings?.carbon_point_to_xaf_rate || '50',
        carbon_credit_min_redemption: settingsRes.data.settings?.carbon_credit_min_redemption || '100',
      });
      setLoading(false);
    });
  }, []);

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.put('/eudr/settings', settingsForm);
      alert('Settings updated!');
      setShowSettingsModal(false);
    } catch { alert('Failed to update settings'); }
    setSubmitting(false);
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowSettingsModal(true)} className="btn-secondary">
          <Icon.Settings className="w-4 h-4" /> Conversion settings
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricTile label="XAF per point"        value={settings?.carbon_point_to_xaf_rate || '50'}    icon={<Icon.Award className="w-4 h-4" />} accent="admin" />
        <MetricTile label="Min redemption pts"   value={settings?.carbon_credit_min_redemption || '100'} icon={<Icon.Card className="w-4 h-4" />} />
        <MetricTile label="Farmers with credits" value={leaderboard.length}                            icon={<Icon.Users className="w-4 h-4" />} />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Leaderboard" title="Carbon credit leaderboard" />
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Rank</th><th>Farmer</th><th>Village</th><th>Total pts</th><th>Available</th><th>Redeemed</th><th>Value (XAF)</th></tr></thead>
            <tbody>
              {leaderboardPag.paginatedItems.map((farmer, idx) => {
                const actualRank = (leaderboardPag.page - 1) * leaderboardPag.pageSize + idx;
                return (
                <tr key={farmer.farmer_id}>
                  <td>
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold nums ${
                      actualRank === 0 ? 'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-500'
                      : actualRank === 1 ? 'bg-ink-200 text-ink-700 dark:bg-ink-700 dark:text-ink-200'
                      : actualRank === 2 ? 'bg-cargo-100 text-cargo-700 dark:bg-cargo-500/20 dark:text-cargo-500'
                      : 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400'
                    }`}>{actualRank + 1}</span>
                  </td>
                  <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{farmer.farmer_name}</td>
                  <td className="text-2xs text-ink-500 dark:text-ink-400">{farmer.village}</td>
                  <td className="nums font-semibold text-ink-900 dark:text-ink-50">{Number(farmer.total_points).toFixed(1)}</td>
                  <td className="nums">{Number(farmer.points_available).toFixed(1)}</td>
                  <td className="nums text-ink-500 dark:text-ink-400">{Number(farmer.points_redeemed).toFixed(1)}</td>
                  <td className="nums font-semibold text-ink-900 dark:text-ink-50">{Number(farmer.total_value_xaf).toLocaleString()}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination
            page={leaderboardPag.page}
            pageSize={leaderboardPag.pageSize}
            total={leaderboardPag.total}
            onPageChange={leaderboardPag.setPage}
            onPageSizeChange={leaderboardPag.setPageSize}
            showPageSize
          />
        </div>
      </Card>

      <Card padding="lg">
        <SectionHeader eyebrow="Mechanics" title="Carbon credit point rules" />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {rules.map((rule) => (
            <div key={rule.id} className="p-3.5 surface-inset rounded-lg flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{rule.rule_name}</p>
                <p className="text-2xs text-ink-500 dark:text-ink-400 text-pretty">{rule.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-base font-semibold nums text-ink-900 dark:text-ink-50">{rule.points_per_unit}</p>
                <p className="text-2xs text-ink-500 dark:text-ink-400">pts / {rule.unit}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {showSettingsModal && (
        <ModalShell title="Carbon credit settings" onClose={() => setShowSettingsModal(false)}>
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <label htmlFor="c-rate" className="label">XAF per carbon point</label>
              <input id="c-rate" type="number" step="0.01" required value={settingsForm.carbon_point_to_xaf_rate}
                onChange={(e) => setSettingsForm({ ...settingsForm, carbon_point_to_xaf_rate: e.target.value })} className="input" />
              <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">How many XAF each carbon point is worth when redeemed.</p>
            </div>
            <div>
              <label htmlFor="c-min" className="label">Minimum points for redemption</label>
              <input id="c-min" type="number" required value={settingsForm.carbon_credit_min_redemption}
                onChange={(e) => setSettingsForm({ ...settingsForm, carbon_credit_min_redemption: e.target.value })} className="input" />
              <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">Minimum carbon points a farmer must have before redeeming.</p>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
              {submitting ? <Spinner size="sm" /> : 'Save settings'}
            </button>
          </form>
        </ModalShell>
      )}
    </div>
  );
};

/* ─── AI Advisory Tab ─── */
const AIAdvisoryTab: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [language, setLanguage] = useState('en');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AdvisoryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setError(null);
    setResult(null);
    setPublished(false);
    try {
      const r = await aiApi.generateAdvisory(topic, language);
      setResult(r);
    } catch (err: any) {
      setError(err.response?.data?.message || 'AI generation failed');
    } finally { setGenerating(false); }
  };

  const handlePublish = async () => {
    if (!result) return;
    setPublishing(true);
    try {
      await apiClient.post('/advisory', {
        title: result.title,
        content: result.content,
        category: result.category,
        language,
      });
      setPublished(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to publish');
    } finally { setPublishing(false); }
  };

  return (
    <div className="space-y-6">
      <Card padding="lg">
        <SectionHeader
          eyebrow="AI-powered"
          title="Advisory content generator"
          subtitle="Enter a topic and the AI generates a practical, actionable advisory tip for farmers. Review, then publish to the farmer portal."
          icon={<Icon.Sparkles className="w-4 h-4" />}
        />
        <div className="mt-4 space-y-4">
          <div>
            <label className="label">Topic</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="input"
              placeholder="e.g. Black pod disease prevention, Pruning techniques, Post-harvest handling"
            />
          </div>
          <div>
            <label className="label">Language</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="input">
              <option value="en">English</option>
              <option value="fr">French</option>
              <option value="pcm">Pidgin</option>
              <option value="mok">Mokpe</option>
              <option value="oroko">Oroko (General)</option>
              <option value="lokundu">Oroko — Lokundu (Bakundu)</option>
              <option value="lolue">Oroko — Lolue (Balue)</option>
              <option value="londo">Oroko — Londo (Balondo)</option>
              <option value="lombonge">Oroko — Lombonge (Mbonge)</option>
              <option value="lokombe">Oroko — Lokombe (Ekombe)</option>
              <option value="longolo">Oroko — Longolo (Ngolo)</option>
              <option value="lokoko">Oroko — Lokoko (Bima)</option>
              <option value="lotanga">Oroko — Lotanga (Batanga)</option>
              <option value="loma">Oroko — Loma</option>
            </select>
          </div>
          <button onClick={handleGenerate} disabled={generating || !topic.trim()}
            className="btn-accent brand-surface-analytics">
            {generating ? <><Spinner size="sm" /> <span>Generating…</span></> : <><Icon.Sparkles className="w-4 h-4" /> <span>Generate with AI</span></>}
          </button>
        </div>
      </Card>

      {error && <Alert tone="danger">{error}</Alert>}

      {result && (
        <Card padding="lg">
          <SectionHeader
            eyebrow="AI-generated"
            title={result.title}
            icon={<Icon.FileText className="w-4 h-4" />}
            actions={
              published ? (
                <StatusPill tone="success" dot>Published</StatusPill>
              ) : (
                <button onClick={handlePublish} disabled={publishing}
                  className="btn-primary text-xs">
                  {publishing ? <Spinner size="sm" /> : <><Icon.Send className="w-3.5 h-3.5" /> Publish to farmers</>}
                </button>
              )
            }
          />
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="pill-info capitalize">{result.category}</span>
              <span className="pill-neutral uppercase">{language}</span>
            </div>
            <div className="p-4 surface-inset rounded-lg">
              <p className="text-sm text-ink-700 dark:text-ink-200 whitespace-pre-wrap text-pretty">{result.content}</p>
            </div>
            {!published && (
              <p className="text-2xs text-ink-400 dark:text-ink-500">
                Review the content above. Click "Publish to farmers" to send it to the farmer portal advisory section.
              </p>
            )}
          </div>
        </Card>
      )}

      {/* MEL AI Analyst */}
      <Card padding="lg">
        <SectionHeader
          eyebrow="AI insights"
          title="MEL data analyst"
          subtitle="AI analyzes your dashboard metrics and provides insights, recommendations, and risk assessment."
          icon={<Icon.BarChart3 className="w-4 h-4" />}
        />
        <MELAnalystButton />
      </Card>

      {/* Knowledge Base */}
      <KnowledgeBaseSection />
    </div>
  );
};

/* ─── Knowledge Base Section ─── */
const KnowledgeBaseSection: React.FC = () => {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', content: '', fileType: 'text', tags: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/ai/knowledge');
      setEntries(res.data.entries || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchEntries(); }, []);

  const handleSave = async () => {
    if (!form.title || !form.content) { setError('Title and content are required'); return; }
    setSaving(true);
    setError(null);
    try {
      await apiClient.post('/ai/knowledge', {
        title: form.title,
        description: form.description || undefined,
        content: form.content,
        fileType: form.fileType,
        tags: form.tags ? form.tags.split(',').map((t: string) => t.trim()) : undefined,
      });
      setForm({ title: '', description: '', content: '', fileType: 'text', tags: '' });
      setShowForm(false);
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this knowledge base entry?')) return;
    try {
      await apiClient.delete(`/ai/knowledge/${id}`);
      fetchEntries();
    } catch {}
  };

  const handleToggle = async (id: string, active: boolean) => {
    try {
      await apiClient.patch(`/ai/knowledge/${id}`, { isActive: !active });
      fetchEntries();
    } catch {}
  };

  return (
    <Card padding="lg">
      <SectionHeader
        eyebrow="Training"
        title="AI knowledge base"
        subtitle="Upload documents, guides, and reference materials. The AI uses these as context when generating advisories and answering questions."
        icon={<Icon.Database className="w-4 h-4" />}
        actions={
          <button onClick={() => setShowForm(!showForm)} className="btn-primary text-xs">
            <Icon.Plus className="w-3.5 h-3.5" /> Add entry
          </button>
        }
      />

      {showForm && (
        <div className="mt-4 p-4 surface-inset rounded-lg space-y-3">
          <div>
            <label className="label">Title *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" placeholder="e.g. Cocoa Black Pod Disease Guide" />
          </div>
          <div>
            <label className="label">Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" placeholder="Brief description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">File type</label>
              <select value={form.fileType} onChange={(e) => setForm({ ...form, fileType: e.target.value })} className="input">
                <option value="text">Text</option>
                <option value="markdown">Markdown</option>
                <option value="pdf">PDF (paste text)</option>
                <option value="csv">CSV data</option>
                <option value="json">JSON</option>
                <option value="docx">Document (paste text)</option>
              </select>
            </div>
            <div>
              <label className="label">Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="input" placeholder="pest, disease, treatment" />
            </div>
          </div>
          <div>
            <label className="label">Content *</label>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} className="input resize-none font-mono text-xs" placeholder="Paste the full text content of your document here..." />
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-xs">
              {saving ? <Spinner size="sm" /> : 'Save to knowledge base'}
            </button>
            <button onClick={() => setShowForm(false)} className="btn-secondary text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : entries.length === 0 ? (
          <EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No knowledge base entries" description="Add documents, guides, or reference materials to train the AI." />
        ) : (
          <div className="space-y-2">
            {entries.map((e) => (
              <div key={e.id} className="p-3 surface-inset rounded-lg flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-500">
                  <Icon.FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-ink-900 dark:text-ink-50 truncate">{e.title}</p>
                    <span className="pill-neutral">{e.file_type}</span>
                    {!e.is_active && <span className="pill-warning">Inactive</span>}
                  </div>
                  {e.description && <p className="text-2xs text-ink-500 dark:text-ink-400 truncate mt-0.5">{e.description}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-2xs text-ink-400 nums">{e.file_size ? `${(e.file_size / 1024).toFixed(1)} KB` : ''}</span>
                    {e.tags && e.tags.length > 0 && (
                      <div className="flex gap-1">
                        {e.tags.map((t: string, i: number) => (
                          <span key={i} className="text-2xs text-ink-400">#{t}</span>
                        ))}
                      </div>
                    )}
                    <span className="text-2xs text-ink-400">{new Date(e.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="shrink-0 flex gap-1">
                  <button onClick={() => handleToggle(e.id, e.is_active)} className="btn-icon-sm" title={e.is_active ? 'Deactivate' : 'Activate'}>
                    {e.is_active ? (
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-success-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /></svg>
                    )}
                  </button>
                  <button onClick={() => handleDelete(e.id)} className="btn-icon-sm text-danger-600 dark:text-danger-500" title="Delete">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

/* ─── MEL Analyst Button (inline) ─── */
const MELAnalystButton: React.FC = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [insights, setInsights] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const [dashRes] = await Promise.all([
        apiClient.get('/mel/dashboard').catch(() => ({ data: {} })),
      ]);
      const r = await aiApi.analyzeMEL(dashRes.data);
      setInsights(r);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  return (
    <div className="mt-4">
      <button onClick={handleAnalyze} disabled={analyzing}
        className="btn-secondary">
        {analyzing ? <><Spinner size="sm" /> <span>Analyzing metrics…</span></> : <><Icon.Sparkles className="w-4 h-4" /> <span>Analyze MEL data with AI</span></>}
      </button>
      {error && <div className="mt-3"><Alert tone="danger">{error}</Alert></div>}
      {insights && (
        <div className="mt-4 space-y-4">
          <div className={`p-3 rounded-lg border ${
            insights.riskLevel === 'high' ? 'bg-danger-50 dark:bg-danger-500/10 border-danger-500/20'
            : insights.riskLevel === 'moderate' ? 'bg-warning-50 dark:bg-warning-500/10 border-warning-500/20'
            : 'bg-success-50 dark:bg-success-500/10 border-success-500/20'
          }`}>
            <p className={`text-sm font-semibold capitalize ${
              insights.riskLevel === 'high' ? 'text-danger-700 dark:text-danger-500'
              : insights.riskLevel === 'moderate' ? 'text-warning-700 dark:text-warning-500'
              : 'text-success-700 dark:text-success-500'
            }`}>Risk level: {insights.riskLevel}</p>
          </div>
          {insights.insights?.length > 0 && (
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">Key insights</p>
              <div className="space-y-1.5">
                {insights.insights.map((ins: string, i: number) => (
                  <div key={i} className="p-2.5 surface-inset rounded-lg text-sm text-ink-700 dark:text-ink-200 text-pretty">{ins}</div>
                ))}
              </div>
            </div>
          )}
          {insights.recommendations?.length > 0 && (
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">Recommendations</p>
              <div className="space-y-1.5">
                {insights.recommendations.map((rec: string, i: number) => (
                  <div key={i} className="p-2.5 surface-inset rounded-lg text-sm text-ink-700 dark:text-ink-200 text-pretty">{rec}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Pest Reports Tab ─── */
const PestReportsTab: React.FC = () => {
  const [reports, setReports] = useState<PestReport[]>([]);
  const [stats, setStats] = useState<PestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        aiApi.getPestReports(200, filter || undefined),
        aiApi.getPestStats(),
      ]);
      setReports(r.reports);
      setStats(s);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [filter]);

  const severityTone: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    low: 'success', moderate: 'warning', high: 'danger', critical: 'danger',
  };

  if (loading) {
    return <Card padding="lg"><div className="flex justify-center py-8"><Spinner /></div></Card>;
  }

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="Total reports"   value={stats.total}     icon={<Icon.Leaf className="w-4 h-4" />} accent="analytics" />
          <MetricTile label="Last 7 days"     value={stats.last7d}    icon={<Icon.Clock className="w-4 h-4" />} />
          <MetricTile label="Diagnoses"       value={stats.byDiagnosis.length} icon={<Icon.Database className="w-4 h-4" />} />
          <MetricTile label="Critical"        value={stats.bySeverity.find(s => s.severity === 'critical')?.count || 0} icon={<Icon.Alert className="w-4 h-4" />} />
        </div>
      )}

      {stats && stats.byDiagnosis.length > 0 && (
        <Card padding="lg">
          <SectionHeader eyebrow="Distribution" title="Pest/disease breakdown" icon={<Icon.BarChart3 className="w-4 h-4" />} />
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {stats.byDiagnosis.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 surface-inset rounded-lg">
                <span className="text-sm text-ink-700 dark:text-ink-200 truncate">{d.diagnosis}</span>
                <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50 shrink-0 ml-2">{d.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between gap-3">
          <SectionHeader eyebrow="Scanner" title="Farmer pest reports" icon={<Icon.Leaf className="w-4 h-4" />} />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input py-1.5 text-xs w-auto">
            <option value="">All statuses</option>
            <option value="REPORTED">Reported</option>
            <option value="REVIEWED">Reviewed</option>
            <option value="TREATED">Treated</option>
            <option value="RESOLVED">Resolved</option>
          </select>
        </div>
        {reports.length === 0 ? (
          <EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No pest reports" description="Farmer pest scans will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Farmer</th><th>Diagnosis</th><th>Severity</th><th>Confidence</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{r.farmer_name}</p>
                      <p className="text-2xs text-ink-500 dark:text-ink-400">{r.farmer_village || ''}</p>
                    </td>
                    <td className="text-sm text-ink-700 dark:text-ink-200">{r.diagnosis}</td>
                    <td><StatusPill tone={severityTone[r.severity] || 'neutral'} dot>{r.severity}</StatusPill></td>
                    <td className="nums text-2xs text-ink-500 dark:text-ink-400">{(r.confidence * 100).toFixed(0)}%</td>
                    <td><SmartStatus status={r.status} /></td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={async () => {
                          const notes = prompt('Admin notes (optional):');
                          await aiApi.updatePestReport(r.id, { status: 'REVIEWED', adminNotes: notes || undefined });
                          fetchAll();
                        }}
                        className="btn-secondary text-xs"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ─── AI API Key Config Component ─── */
const AIApiKeyConfig: React.FC = () => {
  const [configured, setConfigured] = useState(false);
  const [keyPreview, setKeyPreview] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [modelsText, setModelsText] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);

  const checkStatus = async () => {
    try {
      const res = await apiClient.get('/settings/ai-key');
      setConfigured(res.data.configured);
      setKeyPreview(res.data.keyPreview);
    } catch {}
    try {
      const modelsRes = await apiClient.get('/ai/models');
      setModels(modelsRes.data.models || []);
      setModelsText((modelsRes.data.models || []).join(', '));
    } catch {}
  };

  useEffect(() => { checkStatus(); }, []);

  const handleSave = async () => {
    if (!apiKey || apiKey.length < 10) { setError('Please enter a valid API key'); return; }
    setSaving(true);
    setError(null);
    try {
      await apiClient.put('/settings/ai-key', { apiKey });
      setSuccess(true);
      setApiKey('');
      checkStatus();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await apiClient.delete('/settings/ai-key');
      setConfigured(false);
      setKeyPreview(null);
    } catch {} finally { setSaving(false); }
  };

  const handleSaveModels = async () => {
    const modelArr = modelsText.split(',').map(m => m.trim()).filter(Boolean);
    if (modelArr.length === 0) { setError('Enter at least one model name'); return; }
    setSaving(true);
    try {
      await apiClient.put('/ai/models', { models: modelArr });
      setModels(modelArr);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save models');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setTestResults(null);
    try {
      const res = await apiClient.post('/ai/test', {});
      setTestResults(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Test failed');
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-4">
      {/* Status */}
      <div className={`p-3 rounded-lg border ${configured ? 'bg-success-50 dark:bg-success-500/10 border-success-500/20' : 'bg-warning-50 dark:bg-warning-500/10 border-warning-500/20'}`}>
        <div className="flex items-center gap-2">
          {configured ? (
            <>
              <StatusPill tone="success" dot>Configured</StatusPill>
              {keyPreview && <span className="text-2xs font-mono text-ink-500 dark:text-ink-400">{keyPreview}</span>}
            </>
          ) : (
            <StatusPill tone="warning" dot>Not configured</StatusPill>
          )}
        </div>
      </div>

      {/* API Key Input */}
      <div>
        <label className="label">Gemini API Key</label>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          className="input"
          placeholder="AIza..."
        />
        <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1.5">
          Get a free API key at <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener" className="text-info-600 dark:text-info-500 underline">aistudio.google.com/apikey</a>. Uses Google Gemini (free tier). The key is stored securely in the database.
        </p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {success && <Alert tone="success">Settings saved! AI features will use the new configuration.</Alert>}

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving || !apiKey}
          className="btn-primary">
          {saving ? <Spinner size="sm" /> : 'Save key'}
        </button>
        {configured && (
          <button onClick={handleDelete} disabled={saving}
            className="btn-secondary text-danger-600 dark:text-danger-500">
            Remove key
          </button>
        )}
      </div>

      {/* Model Configuration */}
      <div className="pt-3 border-t border-ink-200 dark:border-ink-800">
        <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">Gemini Model Configuration</p>
        <p className="text-xs text-ink-500 dark:text-ink-400 mb-2">
          Google frequently renames/retires models. Configure which models to try (in order). The system will use the first one that works with your API key.
        </p>
        <input
          className="input mb-2"
          value={modelsText}
          onChange={e => setModelsText(e.target.value)}
          placeholder="gemini-3.1-flash-lite-preview, gemini-3.1-flash-lite, gemini-3.5-flash-lite"
        />
        <p className="text-2xs text-ink-400 dark:text-ink-500 mb-2">
          Comma-separated list. Current models being tried: {models.join(', ') || 'defaults'}
        </p>
        <div className="flex gap-2">
          <button onClick={handleSaveModels} disabled={saving} className="btn-secondary text-xs">
            Save models
          </button>
          <button onClick={handleTest} disabled={testing || !configured} className="btn-secondary text-xs">
            {testing ? <><Spinner size="sm" /> Testing...</> : 'Test models'}
          </button>
        </div>

        {/* Test results */}
        {testResults && (
          <div className="mt-3 space-y-2">
            {testResults.error ? (
              <Alert tone="danger">{testResults.error}</Alert>
            ) : (
              <>
                <p className={`text-sm font-medium ${testResults.working_models?.length > 0 ? 'text-success-700 dark:text-success-500' : 'text-danger-700 dark:text-danger-500'}`}>
                  {testResults.recommendation}
                </p>
                {testResults.results?.map((r: any, i: number) => (
                  <div key={i} className={`p-2 rounded-md text-xs flex items-center justify-between ${r.status === 'ok' ? 'bg-success-50 dark:bg-success-500/10' : 'bg-danger-50 dark:bg-danger-500/10'}`}>
                    <span className="font-mono">{r.model}</span>
                    <span className={r.status === 'ok' ? 'text-success-700 dark:text-success-500' : 'text-danger-700 dark:text-danger-500'}>
                      {r.status === 'ok' ? `✅ OK (${r.responseTime}ms)` : `❌ ${r.error}`}
                    </span>
                  </div>
                ))}
                {/* Show available models from Google if no configured models work */}
                {testResults.working_models?.length === 0 && testResults.available_from_google?.length > 0 && (
                  <div className="mt-2 p-2 rounded-md bg-info-50 dark:bg-info-500/10">
                    <p className="text-2xs font-medium text-info-700 dark:text-info-500 mb-1">
                      Models Google says are available for your key ({testResults.available_from_google.length}):
                    </p>
                    <p className="text-2xs font-mono text-ink-600 dark:text-ink-300 break-all">
                      {testResults.available_from_google.join(', ')}
                    </p>
                    <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">
                      Copy the ones you want to try into the model list above and click "Save models", then "Test models" again.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Features list */}
      <div className="pt-3 border-t border-ink-200 dark:border-ink-800">
        <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">AI features enabled by this key</p>
        <div className="space-y-1.5">
          {[
            'Advisory content generation (admin)',
            'MEL data analysis & insights (admin)',
            'Pest & disease photo scanning (farmer)',
            'Smart Advisory timeline (farmer — weather-driven)',
            'Spatial AI insights (admin — disease hotspots)',
          ].map((feat) => (
            <div key={feat} className="flex items-center gap-2 text-xs text-ink-600 dark:text-ink-300">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-success-600 dark:text-success-500" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              {feat}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── QR Codes Tab ─── */
const QRCodesTab: React.FC = () => {
  const [codes, setCodes] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedQR, setSelectedQR] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        qrApi.getAll(200),
        qrApi.getStats(),
      ]);
      setCodes(c.codes);
      setStats(s);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) {
    return <Card padding="lg"><div className="flex justify-center py-8"><Spinner /></div></Card>;
  }

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="Total QR codes" value={stats.total} icon={<Icon.MapPin className="w-4 h-4" />} accent="analytics" />
          <MetricTile label="Scanned" value={stats.scanned} icon={<Icon.Activity className="w-4 h-4" />} />
          <MetricTile label="Scanned (7d)" value={stats.scannedLast7d} icon={<Icon.Clock className="w-4 h-4" />} />
          <MetricTile label="Types" value={stats.byType?.length || 0} icon={<Icon.Database className="w-4 h-4" />} />
        </div>
      )}

      {selectedQR && (
        <Card padding="lg" className="flex flex-col items-center">
          <button onClick={() => setSelectedQR(null)} className="btn-ghost text-xs self-end mb-3">Close</button>
          <QRCode code={selectedQR} size={240} label="COCONNECT Traceability" />
        </Card>
      )}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Traceability" title="QR codes" subtitle="Auto-generated for every batch and delivery. Download, print, or scan to view public traceability page." icon={<Icon.MapPin className="w-4 h-4" />} />
        </div>
        {codes.length === 0 ? (
          <EmptyState icon={<Icon.MapPin className="w-5 h-5" />} title="No QR codes yet" description="QR codes are auto-generated when batches are created and deliveries confirmed." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Type</th><th>Farmer</th><th>Scans</th><th>Created</th><th>Last Scan</th><th>Action</th></tr></thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id}>
                    <td className="font-mono text-2xs text-ink-700 dark:text-ink-200">{c.code}</td>
                    <td><span className="pill-neutral capitalize">{c.entity_type}</span></td>
                    <td className="text-sm text-ink-700 dark:text-ink-200">{c.farmer_name || '—'}</td>
                    <td className="nums">{c.scan_count}</td>
                    <td className="text-2xs text-ink-500 nums">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="text-2xs text-ink-500 nums">{c.last_scanned_at ? new Date(c.last_scanned_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <button onClick={() => setSelectedQR(c.code)} className="btn-secondary text-xs">View QR</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Villages Tab — admin CRUD for the villages master list
 * ═══════════════════════════════════════════════════════════════ */
const VillagesTab: React.FC = () => {
  const [villages, setVillages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({ name: '', region: '', division: '', subdivision: '', latitude: '', longitude: '', notes: '' });
  const villagesPag = usePagination(villages, 12);

  const refresh = () => {
    setLoading(true);
    apiClient.get('/villages').then(res => {
      setVillages(Array.isArray(res.data) ? res.data : []);
      setError(null);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load villages'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', region: '', division: '', subdivision: '', latitude: '', longitude: '', notes: '' });
    setShowForm(true);
  };
  const openEdit = (v: any) => {
    setEditing(v);
    setForm({
      name: v.name || '', region: v.region || '', division: v.division || '',
      subdivision: v.subdivision || '', latitude: v.latitude?.toString() || '',
      longitude: v.longitude?.toString() || '', notes: v.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setError(null); setSuccess(null);
    try {
      const payload = {
        name: form.name.trim(),
        region: form.region || null,
        division: form.division || null,
        subdivision: form.subdivision || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        notes: form.notes || null,
      };
      if (!payload.name) { setError('Village name is required'); return; }
      if (editing) {
        await apiClient.put(`/villages/${editing.id}`, payload);
        setSuccess('Village updated');
      } else {
        await apiClient.post('/villages', payload);
        setSuccess('Village created');
      }
      setShowForm(false);
      refresh();
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save village');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this village? Existing farmers in this village will keep their village name.')) return;
    try {
      await apiClient.delete(`/villages/${id}`);
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success">{success}</Alert>}

      <Card padding="md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="section-eyebrow">Master list</p>
            <p className="text-sm text-ink-700 dark:text-ink-300">Villages are assigned to field agents so they can only register farmers in those villages.</p>
          </div>
          <button onClick={openNew} className="btn-accent brand-surface-admin text-sm">
            <Icon.Plus className="w-4 h-4" /> Add village
          </button>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Villages" title={`All villages (${villagesPag.total})`} icon={<Icon.MapPin className="w-4 h-4" />} />
        </div>
        {villagesPag.total === 0 ? (
          <EmptyState icon={<Icon.MapPin className="w-5 h-5" />} title="No villages" description="Add villages to assign them to field agents." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Region</th><th>Division</th><th>Agents</th><th>Farmers</th><th>Actions</th></tr></thead>
              <tbody>
                {villagesPag.paginatedItems.map((v: any) => (
                  <tr key={v.id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{v.name}</td>
                    <td className="text-sm">{v.region || '—'}</td>
                    <td className="text-sm">{v.division || '—'}</td>
                    <td className="text-sm nums">{v.agent_count || 0}</td>
                    <td className="text-sm nums">{v.farmer_count || 0}</td>
                    <td>
                      <button onClick={() => openEdit(v)} className="text-xs text-admin-700 dark:text-admin-500 hover:underline mr-2">Edit</button>
                      <button onClick={() => handleDelete(v.id)} className="text-xs text-danger-700 dark:text-danger-500 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination page={villagesPag.page} pageSize={villagesPag.pageSize} total={villagesPag.total}
            onPageChange={villagesPag.setPage} onPageSizeChange={villagesPag.setPageSize} showPageSize />
        </div>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink-900/60 backdrop-blur-sm safe-top safe-bottom" onClick={() => setShowForm(false)}>
          <div className="w-full md:max-w-xl max-h-[92vh] overflow-y-auto bg-ink-0 dark:bg-ink-900 md:rounded-2xl rounded-t-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <div>
                <p className="section-eyebrow">{editing ? 'Edit village' : 'Add village'}</p>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{editing ? editing.name : 'New village'}</h3>
              </div>
              <IconButton label="Close" onClick={() => setShowForm(false)}><Icon.X className="w-4 h-4" /></IconButton>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="label">Name *</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mbongué" /></div>
                <div><label className="label">Region</label><input className="input" value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="e.g. South Region" /></div>
                <div><label className="label">Division</label><input className="input" value={form.division} onChange={e => setForm({ ...form, division: e.target.value })} placeholder="e.g. Mvila" /></div>
                <div><label className="label">Subdivision</label><input className="input" value={form.subdivision} onChange={e => setForm({ ...form, subdivision: e.target.value })} placeholder="Subdivision" /></div>
                <div><label className="label">Latitude</label><input className="input" type="number" step="0.0000001" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} placeholder="2.25" /></div>
                <div><label className="label">Longitude</label><input className="input" type="number" step="0.0000001" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} placeholder="11.15" /></div>
              </div>
              <div><label className="label">Notes</label><input className="input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></div>
            </div>
            <div className="sticky bottom-0 flex justify-end gap-2 px-5 py-3.5 border-t border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <button onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleSave} className="btn-accent brand-surface-admin text-sm"><Icon.Check className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Agents Tab — admin creates field agents + assigns villages
 * ═══════════════════════════════════════════════════════════════ */
const AgentsTab: React.FC = () => {
  const [agents, setAgents] = useState<any[]>([]);
  const [villages, setVillages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', village_ids: [] as string[] });
  const agentsPag = usePagination(agents, 12);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/agents').catch(() => ({ data: { agents: [] } })),
      apiClient.get('/villages').catch(() => ({ data: [] })),
    ]).then(([aRes, vRes]) => {
      setAgents(aRes.data.agents || []);
      setVillages(Array.isArray(vRes.data) ? vRes.data : []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', email: '', phone: '', password: '', village_ids: [] });
    setShowForm(true);
  };
  const openEdit = (a: any) => {
    setEditing(a);
    setForm({
      name: a.name || '', email: a.email || '', phone: a.phone || '', password: '',
      village_ids: (a.assigned_villages || []).map((v: any) => v.id),
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setError(null); setSuccess(null);
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    if (!editing && form.village_ids.length === 0) { setError('Assign at least one village'); return; }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone || null,
        password: form.password || undefined,
        village_ids: form.village_ids,
      };
      if (editing) {
        await apiClient.put(`/agents/${editing.id}`, payload);
        setSuccess('Agent updated');
      } else {
        const res = await apiClient.post('/agents', payload);
        const dp = res.data?.default_password;
        setSuccess(dp
          ? `Agent created. Share credentials: email=${form.email} · password=${dp} (must be changed on first login).`
          : 'Agent created');
      }
      setShowForm(false);
      refresh();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save agent');
    }
  };

  const handleResetPassword = async (id: string, name: string) => {
    const pwd = prompt(`Reset password for ${name}.\nLeave blank to auto-generate a random password.`);
    if (pwd === null) return;
    try {
      const res = await apiClient.post(`/agents/${id}/reset-password`, pwd ? { password: pwd } : {});
      alert(`Password reset for ${name}.\nNew password: ${res.data.new_password}\nShare it with the agent — they must change it on next login.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success">{success}</Alert>}

      <Card padding="md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="section-eyebrow">Field Agents</p>
            <p className="text-sm text-ink-700 dark:text-ink-300">Create agents, assign villages, and reset passwords.</p>
          </div>
          <button onClick={openNew} className="btn-accent brand-surface-admin text-sm">
            <Icon.Plus className="w-4 h-4" /> Add agent
          </button>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Field agents" title={`All agents (${agentsPag.total})`} icon={<Icon.Users className="w-4 h-4" />} />
        </div>
        {agentsPag.total === 0 ? (
          <EmptyState icon={<Icon.Users className="w-5 h-5" />} title="No agents" description="Add your first field agent to get started." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Villages</th><th>Last login</th><th>Actions</th></tr></thead>
              <tbody>
                {agentsPag.paginatedItems.map((a: any) => (
                  <tr key={a.id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">
                      {a.name}
                      {a.must_change_password && (
                        <span className="ml-1 text-2xs px-1.5 py-0.5 rounded bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-500">Must change password</span>
                      )}
                    </td>
                    <td className="text-sm">{a.email}</td>
                    <td className="text-sm">{a.phone || '—'}</td>
                    <td className="text-sm">
                      {(a.assigned_villages || []).length === 0
                        ? <span className="text-warning-700 dark:text-warning-500">No villages</span>
                        : (a.assigned_villages || []).map((v: any) => v.name).join(', ')}
                    </td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400">{a.last_login ? new Date(a.last_login).toLocaleDateString() : '—'}</td>
                    <td>
                      <button onClick={() => openEdit(a)} className="text-xs text-admin-700 dark:text-admin-500 hover:underline mr-2">Edit</button>
                      <button onClick={() => handleResetPassword(a.id, a.name)} className="text-xs text-warning-700 dark:text-warning-500 hover:underline">Reset password</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination page={agentsPag.page} pageSize={agentsPag.pageSize} total={agentsPag.total}
            onPageChange={agentsPag.setPage} onPageSizeChange={agentsPag.setPageSize} showPageSize />
        </div>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink-900/60 backdrop-blur-sm safe-top safe-bottom" onClick={() => setShowForm(false)}>
          <div className="w-full md:max-w-2xl max-h-[92vh] overflow-y-auto bg-ink-0 dark:bg-ink-900 md:rounded-2xl rounded-t-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <div>
                <p className="section-eyebrow">{editing ? 'Edit agent' : 'Add field agent'}</p>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{editing ? editing.name : 'New agent'}</h3>
              </div>
              <IconButton label="Close" onClick={() => setShowForm(false)}><Icon.X className="w-4 h-4" /></IconButton>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="label">Name *</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="label">Email *</label><input className="input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+2376XXXXXXXX" /></div>
                <div><label className="label">{editing ? 'New password (optional)' : 'Password (optional — auto-default if blank)'}</label><input className="input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 8 chars" /></div>
              </div>
              <div>
                <label className="label">Assigned villages * (multi-select)</label>
                <div className="border border-ink-200 dark:border-ink-800 rounded-lg p-3 max-h-56 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2">
                  {villages.map(v => {
                    const checked = form.village_ids.includes(v.id);
                    return (
                      <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={checked}
                          onChange={() => setForm(f => ({
                            ...f,
                            village_ids: checked
                              ? f.village_ids.filter(id => id !== v.id)
                              : [...f.village_ids, v.id],
                          }))}
                          className="rounded border-ink-300"
                        />
                        <span>{v.name}</span>
                        {v.region && <span className="text-2xs text-ink-500 dark:text-ink-400">· {v.region}</span>}
                      </label>
                    );
                  })}
                </div>
                {villages.length === 0 && <p className="mt-1 text-2xs text-warning-700 dark:text-warning-500">No villages exist yet. Create them in the Villages tab first.</p>}
              </div>
            </div>
            <div className="sticky bottom-0 flex justify-end gap-2 px-5 py-3.5 border-t border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <button onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleSave} className="btn-accent brand-surface-admin text-sm"><Icon.Check className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Drivers Tab — admin creates drivers + assigns trucks
 * ═══════════════════════════════════════════════════════════════ */
const DriversTab: React.FC = () => {
  const [drivers, setDrivers] = useState<any[]>([]);
  const [trucks, setTrucks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', assign_truck_id: '' });
  const driversPag = usePagination(drivers, 12);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/drivers').catch(() => ({ data: { drivers: [] } })),
      apiClient.get('/trucks').catch(() => ({ data: [] })),
    ]).then(([dRes, tRes]) => {
      setDrivers(dRes.data.drivers || []);
      setTrucks(Array.isArray(tRes.data) ? tRes.data : []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', email: '', phone: '', password: '', assign_truck_id: '' });
    setShowForm(true);
  };
  const openEdit = (d: any) => {
    setEditing(d);
    setForm({ name: d.name || '', email: d.email || '', phone: d.phone || '', password: '', assign_truck_id: d.truck_id || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    setError(null); setSuccess(null);
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone || null,
        password: form.password || undefined,
        assign_truck_id: form.assign_truck_id || undefined,
      };
      if (editing) {
        // For edits, use PUT /drivers/:id (profile only) + separately assign truck if changed
        await apiClient.put(`/drivers/${editing.id}`, { name: payload.name, phone: payload.phone });
        if (form.assign_truck_id) {
          await apiClient.post(`/drivers/${editing.id}/assign-truck`, { truck_id: form.assign_truck_id });
        }
        setSuccess('Driver updated');
      } else {
        const res = await apiClient.post('/drivers', payload);
        const dp = res.data?.default_password;
        setSuccess(dp
          ? `Driver created. Share credentials: email=${form.email} · password=${dp} (must be changed on first login).`
          : 'Driver created');
      }
      setShowForm(false);
      refresh();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save driver');
    }
  };

  const handleResetPassword = async (id: string, name: string) => {
    const pwd = prompt(`Reset password for ${name}.\nLeave blank to auto-generate.`);
    if (pwd === null) return;
    try {
      const res = await apiClient.post(`/drivers/${id}/reset-password`, pwd ? { password: pwd } : {});
      alert(`Password reset for ${name}.\nNew password: ${res.data.new_password}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success">{success}</Alert>}

      <Card padding="md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="section-eyebrow">Drivers</p>
            <p className="text-sm text-ink-700 dark:text-ink-300">Create drivers with login credentials and assign them to trucks.</p>
          </div>
          <button onClick={openNew} className="btn-accent brand-surface-cargo text-sm">
            <Icon.Plus className="w-4 h-4" /> Add driver
          </button>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Drivers" title={`All drivers (${driversPag.total})`} icon={<Icon.Truck className="w-4 h-4" />} />
        </div>
        {driversPag.total === 0 ? (
          <EmptyState icon={<Icon.Truck className="w-5 h-5" />} title="No drivers" description="Add a driver to assign them to a truck." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Assigned truck</th><th>Last login</th><th>Actions</th></tr></thead>
              <tbody>
                {driversPag.paginatedItems.map((d: any) => (
                  <tr key={d.id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">
                      {d.name}
                      {d.must_change_password && (
                        <span className="ml-1 text-2xs px-1.5 py-0.5 rounded bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-500">Must change password</span>
                      )}
                    </td>
                    <td className="text-sm">{d.email}</td>
                    <td className="text-sm">{d.phone || '—'}</td>
                    <td className="text-sm">
                      {d.truck_registration
                        ? <span className="text-cargo-700 dark:text-cargo-500 font-medium">{d.truck_registration}</span>
                        : <span className="text-ink-500 dark:text-ink-400">— unassigned —</span>}
                    </td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400">{d.last_login ? new Date(d.last_login).toLocaleDateString() : '—'}</td>
                    <td>
                      <button onClick={() => openEdit(d)} className="text-xs text-admin-700 dark:text-admin-500 hover:underline mr-2">Edit</button>
                      <button onClick={() => handleResetPassword(d.id, d.name)} className="text-xs text-warning-700 dark:text-warning-500 hover:underline">Reset password</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination page={driversPag.page} pageSize={driversPag.pageSize} total={driversPag.total}
            onPageChange={driversPag.setPage} onPageSizeChange={driversPag.setPageSize} showPageSize />
        </div>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink-900/60 backdrop-blur-sm safe-top safe-bottom" onClick={() => setShowForm(false)}>
          <div className="w-full md:max-w-xl max-h-[92vh] overflow-y-auto bg-ink-0 dark:bg-ink-900 md:rounded-2xl rounded-t-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <div>
                <p className="section-eyebrow">{editing ? 'Edit driver' : 'Add driver'}</p>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{editing ? editing.name : 'New driver'}</h3>
              </div>
              <IconButton label="Close" onClick={() => setShowForm(false)}><Icon.X className="w-4 h-4" /></IconButton>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="label">Name *</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="label">Email *</label><input className="input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+2376XXXXXXXX" /></div>
                <div><label className="label">{editing ? 'New password (optional)' : 'Password (optional — auto-default if blank)'}</label><input className="input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
                <div className="md:col-span-2">
                  <label className="label">Assign truck</label>
                  <select className="input" value={form.assign_truck_id} onChange={e => setForm({ ...form, assign_truck_id: e.target.value })}>
                    <option value="">— No truck —</option>
                    {trucks.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.registration_number} ({Number(t.capacity_kg).toLocaleString()} kg)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 flex justify-end gap-2 px-5 py-3.5 border-t border-ink-200 dark:border-ink-800 bg-ink-0 dark:bg-ink-900">
              <button onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleSave} className="btn-accent brand-surface-cargo text-sm"><Icon.Check className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Users Tab — admin lists all users + reset password + activate/deactivate
 * ═══════════════════════════════════════════════════════════════ */
const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // v1.7.5: added success state + create user sheet (was missing — no Add User button existed)
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', phone: '', role: 'finance', password: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const usersPag = usePagination(
    users.filter(u =>
      (!searchTerm ||
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.phone?.includes(searchTerm)) &&
      (!filterRole || u.role === filterRole)
    ),
    15
  );

  const refresh = () => {
    setLoading(true);
    apiClient.get('/users').then(res => setUsers(res.data.users || []))
      .catch(err => setError(err.response?.data?.error || 'Failed to load users'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const handleResetPassword = async (id: string, name: string) => {
    const pwd = prompt(`Reset password for ${name}.\nLeave blank to auto-generate.`);
    if (pwd === null) return;
    try {
      const res = await apiClient.post(`/users/${id}/reset-password`, pwd ? { password: pwd } : {});
      alert(`Password reset for ${name}.\nNew password: ${res.data.new_password}\nShare it — the user must change it on next login.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  // v1.7.5: Create user handler — calls POST /api/users (admin-only endpoint)
  const handleCreateUser = async () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!createForm.name || !createForm.email) {
      setError('Name and email are required.');
      return;
    }
    if (!emailRegex.test(createForm.email)) {
      setError('Please enter a valid email address (e.g. user@coconnect.com).');
      return;
    }
    // Confirmation dialog for admin role (irreversible privilege grant)
    if (createForm.role === 'admin') {
      if (!confirm(`Create a new ADMIN user?\n\n${createForm.name} (${createForm.email}) will have FULL administrative privileges. Proceed with caution.`)) {
        return;
      }
    }
    setCreateLoading(true);
    setError(null); setSuccess(null);
    try {
      const res = await apiClient.post('/users', {
        name: createForm.name,
        email: createForm.email,
        phone: createForm.phone || undefined,
        role: createForm.role,
        password: createForm.password || undefined,
      });
      const defaultPwd = res.data?.default_password;
      setSuccess(`User created: ${createForm.email} (${createForm.role})${defaultPwd ? `. Default password: ${defaultPwd}` : ''}`);
      setShowCreateSheet(false);
      setShowPassword(false);
      setCreateForm({ name: '', email: '', phone: '', role: 'finance', password: '' });
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const toggleActive = async (u: any) => {
    try {
      await apiClient.put(`/users/${u.id}/activate`, { is_active: !u.is_active });
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle active state');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input className="input pl-10" placeholder="Search by name, email, or phone…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <select className="input md:w-48" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="field_agent">Field Agent</option>
            <option value="farmer">Farmer</option>
            <option value="driver">Driver</option>
            <option value="finance">Finance</option>
            <option value="factory_clerk">Factory Clerk</option>
          </select>
          {/* v1.7.5: Add User button — opens the Create User Sheet */}
          <button onClick={() => setShowCreateSheet(true)} className="btn-primary text-xs whitespace-nowrap">
            <Icon.Plus className="w-4 h-4" /> <span>Add User</span>
          </button>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="User management" title={`All users (${usersPag.total})`} icon={<Icon.Users className="w-4 h-4" />} />
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Villages/Truck</th><th>Status</th><th>Last login</th><th>Actions</th></tr></thead>
            <tbody>
              {usersPag.paginatedItems.map((u: any) => (
                <tr key={u.id}>
                  <td className="text-sm font-medium text-ink-900 dark:text-ink-50">
                    {u.name}
                    {u.must_change_password && (
                      <span className="ml-1 text-2xs px-1.5 py-0.5 rounded bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-500">Must change</span>
                    )}
                  </td>
                  <td className="text-sm">{u.email}</td>
                  <td className="text-sm">{u.phone || '—'}</td>
                  <td><StatusPill tone={u.role === 'admin' ? 'info' : 'success'} dot>{u.role}</StatusPill></td>
                  <td className="text-sm">
                    {u.role === 'field_agent' && `${u.assigned_village_count || 0} villages`}
                    {u.role === 'driver' && (u.truck_registration || '— no truck —')}
                    {u.role === 'farmer' && `${u.linked_farmer_count || 0} farmer profile`}
                  </td>
                  <td>
                    {u.is_active
                      ? <StatusPill tone="success" dot>Active</StatusPill>
                      : <StatusPill tone="danger" dot>Disabled</StatusPill>}
                  </td>
                  <td className="text-2xs text-ink-500 dark:text-ink-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                  <td>
                    <button onClick={() => handleResetPassword(u.id, u.name)} className="text-xs text-warning-700 dark:text-warning-500 hover:underline mr-2">Reset password</button>
                    <button onClick={() => toggleActive(u)} className="text-xs text-ink-700 dark:text-ink-300 hover:underline">
                      {u.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-ink-200 dark:border-ink-800">
          <Pagination page={usersPag.page} pageSize={usersPag.pageSize} total={usersPag.total}
            onPageChange={usersPag.setPage} onPageSizeChange={usersPag.setPageSize} showPageSize />
        </div>
      </Card>

      {/* v1.7.5: Create User Sheet — lets admin create finance, clerk, driver, agent, admin, farmer users */}
      <Sheet open={showCreateSheet} onClose={() => { setShowCreateSheet(false); setShowPassword(false); }} title="Create New User"
        footer={
          <div className="flex gap-2">
            <button onClick={() => { setShowCreateSheet(false); setShowPassword(false); }} className="btn-secondary flex-1">Cancel</button>
            <button onClick={handleCreateUser} disabled={createLoading || !createForm.name || !createForm.email} className="btn-primary flex-1">
              {createLoading ? <Spinner size="sm" /> : <Icon.Plus className="w-4 h-4" />} Create User
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div>
            <label htmlFor="cu-name" className="label">Full name *</label>
            <input id="cu-name" type="text" required value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              className="input" placeholder="e.g. Finance Officer" />
          </div>
          <div>
            <label htmlFor="cu-email" className="label">Email *</label>
            <input id="cu-email" type="email" required value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              className="input" placeholder="e.g. finance@coconnect.com" />
            {createForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email) && (
              <p className="text-2xs text-warning-600 dark:text-warning-500 mt-1">Enter a valid email format (user@domain.com).</p>
            )}
          </div>
          <div>
            <label htmlFor="cu-phone" className="label">Phone</label>
            <input id="cu-phone" type="tel" value={createForm.phone}
              onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
              className="input" placeholder="+237..." />
          </div>
          <div>
            <label htmlFor="cu-role" className="label">Role *</label>
            <select id="cu-role" value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              className="input">
              <option value="admin">Admin</option>
              <option value="finance">Finance Officer</option>
              <option value="factory_clerk">Factory Clerk (Warehouse)</option>
              <option value="field_agent">Field Agent</option>
              <option value="driver">Driver</option>
              <option value="farmer">Farmer</option>
            </select>
          </div>
          <div>
            <label htmlFor="cu-pwd" className="label">Password (optional — leave blank for default)</label>
            <div className="relative">
              <input id="cu-pwd" type={showPassword ? "text" : "password"} value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                className="input pr-10" placeholder="Default: CoConnect@2024 (must change on first login)" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 dark:hover:text-ink-300"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
            {createForm.password && createForm.password.length < 8 && (
              <p className="text-2xs text-warning-600 dark:text-warning-500 mt-1">Password is shorter than 8 characters — recommend at least 8 chars with mixed case + numbers.</p>
            )}
          </div>
          <Alert tone="info">
            <strong>Finance</strong> → can approve payments, process withdrawals, update prices<br />
            <strong>Factory Clerk</strong> → can receive batches at warehouse, grade quality, route batches, manage warehouse inventory<br />
            <strong>Field Agent</strong> → can register farmers, record batches, inspect harvest notifications<br />
            <strong>Driver</strong> → can see dispatches, load bags, update GPS<br />
            <strong>Farmer</strong> → can see their batches, grades, payments, submit harvest notifications
          </Alert>
        </div>
      </Sheet>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Profile Tab — every user can edit own profile + change password
 * ═══════════════════════════════════════════════════════════════ */
const ProfileTab: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Change password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const refresh = () => {
    setLoading(true);
    apiClient.get('/users/me').then(res => {
      const p = res.data.profile;
      setProfile(p);
      setName(p.name || ''); setPhone(p.phone || ''); setBio(p.bio || ''); setProfilePicUrl(p.profile_pic_url || '');
    }).catch(err => setError(err.response?.data?.error || 'Failed to load profile'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const handleSaveProfile = async () => {
    setError(null); setSuccess(null);
    setSavingProfile(true);
    try {
      await apiClient.put('/users/me', { name, phone, bio, profile_pic_url: profilePicUrl });
      setSuccess('Profile updated.');
      refreshUser();
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setError(null); setSuccess(null);
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    setSavingPassword(true);
    try {
      await apiClient.put('/users/me/password', { current_password: currentPassword, new_password: newPassword });
      setSuccess('Password changed successfully.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      refreshUser();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4 max-w-3xl">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success">{success}</Alert>}

      {/* Identity banner */}
      <Card padding="lg">
        <div className="flex items-center gap-4">
          <Avatar name={profile?.name || user?.name || 'U'} size="xl" />
          <div className="min-w-0">
            <p className="section-eyebrow">Your profile</p>
            <h2 className="text-xl font-semibold text-ink-900 dark:text-ink-50 tracking-tight truncate">{profile?.name}</h2>
            <p className="text-sm text-ink-500 dark:text-ink-400 truncate">{profile?.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusPill tone="info" dot>{profile?.role}</StatusPill>
              {profile?.must_change_password && <StatusPill tone="warning" dot>Must change password</StatusPill>}
              {profile?.last_password_change_at && (
                <span className="text-2xs text-ink-500 dark:text-ink-400">
                  Password changed {new Date(profile.last_password_change_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Profile edit */}
      <Card padding="lg">
        <SectionHeader eyebrow="Edit details" title="Profile information" icon={<Icon.Users className="w-4 h-4" />} />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label">Name</label><input className="input" value={name} onChange={e => setName(e.target.value)} /></div>
          <div><label className="label">Phone</label><input className="input" value={phone} onChange={e => setPhone(e.target.value)} /></div>
          <div className="md:col-span-2"><label className="label">Profile picture URL</label><input className="input" value={profilePicUrl} onChange={e => setProfilePicUrl(e.target.value)} placeholder="https://…" /></div>
          <div className="md:col-span-2"><label className="label">Bio</label><textarea className="input" rows={3} value={bio} onChange={e => setBio(e.target.value)} placeholder="Short bio (optional)" /></div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={handleSaveProfile} disabled={savingProfile} className="btn-accent brand-surface-admin text-sm">
            {savingProfile ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save profile
          </button>
        </div>
      </Card>

      {/* Change password */}
      <Card padding="lg">
        <SectionHeader eyebrow="Security" title="Change password" subtitle="Enter your current password to set a new one." icon={<Icon.Shield className="w-4 h-4" />} />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className="label">Current password</label><input className="input" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} autoComplete="current-password" /></div>
          <div><label className="label">New password</label><input className="input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" placeholder="Min 8 chars" /></div>
          <div><label className="label">Confirm new password</label><input className="input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" /></div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={handleChangePassword} disabled={savingPassword} className="btn-secondary text-sm">
            {savingPassword ? <Spinner size="sm" /> : <Icon.Shield className="w-4 h-4" />} Change password
          </button>
        </div>
      </Card>

      {/* Role-specific context */}
      {profile?.role === 'field_agent' && (profile?.assigned_villages?.length > 0) && (
        <Card padding="lg">
          <SectionHeader eyebrow="Your assignments" title="Assigned villages" icon={<Icon.MapPin className="w-4 h-4" />} />
          <div className="mt-3 flex flex-wrap gap-2">
            {profile.assigned_villages.map((v: any) => (
              <span key={v.id} className="pill-info">{v.name}{v.region ? ` · ${v.region}` : ''}</span>
            ))}
          </div>
        </Card>
      )}
      {profile?.role === 'driver' && profile?.driver_truck_registration && (
        <Card padding="lg">
          <SectionHeader eyebrow="Your assignment" title="Assigned truck" icon={<Icon.Truck className="w-4 h-4" />} />
          <p className="mt-3 text-sm">Truck <strong className="text-cargo-700 dark:text-cargo-500">{profile.driver_truck_registration}</strong></p>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Weather Config Card — admin sets weather API provider + key
 * Default: Open-Meteo (FREE, no API key needed, covers Cameroon).
 * Optional: OpenWeatherMap or WeatherAPI.com (free tiers, key required).
 * ═══════════════════════════════════════════════════════════════ */
const WeatherConfigCard: React.FC = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [provider, setProvider] = useState('open-meteo');
  const [apiKey, setApiKey] = useState('');
  const [cacheTtl, setCacheTtl] = useState(30);

  const refresh = () => {
    setLoading(true);
    apiClient.get('/weather/config').then(res => {
      const c = res.data;
      setConfig(c);
      setProvider(c.provider || 'open-meteo');
      setApiKey(''); // never show existing key
      setCacheTtl(c.cache_ttl_minutes || 30);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload: any = { provider, cache_ttl_minutes: cacheTtl };
      // Only send api_key if user typed something
      if (apiKey) payload.api_key = apiKey;
      await apiClient.post('/weather/config', payload);
      setSuccess('Weather config updated');
      setApiKey('');
      refresh();
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleClearCache = async () => {
    try {
      const res = await apiClient.post('/weather/cache/clear');
      setSuccess(`Cleared ${res.data.cleared} cached weather entries`);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to clear cache');
    }
  };

  if (loading) return <div className="flex justify-center py-4"><Spinner /></div>;

  return (
    <div>
      <SectionHeader
        eyebrow="Weather"
        title="Weather provider"
        subtitle="Drives farmer advisory timelines. Default: Open-Meteo (FREE, no key required, covers Cameroon)."
        icon={<Icon.MapPin className="w-4 h-4" />}
      />
      {error && <div className="mt-3"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>}
      {success && <div className="mt-3"><Alert tone="success">{success}</Alert></div>}

      <div className="mt-4 space-y-3">
        <div>
          <label className="label">Provider</label>
          <select className="input" value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="open-meteo">Open-Meteo (FREE — no API key required, recommended)</option>
            <option value="openweathermap">OpenWeatherMap (free tier 1000/day, key required)</option>
            <option value="weatherapi">WeatherAPI.com (free tier 1M/month, key required)</option>
          </select>
        </div>

        {provider !== 'open-meteo' && (
          <div>
            <label className="label">API key{config?.has_api_key ? ` (current: ${config.api_key_preview} — leave blank to keep)` : ' *'}</label>
            <input
              className="input"
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={config?.has_api_key ? '•••••• (leave blank to keep current)' : 'Paste your API key here'}
            />
            <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">
              {provider === 'openweathermap' && 'Get a free key at https://openweathermap.org/api'}
              {provider === 'weatherapi' && 'Get a free key at https://www.weatherapi.com'}
            </p>
          </div>
        )}

        {provider === 'open-meteo' && (
          <Alert tone="info">
            ✅ Open-Meteo is completely free, requires no API key, and covers Cameroon.
            Used by major weather platforms worldwide. No setup needed — just click Save.
          </Alert>
        )}

        <div>
          <label className="label">Cache TTL (minutes)</label>
          <input
            className="input"
            type="number"
            min={5}
            max={1440}
            value={cacheTtl}
            onChange={e => setCacheTtl(parseInt(e.target.value, 10) || 30)}
          />
          <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">
            How long to cache weather responses per location. Lower = fresher but more API calls.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <button onClick={handleClearCache} className="btn-secondary text-xs">
            Clear cache
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save config
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * SMS Config Card — Twilio free trial integration
 * Admin enters Twilio Account SID + Auth Token + From Number.
 * Free trial: $15 credit, ~256 SMS to Cameroon.
 * ═══════════════════════════════════════════════════════════════ */
const SmsConfigCard: React.FC = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [provider, setProvider] = useState('twilio');
  const [enabled, setEnabled] = useState(false);
  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [dailySendTime, setDailySendTime] = useState('06:00');
  const [maxPerDay, setMaxPerDay] = useState(500);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);

  const refresh = () => {
    setLoading(true);
    apiClient.get('/sms/config').then(res => {
      const c = res.data;
      setConfig(c);
      setProvider(c.provider || 'twilio');
      setEnabled(c.enabled);
      setDailySendTime('06:00');
      setMaxPerDay(500);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload: any = { provider, enabled, daily_send_time: dailySendTime, max_per_day: maxPerDay };
      if (accountSid) payload.account_sid = accountSid;
      if (authToken) payload.auth_token = authToken;
      if (fromNumber) payload.from_number = fromNumber;
      const res = await apiClient.post('/sms/config', payload);
      setConfig(res.data.config);
      setSuccess('SMS config updated');
      setAccountSid(''); setAuthToken(''); setFromNumber('');
      refresh();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testPhone) { setError('Enter a phone number to test'); return; }
    setSendingTest(true); setError(null); setSuccess(null);
    try {
      const res = await apiClient.post('/sms/test', { phone: testPhone });
      setSuccess(`Test SMS sent! Cost: $${res.data.cost_usd || '0.0585'}`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Test failed');
    } finally { setSendingTest(false); }
  };

  const handleBatch = async () => {
    if (!confirm('Send daily advisory SMS to all opted-in farmers now? This will use Twilio credits.')) return;
    setSendingBatch(true); setError(null); setSuccess(null);
    try {
      const res = await apiClient.post('/sms/send-daily-batch');
      setSuccess(`Batch complete: ${res.data.sent} sent, ${res.data.failed} failed, ${res.data.skipped} skipped`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Batch failed');
    } finally { setSendingBatch(false); }
  };

  if (loading) return <div className="flex justify-center py-4"><Spinner /></div>;

  const creditsRemaining = config?.free_trial_credits_remaining ?? 15.00;
  const costPerMsg = config?.cost_per_message_usd ?? 0.0585;
  const estimatedMessagesLeft = Math.floor(creditsRemaining / costPerMsg);

  return (
    <div>
      <SectionHeader
        eyebrow="SMS Notifications"
        title="Twilio SMS (free trial)"
        subtitle="Send daily advisory SMS to farmers without smartphones. Free $15 trial credit = ~256 SMS to Cameroon."
        icon={<Icon.MapPin className="w-4 h-4" />}
      />
      {error && <div className="mt-3"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>}
      {success && <div className="mt-3"><Alert tone="success">{success}</Alert></div>}

      {/* Free trial status */}
      <div className="mt-4 p-3 rounded-lg surface-inset">
        <div className="flex items-center justify-between">
          <div>
            <p className="section-eyebrow">Free trial status</p>
            <p className="text-2xl font-semibold nums text-ink-900 dark:text-ink-50">
              ${creditsRemaining.toFixed(2)} <span className="text-sm text-ink-500 dark:text-ink-400">remaining</span>
            </p>
            <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">
              ≈ {estimatedMessagesLeft} SMS to Cameroon (at ${costPerMsg.toFixed(4)}/msg)
            </p>
          </div>
          <StatusPill tone={config?.enabled ? 'success' : 'info'} dot>
            {config?.enabled ? 'Enabled' : 'Disabled'}
          </StatusPill>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between p-3 rounded-lg border border-ink-200 dark:border-ink-800">
          <div>
            <p className="text-sm font-medium text-ink-900 dark:text-ink-50">Enable SMS notifications</p>
            <p className="text-2xs text-ink-500 dark:text-ink-400">Master switch. Farmers must also opt in via their profile.</p>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-success-500' : 'bg-ink-300 dark:bg-ink-700'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : ''}`} />
          </button>
        </div>

        <div>
          <label className="label">Twilio Account SID{config?.has_credentials ? ' (configured — leave blank to keep)' : ' *'}</label>
          <input className="input" value={accountSid} onChange={e => setAccountSid(e.target.value)} placeholder={config?.has_credentials ? '•••• (leave blank to keep)' : 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'} />
          <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">Get a free SID at https://www.twilio.com/console</p>
        </div>

        <div>
          <label className="label">Twilio Auth Token{config?.has_credentials ? ' (configured — leave blank to keep)' : ' *'}</label>
          <input className="input" type="password" value={authToken} onChange={e => setAuthToken(e.target.value)} placeholder={config?.has_credentials ? '•••••• (leave blank to keep)' : 'Your auth token'} />
        </div>

        <div>
          <label className="label">Twilio From Number *</label>
          <input className="input" value={fromNumber} onChange={e => setFromNumber(e.target.value)} placeholder="+1234567890 (your free Twilio number)" />
          <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">Get a free US/Canada number in Twilio console</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Daily send time (Douala)</label>
            <input className="input" type="time" value={dailySendTime} onChange={e => setDailySendTime(e.target.value)} />
          </div>
          <div>
            <label className="label">Max SMS per day</label>
            <input className="input" type="number" min={1} max={1000} value={maxPerDay} onChange={e => setMaxPerDay(parseInt(e.target.value, 10) || 500)} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <button onClick={handleBatch} disabled={sendingBatch || !config?.enabled} className="btn-secondary text-xs disabled:opacity-50">
            {sendingBatch ? 'Sending...' : 'Send batch now'}
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save config
          </button>
        </div>

        {/* Test SMS */}
        <div className="pt-3 border-t border-ink-200 dark:border-ink-800">
          <p className="section-eyebrow mb-2">Test SMS</p>
          <div className="flex gap-2">
            <input className="input flex-1" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+2376XXXXXXXX" />
            <button onClick={handleTest} disabled={sendingTest || !config?.has_credentials} className="btn-secondary text-xs disabled:opacity-50">
              {sendingTest ? 'Sending...' : 'Send test'}
            </button>
          </div>
          <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">
            Note: During free trial, you must verify the recipient's number in Twilio console first.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Smart Advisory Tab — Phase 29 Timeline Performance + Predicted Events
 * Shows adoption metrics, alert resolution stats, predicted events, top farmers.
 * ═══════════════════════════════════════════════════════════════ */
const SmartAdvisoryTab: React.FC = () => {
  const [perf, setPerf] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [smsStats, setSmsStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventsPag = usePagination(events, 15);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/advisory-timeline/performance').catch(() => ({ data: null })),
      apiClient.get('/advisory-timeline/predicted-events').catch(() => ({ data: { events: [] } })),
      apiClient.get('/sms/stats').catch(() => ({ data: null })),
    ]).then(([pRes, eRes, sRes]) => {
      setPerf(pRes.data);
      setEvents(eRes.data?.events || []);
      setSmsStats(sRes.data);
      setError(null);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {/* Adoption + performance KPIs */}
      {perf?.adoption && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="Adoption rate" value={`${perf.adoption.adoption_rate_pct || 0}%`} hint={`${perf.adoption.timelines_with_confirmations || 0} of ${perf.adoption.total_timelines || 0} timelines confirmed`} icon={<Icon.Check className="w-4 h-4" />} accent="admin" />
          <MetricTile label="Active alerts" value={perf.alert_stats?.total_alerts || 0} hint={`${perf.alert_stats?.critical_alerts || 0} critical`} icon={<Icon.Alert className="w-4 h-4" />} />
          <MetricTile label="Resolved alerts" value={perf.alert_stats?.resolved_alerts || 0} hint={`avg ${perf.alert_stats?.avg_hours_to_resolve || 0}h to resolve`} icon={<Icon.Shield className="w-4 h-4" />} />
          <MetricTile label="Predicted events" value={perf.event_stats?.active_events || 0} hint={`${perf.event_stats?.black_pod_events || 0} black pod`} icon={<Icon.MapPin className="w-4 h-4" />} />
        </div>
      )}

      {/* SMS stats */}
      {smsStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="SMS sent" value={smsStats.total || 0} hint={`${smsStats.sent_today || 0} today`} icon={<Icon.MapPin className="w-4 h-4" />} />
          <MetricTile label="SMS delivered" value={smsStats.delivered || 0} hint={`${smsStats.failed || 0} failed`} icon={<Icon.Check className="w-4 h-4" />} />
          <MetricTile label="Farmers messaged" value={smsStats.unique_farmers_messaged || 0} icon={<Icon.Users className="w-4 h-4" />} />
          <MetricTile label="SMS cost" value={`$${Number(smsStats.total_cost_usd || 0).toFixed(2)}`} hint={`$${Number(smsStats.free_trial_credits_remaining || 15).toFixed(2)} trial credit left`} icon={<Icon.Dollar className="w-4 h-4" />} />
        </div>
      )}

      {/* Confirmation status breakdown */}
      {perf?.status_breakdown && perf.status_breakdown.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Phase 29.4 — per-action feedback" title="Action confirmations (last 30 days)" icon={<Icon.Check className="w-4 h-4" />} />
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            {perf.status_breakdown.map((s: any) => (
              <div key={s.status} className="p-3 rounded-lg surface-inset">
                <p className="text-2xl font-semibold nums text-ink-900 dark:text-ink-50">{s.count}</p>
                <p className="text-2xs text-ink-500 dark:text-ink-400 capitalize">{s.status}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Top farmers by engagement */}
      {perf?.top_farmers && perf.top_farmers.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Most engaged farmers" title="Top 20 by actions confirmed" icon={<Icon.Users className="w-4 h-4" />} />
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Farmer</th><th>Village</th><th>Timelines</th><th>Actions confirmed</th><th>Engagement</th></tr></thead>
              <tbody>
                {perf.top_farmers.map((f: any) => (
                  <tr key={f.farmer_id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{f.name}</td>
                    <td className="text-sm">{f.village || '—'}</td>
                    <td className="nums">{f.timelines_generated}</td>
                    <td className="nums">{f.actions_confirmed}</td>
                    <td className="nums">{f.engagement_pct || 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Active predicted events */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
          <SectionHeader eyebrow="Phase 29.3 — infection windows" title={`Active predicted events (${eventsPag.total})`} icon={<Icon.Shield className="w-4 h-4" />} />
          <button onClick={refresh} className="btn-secondary text-xs">
            <Icon.Refresh className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {eventsPag.total === 0 ? (
          <EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No active predicted events" description="When disease risk crosses the high threshold (≥7/10), infection windows will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Event type</th><th>Farmer</th><th>Village</th><th>Window</th><th>Severity</th><th>Risk</th><th>Recommendation</th></tr></thead>
              <tbody>
                {eventsPag.paginatedItems.map((e: any) => (
                  <tr key={e.id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{e.event_type?.replace(/([A-Z])/g, ' $1').trim()}</td>
                    <td className="text-sm">{e.farmer_name || '—'}</td>
                    <td className="text-sm">{e.village || '—'}</td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{e.start_date} → {e.end_date}</td>
                    <td><StatusPill tone={e.severity >= 80 ? 'danger' : e.severity >= 60 ? 'warning' : 'info'} dot>{e.severity}/100</StatusPill></td>
                    <td className="nums">{e.risk_score}/10</td>
                    <td className="text-2xs text-ink-600 dark:text-ink-400 max-w-xs truncate" title={e.recommendation}>{e.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {eventsPag.total > 0 && (
          <div className="border-t border-ink-200 dark:border-ink-800">
            <Pagination page={eventsPag.page} pageSize={eventsPag.pageSize} total={eventsPag.total}
              onPageChange={eventsPag.setPage} onPageSizeChange={eventsPag.setPageSize} showPageSize />
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Map Config Card — choose Google Maps or OpenStreetMap for map tiles
 * Google Maps: smoother, satellite imagery, requires API key
 * OpenStreetMap: free, no key, but lower quality in rural Cameroon
 * ═══════════════════════════════════════════════════════════════ */
const MapConfigCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [provider, setProvider] = useState('google');
  const [apiKey, setApiKey] = useState('');
  const [tileType, setTileType] = useState('hybrid');
  const [hasKey, setHasKey] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/settings').catch(() => ({ data: {} })),
    ]).then(([settingsRes]) => {
      const s = settingsRes.data || {};
      setProvider(s.map_tile_provider || 'google');
      setTileType(s.google_maps_tile_type || 'hybrid');
      setHasKey(!!s.google_maps_api_key);
      setApiKey('');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      // Save via settings endpoint
      const updates: any = {
        map_tile_provider: provider,
        google_maps_tile_type: tileType,
      };
      if (apiKey) updates.google_maps_api_key = apiKey;
      await apiClient.put('/settings', updates);
      setSuccess('Map config saved');
      setApiKey('');
      refresh();
      // Reload the page so map components pick up the new tile provider
      setTimeout(() => window.location.reload(), 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-4"><Spinner /></div>;

  return (
    <div>
      <SectionHeader
        eyebrow="Maps"
        title="Map tiles provider"
        subtitle="Google Maps gives smoother, higher-quality satellite imagery for rural Cameroon. OpenStreetMap is free but lower detail."
        icon={<Icon.MapPin className="w-4 h-4" />}
      />
      {error && <div className="mt-3"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>}
      {success && <div className="mt-3"><Alert tone="success">{success}</Alert></div>}

      <div className="mt-4 space-y-3">
        <div>
          <label className="label">Tile provider</label>
          <select className="input" value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="google">Google Maps (recommended — satellite + hybrid + street, requires API key)</option>
            <option value="openstreetmap">OpenStreetMap (free, no key needed, lower detail in rural areas)</option>
          </select>
        </div>

        {provider === 'google' && (
          <>
            <div>
              <label className="label">Google Maps API key{hasKey ? ' (configured — leave blank to keep)' : ' *'}</label>
              <input
                className="input"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={hasKey ? '•••••• (leave blank to keep current)' : 'AIzaSy... (get from Google Cloud Console)'}
              />
              <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">
                Get a free API key at https://console.cloud.google.com/google/maps-apis
                <br />
                Enable: Maps JavaScript API + Maps Static API for this project.
              </p>
            </div>
            <div>
              <label className="label">Default tile type</label>
              <select className="input" value={tileType} onChange={e => setTileType(e.target.value)}>
                <option value="hybrid">Hybrid (satellite + labels + roads — best for field work)</option>
                <option value="satellite">Satellite (pure imagery, no labels)</option>
                <option value="street">Street (roadmap with labels)</option>
                <option value="terrain">Terrain (topographic + roads)</option>
              </select>
            </div>
          </>
        )}

        {provider === 'openstreetmap' && (
          <Alert tone="info">
            OpenStreetMap is free and requires no API key. However, tile quality in rural Cameroon
            may be lower than Google Maps. Switch to Google Maps for the best experience.
          </Alert>
        )}

        <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
          {saving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save map config
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * Products Tab — Cameroon Agro-Chemical Registry + Scan Analytics
 * CC-DTIP v4.0 Phase 19.6
 * ═══════════════════════════════════════════════════════════════ */
const ProductsTab: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [scans, setScans] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [banned, setBanned] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'catalog' | 'scans' | 'banned' | 'ai-finder'>('catalog');
  const productsPag = usePagination(products, 15);
  const scansPag = usePagination(scans, 20);
  // v4.4.7: AI Treatment Finder state
  const [aiFinderSymptoms, setAiFinderSymptoms] = useState('');
  const [aiFinderResult, setAiFinderResult] = useState<any>(null);
  const [aiFinderLoading, setAiFinderLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/agro-products').catch(() => ({ data: { products: [] } })),
      apiClient.get('/agro-products/scans/recent?limit=100').catch(() => ({ data: { scans: [] } })),
      apiClient.get('/agro-products/scans/stats?days=30').catch(() => ({ data: null })),
      apiClient.get('/agro-products/banned/list').catch(() => ({ data: { banned: [] } })),
    ]).then(([prodRes, scanRes, statsRes, bannedRes]) => {
      setProducts(prodRes.data?.products || []);
      setScans(scanRes.data?.scans || []);
      setStats(statsRes.data);
      setBanned(bannedRes.data?.banned || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  // v4.4.7: AI Treatment Finder — runs the DISEASE agent with the user's symptoms
  // The agent queries commercial_products + knowledge base + LLM to recommend
  // specific MINADER-registered products with dosages, PHI, prices.
  const handleAiFinder = async () => {
    if (!aiFinderSymptoms.trim()) return;
    setAiFinderLoading(true);
    setAiFinderResult(null);
    try {
      const res = await apiClient.post('/ai-agents/DISEASE/run', { symptoms: aiFinderSymptoms }, { timeout: 180000 });
      setAiFinderResult(res.data);
    } catch (err: any) {
      const isAuth = err.response?.status === 401;
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      setAiFinderResult({
        success: false,
        error: isAuth ? 'Authentication required — please log in again.'
              : isTimeout ? 'AI treatment finder timed out (180s). The AI provider may be rate-limited. Try again in 60 seconds.'
              : (err.response?.data?.error || err.response?.data?.message || 'AI treatment finder failed'),
      });
    } finally {
      setAiFinderLoading(false);
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {/* Scan stats KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricTile label="Total scans (30d)" value={stats.total_scans || 0} icon={<Icon.Shield className="w-4 h-4" />} accent="admin" />
          <MetricTile label="Verified" value={stats.verified_scans || 0} icon={<Icon.Check className="w-4 h-4" />} />
          <MetricTile label="Counterfeit" value={stats.counterfeit_detected || 0} icon={<Icon.Alert className="w-4 h-4" />} />
          <MetricTile label="Banned detected" value={stats.banned_detected || 0} icon={<Icon.Alert className="w-4 h-4" />} />
          <MetricTile label="Unregistered" value={stats.unregistered_products || 0} icon={<Icon.Alert className="w-4 h-4" />} />
        </div>
      )}

      {/* View toggle */}
      <div className="flex gap-2">
        <button onClick={() => setView('catalog')} className={`text-xs px-3 py-2 rounded-lg font-medium ${view === 'catalog' ? 'bg-admin-500 text-white' : 'bg-ink-100 dark:bg-ink-800'}`}>📋 Product Catalog ({products.length})</button>
        <button onClick={() => setView('scans')} className={`text-xs px-3 py-2 rounded-lg font-medium ${view === 'scans' ? 'bg-admin-500 text-white' : 'bg-ink-100 dark:bg-ink-800'}`}>📱 Scan Log ({scans.length})</button>
        <button onClick={() => setView('banned')} className={`text-xs px-3 py-2 rounded-lg font-medium ${view === 'banned' ? 'bg-admin-500 text-white' : 'bg-ink-100 dark:bg-ink-800'}`}>🚫 Banned ({banned.length})</button>
        <button onClick={() => setView('ai-finder')} className={`text-xs px-3 py-2 rounded-lg font-medium ${view === 'ai-finder' ? 'bg-admin-500 text-white' : 'bg-ink-100 dark:bg-ink-800'}`}>✨ AI Treatment Finder</button>
      </div>

      {/* Catalog view */}
      {view === 'catalog' && (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Trade Name</th><th>Type</th><th>Active Ingredients</th><th>FRAC/IRAC</th><th>MINADER ID</th><th>Dosage</th><th>PHI (days)</th><th>Price (XAF)</th><th>Scans</th></tr></thead>
              <tbody>
                {productsPag.paginatedItems.map((p: any) => (
                  <tr key={p.id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{p.trade_name}</td>
                    <td><StatusPill tone={p.product_type === 'fungicide' ? 'info' : p.product_type === 'insecticide' ? 'warning' : 'success'} dot>{p.product_type}</StatusPill></td>
                    <td className="text-2xs text-ink-600 dark:text-ink-300">{p.active_ingredients}</td>
                    <td className="text-2xs font-mono text-ink-500">{p.frac_irac_code || '—'}</td>
                    <td className="text-2xs font-mono text-ink-500">{p.minader_homologation_id || (p.is_minader_verified ? 'Verified' : '—')}</td>
                    <td className="text-2xs text-ink-600 dark:text-ink-300">{p.dosage_per_15l || p.dosage_per_ha || '—'}</td>
                    <td className="nums">{p.pre_harvest_interval_days}</td>
                    <td className="nums">{p.average_price_xaf ? Number(p.average_price_xaf).toLocaleString() : '—'}</td>
                    <td className="nums">
                      {p.total_scan_count > 0 ? (
                        <span className={p.counterfeit_scan_count > 0 ? 'text-danger-700 dark:text-danger-500 font-medium' : ''}>
                          {p.total_scan_count} ({p.counterfeit_scan_count} ⚠️)
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-ink-200 dark:border-ink-800">
            <Pagination page={productsPag.page} pageSize={productsPag.pageSize} total={productsPag.total}
              onPageChange={productsPag.setPage} onPageSizeChange={productsPag.setPageSize} showPageSize />
          </div>
        </Card>
      )}

      {/* Scans view */}
      {view === 'scans' && (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Time</th><th>Scanner</th><th>Product</th><th>Status</th><th>Counterfeit?</th><th>Region</th><th>Price</th></tr></thead>
              <tbody>
                {scansPag.paginatedItems.map((s: any) => (
                  <tr key={s.id}>
                    <td className="text-2xs text-ink-500">{new Date(s.scanned_at).toLocaleString()}</td>
                    <td className="text-sm">{s.scanner_name || s.farmer_name || '—'}</td>
                    <td className="text-sm">{s.scanned_trade_name || s.matched_product_name || '—'}</td>
                    <td>
                      <StatusPill tone={s.verification_status === 'verified' ? 'success' : s.verification_status === 'banned_substance' ? 'danger' : s.verification_status === 'not_found' ? 'danger' : s.verification_status === 'suspected_counterfeit' ? 'warning' : 'info'} dot>
                        {s.verification_status.replace(/_/g, ' ')}
                      </StatusPill>
                    </td>
                    <td>{s.is_counterfeit ? <span className="text-danger-700 dark:text-danger-500 font-medium">⚠️ Yes</span> : '—'}</td>
                    <td className="text-2xs">{s.scan_region || s.scan_village || '—'}</td>
                    <td className="nums">{s.farmer_reported_price ? Number(s.farmer_reported_price).toLocaleString() + ' XAF' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-ink-200 dark:border-ink-800">
            <Pagination page={scansPag.page} pageSize={scansPag.pageSize} total={scansPag.total}
              onPageChange={scansPag.setPage} onPageSizeChange={scansPag.setPageSize} showPageSize />
          </div>
        </Card>
      )}

      {/* Banned substances view */}
      {view === 'banned' && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="MINADER Arrêté n°00000201 (11 Sep 2023)" title="Banned Active Ingredients" icon={<Icon.Alert className="w-4 h-4" />} />
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Active Ingredient</th><th>Chemical Family</th><th>Ban Date</th><th>Reason</th><th>Affected Products</th><th>Alternatives</th></tr></thead>
              <tbody>
                {banned.map((b: any) => (
                  <tr key={b.id}>
                    <td className="text-sm font-medium text-danger-700 dark:text-danger-500">{b.active_ingredient}</td>
                    <td className="text-2xs text-ink-500">{b.chemical_family || '—'}</td>
                    <td className="text-2xs">{new Date(b.ban_date).toLocaleDateString()}</td>
                    <td className="text-2xs text-ink-600 dark:text-ink-300 max-w-xs">{b.ban_reason}</td>
                    <td className="text-2xs">{b.affected_products?.join(', ') || '—'}</td>
                    <td className="text-2xs text-success-700 dark:text-success-500">{b.alternatives?.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* v4.4.7: AI Treatment Finder — runs the DISEASE agent against the commercial_products registry */}
      {view === 'ai-finder' && (
        <div className="space-y-4">
          <Card padding="md" className="bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
            <div className="text-sm space-y-1">
              <p className="font-semibold text-ink-900 dark:text-ink-50">✨ AI Treatment Finder — DISEASE Agent (SVC-001)</p>
              <p className="text-xs text-ink-600 dark:text-ink-400">
                Describe the symptoms observed on your cocoa trees. The DISEASE agent will:
              </p>
              <ol className="text-xs text-ink-600 dark:text-ink-400 list-decimal list-inside space-y-0.5 ml-2">
                <li>Search the knowledge base (hybrid vector + keyword) for relevant IRAD/CABI disease info</li>
                <li>Query the <strong>commercial_products</strong> registry for MINADER-homologated fungicides + biocontrols</li>
                <li>Diagnose the disease + recommend specific products with dosages, PHI, prices</li>
                <li>Use the highest-ranked configured AI (DeepSeek → Grok → Groq → ... → Gemini system default)</li>
              </ol>
              <p className="text-2xs text-ink-500 mt-1">
                The Products tab is now 100% integrated with the AI system — every treatment recommendation references specific registered products from this catalog.
              </p>
            </div>
          </Card>

          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800 space-y-3">
              <label className="label">Describe the symptoms</label>
              <textarea
                className="input"
                rows={3}
                placeholder="e.g. 'Brown spots on pods, white fungal growth underneath, spreading quickly after rain'"
                value={aiFinderSymptoms}
                onChange={e => setAiFinderSymptoms(e.target.value)}
              />
              <button onClick={handleAiFinder} disabled={aiFinderLoading || !aiFinderSymptoms.trim()} className="btn-primary inline-flex items-center gap-1.5">
                {aiFinderLoading ? <Spinner size="sm" /> : <Icon.Sparkles className="w-4 h-4" />}
                {aiFinderLoading ? 'Diagnosing…' : 'Diagnose + Recommend Treatment'}
              </button>
            </div>

            {aiFinderResult && (
              <div className="p-5 space-y-4">
                {aiFinderResult.success === false ? (
                  <Alert tone="danger">{aiFinderResult.error || 'AI treatment finder failed'}</Alert>
                ) : (
                  <>
                    {/* Diagnosis */}
                    {aiFinderResult.output?.diagnosis && (
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Diagnosis</p>
                        <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{aiFinderResult.output.diagnosis}</p>
                      </div>
                    )}

                    {/* Confidence + severity + model */}
                    <div className="flex flex-wrap gap-3 text-2xs">
                      {aiFinderResult.output?.confidence != null && (
                        <span className="pill-info">Confidence: {aiFinderResult.output.confidence}%</span>
                      )}
                      {aiFinderResult.output?.severity && (
                        <span className={`pill ${aiFinderResult.output.severity === 'high' ? 'bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-500 border-danger-500/20' : aiFinderResult.output.severity === 'medium' ? 'bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-500 border-warning-500/20' : 'bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-500 border-success-500/20'}`}>
                          Severity: {aiFinderResult.output.severity}
                        </span>
                      )}
                      {aiFinderResult.model && (
                        <span className="pill-neutral">Model: {aiFinderResult.model}</span>
                      )}
                      {aiFinderResult.output?.retrieval_count != null && (
                        <span className="pill-neutral">Products retrieved: {aiFinderResult.output.retrieval_count}</span>
                      )}
                      {aiFinderResult.durationMs != null && (
                        <span className="pill-neutral">Duration: {(aiFinderResult.durationMs / 1000).toFixed(1)}s</span>
                      )}
                    </div>

                    {/* Treatment plan */}
                    {Array.isArray(aiFinderResult.output?.treatment_plan) && aiFinderResult.output.treatment_plan.length > 0 && (
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Treatment Plan</p>
                        <div className="space-y-2">
                          {aiFinderResult.output.treatment_plan.map((t: any, i: number) => (
                            <div key={i} className="p-3 bg-ink-50 dark:bg-ink-900 rounded-lg border border-ink-200 dark:border-ink-700">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-sm font-medium">
                                  {t.product_index ? `[${t.product_index}] ` : ''}{t.product_name || t.product || '—'}
                                </span>
                                {t.cost_xaf && <span className="text-2xs text-success-700 dark:text-success-500">{Number(t.cost_xaf).toLocaleString()} XAF</span>}
                              </div>
                              {t.dosage && <p className="text-xs text-ink-600 dark:text-ink-400">Dosage: {t.dosage}</p>}
                              {t.frequency && <p className="text-xs text-ink-600 dark:text-ink-400">Frequency: {t.frequency}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Retrieved products (full commercial_products rows) */}
                    {Array.isArray(aiFinderResult.output?.retrieved_products) && aiFinderResult.output.retrieved_products.length > 0 && (
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Recommended Products (from commercial registry)</p>
                        <div className="overflow-x-auto">
                          <table className="data-table">
                            <thead><tr><th>#</th><th>Trade Name</th><th>Type</th><th>Active Ingredients</th><th>FRAC/IRAC</th><th>MINADER ID</th><th>Dosage</th><th>PHI</th><th>Price</th></tr></thead>
                            <tbody>
                              {aiFinderResult.output.retrieved_products.map((p: any, i: number) => (
                                <tr key={p.id || i}>
                                  <td className="text-2xs font-mono">[{i + 1}]</td>
                                  <td className="text-sm font-medium">{p.trade_name}</td>
                                  <td><StatusPill tone={p.product_type === 'fungicide' ? 'info' : p.product_type === 'insecticide' ? 'warning' : p.product_type === 'biocontrol' ? 'success' : 'neutral'} dot>{p.product_type}</StatusPill></td>
                                  <td className="text-2xs">{p.active_ingredients}</td>
                                  <td className="text-2xs font-mono">{p.frac_irac_code || '—'}</td>
                                  <td className="text-2xs font-mono">{p.minader_homologation_id || '—'}</td>
                                  <td className="text-2xs">{p.dosage_per_15l || p.dosage_per_ha || '—'}</td>
                                  <td className="text-2xs nums">{p.pre_harvest_interval_days}d</td>
                                  <td className="text-2xs nums">{p.average_price_xaf ? Number(p.average_price_xaf).toLocaleString() + ' XAF' : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Preventive advice */}
                    {Array.isArray(aiFinderResult.output?.preventive_advice) && aiFinderResult.output.preventive_advice.length > 0 && (
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1">Preventive Advice</p>
                        <ul className="text-xs text-ink-600 dark:text-ink-400 list-disc list-inside space-y-0.5">
                          {aiFinderResult.output.preventive_advice.map((a: string, i: number) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}

                    {/* Reasoning */}
                    {aiFinderResult.output?.reasoning && (
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-300">Reasoning</summary>
                        <p className="mt-2 p-3 bg-ink-50 dark:bg-ink-900 rounded text-ink-600 dark:text-ink-400">{aiFinderResult.output.reasoning}</p>
                      </details>
                    )}

                    {/* Raw JSON */}
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium text-ink-700 dark:text-ink-300">Raw agent output (JSON)</summary>
                      <pre className="mt-2 p-3 bg-ink-100 dark:bg-ink-800 rounded-lg overflow-x-auto text-2xs font-mono">
                        {JSON.stringify(aiFinderResult, null, 2)}
                      </pre>
                    </details>
                  </>
                )}
              </div>
            )}

            {!aiFinderResult && !aiFinderLoading && (
              <div className="p-8">
                <EmptyState
                  icon={<Icon.Sparkles className="w-5 h-5" />}
                  title="AI Treatment Finder"
                  description="Describe the symptoms above and click 'Diagnose + Recommend Treatment'. The DISEASE agent will search the knowledge base + commercial products registry and recommend specific MINADER-registered treatments."
                />
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Counterfeit by region */}
      {stats?.by_region && stats.by_region.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Spatial intelligence" title="Counterfeit detections by region (30 days)" icon={<Icon.MapPin className="w-4 h-4" />} />
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Region</th><th>Total scans</th><th>Counterfeit</th><th>Banned</th><th>Counterfeit rate</th></tr></thead>
              <tbody>
                {stats.by_region.filter((r: any) => r.region !== 'Unknown').map((r: any, i: number) => (
                  <tr key={i}>
                    <td className="text-sm font-medium">{r.region}</td>
                    <td className="nums">{r.total}</td>
                    <td className="nums text-danger-700 dark:text-danger-500">{r.counterfeit}</td>
                    <td className="nums text-warning-700 dark:text-warning-500">{r.banned}</td>
                    <td className="nums">{r.total > 0 ? Math.round(r.counterfeit / r.total * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.6.0 — Chain of Custody Tab
 * Shows the full farm-to-warehouse traceability chain for every batch.
 * Implements the COCOE Value Chain "Full Traceability" phase.
 * ═══════════════════════════════════════════════════════════════ */
const ChainOfCustodyTab: React.FC = () => {
  const [chainData, setChainData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchBatch, setSearchBatch] = useState('');
  const [traceResult, setTraceResult] = useState<any | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      // v1.6.0 fix: don't make a dummy trace call — just fetch stats + all batches
      const [statsRes, batchesRes] = await Promise.all([
        apiClient.get('/chain/stats').catch(() => ({ data: null })),
        apiClient.get('/batches?limit=50').catch(() => ({ data: [] })),
      ]);
      const allBatches = Array.isArray(batchesRes.data) ? batchesRes.data : (batchesRes.data?.batches || []);
      setChainData(allBatches);
      setStats(statsRes.data?.stats || null);
    } catch (err) {
      console.error('Chain of custody error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleTrace = async (batchId: string) => {
    try {
      const res = await apiClient.get(`/chain/trace/${batchId}`);
      setTraceResult(res.data);
    } catch (err: any) {
      setTraceResult({ error: err.response?.data?.error || 'Trace failed' });
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricTile label="Total Batches" value={stats.total_batches || 0} icon={<Icon.Shield className="w-4 h-4" />} accent="admin" />
          <MetricTile label="Farmer Verified" value={stats.farmer_verified_batches || 0} icon={<Icon.Check className="w-4 h-4" />} />
          <MetricTile label="Decided" value={stats.decided_batches || 0} />
          <MetricTile label="Export" value={stats.export_batches || 0} />
          <MetricTile label="Pending Variance" value={stats.pending_variance_investigations || 0} icon={<Icon.Alert className="w-4 h-4" />} />
        </div>
      )}

      {/* Trace lookup */}
      <Card padding="lg">
        <SectionHeader eyebrow="Full Traceability" title="Chain of Custody Lookup"
          subtitle="Trace any batch from farmer to warehouse to payment" icon={<Icon.Shield className="w-4 h-4" />} />
        <div className="mt-4 flex gap-2">
          <input
            placeholder="Enter batch ID or search…"
            value={searchBatch}
            onChange={(e) => setSearchBatch(e.target.value)}
            className="input flex-1"
          />
          <button
            onClick={() => handleTrace(searchBatch)}
            disabled={!searchBatch}
            className="btn-primary"
          >
            <Icon.Search className="w-4 h-4" /> Trace
          </button>
        </div>

        {traceResult && !traceResult.error && (
          /* v1.7.8: Enterprise 10/10 Chain of Custody trace — visual timeline + detailed cards */
          <div className="mt-6 space-y-5">
            {/* ─── Header card ─── */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-ink-100 to-ink-50 dark:from-ink-800 dark:to-ink-900 border border-ink-200 dark:border-ink-800">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold">Chain of Custody</p>
                  <h3 className="text-lg font-bold text-ink-900 dark:text-ink-50 mt-0.5">{traceResult.chain?.farmer_name || 'Unknown Farmer'}</h3>
                  <p className="text-xs text-ink-500 mt-0.5">
                    {traceResult.chain?.farmer_village || '—'}
                    {traceResult.chain?.farmer_cooperative ? ` · ${traceResult.chain.farmer_cooperative}` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                    traceResult.chain?.current_chain_stage === 'COMPLETED' ? 'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400'
                    : traceResult.chain?.current_chain_stage === 'INTAKED' || traceResult.chain?.current_chain_stage === 'DECIDED' ? 'bg-info-100 text-info-700 dark:bg-info-500/20 dark:text-info-400'
                    : 'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-400'
                  }`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    {traceResult.chain?.current_chain_stage || 'UNKNOWN'}
                  </span>
                  <p className="text-2xs text-ink-400 mt-1">Completeness: {traceResult.chain?.chain_completeness_score}/6</p>
                </div>
              </div>
            </div>

            {/* ─── Key metrics row ─── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800">
                <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold">Farm Area</p>
                <p className="text-sm font-bold text-ink-900 dark:text-ink-50 mt-0.5 nums">{Number(traceResult.chain?.farm_area_ha || 0).toFixed(2)} ha</p>
                <p className="text-2xs text-ink-400">{traceResult.chain?.farm_name || '—'}</p>
              </div>
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800">
                <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold">Quality Grade</p>
                <p className={`text-sm font-bold mt-0.5 ${traceResult.chain?.batch_quality_grade === 'A' ? 'text-success-600 dark:text-success-400' : traceResult.chain?.batch_quality_grade === 'B' ? 'text-warning-600 dark:text-warning-400' : 'text-danger-600 dark:text-danger-400'}`}>
                  Grade {traceResult.chain?.batch_quality_grade || '—'}
                </p>
                <p className="text-2xs text-ink-400">Score: {traceResult.chain?.batch_quality_score || '—'}/100</p>
              </div>
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800">
                <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold">Bags</p>
                <p className="text-sm font-bold text-ink-900 dark:text-ink-50 mt-0.5 nums">{traceResult.chain?.bag_count || 0}</p>
                <p className="text-2xs text-ink-400">{traceResult.chain?.bags_received || 0} received · {Number(traceResult.chain?.bags_total_weight_kg || 0).toFixed(1)} kg</p>
              </div>
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800">
                <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold">Decision</p>
                <p className="text-sm font-bold text-ink-900 dark:text-ink-50 mt-0.5">{traceResult.chain?.batch_decision || 'Pending'}</p>
                <p className="text-2xs text-ink-400">{traceResult.chain?.batch_amount_xaf ? `${Number(traceResult.chain.batch_amount_xaf).toLocaleString()} XAF` : '—'}</p>
              </div>
            </div>

            {/* ─── Visual timeline ─── */}
            {traceResult.timeline && traceResult.timeline.length > 0 && (
              <div className="p-5 rounded-xl bg-ink-0 dark:bg-ink-900 border border-ink-200 dark:border-ink-800">
                <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold mb-4">Chain Timeline</p>
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-ink-200 dark:bg-ink-700" />
                  <div className="space-y-4">
                    {traceResult.timeline.map((event: any, i: number) => (
                      <div key={i} className="relative flex items-start gap-3 pl-0">
                        {/* Timeline dot */}
                        <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          event.stage === 'VARIANCE_DETECTED' ? 'bg-danger-100 dark:bg-danger-500/20 text-danger-600 dark:text-danger-400'
                          : event.stage === 'PAID' || event.stage === 'COMPLETED' ? 'bg-success-100 dark:bg-success-500/20 text-success-600 dark:text-success-400'
                          : 'bg-info-100 dark:bg-info-500/20 text-info-600 dark:text-info-400'
                        }`}>
                          {event.icon === 'user' && <Icon.Users className="w-4 h-4" />}
                          {event.icon === 'map' && <Icon.MapPin className="w-4 h-4" />}
                          {event.icon === 'package' && <Icon.Package className="w-4 h-4" />}
                          {event.icon === 'check' && <Icon.Check className="w-4 h-4" />}
                          {event.icon === 'truck' && <Icon.Truck className="w-4 h-4" />}
                          {event.icon === 'warehouse' && <Icon.Package className="w-4 h-4" />}
                          {event.icon === 'alert' && <Icon.Alert className="w-4 h-4" />}
                          {event.icon === 'shield' && <Icon.Shield className="w-4 h-4" />}
                          {event.icon === 'dollar' && <Icon.Dollar className="w-4 h-4" />}
                        </div>
                        {/* Event content */}
                        <div className="flex-1 pb-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{event.label}</p>
                            {event.timestamp && (
                              <p className="text-2xs text-ink-400 nums whitespace-nowrap">
                                {new Date(event.timestamp).toLocaleDateString()} {new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                          {event.detail && (
                            <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{event.detail}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ─── Bag QR codes ─── */}
            {traceResult.bags?.length > 0 && (
              <div className="p-5 rounded-xl bg-ink-0 dark:bg-ink-900 border border-ink-200 dark:border-ink-800">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold">Bag-level Traceability ({traceResult.bags.length} bags)</p>
                  <div className="flex gap-2 text-2xs">
                    <span className="px-2 py-0.5 rounded bg-success-100 dark:bg-success-500/20 text-success-700 dark:text-success-400 font-medium">{traceResult.chain?.bags_received || 0} received</span>
                    <span className="px-2 py-0.5 rounded bg-info-100 dark:bg-info-500/20 text-info-700 dark:text-info-400 font-medium">{traceResult.chain?.bags_loaded || 0} loaded</span>
                    {(traceResult.chain?.bags_missing || 0) > 0 && (
                      <span className="px-2 py-0.5 rounded bg-danger-100 dark:bg-danger-500/20 text-danger-700 dark:text-danger-400 font-medium">{traceResult.chain?.bags_missing} missing</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {traceResult.bags.map((bag: any) => (
                    <div key={bag.bag_qr} className={`p-2 rounded-lg border text-center ${
                      bag.status === 'RECEIVED' ? 'border-success-300 bg-success-50 dark:bg-success-500/10'
                      : bag.status === 'MISSING' ? 'border-danger-300 bg-danger-50 dark:bg-danger-500/10'
                      : bag.status === 'STORED' ? 'border-info-300 bg-info-50 dark:bg-info-500/10'
                      : 'border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800/50'
                    }`}>
                      <p className="text-2xs font-mono font-bold text-ink-900 dark:text-ink-50 truncate">{bag.bag_qr}</p>
                      <p className="text-2xs text-ink-500 nums">{bag.weight_kg} kg</p>
                      <p className={`text-2xs font-medium mt-0.5 ${
                        bag.status === 'RECEIVED' ? 'text-success-600 dark:text-success-400'
                        : bag.status === 'MISSING' ? 'text-danger-600 dark:text-danger-400'
                        : 'text-ink-400'
                      }`}>{bag.status}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Variance alert ─── */}
            {traceResult.chain?.variance_pct != null && Math.abs(traceResult.chain.variance_pct) > 2 && (
              <div className={`p-4 rounded-xl border-2 ${traceResult.chain.variance_investigated ? 'border-warning-300 bg-warning-50 dark:bg-warning-500/10' : 'border-danger-300 bg-danger-50 dark:bg-danger-500/10'}`}>
                <div className="flex items-start gap-3">
                  <Icon.Alert className={`w-5 h-5 mt-0.5 ${traceResult.chain.variance_investigated ? 'text-warning-600 dark:text-warning-400' : 'text-danger-600 dark:text-danger-400'}`} />
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${traceResult.chain.variance_investigated ? 'text-warning-700 dark:text-warning-400' : 'text-danger-700 dark:text-danger-400'}`}>
                      Weight Variance {traceResult.chain.variance_investigated ? '(Investigated)' : '(UNINVESTIGATED)'}
                    </p>
                    <p className="text-2xs text-ink-600 dark:text-ink-400 mt-1">
                      Expected: {Number(traceResult.chain.expected_weight_kg || 0).toFixed(1)} kg ·
                      Received: {Number(traceResult.chain.received_weight_kg || 0).toFixed(1)} kg ·
                      Variance: {Number(traceResult.chain.variance_kg || 0).toFixed(1)} kg ({Number(traceResult.chain.variance_pct || 0).toFixed(2)}%)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {traceResult?.error && (
          <Alert tone="danger" className="mt-4">{traceResult.error}</Alert>
        )}
      </Card>

      {/* All batches with chain status */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="All batches" title="Chain Status" subtitle="Current stage in the value chain for every batch" />
        </div>
        {chainData.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No batches" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Batch ID</th><th>Farmer</th><th>Grade</th><th>Verified</th><th>Decision</th><th>Payment</th><th>Action</th></tr></thead>
              <tbody>
                {chainData.map((b: any) => (
                  <tr key={b.id}>
                    <td className="text-xs font-mono">{b.id?.slice(0, 8)}…</td>
                    <td className="text-sm">{b.farmer_name || '—'}</td>
                    <td><StatusPill tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.quality_grade || '—'}</StatusPill></td>
                    <td>{b.farmer_verified ? <span className="text-success-600 dark:text-success-500 text-xs">✓ Verified</span> : <span className="text-ink-400 text-xs">Pending</span>}</td>
                    <td>{b.batch_decision ? <StatusPill tone={b.batch_decision === 'EXPORT' ? 'success' : b.batch_decision === 'REJECT' ? 'danger' : 'info'}>{b.batch_decision}</StatusPill> : <span className="text-ink-400 text-xs">—</span>}</td>
                    <td><SmartStatus status={b.payment_status} /></td>
                    <td>
                      <button onClick={() => { setSearchBatch(b.id); handleTrace(b.id); }} className="btn-secondary text-xs">
                        <Icon.Search className="w-3 h-3" /> Trace
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
 * v3.2.21: KnowledgeToolsHealthPanel
 * ───────────────────────────────────
 * Shows the current state of the knowledge tools pipeline:
 *   - pgvector extension installed?
 *   - documents / chunks / embeddings counts
 *   - knowledge graph nodes / edges
 *   - commercial products count
 *   - AI keys configured (esp. HuggingFace / OpenRouter for embeddings)
 *
 * Provides a "Generate Missing Embeddings" button that calls
 * POST /api/ai/knowledge/generate-embeddings to backfill any chunks
 * that don't yet have a vector embedding. This addresses the
 * orchestrator's knowledge_tools_health finding:
 *   "no embeddings generated (semantic search unavailable)"
 *   "pgvector extension not installed (vector search unavailable)"
 * ════════════════════════════════════════════════════════════════ */
const KnowledgeToolsHealthPanel: React.FC = () => {
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/ai/knowledge/health');
      setHealth(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load knowledge tools health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleGenerate = async () => {
    if (!confirm('Generate embeddings for all chunks missing them? This may take a few minutes for large document sets.')) return;
    setGenerating(true);
    setGenResult(null);
    setError(null);
    try {
      const res = await apiClient.post('/ai/knowledge/generate-embeddings');
      setGenResult(res.data);
      refresh(); // refresh health after generation
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to generate embeddings');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Card padding="lg">
        <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
          <Spinner size="sm" /> <span>Checking knowledge tools health…</span>
        </div>
      </Card>
    );
  }

  if (error && !health) {
    return (
      <Card padding="lg">
        <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>
      </Card>
    );
  }

  const h = health || {};
  const stats = [
    { label: 'Documents', value: h.documents ?? '?', tone: h.documents > 0 ? 'success' : 'danger' },
    { label: 'Chunks', value: h.chunks ?? '?', tone: h.chunks > 0 ? 'success' : 'danger' },
    { label: 'Embeddings', value: h.embeddings ?? '?', tone: h.embeddings > 0 ? 'success' : (h.chunks > 0 ? 'warning' : 'danger') },
    { label: 'Graph Nodes', value: h.graph_nodes ?? '?', tone: h.graph_nodes > 0 ? 'success' : 'warning' },
    { label: 'Graph Edges', value: h.graph_edges ?? '?', tone: h.graph_edges > 0 ? 'success' : 'warning' },
    { label: 'Products', value: h.commercial_products ?? '?', tone: h.commercial_products > 0 ? 'success' : 'warning' },
    { label: 'pgvector', value: h.pgvector_installed ? 'Installed' : 'Missing', tone: h.pgvector_installed ? 'success' : 'danger' },
    { label: 'Embedding Key', value: h.ai_keys_configured?.huggingface || h.ai_keys_configured?.openrouter ? 'Configured' : 'Missing', tone: (h.ai_keys_configured?.huggingface || h.ai_keys_configured?.openrouter) ? 'success' : 'danger' },
  ];

  return (
    <Card padding="lg">
      <SectionHeader
        eyebrow="v3.2.21 · Knowledge tools health"
        title="Knowledge Tools Health"
        subtitle="Pipeline state for RAG, vector search, and the knowledge graph. The orchestrator's knowledge_tools_health check verifies these."
        icon={<Icon.Database className="w-4 h-4" />}
        actions={
          <button
            onClick={handleGenerate}
            disabled={generating || !h.ai_keys_configured?.huggingface && !h.ai_keys_configured?.openrouter}
            className="btn-accent text-xs inline-flex items-center gap-1.5"
            title={!h.ai_keys_configured?.huggingface && !h.ai_keys_configured?.openrouter ? 'Configure a HuggingFace or OpenRouter API key first' : 'Generate embeddings for all chunks missing them'}
          >
            {generating ? <><Spinner size="sm" /> <span>Generating…</span></> : <><Icon.Sparkles className="w-3 h-3" /> <span>Generate Missing Embeddings</span></>}
          </button>
        }
      />

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {stats.map((s, i) => (
          <div key={i} className={`p-3 rounded-lg border ${s.tone === 'success' ? 'border-success-300 dark:border-success-700 bg-success-50 dark:bg-success-900/20' : s.tone === 'warning' ? 'border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20' : 'border-danger-300 dark:border-danger-700 bg-danger-50 dark:bg-danger-900/20'}`}>
            <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">{s.label}</p>
            <p className={`text-lg font-semibold ${s.tone === 'success' ? 'text-success-700 dark:text-success-400' : s.tone === 'warning' ? 'text-warning-700 dark:text-warning-400' : 'text-danger-700 dark:text-danger-400'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {h.unembedded_chunks > 0 && (
        <div className="mt-3 p-3 rounded-lg border border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20 text-sm text-warning-800 dark:text-warning-300">
          <strong>{h.unembedded_chunks}</strong> chunk(s) are missing embeddings. Click "Generate Missing Embeddings" above to backfill them using {h.ai_keys_configured?.huggingface ? 'HuggingFace BGE-M3' : h.ai_keys_configured?.openrouter ? 'OpenRouter bge-m3:free' : 'a configured embedding API'}.
        </div>
      )}

      {h.issues && h.issues.length > 0 && (
        <div className="mt-3">
          <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400 mb-1.5">Issues ({h.issues.length})</p>
          <ul className="space-y-1 text-sm text-ink-700 dark:text-ink-300">
            {h.issues.map((issue: string, i: number) => (
              <li key={i} className="flex gap-2">
                <span className="text-warning-600 dark:text-warning-500 shrink-0">⚠</span>
                <span>{issue}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {genResult && (
        <div className="mt-3 p-3 rounded-lg border border-success-300 dark:border-success-700 bg-success-50 dark:bg-success-900/20 text-sm">
          <p className="font-semibold text-success-800 dark:text-success-300">Embedding generation complete</p>
          <p className="text-success-700 dark:text-success-400 mt-1">
            Generated: <strong>{genResult.generated}</strong> · Failed: <strong>{genResult.failed}</strong> · Attempted: <strong>{genResult.attempted}</strong> · Model: <strong>{genResult.model_used}</strong>
          </p>
          {genResult.remaining > 0 && (
            <p className="text-warning-700 dark:text-warning-400 mt-1">{genResult.remaining} chunk(s) still missing embeddings — click the button again to process the next batch.</p>
          )}
          {genResult.errors && genResult.errors.length > 0 && (
            <details className="mt-2">
              <summary className="text-2xs text-ink-500 dark:text-ink-400 cursor-pointer">First {genResult.errors.length} error(s)</summary>
              <ul className="mt-1 space-y-1 text-2xs text-danger-700 dark:text-danger-400">
                {genResult.errors.map((e: string, i: number) => <li key={i}>• {e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {error && <div className="mt-3"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>}
    </Card>
  );
};

/* ════════════════════════════════════════════════════════════════
 * v3.10.2: TraceabilityTab — CNCC Waybills + Checkpoints + Warehouse Bins
 * ════════════════════════════════════════════════════════════════ */
const TraceabilityTab: React.FC = () => {
  const [waybills, setWaybills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ waybill_number: '', transporter_license_plate: '', driver_national_id: '', origin_cooperative_id: '', destination_exporter_warehouse: '', departure_timestamp: '', estimated_arrival_date: '' });

  const fetchWaybills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/traceability/waybills');
      setWaybills(res.data?.waybills || []);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchWaybills(); }, [fetchWaybills]);

  const createWaybill = async () => {
    try {
      await apiClient.post('/traceability/waybills', form);
      setShowCreate(false);
      setForm({ waybill_number: '', transporter_license_plate: '', driver_national_id: '', origin_cooperative_id: '', destination_exporter_warehouse: '', departure_timestamp: '', estimated_arrival_date: '' });
      fetchWaybills();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to create'); }
  };

  return (
    <div className="space-y-4">
      <SectionHeader eyebrow="EUDR Articles 3 & 12" title="Traceability & Logistics" subtitle="CNCC waybills, checkpoint scans, warehouse bin segregation, and EU TRACES XML export." icon={<Icon.Send className="w-4 h-4" />}
        actions={<button onClick={() => setShowCreate(!showCreate)} className="btn-accent text-xs">{showCreate ? 'Cancel' : 'New Waybill'}</button>} />
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {showCreate && (
        <Card padding="lg">
          <SectionHeader eyebrow="New CNCC Waybill" title="Create Transport Waybill" />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <input className="input" placeholder="Waybill Number (e.g. BTRAN-2026-CMR-001)" value={form.waybill_number} onChange={e => setForm({...form, waybill_number: e.target.value})} />
            <input className="input" placeholder="License Plate" value={form.transporter_license_plate} onChange={e => setForm({...form, transporter_license_plate: e.target.value})} />
            <input className="input" placeholder="Driver National ID" value={form.driver_national_id} onChange={e => setForm({...form, driver_national_id: e.target.value})} />
            <input className="input" placeholder="Origin Cooperative ID" value={form.origin_cooperative_id} onChange={e => setForm({...form, origin_cooperative_id: e.target.value})} />
            <input className="input" placeholder="Destination Warehouse" value={form.destination_exporter_warehouse} onChange={e => setForm({...form, destination_exporter_warehouse: e.target.value})} />
            <input className="input" type="datetime-local" value={form.departure_timestamp} onChange={e => setForm({...form, departure_timestamp: e.target.value})} />
            <input className="input" type="date" value={form.estimated_arrival_date} onChange={e => setForm({...form, estimated_arrival_date: e.target.value})} />
          </div>
          <button onClick={createWaybill} className="btn-accent mt-3 text-sm">Create Waybill</button>
        </Card>
      )}
      {loading ? <Spinner /> : waybills.length === 0 ? (
        <Card padding="lg"><EmptyState icon={<Icon.Send className="w-5 h-5" />} title="No waybills yet" description="Create a CNCC waybill to start tracking cocoa transit." /></Card>
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Waybill #</th><th>Truck</th><th>Driver</th><th>Origin</th><th>Destination</th><th>Departure</th><th>Items</th><th>Weight (kg)</th><th>Status</th></tr></thead>
              <tbody>
                {waybills.map((w: any) => (
                  <tr key={w.waybill_number}>
                    <td className="font-medium">{w.waybill_number}</td>
                    <td>{w.transporter_license_plate}</td>
                    <td className="text-xs">{w.driver_national_id}</td>
                    <td className="text-xs">{w.origin_cooperative_id || '—'}</td>
                    <td className="text-xs">{w.destination_exporter_warehouse || '—'}</td>
                    <td className="text-xs">{new Date(w.departure_timestamp).toLocaleDateString()}</td>
                    <td className="nums">{w.item_count || 0}</td>
                    <td className="nums">{w.total_weight_kg ? Number(w.total_weight_kg).toFixed(1) : '0'}</td>
                    <td><StatusPill tone={w.is_active_transit ? 'info' : 'success'} dot>{w.is_active_transit ? 'In Transit' : 'Arrived'}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
 * v3.10.2: MarketPricesTab — ONCC cocoa price data
 * ════════════════════════════════════════════════════════════════ */
const MarketPricesTab: React.FC = () => {
  const [prices, setPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get('/advisory-timeline/performance').catch(() => ({})); // warm up
    // Fetch from market_prices via a direct query endpoint
    apiClient.get('/eudr/carbon-monetization').catch(() => ({})); // just to test connectivity
    // Use the data-sources sync endpoint to check if ONCC has been synced
    apiClient.get('/data-sources/sync-log?source_code=DS003').then(res => {
      setPrices(res.data?.logs || []);
      apiClient.get('/data-sources/oncc-prices').then(r => { if (r.data) setOnccPrices(r.data); }).catch(() => {});
      setLoading(false);
    }).catch(() => {
      // Fallback: show instructions
      setLoading(false);
    });
  }, []);

    // v3.11.40: Real-time ONCC prices
  const [onccPrices, setOnccPrices] = useState<any>({ cif: 3168, fob: 3091, douala_min: 2650, douala_max: 2700, scraped_at: null, is_fallback: true });
  useEffect(() => {
    apiClient.get('/data-sources/oncc-prices').then(res => { if (res.data) setOnccPrices(res.data); }).catch(() => {});
  }, []);
  const syncONCC = async () => {
    setLoading(true);
    try {
      await apiClient.post('/data-sources/sync', { source_code: 'DS003' });
      setError(null);
      // Refresh
      apiClient.get('/data-sources/sync-log?source_code=DS003').then(res => {
        setPrices(res.data?.logs || []);
        setLoading(false);
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Sync failed');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader eyebrow="ONCC Cameroon" title="Cocoa Market Prices" subtitle="Official daily cocoa prices from the National Cocoa and Coffee Board (ONCC). Prices are scraped from oncc.cm/resources/c/cocoa-prices." icon={<Icon.Dollar className="w-4 h-4" />}
        actions={<button onClick={syncONCC} disabled={loading} className="btn-accent text-xs">{loading ? 'Syncing...' : 'Sync ONCC Prices'}</button>} />
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <Spinner /> : (
        <Card padding="lg">
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-ink-100 dark:bg-ink-800">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">How to get price data</p>
              <p className="text-xs text-ink-600 dark:text-ink-400 mt-1">Click "Sync ONCC Prices" above. The system will fetch live prices from oncc.cm and store them in the market_prices table. The MARKET agent and Smart Advisory will then use real prices for sell/hold recommendations.</p>
              <p className="text-xs text-ink-600 dark:text-ink-400 mt-2">Prices update automatically when you sync. Last synced: {onccPrices.scraped_at ? new Date(onccPrices.scraped_at).toLocaleString() : 'never'}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700">
                <p className="text-2xs uppercase tracking-micro text-ink-500">CIF Price</p>
                <p className="text-lg font-bold text-ink-900 dark:text-ink-50">{(onccPrices.cif || 3168).toLocaleString()} <span className="text-xs text-ink-500">XAF/kg</span></p>
              </div>
              <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700">
                <p className="text-2xs uppercase tracking-micro text-ink-500">FOB Price</p>
                <p className="text-lg font-bold text-ink-900 dark:text-ink-50">{(onccPrices.fob || 3091).toLocaleString()} <span className="text-xs text-ink-500">XAF/kg</span></p>
              </div>
              <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700">
                <p className="text-2xs uppercase tracking-micro text-ink-500">Douala Port</p>
                <p className="text-lg font-bold text-ink-900 dark:text-ink-50">{(onccPrices.douala_min || 2650).toLocaleString()}-{(onccPrices.douala_max || 2700).toLocaleString()} <span className="text-xs text-ink-500">XAF/kg</span></p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
 * v3.10.2: CarbonCreditsTab — real carbon measurement + monetization
 * ════════════════════════════════════════════════════════════════ */
const CarbonCreditsTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/eudr/carbon-monetization');
      setData(res.data);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      <SectionHeader eyebrow="EUDR Article 9" title="Carbon Credits & Monetization" subtitle="Real carbon stock measurement (AGB/BGB/SOC), CO2 equivalent, market value, and farmer sustainability bonuses." icon={<Icon.Award className="w-4 h-4" />}
        actions={<button onClick={refresh} className="btn-secondary text-xs">Refresh</button>} />
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <Spinner /> : data ? (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Card padding="md"><p className="text-2xs uppercase tracking-micro text-ink-500">Total CO2 Credits</p><p className="text-xl font-bold text-ink-900 dark:text-ink-50">{data.total_co2_credits?.toFixed(1) || 0} <span className="text-xs text-ink-500">tCO2e</span></p></Card>
            <Card padding="md"><p className="text-2xs uppercase tracking-micro text-ink-500">Market Value</p><p className="text-xl font-bold text-green-700 dark:text-green-400">€{data.total_market_value_eur?.toFixed(2) || 0}</p></Card>
            <Card padding="md"><p className="text-2xs uppercase tracking-micro text-ink-500">Farmer Bonuses</p><p className="text-xl font-bold text-ink-900 dark:text-ink-50">{(data.total_farmer_bonus_xaf || 0).toLocaleString()} <span className="text-xs text-ink-500">XAF</span></p></Card>
            <Card padding="md"><p className="text-2xs uppercase tracking-micro text-ink-500">Degradation Flags</p><p className="text-xl font-bold text-red-700 dark:text-red-400">{data.degradation_flagged || 0}</p></Card>
          </div>
          {data.farms && data.farms.length > 0 ? (
            <Card padding="none" className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Farm</th><th>Farmer</th><th>Year</th><th>AGB (tC/ha)</th><th>Total Carbon (t)</th><th>CO2 (t)</th><th>Value (€)</th><th>Farmer Bonus (XAF)</th><th>Status</th></tr></thead>
                  <tbody>
                    {data.farms.map((f: any, i: number) => (
                      <tr key={i}>
                        <td className="font-medium">{f.farm_name}</td>
                        <td>{f.farmer_name}</td>
                        <td className="nums">{f.monitoring_year}</td>
                        <td className="nums">{parseFloat(f.above_ground_biomass_tc_ha).toFixed(2)}</td>
                        <td className="nums">{parseFloat(f.total_stored_carbon_tonnes).toFixed(2)}</td>
                        <td className="nums">{parseFloat(f.accumulated_co2_equivalent_tons).toFixed(2)}</td>
                        <td className="nums">€{parseFloat(f.estimated_market_value_eur || 0).toFixed(2)}</td>
                        <td className="nums">{parseFloat(f.farmer_sustainability_bonus_xaf || 0).toLocaleString()}</td>
                        <td><StatusPill tone={f.is_degradation_flagged ? 'danger' : f.certification_status === 'CERTIFIED_ASSET' ? 'success' : 'warning'} dot>{f.certification_status}</StatusPill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card padding="lg"><EmptyState icon={<Icon.Award className="w-5 h-5" />} title="No carbon data yet" description="Log annual carbon stock measurements via POST /api/eudr/carbon/log to start tracking carbon credits." /></Card>
          )}
        </>
      ) : null}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
 * v3.10.2: BiomassTab — biomass waste ledger + fraud detection
 * ════════════════════════════════════════════════════════════════ */
const BiomassTab: React.FC = () => {
  return (
    <div className="space-y-4">
      <SectionHeader eyebrow="EUDR Article 9" title="Biomass Waste Ledger" subtitle="Tracks pod husks, pulp juice, and carbon offset per harvest. Auto-detects supply chain mixing fraud if bean weight exceeds plot biomass capacity." icon={<Icon.Leaf className="w-4 h-4" />} />
      <Card padding="lg">
        <div className="p-4 rounded-lg bg-ink-100 dark:bg-ink-800">
          <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">How Biomass Tracking Works</p>
          <div className="mt-2 space-y-2 text-xs text-ink-600 dark:text-ink-400">
            <p>1. When a farmer delivers wet beans, log the weight via <code className="text-ink-800 dark:text-ink-200">POST /api/eudr/biomass/log</code></p>
            <p>2. The system auto-calculates: Pod Husk (5:1 ratio), Pulp Juice (0.66:1), Dry Beans (40%), Carbon Offset (9% of husks)</p>
            <p>3. <strong>Fraud Detection:</strong> If the total annual husk waste exceeds the plot's capacity (3,250 kg/ha), the INSERT is <span className="text-red-600 dark:text-red-400 font-semibold">REJECTED</span> with "Potential fraudulent bean inflation detected"</p>
            <p>4. This prevents supply chain mixing — the #1 EUDR audit failure</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-3">
          <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700 text-center">
            <p className="text-2xs uppercase tracking-micro text-ink-500">Pod Husk Ratio</p>
            <p className="text-lg font-bold text-ink-900 dark:text-ink-50">5:1</p>
            <p className="text-2xs text-ink-400">to wet beans</p>
          </div>
          <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700 text-center">
            <p className="text-2xs uppercase tracking-micro text-ink-500">Pulp Juice Ratio</p>
            <p className="text-lg font-bold text-ink-900 dark:text-ink-50">0.66:1</p>
            <p className="text-2xs text-ink-400">to wet beans</p>
          </div>
          <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700 text-center">
            <p className="text-2xs uppercase tracking-micro text-ink-500">Dry Bean Recovery</p>
            <p className="text-lg font-bold text-ink-900 dark:text-ink-50">40%</p>
            <p className="text-2xs text-ink-400">of wet beans</p>
          </div>
          <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700 text-center">
            <p className="text-2xs uppercase tracking-micro text-ink-500">Max Husk/Ha/Year</p>
            <p className="text-lg font-bold text-red-700 dark:text-red-400">3,250 kg</p>
            <p className="text-2xs text-ink-400">fraud threshold</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;

/* ═══════════════════════════════════════════════════════════════
 * v2.0.0 — Regulatory API Gateway Tab
 * Admin manages API tokens for government ministries
 * ═══════════════════════════════════════════════════════════════ */
const RegulatoryApiTab: React.FC = () => {
  const [tokens, setTokens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [form, setForm] = useState({ agency_name: '', agency_type: 'MINADER', contact_email: '', scopes: 'soil,quality,export' });

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/regulatory/tokens');
      setTokens(res.data?.tokens || []);
    } catch { setTokens([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      const res = await apiClient.post('/regulatory/tokens', form);
      setNewToken(res.data.raw_token);
      setSuccess(`Token created for ${form.agency_name}`);
      setShowCreate(false);
      setForm({ agency_name: '', agency_type: 'MINADER', contact_email: '', scopes: 'soil,quality,export' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to create token'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  const AGENCIES = [
    { value: 'MINADER', label: 'MINADER — Ministry of Agriculture' },
    { value: 'NCCB', label: 'NCCB — National Cocoa & Coffee Board' },
    { value: 'MINEE', label: 'MINEE — Ministry of Energy & Water' },
    { value: 'MINCOMMERCE', label: 'MINCOMMERCE — Ministry of Commerce' },
    { value: 'MINAS', label: 'MINAS — Ministry of Social Affairs' },
    { value: 'MINTSS', label: 'MINTSS — Ministry of Labour' },
    { value: 'MINT', label: 'MINT — Ministry of Transport' },
    { value: 'IRAD', label: 'IRAD — Agricultural Research Institute' },
  ];

  const ENDPOINTS = [
    { method: 'GET', path: '/api/regulatory/minader/soil-report', desc: 'Soil degradation + farm area + farmer stats' },
    { method: 'GET', path: '/api/regulatory/nccb/quality-report', desc: 'PAH, cadmium, ochratoxin, smoke, export compliance' },
    { method: 'GET', path: '/api/regulatory/minee/energy-report', desc: 'Energy + manufacturing (empty until factory)' },
    { method: 'GET', path: '/api/regulatory/mincommerce/export-data', desc: 'Export volumes, DDS, trade value' },
    { method: 'GET', path: '/api/regulatory/minas/social-report', desc: 'Child labor, gender equity, income data' },
    { method: 'GET', path: '/api/regulatory/mint/logistics-report', desc: 'Transit, checkpoint delays, port clearance' },
    { method: 'GET', path: '/api/regulatory/full-dashboard', desc: 'Complete MEL overview (all 40+ metrics)' },
  ];

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Info */}
      <Card padding="lg" className="bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
        <div className="flex items-start gap-3">
          <span className="text-3xl">🏛️</span>
          <div>
            <p className="text-sm font-bold text-ink-900 dark:text-ink-50">National Authority Data Node</p>
            <p className="text-xs text-ink-600 dark:text-ink-400 mt-1">
              This is the regulatory API gateway. Government ministries (MINADER, NCCB, MINEE, MINCOMMERCE, MINAS, MINT)
              can access anonymized, aggregate data from your system via secure API tokens. Once they integrate,
              your system becomes the official data provider for the Cameroon cocoa sector.
            </p>
          </div>
        </div>
      </Card>

      {/* New token display */}
      {newToken && (
        <Card padding="lg" className="border-2 border-success-300 bg-success-50 dark:bg-success-500/10">
          <p className="text-sm font-bold text-success-700 dark:text-success-400 mb-2">✅ Token Created — Copy Now!</p>
          <p className="text-xs text-ink-600 dark:text-ink-400 mb-2">This token will NOT be shown again. Share it securely with the agency.</p>
          <code className="block p-3 bg-ink-100 dark:bg-ink-800 rounded-lg text-xs font-mono break-all">{newToken}</code>
          <button onClick={() => { navigator.clipboard.writeText(newToken); setSuccess('Token copied to clipboard!'); }} className="btn-primary text-xs mt-2">Copy Token</button>
          <button onClick={() => setNewToken(null)} className="btn-secondary text-xs mt-2 ml-2">Dismiss</button>
        </Card>
      )}

      {/* API Endpoints reference */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Government Integration" title="Available API Endpoints" subtitle="Share these URLs with ministries for integration" icon={<Icon.Shield className="w-4 h-4" />} />
        </div>
        <div className="divide-y divide-ink-100 dark:divide-ink-800">
          {ENDPOINTS.map((ep, i) => (
            <div key={i} className="p-4 flex items-center gap-3">
              <span className="text-2xs font-bold px-2 py-1 rounded bg-info-100 dark:bg-info-500/20 text-info-700 dark:text-info-400">{ep.method}</span>
              <code className="text-xs font-mono text-ink-700 dark:text-ink-300 flex-1">{ep.path}</code>
              <span className="text-2xs text-ink-500 hidden md:inline">{ep.desc}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Tokens table */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Access Management" title="API Tokens" subtitle="Tokens issued to government agencies" icon={<Icon.Shield className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Plus className="w-4 h-4" /> Issue Token</button>
        </div>
        {tokens.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No tokens issued" description="Issue a token to a government agency to grant them read-only API access." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Agency</th><th>Type</th><th>Contact</th><th>Scopes</th><th>Last Access</th><th>Status</th></tr></thead>
              <tbody>
                {tokens.map(t => (
                  <tr key={t.id}>
                    <td className="text-sm font-medium">{t.agency_name}</td>
                    <td><StatusPill tone="info" dot>{t.agency_type}</StatusPill></td>
                    <td className="text-2xs">{t.contact_email || '---'}</td>
                    <td className="text-2xs">{t.scopes || 'all'}</td>
                    <td className="text-2xs">{t.last_access_at ? new Date(t.last_access_at).toLocaleString() : 'Never'}</td>
                    <td>{t.is_active ? <span className="text-success-600 text-xs">✓ Active</span> : <span className="text-danger-600 text-xs">Revoked</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Issue Regulatory API Token"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.agency_name} className="btn-primary flex-1">Create Token</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Agency Name *</label><input className="input" value={form.agency_name} onChange={e => setForm({ ...form, agency_name: e.target.value })} placeholder="e.g. MINADER Soil Bureau" /></div>
            <div><label className="label">Agency Type *</label><select className="input" value={form.agency_type} onChange={e => setForm({ ...form, agency_type: e.target.value })}>{AGENCIES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}</select></div>
            <div><label className="label">Contact Email</label><input type="email" className="input" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@minader.gov.cm" /></div>
            <div><label className="label">Scopes (comma-separated)</label><input className="input" value={form.scopes} onChange={e => setForm({ ...form, scopes: e.target.value })} placeholder="soil,quality,export" /></div>
            <Alert tone="info">The token grants read-only access to the specified data scopes. The agency must include it as the <code>X-Regulatory-Token</code> header in all API requests.</Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.9.2 — Points Rules Tab (admin configures sustainability points)
 * ═══════════════════════════════════════════════════════════════ */
const PointsRulesTab: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/post-harvest/points-rules');
      setRules(res.data?.rules || []);
    } catch { setRules([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const toggleActive = async (ruleId: string, currentActive: boolean) => {
    try {
      await apiClient.patch(`/post-harvest/points-rules/${ruleId}`, { is_active: !currentActive });
      setSuccess(`Rule ${!currentActive ? 'activated' : 'deactivated'}`);
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to update rule'); }
  };

  const updatePoints = async (ruleId: string, newPoints: number) => {
    try {
      await apiClient.patch(`/post-harvest/points-rules/${ruleId}`, { points: newPoints });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to update points'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Alert tone="info">Configure sustainability points awarded to farmers for good post-harvest practices. Changes take effect immediately for all new activities.</Alert>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Admin Configuration" title="Sustainability Points Rules" subtitle="Configure how many points each practice earns" icon={<Icon.Award className="w-4 h-4" />} />
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Rule</th><th>Description</th><th>Activity</th><th>Condition</th><th>Points</th><th>Active</th><th>Action</th></tr></thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} className={!r.is_active ? 'opacity-50' : ''}>
                  <td className="text-sm font-medium">{r.rule_name}</td>
                  <td className="text-2xs text-ink-500 max-w-xs truncate">{r.description || '---'}</td>
                  <td><StatusPill tone="info" dot>{r.activity_type || 'ALL'}</StatusPill></td>
                  <td className="text-2xs">{r.condition_field ? `${r.condition_field} ${r.condition_operator} ${r.condition_value}` : 'Base points'}</td>
                  <td className="nums">
                    <input
                      type="number"
                      defaultValue={r.points}
                      onBlur={(e) => { const v = parseInt(e.target.value); if (v !== r.points) updatePoints(r.id, v); }}
                      className="input text-sm w-20 py-1"
                    />
                  </td>
                  <td>{r.is_active ? <span className="text-success-600 text-xs">✓ Active</span> : <span className="text-ink-400 text-xs">Inactive</span>}</td>
                  <td><button onClick={() => toggleActive(r.id, r.is_active)} className="btn-secondary text-xs">{r.is_active ? 'Deactivate' : 'Activate'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.9.2 — MEL Overview Tab (comprehensive system-wide tracing + AI analysis)
 * ═══════════════════════════════════════════════════════════════ */
const MELOverviewTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/post-harvest/mel-overview');
      setData(res.data?.overview || null);
      setAnalysis(res.data?.ai_analysis || null);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;
  if (!data) return <Card padding="lg"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No data available" /></Card>;

  return (
    <div className="space-y-4">
      {/* AI Analysis */}
      {analysis && (
        <div className="space-y-3">
          {analysis.insights?.length > 0 && (
            <Card padding="md" className="bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
              <p className="text-sm font-semibold mb-2">📊 Key Insights</p>
              <ul className="space-y-1">
                {analysis.insights.map((insight: string, i: number) => (
                  <li key={i} className="text-xs text-ink-700 dark:text-ink-300 flex items-start gap-2">
                    <span className="text-info-500">•</span> {insight}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {analysis.risks?.length > 0 && (
            <Card padding="md" className="bg-danger-50 dark:bg-danger-500/10 border-danger-200 dark:border-danger-500/30">
              <p className="text-sm font-semibold mb-2 text-danger-700 dark:text-danger-400">⚠️ Risk Flags</p>
              <ul className="space-y-1">
                {analysis.risks.map((risk: string, i: number) => (
                  <li key={i} className="text-xs text-ink-700 dark:text-ink-300 flex items-start gap-2">
                    <span className="text-danger-500">•</span> {risk}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {analysis.recommendations?.length > 0 && (
            <Card padding="md" className="bg-success-50 dark:bg-success-500/10 border-success-200 dark:border-success-500/30">
              <p className="text-sm font-semibold mb-2 text-success-700 dark:text-success-400">💡 Recommendations</p>
              <ul className="space-y-1">
                {analysis.recommendations.map((rec: string, i: number) => (
                  <li key={i} className="text-xs text-ink-700 dark:text-ink-300 flex items-start gap-2">
                    <span className="text-success-500">•</span> {rec}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {analysis.summary && (
            <Card padding="md">
              <p className="text-sm font-semibold mb-1">📋 Performance Summary</p>
              <p className="text-xs text-ink-600 dark:text-ink-400">{analysis.summary}</p>
            </Card>
          )}
        </div>
      )}

      {/* Comprehensive metrics grid */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="System-wide tracing" title="MEL Comprehensive Overview" subtitle="Every database table aggregated into one view" icon={<Icon.Database className="w-4 h-4" />} />
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Farmer Registry */}
          <MetricTile label="Total Farmers" value={data.total_farmers || 0} icon={<Icon.Users className="w-4 h-4" />} />
          <MetricTile label="Consented" value={data.consented_farmers || 0} />
          <MetricTile label="Total Farms" value={data.total_farms || 0} />
          <MetricTile label="Farm Area (ha)" value={Number(data.total_farm_area_ha || 0).toFixed(1)} />
          {/* Batches */}
          <MetricTile label="Total Batches" value={data.total_batches || 0} accent="cargo" />
          <MetricTile label="Intaked" value={data.intaked_batches || 0} />
          <MetricTile label="Paid" value={data.paid_batches || 0} />
          <MetricTile label="Weight (kg)" value={Number(data.total_weight_kg || 0).toFixed(0)} />
          {/* Quality */}
          <MetricTile label="Lab Tests" value={data.lab_tests_count || 0} />
          <MetricTile label="Lab Pass" value={data.lab_tests_pass || 0} accent="cargo" />
          <MetricTile label="Sensory Tests" value={data.organoleptic_tests || 0} />
          <MetricTile label="Smoke Contam." value={data.smoke_contaminated || 0} accent="cargo" />
          {/* Chain */}
          <MetricTile label="Dispatches" value={data.total_dispatches || 0} />
          <MetricTile label="Total Bags" value={data.total_bags || 0} />
          <MetricTile label="Fraud Blocked" value={data.fraud_blocked || 0} accent="cargo" />
          <MetricTile label="Fraud Warnings" value={data.fraud_warnings || 0} />
          {/* Post-Harvest */}
          <MetricTile label="Farm Activities" value={data.farm_activities_count || 0} />
          <MetricTile label="Drying Centers" value={data.drying_centers_count || 0} />
          <MetricTile label="Ferm. Centers" value={data.ferm_centers_count || 0} />
          <MetricTile label="Waste Available" value={data.waste_available || 0} accent="cargo" />
          {/* Sustainability */}
          <MetricTile label="Sustain. Farmers" value={data.sustainability_farmers || 0} />
          <MetricTile label="Total Points" value={data.total_sustainability_points || 0} />
          <MetricTile label="Platinum" value={data.platinum_farmers || 0} />
          <MetricTile label="Gold" value={data.gold_farmers || 0} />
          {/* Financial */}
          <MetricTile label="Paid (XAF)" value={Number(data.total_paid_xaf || 0).toLocaleString()} />
          <MetricTile label="Transactions" value={data.total_transactions || 0} />
          <MetricTile label="Outstanding Credit" value={Number(data.outstanding_input_credit_xaf || 0).toLocaleString()} accent="cargo" />
          <MetricTile label="Active Floats" value={data.active_cash_floats || 0} />
          {/* Export */}
          <MetricTile label="Shipments" value={data.export_shipments || 0} />
          <MetricTile label="Export Docs" value={data.export_documents_count || 0} />
          <MetricTile label="DDS Generated" value={data.dds_count || 0} />
          <MetricTile label="EUDR Verified" value={data.eudr_verified_farms || 0} />
          {/* Infrastructure */}
          <MetricTile label="Villages" value={data.total_villages || 0} />
          <MetricTile label="Field Agents" value={data.field_agents || 0} />
          <MetricTile label="Drivers" value={data.drivers || 0} />
          <MetricTile label="Unread Notifs" value={data.unread_activity_notifications || 0} accent="cargo" />
        </div>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.9.2 — Quality Correlation Tab (farmer-reported vs clerk-tested)
 * ═══════════════════════════════════════════════════════════════ */
const QualityCorrelationTab: React.FC = () => {
  const [correlations, setCorrelations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/post-harvest/quality-correlation');
      setCorrelations(res.data?.correlations || []);
    } catch { setCorrelations([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      <Alert tone="info">Compares farmer-reported moisture/fermentation with clerk intake test results. MISMATCH indicates the farmer's self-reporting may be inaccurate.</Alert>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Quality Traceability" title="Quality Correlation" subtitle="Farmer-reported vs clerk-tested quality data" icon={<Icon.Shield className="w-4 h-4" />} />
        </div>
        {correlations.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No correlation data" description="Data appears when farmers record fermentation/drying activities linked to batches that have been intaked." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Activity #</th><th>Farmer</th><th>Activity</th><th>Farmer Moisture</th><th>Clerk Moisture</th><th>Farmer Bean</th><th>Clerk Grade</th><th>Match</th><th>Points</th></tr></thead>
              <tbody>
                {correlations.map(c => (
                  <tr key={c.activity_id} className={c.moisture_match === 'MISMATCH' ? 'bg-danger-50/50 dark:bg-danger-500/5' : c.moisture_match === 'MATCH' ? 'bg-success-50/30 dark:bg-success-500/5' : ''}>
                    <td className="text-xs font-mono">{c.activity_number}</td>
                    <td className="text-sm">{c.farmer_name}</td>
                    <td><StatusPill tone={c.activity_type === 'FERMENTATION' ? 'info' : 'warning'} dot>{c.activity_type}</StatusPill></td>
                    <td className="nums">{c.farmer_moisture ? `${Number(c.farmer_moisture).toFixed(1)}%` : '---'}</td>
                    <td className="nums">{c.clerk_moisture ? `${Number(c.clerk_moisture).toFixed(1)}%` : '---'}</td>
                    <td className="text-2xs">{c.farmer_bean_color || '---'}</td>
                    <td>{c.clerk_grade ? <StatusPill tone={c.clerk_grade === 'A' ? 'success' : c.clerk_grade === 'B' ? 'warning' : 'danger'} dot>{c.clerk_grade}</StatusPill> : '---'}</td>
                    <td><StatusPill tone={c.moisture_match === 'MATCH' ? 'success' : c.moisture_match === 'MISMATCH' ? 'danger' : 'neutral'} dot>{c.moisture_match}</StatusPill></td>
                    <td className="nums">{c.sustainability_points || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.2 — Post-Harvest Tab (Drying Centers + Fermentation Centers + Activities)
 * ═══════════════════════════════════════════════════════════════ */
const PostHarvestTab: React.FC = () => {
  const [dryingCenters, setDryingCenters] = useState<any[]>([]);
  const [fermCenters, setFermCenters] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState<'drying' | 'fermentation' | 'activities'>('drying');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/post-harvest/drying-centers').catch(() => ({ data: { centers: [] } })),
      apiClient.get('/post-harvest/fermentation-centers').catch(() => ({ data: { centers: [] } })),
      apiClient.get('/post-harvest/activities').catch(() => ({ data: { activities: [] } })),
      apiClient.get('/post-harvest/activities/stats').catch(() => ({ data: { stats: {} } })),
    ]).then(([dRes, fRes, aRes, sRes]) => {
      setDryingCenters(dRes.data?.centers || []);
      setFermCenters(fRes.data?.centers || []);
      setActivities(aRes.data?.activities || []);
      setStats(sRes.data?.stats || {});
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricTile label="Drying Centers" value={dryingCenters.length} icon={<Icon.Map className="w-4 h-4" />} />
        <MetricTile label="Ferm. Centers" value={fermCenters.length} />
        <MetricTile label="Activities (90d)" value={stats?.total_activities || 0} accent="cargo" />
        <MetricTile label="Active Farmers" value={stats?.active_farmers || 0} />
        <MetricTile label="Total Qty (kg)" value={Number(stats?.total_quantity_kg || 0).toFixed(0)} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        <button onClick={() => setSubTab('drying')} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${subTab === 'drying' ? 'bg-ink-900 text-white' : 'bg-ink-100 dark:bg-ink-800 text-ink-600'}`}>Drying Centers</button>
        <button onClick={() => setSubTab('fermentation')} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${subTab === 'fermentation' ? 'bg-ink-900 text-white' : 'bg-ink-100 dark:bg-ink-800 text-ink-600'}`}>Fermentation Centers</button>
        <button onClick={() => setSubTab('activities')} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${subTab === 'activities' ? 'bg-ink-900 text-white' : 'bg-ink-100 dark:bg-ink-800 text-ink-600'}`}>Farm Activities</button>
      </div>

      {/* Drying Centers */}
      {subTab === 'drying' && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Post-harvest infrastructure" title="Drying Centers" subtitle="Where farmers dry their cocoa beans (sun, solar, mechanical)" icon={<Icon.Map className="w-4 h-4" />} />
          </div>
          {dryingCenters.length === 0 ? (
            <div className="p-8"><EmptyState icon={<Icon.Map className="w-5 h-5" />} title="No drying centers" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Code</th><th>Name</th><th>Village</th><th>Method</th><th>Capacity</th><th>Roofed</th><th>Floor</th><th>Grade</th><th>Status</th></tr></thead>
                <tbody>
                  {dryingCenters.map(c => (
                    <tr key={c.id}>
                      <td className="text-xs font-mono">{c.center_code}</td>
                      <td className="text-sm font-medium">{c.center_name}</td>
                      <td className="text-2xs">{c.village || '---'}</td>
                      <td><StatusPill tone={c.drying_method === 'SOLAR_DRYER' || c.drying_method === 'MECHANICAL' ? 'success' : c.drying_method === 'WOOD_FIRE' ? 'warning' : 'info'} dot>{c.drying_method.replace(/_/g, ' ')}</StatusPill></td>
                      <td className="nums">{c.capacity_bags ? `${c.capacity_bags} bags` : '---'} {c.capacity_kg ? `(${Number(c.capacity_kg).toLocaleString()} kg)` : ''}</td>
                      <td>{c.has_roofing ? '✓' : '---'}</td>
                      <td>{c.has_cement_floor ? '✓' : '---'}</td>
                      <td><StatusPill tone={c.sanitary_grade === 'CERTIFIED' ? 'success' : c.sanitary_grade === 'IMPROVED' ? 'info' : 'warning'} dot>{c.sanitary_grade}</StatusPill></td>
                      <td>{c.is_active ? <span className="text-success-600 text-xs">Active</span> : <span className="text-danger-600 text-xs">Inactive</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Fermentation Centers */}
      {subTab === 'fermentation' && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Post-harvest infrastructure" title="Fermentation Centers" subtitle="Where farmers ferment cocoa beans (boxes, baskets, heaps)" icon={<Icon.Map className="w-4 h-4" />} />
          </div>
          {fermCenters.length === 0 ? (
            <div className="p-8"><EmptyState icon={<Icon.Map className="w-5 h-5" />} title="No fermentation centers" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Code</th><th>Name</th><th>Village</th><th>Method</th><th>Duration</th><th>Capacity</th><th>Thermo</th><th>Turns/day</th><th>Grade</th></tr></thead>
                <tbody>
                  {fermCenters.map(c => (
                    <tr key={c.id}>
                      <td className="text-xs font-mono">{c.center_code}</td>
                      <td className="text-sm font-medium">{c.center_name}</td>
                      <td className="text-2xs">{c.village || '---'}</td>
                      <td><StatusPill tone="info" dot>{c.fermentation_method.replace(/_/g, ' ')}</StatusPill></td>
                      <td className="nums">{c.typical_duration_days} days</td>
                      <td className="nums">{c.capacity_bags ? `${c.capacity_bags} bags` : '---'}</td>
                      <td>{c.has_thermometer ? '✓' : '---'}</td>
                      <td className="nums">{c.turns_per_day}</td>
                      <td><StatusPill tone={c.sanitary_grade === 'CERTIFIED' ? 'success' : c.sanitary_grade === 'IMPROVED' ? 'info' : 'warning'} dot>{c.sanitary_grade}</StatusPill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Farm Activities */}
      {subTab === 'activities' && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Value chain tracking" title="Farm Activities Log" subtitle="Post-harvest activities recorded by farmers and agents" icon={<Icon.Activity className="w-4 h-4" />} />
          </div>
          {activities.length === 0 ? (
            <div className="p-8"><EmptyState icon={<Icon.Activity className="w-5 h-5" />} title="No activities recorded" description="Farmers and field agents can record harvest, fermentation, drying, and bagging activities." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Activity #</th><th>Farmer</th><th>Type</th><th>Date</th><th>Qty (kg)</th><th>Method</th><th>Days</th><th>Moisture</th><th>Center</th><th>Notes</th></tr></thead>
                <tbody>
                  {activities.slice(0, 50).map(a => (
                    <tr key={a.id}>
                      <td className="text-xs font-mono">{a.activity_number}</td>
                      <td className="text-sm">{a.farmer_name || '---'}</td>
                      <td><StatusPill tone={a.activity_type === 'HARVEST' ? 'success' : a.activity_type === 'FERMENTATION' ? 'info' : a.activity_type === 'DRYING' ? 'warning' : 'neutral'} dot>{a.activity_type}</StatusPill></td>
                      <td className="text-2xs">{new Date(a.activity_date).toLocaleDateString()}</td>
                      <td className="nums">{a.quantity_kg ? Number(a.quantity_kg).toFixed(1) : '---'}</td>
                      <td className="text-2xs">{a.fermentation_method || a.drying_method || '---'}</td>
                      <td className="nums">{a.fermentation_days || a.drying_days || '---'}</td>
                      <td className="nums">{a.moisture_pct ? `${Number(a.moisture_pct).toFixed(1)}%` : '---'}</td>
                      <td className="text-2xs">{a.drying_center_name || a.fermentation_center_name || '---'}</td>
                      <td className="text-2xs max-w-xs truncate">{a.notes || '---'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Organoleptic Tab (Sensory QC + Smoke Contamination)
 * ═══════════════════════════════════════════════════════════════ */
const OrganolepticTab: React.FC = () => {
  const [tests, setTests] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    batch_id: '', aroma_score: '', flavor_score: '', acidity_score: '', bitterness_score: '',
    body_score: '', smoke_odor_detected: false, smoke_odor_intensity: 'NONE',
    smoke_taste_detected: false, smoke_taste_intensity: 'NONE',
    mold_odor: false, notes: ''
  });

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/organoleptic').catch(() => ({ data: { tests: [] } })),
      apiClient.get('/organoleptic/stats').catch(() => ({ data: { stats: {} } })),
      apiClient.get('/batches?status=INTAKED&limit=50').catch(() => ({ data: [] })),
    ]).then(([tRes, sRes, bRes]) => {
      setTests(tRes.data?.tests || []);
      setStats(sRes.data?.stats || {});
      const bArr = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.batches || []);
      setBatches(bArr);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      const payload: any = { ...form };
      ['aroma_score', 'flavor_score', 'acidity_score', 'bitterness_score', 'body_score'].forEach(f => {
        payload[f] = payload[f] ? parseFloat(payload[f]) : undefined;
      });
      if (payload.smoke_odor_intensity === 'NONE') payload.smoke_odor_intensity = null;
      if (payload.smoke_taste_intensity === 'NONE') payload.smoke_taste_intensity = null;
      const res = await apiClient.post('/organoleptic', payload);
      setSuccess(res.data.message);
      setShowCreate(false);
      setForm({ batch_id: '', aroma_score: '', flavor_score: '', acidity_score: '', bitterness_score: '', body_score: '', smoke_odor_detected: false, smoke_odor_intensity: 'NONE', smoke_taste_detected: false, smoke_taste_intensity: 'NONE', mold_odor: false, notes: '' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to create test'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Tests (90d)" value={stats?.total_tests || 0} icon={<Icon.Leaf className="w-4 h-4" />} />
        <MetricTile label="Smoke Contaminated" value={stats?.smoke_contaminated_count || 0} accent="cargo" />
        <MetricTile label="Quarantined" value={stats?.quarantined_count || 0} accent="cargo" />
        <MetricTile label="Avg Score" value={Number(stats?.avg_overall_score || 0).toFixed(1)} />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Sensory Analysis" title="Organoleptic Tests" subtitle="Aroma, flavor, smoke contamination, mold detection" icon={<Icon.Leaf className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Plus className="w-4 h-4" /> Add Test</button>
        </div>
        {tests.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No organoleptic tests" description="Add a sensory analysis test for an INTAKED batch." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Batch</th><th>Farmer</th><th>Overall Score</th><th>Smoke Odor</th><th>Smoke Taste</th><th>Disposition</th><th>Discount</th><th>Date</th></tr></thead>
              <tbody>
                {tests.map(t => (
                  <tr key={t.id} className={t.disposition === 'REJECTED' ? 'bg-danger-50/50 dark:bg-danger-500/5' : t.disposition === 'HOLD_DRYING' ? 'bg-warning-50/50 dark:bg-warning-500/5' : ''}>
                    <td className="text-xs font-mono">{t.batch_id_short?.slice(0, 8)}...</td>
                    <td className="text-sm">{t.farmer_name || '---'}</td>
                    <td className={`nums font-medium ${Number(t.overall_score) >= 8 ? 'text-success-600' : Number(t.overall_score) >= 6 ? 'text-warning-600' : 'text-danger-600'}`}>{t.overall_score ? Number(t.overall_score).toFixed(1) : '---'}</td>
                    <td>{t.smoke_odor_detected ? <StatusPill tone={t.smoke_odor_intensity === 'SEVERE' || t.smoke_odor_intensity === 'STRONG' ? 'danger' : 'warning'} dot>{t.smoke_odor_intensity}</StatusPill> : <span className="text-success-600 text-xs">None</span>}</td>
                    <td>{t.smoke_taste_detected ? <StatusPill tone={t.smoke_taste_intensity === 'SEVERE' || t.smoke_taste_intensity === 'STRONG' ? 'danger' : 'warning'} dot>{t.smoke_taste_intensity}</StatusPill> : <span className="text-success-600 text-xs">None</span>}</td>
                    <td><StatusPill tone={t.disposition === 'APPROVED_PREMIUM' ? 'success' : t.disposition === 'APPROVED_SECONDARY' ? 'info' : t.disposition === 'HOLD_DRYING' ? 'warning' : t.disposition === 'REJECTED' ? 'danger' : 'neutral'} dot>{t.disposition.replace(/_/g, ' ')}</StatusPill></td>
                    <td className={`nums ${Number(t.discount_pct) > 0 ? 'text-warning-600 font-medium' : ''}`}>{Number(t.discount_pct).toFixed(0)}%</td>
                    <td className="text-2xs">{new Date(t.tested_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Add Organoleptic Test"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.batch_id} className="btn-primary flex-1">Create Test</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Batch *</label><select className="input" value={form.batch_id} onChange={e => setForm({ ...form, batch_id: e.target.value })}><option value="">Select INTAKED batch</option>{batches.map((b: any) => <option key={b.id} value={b.id}>{b.id.slice(0, 8)}... - {b.farmer_name} ({b.actual_weight || b.est_weight} kg)</option>)}</select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label">Aroma (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.aroma_score} onChange={e => setForm({ ...form, aroma_score: e.target.value })} /></div>
              <div><label className="label">Flavor (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.flavor_score} onChange={e => setForm({ ...form, flavor_score: e.target.value })} /></div>
              <div><label className="label">Acidity (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.acidity_score} onChange={e => setForm({ ...form, acidity_score: e.target.value })} /></div>
              <div><label className="label">Bitterness (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.bitterness_score} onChange={e => setForm({ ...form, bitterness_score: e.target.value })} /></div>
              <div><label className="label">Body (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.body_score} onChange={e => setForm({ ...form, body_score: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.smoke_odor_detected} onChange={e => setForm({ ...form, smoke_odor_detected: e.target.checked })} className="w-4 h-4" /><span>Smoke odor detected</span></label>{form.smoke_odor_detected && <select className="input mt-1" value={form.smoke_odor_intensity} onChange={e => setForm({ ...form, smoke_odor_intensity: e.target.value })}><option value="NONE">None</option><option value="FAINT">Faint</option><option value="MODERATE">Moderate</option><option value="STRONG">Strong</option><option value="SEVERE">Severe</option></select>}</div>
              <div><label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.smoke_taste_detected} onChange={e => setForm({ ...form, smoke_taste_detected: e.target.checked })} className="w-4 h-4" /><span>Smoke taste detected</span></label>{form.smoke_taste_detected && <select className="input mt-1" value={form.smoke_taste_intensity} onChange={e => setForm({ ...form, smoke_taste_intensity: e.target.value })}><option value="NONE">None</option><option value="FAINT">Faint</option><option value="MODERATE">Moderate</option><option value="STRONG">Strong</option><option value="SEVERE">Severe</option></select>}</div>
            </div>
            <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Alert tone="info">Smoke contamination is common in Cameroon cocoa dried over wood fires. Strong/severe = auto-reject + quarantine. Moderate = hold for re-drying + 15% discount. Faint = secondary grade + 5% discount.</Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Farm Inputs Tab (Input Credit Ledger)
 * ═══════════════════════════════════════════════════════════════ */
const FarmInputsTab: React.FC = () => {
  const [distributions, setDistributions] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [farmers, setFarmers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ farmer_id: '', input_type_id: '', quantity: '', unit_cost_xaf: '', notes: '' });

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/farm-inputs/distributions').catch(() => ({ data: { distributions: [] } })),
      apiClient.get('/farm-inputs/types').catch(() => ({ data: { types: [] } })),
      apiClient.get('/farm-inputs/stats').catch(() => ({ data: { stats: {} } })),
      apiClient.get('/farmers?limit=100').catch(() => ({ data: [] })),
    ]).then(([dRes, tRes, sRes, fRes]) => {
      setDistributions(dRes.data?.distributions || []);
      setTypes(tRes.data?.types || []);
      setStats(sRes.data?.stats || {});
      setFarmers(Array.isArray(fRes.data) ? fRes.data : (fRes.data?.farmers || []));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      const res = await apiClient.post('/farm-inputs/distributions', {
        ...form,
        quantity: parseFloat(form.quantity),
        unit_cost_xaf: parseFloat(form.unit_cost_xaf),
      });
      setSuccess(res.data.message);
      setShowCreate(false);
      setForm({ farmer_id: '', input_type_id: '', quantity: '', unit_cost_xaf: '', notes: '' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to distribute inputs'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Distributions" value={stats?.total_distributions || 0} icon={<Icon.Package className="w-4 h-4" />} />
        <MetricTile label="Outstanding Credit" value={`${Number(stats?.total_outstanding_xaf || 0).toLocaleString()} XAF`} accent="cargo" />
        <MetricTile label="Total Repaid" value={`${Number(stats?.total_repaid_xaf || 0).toLocaleString()} XAF`} />
        <MetricTile label="Outstanding Count" value={stats?.outstanding_count || 0} />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Credit Ledger" title="Farm Input Distributions" subtitle="Track inputs distributed on credit + auto-deduct from payouts" icon={<Icon.Package className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Plus className="w-4 h-4" /> Distribute Input</button>
        </div>
        {distributions.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No input distributions" description="Distribute fertilizer, fungicide, seedlings, etc. to farmers on credit." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Distribution #</th><th>Farmer</th><th>Input</th><th>Category</th><th>Qty</th><th>Total Value</th><th>Balance</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {distributions.map(d => (
                  <tr key={d.id} className={d.status === 'OUTSTANDING' ? 'bg-warning-50/30 dark:bg-warning-500/5' : ''}>
                    <td className="text-xs font-mono">{d.distribution_number}</td>
                    <td className="text-sm">{d.farmer_name}</td>
                    <td className="text-sm">{d.input_name}</td>
                    <td><StatusPill tone="info" dot>{d.input_category}</StatusPill></td>
                    <td className="nums">{d.quantity} {d.unit}</td>
                    <td className="nums">{Number(d.total_value_xaf).toLocaleString()}</td>
                    <td className={`nums font-medium ${Number(d.balance_xaf) > 0 ? 'text-warning-600' : 'text-success-600'}`}>{Number(d.balance_xaf).toLocaleString()}</td>
                    <td><StatusPill tone={d.status === 'OUTSTANDING' ? 'warning' : d.status === 'PARTIALLY_REPAID' ? 'info' : d.status === 'FULLY_REPAID' ? 'success' : 'neutral'} dot>{d.status.replace(/_/g, ' ')}</StatusPill></td>
                    <td className="text-2xs">{new Date(d.distribution_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Distribute Input on Credit"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.farmer_id || !form.input_type_id || !form.quantity} className="btn-primary flex-1">Distribute</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Farmer *</label><select className="input" value={form.farmer_id} onChange={e => setForm({ ...form, farmer_id: e.target.value })}><option value="">Select farmer</option>{farmers.map((f: any) => <option key={f.id} value={f.id}>{f.name} ({f.village || '---'})</option>)}</select></div>
            <div><label className="label">Input Type *</label><select className="input" value={form.input_type_id} onChange={e => { const t = types.find(x => x.id === e.target.value); setForm({ ...form, input_type_id: e.target.value, unit_cost_xaf: t ? String(t.unit_cost_xaf) : '' }); }}><option value="">Select input</option>{types.map((t: any) => <option key={t.id} value={t.id}>{t.name} ({t.category}) - {Number(t.unit_cost_xaf).toLocaleString()} XAF/{t.unit}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Quantity *</label><input type="number" step="0.1" className="input" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 50" /></div>
              <div><label className="label">Unit Cost (XAF)</label><input type="number" className="input" value={form.unit_cost_xaf} onChange={e => setForm({ ...form, unit_cost_xaf: e.target.value })} /></div>
            </div>
            {form.quantity && form.unit_cost_xaf && <Alert tone="info">Total credit value: {(parseFloat(form.quantity) * parseFloat(form.unit_cost_xaf)).toLocaleString()} XAF. This will be auto-deducted from the farmer's next batch payout.</Alert>}
            <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Stock Counts Tab (Cycle Counting + Reconciliation)
 * ═══════════════════════════════════════════════════════════════ */
const StockCountsTab: React.FC = () => {
  const [counts, setCounts] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [viewCount, setViewCount] = useState<any | null>(null);
  const [form, setForm] = useState({ count_type: 'CYCLE', warehouse_section: '', notes: '' });

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/stock-reconciliation/counts').catch(() => ({ data: { counts: [] } })),
      apiClient.get('/stock-reconciliation/stats').catch(() => ({ data: { stats: {} } })),
    ]).then(([cRes, sRes]) => {
      setCounts(cRes.data?.counts || []);
      setStats(sRes.data?.stats || {});
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      const res = await apiClient.post('/stock-reconciliation/counts', form);
      setSuccess(res.data.message);
      setShowCreate(false);
      setForm({ count_type: 'CYCLE', warehouse_section: '', notes: '' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to create count'); }
  };

  const handleView = async (countId: string) => {
    try {
      const res = await apiClient.get(`/stock-reconciliation/counts/${countId}`);
      setViewCount(res.data);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to load count'); }
  };

  const handleReconcile = async (countId: string) => {
    try {
      const res = await apiClient.post(`/stock-reconciliation/counts/${countId}/reconcile`);
      setSuccess(res.data.message);
      handleView(countId);
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Reconciliation failed'); }
  };

  const handleApprove = async (countId: string) => {
    try {
      await apiClient.post(`/stock-reconciliation/counts/${countId}/approve`);
      setSuccess('Count approved');
      handleView(countId);
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Approval failed'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Counts (90d)" value={stats?.total_counts || 0} icon={<Icon.Database className="w-4 h-4" />} />
        <MetricTile label="Total Variance (kg)" value={Number(stats?.total_variance_kg || 0).toFixed(1)} accent="cargo" />
        <MetricTile label="Total Variance (XAF)" value={Number(stats?.total_variance_xaf || 0).toLocaleString()} accent="cargo" />
        <MetricTile label="Approved" value={stats?.approved_count || 0} />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Inventory Audit" title="Stock Cycle Counts" subtitle="Periodic inventory audits + blind counting sheets + variance tracking" icon={<Icon.Database className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Plus className="w-4 h-4" /> Start Count</button>
        </div>
        {counts.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No cycle counts" description="Start a new count to audit warehouse inventory." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Count #</th><th>Type</th><th>Section</th><th>Date</th><th>Items</th><th>Matched</th><th>Over</th><th>Under</th><th>Variance (kg)</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {counts.map(c => (
                  <tr key={c.id} className={c.status === 'RECONCILED' && Number(c.total_variance_kg) !== 0 ? 'bg-warning-50/30 dark:bg-warning-500/5' : ''}>
                    <td className="text-xs font-mono">{c.count_number}</td>
                    <td><StatusPill tone="info" dot>{c.count_type}</StatusPill></td>
                    <td className="text-2xs">{c.warehouse_section || 'Full warehouse'}</td>
                    <td className="text-2xs">{new Date(c.count_date).toLocaleDateString()}</td>
                    <td className="nums">{c.total_items}</td>
                    <td className="nums text-success-600">{c.items_matched || 0}</td>
                    <td className="nums text-warning-600">{c.items_over || 0}</td>
                    <td className="nums text-danger-600">{c.items_under || 0}</td>
                    <td className={`nums font-medium ${Number(c.total_variance_kg) < 0 ? 'text-danger-600' : Number(c.total_variance_kg) > 0 ? 'text-warning-600' : ''}`}>{Number(c.total_variance_kg || 0).toFixed(1)}</td>
                    <td><StatusPill tone={c.status === 'APPROVED' ? 'success' : c.status === 'RECONCILED' ? 'info' : 'warning'} dot>{c.status}</StatusPill></td>
                    <td><button onClick={() => handleView(c.id)} className="btn-secondary text-xs">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Start Cycle Count"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} className="btn-primary flex-1">Start Count</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Count Type</label><select className="input" value={form.count_type} onChange={e => setForm({ ...form, count_type: e.target.value })}><option value="CYCLE">Cycle (section by section)</option><option value="FULL">Full (entire warehouse)</option><option value="SPOT_CHECK">Spot check (random)</option><option value="BLIND">Blind (no system quantities shown)</option></select></div>
            <div><label className="label">Warehouse Section (optional)</label><input className="input" value={form.warehouse_section} onChange={e => setForm({ ...form, warehouse_section: e.target.value })} placeholder="e.g. A-1 (leave blank for full warehouse)" /></div>
            <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Alert tone="info">The count will be pre-populated with current system inventory. Staff enter physical counts, then reconcile to compute variances.</Alert>
          </div>
        </Sheet>
      )}

      {viewCount && (
        <Sheet open={!!viewCount} onClose={() => setViewCount(null)} title={`Cycle Count ${viewCount.count?.count_number}`}
          footer={<div className="flex gap-2">
            <button onClick={() => setViewCount(null)} className="btn-secondary flex-1">Close</button>
            {viewCount.count?.status === 'IN_PROGRESS' && <button onClick={() => handleReconcile(viewCount.count.id)} className="btn-primary flex-1">Reconcile</button>}
            {viewCount.count?.status === 'RECONCILED' && <button onClick={() => handleApprove(viewCount.count.id)} className="btn-primary flex-1">Approve</button>}
          </div>}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-ink-500">Type:</span> {viewCount.count?.count_type}</div>
              <div><span className="text-ink-500">Section:</span> {viewCount.count?.warehouse_section || 'Full'}</div>
              <div><span className="text-ink-500">Status:</span> <StatusPill tone={viewCount.count?.status === 'APPROVED' ? 'success' : 'info'} dot>{viewCount.count?.status}</StatusPill></div>
              <div><span className="text-ink-500">Matched:</span> <span className="text-success-600">{viewCount.count?.items_matched}</span></div>
              <div><span className="text-ink-500">Over:</span> <span className="text-warning-600">{viewCount.count?.items_over}</span></div>
              <div><span className="text-ink-500">Under:</span> <span className="text-danger-600">{viewCount.count?.items_under}</span></div>
              <div><span className="text-ink-500">Variance:</span> <span className="nums font-medium">{Number(viewCount.count?.total_variance_kg || 0).toFixed(1)} kg</span></div>
              <div><span className="text-ink-500">Value:</span> <span className="nums">{Number(viewCount.count?.total_variance_xaf || 0).toLocaleString()} XAF</span></div>
              <div><span className="text-ink-500">Items:</span> {viewCount.count?.total_items}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Batch</th><th>Farmer</th><th>Section</th><th>System (kg)</th><th>Counted (kg)</th><th>Variance</th><th>Type</th></tr></thead>
                <tbody>
                  {(viewCount.items || []).map((item: any) => (
                    <tr key={item.id} className={item.variance_type === 'UNDER' ? 'bg-danger-50/30' : item.variance_type === 'OVER' ? 'bg-warning-50/30' : ''}>
                      <td className="text-xs font-mono">{item.batch_id_short?.slice(0, 8)}...</td>
                      <td className="text-sm">{item.farmer_name || '---'}</td>
                      <td className="text-2xs">{item.warehouse_section || '---'}</td>
                      <td className="nums">{Number(item.system_weight_kg).toFixed(1)}</td>
                      <td className="nums">{item.counted_weight_kg ? Number(item.counted_weight_kg).toFixed(1) : '---'}</td>
                      <td className={`nums font-medium ${Number(item.variance_kg) < 0 ? 'text-danger-600' : Number(item.variance_kg) > 0 ? 'text-warning-600' : 'text-success-600'}`}>{item.variance_kg ? Number(item.variance_kg).toFixed(1) : '0.0'}</td>
                      <td><StatusPill tone={item.variance_type === 'MATCH' ? 'success' : item.variance_type === 'OVER' ? 'warning' : 'danger'} dot>{item.variance_type}</StatusPill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.0 — EUDR DDS Tab (Due Diligence Statement Generator)
 * Generates EU Information System compliant DDS + JSON export
 * ═══════════════════════════════════════════════════════════════ */
const EudrDDSTab: React.FC = () => {
  const [ddsRecords, setDdsRecords] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [generating, setGenerating] = useState(false);
  const [viewDds, setViewDds] = useState<any | null>(null);
  const [euIsJson, setEuIsJson] = useState<any | null>(null);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/eudr/dds').catch(() => ({ data: { dds_records: [] } })),
      apiClient.get('/batches?status=INTAKED&limit=50').catch(() => ({ data: [] })),
    ]).then(([dRes, bRes]) => {
      setDdsRecords(dRes.data?.dds_records || []);
      const bArr = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.batches || []);
      setBatches(bArr);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleGenerate = async () => {
    if (!selectedBatch) { setError('Select a batch first'); return; }
    setGenerating(true); setError(null); setSuccess(null);
    try {
      const res = await apiClient.post(`/eudr/dds/${selectedBatch}`);
      setSuccess(`DDS generated: ${res.data.dds.dds_reference}. Batch is now EUDR compliant.`);
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate DDS');
    } finally { setGenerating(false); }
  };

  const handleViewEuIsJson = async (ddsId: string) => {
    try {
      const res = await apiClient.get(`/eudr/dds/${ddsId}/eu-is-json`);
      setEuIsJson(res.data);
      setViewDds(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate EU IS JSON');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Generate DDS card */}
      <Card padding="lg">
        <SectionHeader eyebrow="EU Deforestation Regulation" title="Generate Due Diligence Statement" subtitle="Creates a DDS record with farm geolocation data for EU Information System submission" icon={<Icon.Shield className="w-4 h-4" />} />
        <div className="mt-4 space-y-3">
          <div>
            <label className="label">Select INTAKED batch to generate DDS for</label>
            <select className="input" value={selectedBatch} onChange={e => setSelectedBatch(e.target.value)}>
              <option value="">— Select a batch —</option>
              {batches.map((b: any) => (
                <option key={b.id} value={b.id}>
                  {b.id.slice(0, 8)}… — {b.farmer_name || 'Unknown'} ({b.actual_weight || b.est_weight} kg, Grade {b.quality_grade || '—'}){b.dds_reference ? ' ✓ DDS' : ''}
                </option>
              ))}
            </select>
          </div>
          <button onClick={handleGenerate} disabled={!selectedBatch || generating} className="btn-primary">
            {generating ? <><Spinner size="sm" /> Generating…</> : <><Icon.Shield className="w-4 h-4" /> Generate DDS</>}
          </button>
          <Alert tone="info">
            <strong>EUDR Compliance:</strong> The DDS (Due Diligence Statement) is required by EU Regulation 2023/1115 for all cocoa imports into the EU.
            It packages farm GPS coordinates, deforestation verification, and legality proofs. After generation, click "View EU IS JSON" to get the payload for the EU Information System portal.
          </Alert>
        </div>
      </Card>

      {/* DDS records table */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="EU Compliance" title="DDS Records" subtitle="Generated Due Diligence Statements" icon={<Icon.Shield className="w-4 h-4" />} />
        </div>
        {ddsRecords.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No DDS records yet" description="Generate a DDS for an INTAKED batch above." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>DDS Reference</th><th>Batch</th><th>Farmer</th><th>Farm</th><th>Deforestation</th><th>Legality</th><th>Status</th><th>Submitted</th><th>Action</th></tr></thead>
              <tbody>
                {ddsRecords.map(d => (
                  <tr key={d.id}>
                    <td className="text-xs font-mono">{d.dds_reference}</td>
                    <td className="text-xs font-mono">{d.batch_id_short?.slice(0, 8)}…</td>
                    <td className="text-sm">{d.farmer_name}</td>
                    <td className="text-2xs">{d.farm_name || '—'} ({Number(d.farm_area_ha || 0).toFixed(2)} ha)</td>
                    <td>{d.deforestation_free ? <span className="text-success-600 text-xs">✓ Verified</span> : <span className="text-warning-600 text-xs">Pending</span>}</td>
                    <td>{d.legality_verified ? <span className="text-success-600 text-xs">✓ Verified</span> : <span className="text-warning-600 text-xs">Pending</span>}</td>
                    <td><StatusPill tone={d.status === 'APPROVED' ? 'success' : d.status === 'SUBMITTED' ? 'info' : 'warning'} dot>{d.status}</StatusPill></td>
                    <td className="text-2xs">{d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : '—'}</td>
                    <td><button onClick={() => handleViewEuIsJson(d.id)} className="btn-secondary text-xs">View EU IS JSON</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* EU IS JSON modal */}
      {euIsJson && (
        <Sheet open={!!euIsJson} onClose={() => setEuIsJson(null)} title={`EU Information System Payload — ${euIsJson.dds_reference}`}
          footer={<div className="flex gap-2"><button onClick={() => setEuIsJson(null)} className="btn-secondary flex-1">Close</button><button onClick={() => { navigator.clipboard.writeText(JSON.stringify(euIsJson.eu_is_payload, null, 2)); setSuccess('JSON copied to clipboard!'); }} className="btn-primary flex-1">Copy JSON</button></div>}>
          <div className="space-y-3">
            <Alert tone="success">{euIsJson.message}</Alert>
            <div>
              <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-2">EU IS Payload (JSON)</p>
              <pre className="text-2xs bg-ink-100 dark:bg-ink-800 p-3 rounded-lg overflow-x-auto max-h-96 font-mono">
                {JSON.stringify(euIsJson.eu_is_payload, null, 2)}
              </pre>
            </div>
            <Alert tone="info">
              <strong>Next steps:</strong> Copy this JSON and upload it to the EU Information System portal (https://information-system.eudr-webgate.ec.europa.eu).
              Once submitted, you'll receive an EU IS reference number — record it on the DDS.
            </Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.0 — Export Documents Tab
 * Generates Phytosanitary, COO, BOL, NCCB, Customs documents
 * ═══════════════════════════════════════════════════════════════ */
const ExportDocsTab: React.FC = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [shipments, setShipments] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showShipment, setShowShipment] = useState(false);
  const [viewDoc, setViewDoc] = useState<any | null>(null);
  const [form, setForm] = useState({
    document_type: 'PHYTOSANITARY', batch_id: '',
    importer_name: '', port_of_discharge: '', container_number: '', etd: '', eta: '',
  });
  const [shipmentForm, setShipmentForm] = useState({
    container_number: '', seal_number: '', vessel_name: '', shipping_line: '',
    port_of_loading: 'Douala', port_of_discharge: '', destination_country: '', etd: '', eta: '',
    buyer_name: '', buyer_country: '', contract_reference: '',
  });

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/export-docs/documents').catch(() => ({ data: { documents: [] } })),
      apiClient.get('/export-docs/shipments').catch(() => ({ data: { shipments: [] } })),
      apiClient.get('/export-docs/stats').catch(() => ({ data: { stats: {} } })),
      apiClient.get('/batches?status=INTAKED&limit=50').catch(() => ({ data: [] })),
    ]).then(([dRes, sRes, stRes, bRes]) => {
      setDocuments(dRes.data?.documents || []);
      setShipments(sRes.data?.shipments || []);
      setStats(stRes.data?.stats || {});
      const bArr = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.batches || []);
      setBatches(bArr);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleCreateDoc = async () => {
    setError(null);
    try {
      const res = await apiClient.post('/export-docs/documents', {
        ...form,
        etd: form.etd || undefined,
        eta: form.eta || undefined,
      });
      setSuccess(`${res.data.message}`);
      setShowCreate(false);
      setForm({ document_type: 'PHYTOSANITARY', batch_id: '', importer_name: '', port_of_discharge: '', container_number: '', etd: '', eta: '' });
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create document');
    }
  };

  const handleCreateShipment = async () => {
    setError(null);
    try {
      const res = await apiClient.post('/export-docs/shipments', {
        ...shipmentForm,
        etd: shipmentForm.etd || undefined,
        eta: shipmentForm.eta || undefined,
      });
      setSuccess(`Shipment ${res.data.shipment.shipment_number} created`);
      setShowShipment(false);
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create shipment');
    }
  };

  const handleApprove = async (docId: string) => {
    try {
      await apiClient.post(`/export-docs/documents/${docId}/approve`);
      setSuccess('Document approved');
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Approval failed');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  const DOC_TYPES = [
    { value: 'PHYTOSANITARY', label: 'Phytosanitary Certificate' },
    { value: 'CERTIFICATE_OF_ORIGIN', label: 'Certificate of Origin' },
    { value: 'BILL_OF_LADING', label: 'Bill of Lading' },
    { value: 'NCCB_DECLARATION', label: 'NCCB Declaration' },
    { value: 'CUSTOMS_DECLARATION', label: 'Customs Declaration' },
  ];

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Documents" value={stats?.total_documents || 0} icon={<Icon.FileText className="w-4 h-4" />} />
        <MetricTile label="Approved" value={stats?.approved_count || 0} accent="cargo" />
        <MetricTile label="Shipments" value={stats?.total_shipments || 0} />
        <MetricTile label="Export Weight (kg)" value={Number(stats?.total_export_weight_kg || 0).toFixed(0)} />
      </div>

      {/* Document type breakdown */}
      <div className="grid grid-cols-5 gap-2">
        {DOC_TYPES.map(t => (
          <div key={t.value} className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800 text-center">
            <p className="text-2xs uppercase tracking-micro text-ink-500 font-semibold truncate">{t.label.split(' ')[0]}</p>
            <p className="text-sm font-bold nums text-ink-900 dark:text-ink-50">
              {stats?.[t.value.toLowerCase().replace('_of_origin', '_count').replace('_of_lading', '_count') + '_count'] || stats?.[t.value === 'PHYTOSANITARY' ? 'phytosanitary_count' : t.value === 'CERTIFICATE_OF_ORIGIN' ? 'coo_count' : t.value === 'BILL_OF_LADING' ? 'bol_count' : t.value === 'NCCB_DECLARATION' ? 'nccb_count' : 'customs_count'] || 0}
            </p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs">
          <Icon.Plus className="w-4 h-4" /> Create Document
        </button>
        <button onClick={() => setShowShipment(true)} className="btn-secondary text-xs">
          <Icon.Plus className="w-4 h-4" /> Create Shipment
        </button>
      </div>

      {/* Shipments table */}
      {shipments.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="Container tracking" title="Export Shipments" subtitle="Ocean freight shipments from Douala/Kribi to destination ports" icon={<Icon.Send className="w-4 h-4" />} />
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Shipment #</th><th>Container</th><th>Vessel</th><th>Route</th><th>ETD</th><th>ETA</th><th>Status</th><th>Docs</th></tr></thead>
              <tbody>
                {shipments.map(s => (
                  <tr key={s.id}>
                    <td className="text-xs font-mono">{s.shipment_number}</td>
                    <td className="text-xs font-mono">{s.container_number || '—'}</td>
                    <td className="text-2xs">{s.vessel_name || '—'}</td>
                    <td className="text-2xs">{s.port_of_loading} → {s.port_of_discharge || '—'}</td>
                    <td className="text-2xs">{s.etd ? new Date(s.etd).toLocaleDateString() : '—'}</td>
                    <td className="text-2xs">{s.eta ? new Date(s.eta).toLocaleDateString() : '—'}</td>
                    <td><StatusPill tone={s.status === 'DELIVERED' ? 'success' : s.status === 'IN_TRANSIT' ? 'info' : s.status === 'CANCELLED' ? 'danger' : 'warning'} dot>{s.status}</StatusPill></td>
                    <td className="nums">{s.document_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Documents table */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Export compliance" title="Export Documents" subtitle="Phytosanitary, Certificate of Origin, Bill of Lading, NCCB, Customs declarations" icon={<Icon.FileText className="w-4 h-4" />} />
        </div>
        {documents.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.FileText className="w-5 h-5" />} title="No export documents" description="Create a document for an INTAKED batch to start the export process." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Doc Number</th><th>Type</th><th>Batch</th><th>Route</th><th>Container</th><th>Weight</th><th>Value</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {documents.map(d => (
                  <tr key={d.id}>
                    <td className="text-xs font-mono">{d.document_number}</td>
                    <td><StatusPill tone="info" dot>{d.document_type.replace(/_/g, ' ')}</StatusPill></td>
                    <td className="text-xs font-mono">{d.batch_id_short?.slice(0, 8) || '—'}…</td>
                    <td className="text-2xs">{d.port_of_loading} → {d.port_of_discharge}</td>
                    <td className="text-xs font-mono">{d.container_number || '—'}</td>
                    <td className="nums">{Number(d.total_weight_kg || 0).toFixed(0)} kg</td>
                    <td className="nums">{Number(d.total_value_xaf || 0).toLocaleString()}</td>
                    <td><StatusPill tone={d.status === 'APPROVED' ? 'success' : d.status === 'DRAFT' ? 'warning' : 'info'} dot>{d.status}</StatusPill></td>
                    <td>
                      <button onClick={() => setViewDoc(d)} className="text-xs text-info-600 hover:underline mr-2">View</button>
                      {d.status === 'DRAFT' && <button onClick={() => handleApprove(d.id)} className="text-xs text-success-600 hover:underline">Approve</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Document Sheet */}
      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Create Export Document"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreateDoc} disabled={!form.batch_id} className="btn-primary flex-1">Create</button></div>}>
          <div className="space-y-3">
            <div>
              <label className="label">Document Type *</label>
              <select className="input" value={form.document_type} onChange={e => setForm({ ...form, document_type: e.target.value })}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Batch *</label>
              <select className="input" value={form.batch_id} onChange={e => setForm({ ...form, batch_id: e.target.value })}>
                <option value="">— Select INTAKED batch —</option>
                {batches.map((b: any) => <option key={b.id} value={b.id}>{b.id.slice(0, 8)}… — {b.farmer_name} ({b.actual_weight || b.est_weight} kg)</option>)}
              </select>
            </div>
            <div><label className="label">Importer Name</label><input className="input" value={form.importer_name} onChange={e => setForm({ ...form, importer_name: e.target.value })} placeholder="e.g. Lindt & Sprüngli GmbH" /></div>
            <div><label className="label">Port of Discharge</label><input className="input" value={form.port_of_discharge} onChange={e => setForm({ ...form, port_of_discharge: e.target.value })} placeholder="e.g. Hamburg, Rotterdam" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Container #</label><input className="input" value={form.container_number} onChange={e => setForm({ ...form, container_number: e.target.value })} placeholder="CONT-2026-001" /></div>
              <div><label className="label">ETD</label><input type="date" className="input" value={form.etd} onChange={e => setForm({ ...form, etd: e.target.value })} /></div>
            </div>
            <Alert tone="info">Document data will be auto-populated from the selected batch (farmer, weight, grade, value).</Alert>
          </div>
        </Sheet>
      )}

      {/* Create Shipment Sheet */}
      {showShipment && (
        <Sheet open={showShipment} onClose={() => setShowShipment(false)} title="Create Export Shipment"
          footer={<div className="flex gap-2"><button onClick={() => setShowShipment(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreateShipment} className="btn-primary flex-1">Create</button></div>}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Container #</label><input className="input" value={shipmentForm.container_number} onChange={e => setShipmentForm({ ...shipmentForm, container_number: e.target.value })} /></div>
              <div><label className="label">Seal #</label><input className="input" value={shipmentForm.seal_number} onChange={e => setShipmentForm({ ...shipmentForm, seal_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Vessel Name</label><input className="input" value={shipmentForm.vessel_name} onChange={e => setShipmentForm({ ...shipmentForm, vessel_name: e.target.value })} /></div>
              <div><label className="label">Shipping Line</label><input className="input" value={shipmentForm.shipping_line} onChange={e => setShipmentForm({ ...shipmentForm, shipping_line: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Port of Loading</label><input className="input" value={shipmentForm.port_of_loading} onChange={e => setShipmentForm({ ...shipmentForm, port_of_loading: e.target.value })} /></div>
              <div><label className="label">Port of Discharge</label><input className="input" value={shipmentForm.port_of_discharge} onChange={e => setShipmentForm({ ...shipmentForm, port_of_discharge: e.target.value })} placeholder="e.g. Hamburg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Destination Country</label><input className="input" value={shipmentForm.destination_country} onChange={e => setShipmentForm({ ...shipmentForm, destination_country: e.target.value })} placeholder="e.g. Germany" /></div>
              <div><label className="label">Buyer Name</label><input className="input" value={shipmentForm.buyer_name} onChange={e => setShipmentForm({ ...shipmentForm, buyer_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">ETD</label><input type="date" className="input" value={shipmentForm.etd} onChange={e => setShipmentForm({ ...shipmentForm, etd: e.target.value })} /></div>
              <div><label className="label">ETA</label><input type="date" className="input" value={shipmentForm.eta} onChange={e => setShipmentForm({ ...shipmentForm, eta: e.target.value })} /></div>
            </div>
            <div><label className="label">Contract Reference</label><input className="input" value={shipmentForm.contract_reference} onChange={e => setShipmentForm({ ...shipmentForm, contract_reference: e.target.value })} /></div>
          </div>
        </Sheet>
      )}

      {/* View Document Sheet */}
      {viewDoc && (
        <Sheet open={!!viewDoc} onClose={() => setViewDoc(null)} title={`${viewDoc.document_type.replace(/_/g, ' ')} — ${viewDoc.document_number}`}
          footer={<div className="flex gap-2"><button onClick={() => setViewDoc(null)} className="btn-secondary flex-1">Close</button><button onClick={() => { navigator.clipboard.writeText(JSON.stringify(viewDoc.document_data, null, 2)); setSuccess('Document data copied!'); }} className="btn-primary flex-1">Copy Data JSON</button></div>}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-ink-500">Status:</span> <StatusPill tone={viewDoc.status === 'APPROVED' ? 'success' : 'warning'} dot>{viewDoc.status}</StatusPill></div>
              <div><span className="text-ink-500">Batch:</span> <span className="font-mono">{viewDoc.batch_id_short?.slice(0, 8)}…</span></div>
              <div><span className="text-ink-500">Exporter:</span> {viewDoc.exporter_name}</div>
              <div><span className="text-ink-500">Importer:</span> {viewDoc.importer_name}</div>
              <div><span className="text-ink-500">Loading:</span> {viewDoc.port_of_loading}</div>
              <div><span className="text-ink-500">Discharge:</span> {viewDoc.port_of_discharge}</div>
              <div><span className="text-ink-500">Container:</span> <span className="font-mono">{viewDoc.container_number || '—'}</span></div>
              <div><span className="text-ink-500">Seal:</span> <span className="font-mono">{viewDoc.seal_number || '—'}</span></div>
              <div><span className="text-ink-500">Weight:</span> <span className="nums">{Number(viewDoc.total_weight_kg || 0).toFixed(0)} kg</span></div>
              <div><span className="text-ink-500">Value:</span> <span className="nums">{Number(viewDoc.total_value_xaf || 0).toLocaleString()} XAF</span></div>
            </div>
            <div>
              <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-2">Document Data (JSON)</p>
              <pre className="text-2xs bg-ink-100 dark:bg-ink-800 p-3 rounded-lg overflow-x-auto max-h-60 font-mono">
                {JSON.stringify(viewDoc.document_data, null, 2)}
              </pre>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.7.9 — Yield Forecast Tab (anti-fraud)
 * Shows farm yield forecasts + fraud alerts + cumulative tracking
 * ═══════════════════════════════════════════════════════════════ */
const YieldForecastTab: React.FC = () => {
  const [forecasts, setForecasts] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/yield/forecasts').catch(() => ({ data: { forecasts: [] } })),
      apiClient.get('/yield/stats').catch(() => ({ data: { stats: {} } })),
      apiClient.get('/yield/alerts').catch(() => ({ data: { alerts: [] } })),
    ]).then(([fRes, sRes, aRes]) => {
      setForecasts(fRes.data?.forecasts || []);
      setStats(sRes.data?.stats || {});
      setAlerts(aRes.data?.alerts || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Forecasts" value={stats?.total_forecasts || 0} icon={<Icon.TrendingUp className="w-4 h-4" />} />
        <MetricTile label="Total Forecast (kg)" value={Number(stats?.total_forecast_kg || 0).toFixed(0)} />
        <MetricTile label="Delivered (kg)" value={Number(stats?.total_delivered_kg || 0).toFixed(0)} accent="cargo" />
        <MetricTile label="Avg Utilization" value={`${Number(stats?.avg_utilization_pct || 0).toFixed(1)}%`} />
      </div>
      <div className="grid grid-cols-4 gap-3">
        <MetricTile label="Clear" value={stats?.clear_count || 0} />
        <MetricTile label="Warning" value={stats?.warning_count || 0} accent="cargo" />
        <MetricTile label="Critical" value={stats?.critical_count || 0} accent="cargo" />
        <MetricTile label="Blocked" value={stats?.blocked_count || 0} accent="cargo" />
      </div>

      {/* Fraud alerts toggle */}
      {alerts.length > 0 && (
        <Card padding="none" className="overflow-hidden border-2 border-danger-500/30">
          <button onClick={() => setShowAlerts(!showAlerts)} className="w-full p-4 flex items-center justify-between bg-danger-50 dark:bg-danger-500/10">
            <div className="flex items-center gap-2 text-danger-700 dark:text-danger-400">
              <Icon.Alert className="w-5 h-5" />
              <span className="text-sm font-semibold">{alerts.length} Yield Fraud Alerts ({alerts.filter(a => a.alert_level === 'BLOCKED').length} blocked)</span>
            </div>
            <svg className={`w-4 h-4 transition-transform ${showAlerts ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {showAlerts && (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Date</th><th>Farm</th><th>Farmer</th><th>Level</th><th>Delivered</th><th>Forecast</th><th>Utilization</th><th>Message</th></tr></thead>
                <tbody>
                  {alerts.slice(0, 20).map(a => (
                    <tr key={a.id} className={a.alert_level === 'BLOCKED' ? 'bg-danger-50/50 dark:bg-danger-500/5' : ''}>
                      <td className="text-2xs">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="text-sm">{a.farm_name || '—'}</td>
                      <td className="text-sm">{a.farmer_name || '—'}</td>
                      <td><StatusPill tone={a.alert_level === 'BLOCKED' ? 'danger' : a.alert_level === 'CRITICAL' ? 'danger' : 'warning'} dot>{a.alert_level}</StatusPill></td>
                      <td className="nums">{Number(a.delivered_kg).toFixed(1)} kg</td>
                      <td className="nums">{Number(a.forecast_kg).toFixed(1)} kg</td>
                      <td className={`nums font-medium ${a.utilization_pct > 150 ? 'text-danger-600' : 'text-warning-600'}`}>{Number(a.utilization_pct).toFixed(0)}%</td>
                      <td className="text-2xs max-w-xs truncate">{a.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Forecasts table */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Anti-fraud" title="Yield Forecasts" subtitle="Predictive biological capacity per farm — flags cocoa laundering" icon={<Icon.TrendingUp className="w-4 h-4" />} />
        </div>
        {forecasts.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.TrendingUp className="w-5 h-5" />} title="No forecasts yet" description="Forecasts are auto-generated when batches are logged for a farm." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Farm</th><th>Farmer</th><th>Village</th><th>Trees</th><th>Variety</th><th>Max Forecast</th><th>Delivered</th><th>Utilization</th><th>Status</th></tr></thead>
              <tbody>
                {forecasts.map(f => (
                  <tr key={f.id} className={f.fraud_alert_level === 'BLOCKED' ? 'bg-danger-50/50 dark:bg-danger-500/5' : f.fraud_alert_level !== 'CLEAR' ? 'bg-warning-50/50 dark:bg-warning-500/5' : ''}>
                    <td className="text-sm font-medium">{f.farm_name || '—'}</td>
                    <td className="text-sm">{f.farmer_name || '—'}</td>
                    <td className="text-2xs">{f.farmer_village || '—'}</td>
                    <td className="nums">{f.tree_count || '—'}</td>
                    <td className="text-2xs">{f.cocoa_variety || 'Traditional'}</td>
                    <td className="nums font-medium">{Number(f.max_expected_kg).toFixed(0)} kg</td>
                    <td className="nums">{Number(f.cumulative_delivered_kg).toFixed(0)} kg</td>
                    <td className={`nums font-medium ${Number(f.utilization_pct) > 150 ? 'text-danger-600' : Number(f.utilization_pct) > 100 ? 'text-warning-600' : 'text-success-600'}`}>
                      {Number(f.utilization_pct).toFixed(0)}%
                    </td>
                    <td><StatusPill tone={f.fraud_alert_level === 'BLOCKED' ? 'danger' : f.fraud_alert_level === 'CRITICAL' ? 'danger' : f.fraud_alert_level === 'WARNING' ? 'warning' : 'success'} dot>{f.fraud_alert_level}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.7.9 — Lab Tests Tab (MRL / Cadmium Compliance)
 * Records lab test results and blocks export for non-compliant batches
 * ═══════════════════════════════════════════════════════════════ */
const LabTestsTab: React.FC = () => {
  const [tests, setTests] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    batch_id: '', test_reference: '', lab_name: '', test_date: new Date().toISOString().slice(0, 10),
    cadmium_mg_kg: '', overall_result: 'CONDITIONAL', notes: ''
  });

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/lab-tests').catch(() => ({ data: { lab_tests: [] } })),
      apiClient.get('/lab-tests/stats').catch(() => ({ data: { stats: {} } })),
    ]).then(([tRes, sRes]) => {
      setTests(tRes.data?.lab_tests || []);
      setStats(sRes.data?.stats || {});
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      await apiClient.post('/lab-tests', {
        ...form,
        cadmium_mg_kg: form.cadmium_mg_kg ? parseFloat(form.cadmium_mg_kg) : undefined,
      });
      setShowCreate(false);
      setForm({ batch_id: '', test_reference: '', lab_name: '', test_date: new Date().toISOString().slice(0, 10), cadmium_mg_kg: '', overall_result: 'CONDITIONAL', notes: '' });
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create lab test');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Tests (90d)" value={stats?.total_tests || 0} icon={<Icon.Shield className="w-4 h-4" />} />
        <MetricTile label="Passing" value={stats?.pass_count || 0} accent="cargo" />
        <MetricTile label="Failing" value={stats?.fail_count || 0} accent="cargo" />
        <MetricTile label="Export Ready" value={stats?.export_ready_batches || 0} />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="EU Compliance" title="Lab Tests (MRL / Cadmium)" subtitle="Cadmium + heavy metal + pesticide residue test results" icon={<Icon.Shield className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs">
            <Icon.Plus className="w-4 h-4" /> Add Lab Test
          </button>
        </div>
        {tests.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No lab tests recorded" description="Add a lab test result to enable EU export routing for a batch." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Test Ref</th><th>Batch</th><th>Farmer</th><th>Lab</th><th>Date</th><th>Cadmium</th><th>Result</th><th>Approved</th></tr></thead>
              <tbody>
                {tests.map(t => (
                  <tr key={t.id}>
                    <td className="text-xs font-mono">{t.test_reference}</td>
                    <td className="text-xs font-mono">{t.batch_id_short?.slice(0, 8)}…</td>
                    <td className="text-sm">{t.farmer_name || '—'}</td>
                    <td className="text-2xs">{t.lab_name}</td>
                    <td className="text-2xs">{new Date(t.test_date).toLocaleDateString()}</td>
                    <td className={`nums ${t.cadmium_pass === false ? 'text-danger-600 font-bold' : t.cadmium_pass ? 'text-success-600' : ''}`}>
                      {t.cadmium_mg_kg ? `${Number(t.cadmium_mg_kg).toFixed(3)} mg/kg` : '—'}
                    </td>
                    <td><StatusPill tone={t.overall_result === 'PASS' ? 'success' : t.overall_result === 'FAIL' ? 'danger' : 'warning'} dot>{t.overall_result}</StatusPill></td>
                    <td className="text-2xs">{t.approved_at ? '✓' : 'Pending'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Add Lab Test Result"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.batch_id || !form.test_reference || !form.lab_name} className="btn-primary flex-1">Create Test</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Batch ID *</label><input className="input" value={form.batch_id} onChange={e => setForm({ ...form, batch_id: e.target.value })} placeholder="Paste batch UUID" /></div>
            <div><label className="label">Test Reference *</label><input className="input" value={form.test_reference} onChange={e => setForm({ ...form, test_reference: e.target.value })} placeholder="e.g. LAB-2026-001" /></div>
            <div><label className="label">Lab Name *</label><input className="input" value={form.lab_name} onChange={e => setForm({ ...form, lab_name: e.target.value })} placeholder="e.g. SGS Cameroon" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Test Date *</label><input type="date" className="input" value={form.test_date} onChange={e => setForm({ ...form, test_date: e.target.value })} /></div>
              <div><label className="label">Cadmium (mg/kg)</label><input type="number" step="0.001" className="input" value={form.cadmium_mg_kg} onChange={e => setForm({ ...form, cadmium_mg_kg: e.target.value })} placeholder="0.8 max" /></div>
            </div>
            <div><label className="label">Overall Result</label><select className="input" value={form.overall_result} onChange={e => setForm({ ...form, overall_result: e.target.value })}><option value="PASS">PASS</option><option value="FAIL">FAIL</option><option value="CONDITIONAL">CONDITIONAL</option></select></div>
            <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Alert tone="info">EU cadmium limit for raw cocoa beans: 0.8 mg/kg. A batch with cadmium &gt; 0.8 cannot be routed to EXPORT.</Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.7.9 — Cash Floats Tab (Field Agent Cash Tracker)
 * Tracks cash advances given to agents + reconciliation
 * ═══════════════════════════════════════════════════════════════ */
const CashFloatsTab: React.FC = () => {
  const [floats, setFloats] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ agent_id: '', advance_amount_xaf: '', village_scope: '', notes: '' });

  const refresh = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/cash-floats').catch(() => ({ data: { floats: [] } })),
      apiClient.get('/cash-floats/stats').catch(() => ({ data: { stats: {} } })),
      apiClient.get('/agents').catch(() => ({ data: { agents: [] } })),
    ]).then(([fRes, sRes, aRes]) => {
      setFloats(fRes.data?.floats || []);
      setStats(sRes.data?.stats || {});
      setAgents(Array.isArray(aRes.data) ? aRes.data : (aRes.data?.agents || []));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      await apiClient.post('/cash-floats', {
        ...form,
        advance_amount_xaf: parseFloat(form.advance_amount_xaf),
      });
      setShowCreate(false);
      setForm({ agent_id: '', advance_amount_xaf: '', village_scope: '', notes: '' });
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to issue cash float');
    }
  };

  const handleReconcile = async (floatId: string) => {
    const shortageStr = prompt('Enter shortage amount in XAF (0 if none):');
    if (shortageStr === null) return;
    const shortage = parseFloat(shortageStr) || 0;
    const reason = shortage > 0 ? prompt('Reason for shortage:') : null;
    try {
      await apiClient.post(`/cash-floats/${floatId}/reconcile`, { shortage_amount_xaf: shortage, shortage_reason: reason });
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Reconciliation failed');
    }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Active Floats" value={stats?.active_floats || 0} icon={<Icon.Wallet className="w-4 h-4" />} />
        <MetricTile label="Total Advance (XAF)" value={Number(stats?.total_active_xaf || 0).toLocaleString()} />
        <MetricTile label="Total Spent (XAF)" value={Number(stats?.total_spent_xaf || 0).toLocaleString()} accent="cargo" />
        <MetricTile label="Shortages" value={stats?.shortage_floats || 0} accent="cargo" />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Anti-fraud" title="Cash Floats" subtitle="Track cash advances to field agents + reconciliation" icon={<Icon.Wallet className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs">
            <Icon.Plus className="w-4 h-4" /> Issue Advance
          </button>
        </div>
        {floats.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Wallet className="w-5 h-5" />} title="No cash floats" description="Issue a cash advance to a field agent to start tracking." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Float #</th><th>Agent</th><th>Advance</th><th>Spent</th><th>Balance</th><th>Utilization</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
              <tbody>
                {floats.map(f => (
                  <tr key={f.id} className={f.status === 'SHORTAGE' ? 'bg-danger-50/50 dark:bg-danger-500/5' : f.status === 'OVER' ? 'bg-warning-50/50 dark:bg-warning-500/5' : ''}>
                    <td className="text-xs font-mono">{f.float_number}</td>
                    <td className="text-sm">{f.agent_name || '—'}</td>
                    <td className="nums">{Number(f.advance_amount_xaf).toLocaleString()}</td>
                    <td className="nums">{Number(f.total_spent_xaf).toLocaleString()}</td>
                    <td className={`nums font-medium ${Number(f.balance_xaf) < 0 ? 'text-danger-600' : ''}`}>{Number(f.balance_xaf).toLocaleString()}</td>
                    <td className={`nums ${Number(f.utilization_pct) > 100 ? 'text-danger-600 font-bold' : Number(f.utilization_pct) > 80 ? 'text-warning-600' : ''}`}>{Number(f.utilization_pct).toFixed(0)}%</td>
                    <td><StatusPill tone={f.status === 'ACTIVE' ? 'info' : f.status === 'RECONCILED' ? 'success' : f.status === 'SHORTAGE' ? 'danger' : 'warning'} dot>{f.status}</StatusPill></td>
                    <td className="text-2xs">{new Date(f.advance_date).toLocaleDateString()}</td>
                    <td>{f.status === 'ACTIVE' && <button onClick={() => handleReconcile(f.id)} className="btn-secondary text-xs">Reconcile</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Issue Cash Advance"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.agent_id || !form.advance_amount_xaf} className="btn-primary flex-1">Issue Advance</button></div>}>
          <div className="space-y-3">
            <div>
              <label className="label">Field Agent *</label>
              <select className="input" value={form.agent_id} onChange={e => setForm({ ...form, agent_id: e.target.value })}>
                <option value="">— Select agent —</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.phone || 'no phone'})</option>)}
              </select>
            </div>
            <div><label className="label">Advance Amount (XAF) *</label><input type="number" className="input" value={form.advance_amount_xaf} onChange={e => setForm({ ...form, advance_amount_xaf: e.target.value })} placeholder="e.g. 500000" /></div>
            <div><label className="label">Village Scope</label><input className="input" value={form.village_scope} onChange={e => setForm({ ...form, village_scope: e.target.value })} placeholder="e.g. Buea, Tiko, Limbe" /></div>
            <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Alert tone="info">The agent's float balance will be automatically reduced as they log batches. Reconcile when the agent returns unused cash.</Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v2.8: API KEYS TAB (GAP 3)                                       */
/* ════════════════════════════════════════════════════════════════ */
/* Surfaces the model registry + routing table from the v2.3 AI     */
/* Governance layer. Each row maps a model_code → its env var +     */
/* signup URL (sourced from server/.env.example documentation).     */
/* The Test button runs the COPILOT agent end-to-end to verify the  */
/* LLM path is operational.                                         */
/* ════════════════════════════════════════════════════════════════ */
const ApiKeysTab: React.FC = () => { // v4.1 — ONE KEY PER PROVIDER (was one-per-model in v3.0)
  // v4.1: state for provider-grouped key management
  const [providers, setProviders] = useState<any[]>([]); // v4.1 — array from GET /api/ai-keys
  const [aiKeysLoading, setAiKeysLoading] = useState(true);
  const [aiKeysError, setAiKeysError] = useState<string | null>(null);
  const [keySheetOpen, setKeySheetOpen] = useState(false);
  const [keySheetData, setKeySheetData] = useState<any>(null); // v4.1 — which provider is being edited
  const [keyInputValue, setKeyInputValue] = useState('');
  const [baseUrlInputValue, setBaseUrlInputValue] = useState(''); // v4.1.1 — custom base URL
  const [testingProvider, setTestingProvider] = useState<string | null>(null); // v4.1 — provider_code being tested
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({}); // v4.1 — provider_code → result
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null); // v4.1 — which provider's models are expanded

  // v4.1.4: Custom provider/model management state
  const [customProviderSheetOpen, setCustomProviderSheetOpen] = useState(false);
  const [editingCustomProvider, setEditingCustomProvider] = useState<any>(null);
  const [customModelSheetOpen, setCustomModelSheetOpen] = useState(false);
  const [editingCustomModel, setEditingCustomModel] = useState<any>(null);
  const [customModelProviderCode, setCustomModelProviderCode] = useState<string>('');
  const [customProviderForm, setCustomProviderForm] = useState<any>({ provider_code: '', provider_name: '', env_var: '', signup_url: '', is_free: true, priority: 'MEDIUM', purpose: '', key_prefix_hint: '', base_url: '', api_style: 'openai' });
  const [customModelForm, setCustomModelForm] = useState<any>({ model_code: '', model_name: '', task_type: 'fast_response', is_default: false, note: '' });

  const PRIORITY_STYLE: Record<string, string> = {
    CRITICAL: 'bg-danger-500 text-white',
    HIGH:     'bg-cargo-500 text-white',
    MEDIUM:   'bg-warning-500 text-white',
    LOW:      'bg-ink-200 dark:bg-ink-700 text-ink-700 dark:text-ink-200',
  };
  const SOURCE_LABEL: Record<string, string> = { database: 'Database', env: 'Env', none: 'None' };

  // v4.1: fetch all provider key statuses from the backend
  const fetchAiKeys = useCallback(async () => {
    setAiKeysLoading(true);
    setAiKeysError(null);
    try {
      const res = await apiClient.get('/ai-keys');
      // v4.1: API now returns { providers: [...], total_providers, total_models, ... }
      const provs = res.data?.providers ?? [];
      setProviders(Array.isArray(provs) ? provs : []);
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to load AI keys from /api/ai-keys');
    } finally {
      setAiKeysLoading(false);
    }
  }, []);

  useEffect(() => { fetchAiKeys(); }, [fetchAiKeys]);

  const openKeySheet = (p: any) => {
    setKeySheetData(p);
    setKeyInputValue('');
    setBaseUrlInputValue(p.base_url || ''); // v4.1.1 — pre-fill existing base_url
    setKeySheetOpen(true);
    setSaveSuccess(null);
    setAiKeysError(null);
  };
  const closeKeySheet = () => {
    setKeySheetOpen(false);
    setKeySheetData(null);
    setKeyInputValue('');
    setBaseUrlInputValue('');
    setSaveSuccess(null);
  };

  // v4.1: test a provider's key — POST /api/ai-keys/:providerCode/test
  const handleTestKey = async (providerCode: string) => {
    setTestingProvider(providerCode);
    setTestResults(prev => ({ ...prev, [providerCode]: { success: false, message: 'Testing…' } }));
    try {
      const res = await apiClient.post(`/ai-keys/${providerCode}/test`);
      const data = res.data ?? { success: false, message: 'No response from server' };
      setTestResults(prev => ({ ...prev, [providerCode]: { success: !!data.success, message: data.message || (data.success ? 'Test passed' : 'Test failed') } }));
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Test request failed';
      setTestResults(prev => ({ ...prev, [providerCode]: { success: false, message: msg } }));
    } finally {
      setTestingProvider(null);
    }
  };

  // v4.1: save a provider's key — PUT /api/ai-keys/:providerCode
  const handleSaveKey = async (runTest: boolean) => {
    if (!keySheetData) return;
    if (!keyInputValue || keyInputValue.trim().length < 5) {
      setAiKeysError('API key must be at least 5 characters.');
      return;
    }
    setSaving(true);
    setSaveSuccess(null);
    setAiKeysError(null);
    try {
      // v4.1.1: Send both key and optional base_url
      await apiClient.put(`/ai-keys/${keySheetData.provider_code}`, {
        value: keyInputValue.trim(),
        base_url: baseUrlInputValue.trim() || undefined,
      });
      setSaveSuccess(`${keySheetData.provider} key saved. All ${keySheetData.models_count} model(s) now share this key.`);
      if (runTest) {
        await handleTestKey(keySheetData.provider_code);
        closeKeySheet();
      } else {
        await fetchAiKeys();
      }
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to save API key.');
    } finally {
      setSaving(false);
    }
  };

  // v4.1: remove a provider's key — DELETE /api/ai-keys/:providerCode
  const handleRemoveKey = async (providerCode: string, providerName: string, modelCount: number) => {
    if (!window.confirm(`Remove ${providerName}? This deletes the key and all ${modelCount} model(s) will stop working until you re-add a key.`)) return;
    try {
      await apiClient.delete(`/ai-keys/${providerCode}`);
      setSaveSuccess(`${providerName} key removed.`);
      setTestResults(prev => { const next = { ...prev }; delete next[providerCode]; return next; });
      await fetchAiKeys();
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to remove key.');
    }
  };

  // v4.2: View/Copy individual provider API key
  const [viewKeySheetOpen, setViewKeySheetOpen] = useState(false);
  const [viewKeyData, setViewKeyData] = useState<any>(null);
  const [viewKeyValue, setViewKeyValue] = useState<string | null>(null);
  const [viewKeyLoading, setViewKeyLoading] = useState(false);
  const [viewKeyCopied, setViewKeyCopied] = useState(false);

  const handleViewProviderKey = async (provider: any) => {
    setViewKeyData(provider);
    setViewKeySheetOpen(true);
    setViewKeyValue(null);
    setViewKeyLoading(true);
    setViewKeyCopied(false);
    try {
      // Fetch the full key value from the all-keys endpoint
      const res = await apiClient.get('/data-sources/all-keys/list');
      const allKeys = res.data?.keys || [];
      const keyEntry = allKeys.find((k: any) => k.key === provider.db_key);
      setViewKeyValue(keyEntry?.full_value || null);
    } catch {
      setViewKeyValue(null);
    } finally {
      setViewKeyLoading(false);
    }
  };

  const handleCopyViewKey = async () => {
    if (!viewKeyValue) return;
    try {
      await navigator.clipboard.writeText(viewKeyValue);
      setViewKeyCopied(true);
      setTimeout(() => setViewKeyCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = viewKeyValue;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setViewKeyCopied(true);
      setTimeout(() => setViewKeyCopied(false), 2000);
    }
  };

  // v4.1.4: Custom provider handlers
  const handleSaveCustomProvider = async () => {
    if (!customProviderForm.provider_code || !customProviderForm.provider_name || !customProviderForm.env_var) {
      setAiKeysError('Provider code, name, and env var are required.');
      return;
    }
    setSaving(true);
    setAiKeysError(null);
    try {
      if (editingCustomProvider) {
        await apiClient.put(`/ai-keys/custom/provider/${editingCustomProvider.provider_code}`, customProviderForm);
        setSaveSuccess('Custom provider updated.');
      } else {
        await apiClient.post('/ai-keys/custom/provider', customProviderForm);
        setSaveSuccess('Custom provider added.');
      }
      setCustomProviderSheetOpen(false);
      setEditingCustomProvider(null);
      setCustomProviderForm({ provider_code: '', provider_name: '', env_var: '', signup_url: '', is_free: true, priority: 'MEDIUM', purpose: '', key_prefix_hint: '', base_url: '', api_style: 'openai' });
      await fetchAiKeys();
      setTimeout(() => setSaveSuccess(null), 4000);
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to save custom provider.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomProvider = async (providerCode: string, providerName: string) => {
    if (!window.confirm(`Delete custom provider "${providerName}"? This removes the provider, all its models, and any stored API key.`)) return;
    try {
      await apiClient.delete(`/ai-keys/custom/provider/${providerCode}`);
      setSaveSuccess(`Custom provider "${providerName}" deleted.`);
      await fetchAiKeys();
      setTimeout(() => setSaveSuccess(null), 4000);
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to delete custom provider.');
    }
  };

  const handleSaveCustomModel = async () => {
    if (!customModelProviderCode || !customModelForm.model_code || !customModelForm.model_name) {
      setAiKeysError('Provider, model code, and model name are required.');
      return;
    }
    setSaving(true);
    setAiKeysError(null);
    try {
      await apiClient.post('/ai-keys/custom/model', { ...customModelForm, provider_code: customModelProviderCode });
      setSaveSuccess('Custom model saved.');
      setCustomModelSheetOpen(false);
      setCustomModelForm({ model_code: '', model_name: '', task_type: 'fast_response', is_default: false, note: '' });
      await fetchAiKeys();
      setTimeout(() => setSaveSuccess(null), 4000);
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to save custom model.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomModel = async (providerCode: string, modelCode: string) => {
    if (!window.confirm(`Delete model "${modelCode}"?`)) return;
    try {
      // v4.1.10: Use encodeURIComponent to handle slashes in model codes (e.g. "meta-llama/llama-3.3-70b-instruct")
      await apiClient.delete(`/ai-keys/custom/model/${providerCode}/${modelCode}`);
      setSaveSuccess(`Model "${modelCode}" deleted.`);
      await fetchAiKeys();
      setTimeout(() => setSaveSuccess(null), 4000);
    } catch (err: any) {
      setAiKeysError(err.response?.data?.error || 'Failed to delete model.');
    }
  };

  if (aiKeysLoading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  const configuredCount = providers.filter((p: any) => p.configured).length;
  const notConfiguredCount = providers.length - configuredCount;
  const totalModels = providers.reduce((sum: number, p: any) => sum + (p.models_count || 0), 0);

  return (
    <div className="space-y-4 api-keys-tab">
      <SectionHeader
        eyebrow="Configuration"
        title="API Key Management"
        subtitle="v4.1: One key per provider — enter a key once and all models of that provider share it. Keys take effect immediately."
        icon={<Icon.Settings className="w-4 h-4" />}
        actions={<button onClick={fetchAiKeys} className="btn-secondary text-xs inline-flex items-center gap-1.5"><Icon.Refresh className="w-3 h-3" /> Refresh</button>}
      />

      {aiKeysError && <Alert tone="danger" onClose={() => setAiKeysError(null)}>{aiKeysError}</Alert>}
      {saveSuccess && <Alert tone="success" onClose={() => setSaveSuccess(null)}>{saveSuccess}</Alert>}

      {/* v4.1: ─── Summary tiles ─── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricTile label="Total Providers" value={providers.length}  hint="Supported AI providers" icon={<Icon.Cpu className="w-4 h-4" />} accent="admin" />
        <MetricTile label="Total Models"    value={totalModels}        hint="Across all providers"  icon={<Icon.Layers className="w-4 h-4" />} />
        <MetricTile label="Configured"      value={configuredCount}    hint="Ready to use"           icon={<Icon.Check className="w-4 h-4" />} />
        <MetricTile label="Not Configured"  value={notConfiguredCount} hint="Action required"        icon={<Icon.Alert className="w-4 h-4" />} accent="cargo" />
      </div>

      {/* v4.1: ─── Provider Registry (ONE row per provider, with model chips) ─── */}
      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader
            eyebrow="Registry"
            title="AI Providers — One Key Per Provider"
            subtitle="Live from GET /api/ai-keys · Click a provider to see its models · Custom providers can be added/edited"
            icon={<Icon.Database className="w-4 h-4" />}
            actions={
              <button onClick={() => { setEditingCustomProvider(null); setCustomProviderSheetOpen(true); }} className="btn-accent bg-admin-600 hover:bg-admin-700 text-xs inline-flex items-center gap-1.5">
                <Icon.Plus className="w-3 h-3" /> Add Provider
              </button>
            }
          />
        </div>
        {providers.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Cpu className="w-5 h-5" />} title="No providers in registry" description="The /api/ai-keys endpoint returned an empty list." /></div>
        ) : (
          <div className="divide-y divide-ink-200 dark:divide-ink-800 api-keys-provider-list">
            {providers.map((p: any) => {
              const result = testResults[p.provider_code];
              const isExpanded = expandedProvider === p.provider_code;
              return (
                <div key={p.provider_code} className="p-4 api-keys-provider-row">
                  {/* Provider header row */}
                  <div className="flex items-start justify-between gap-3 flex-wrap api-keys-provider-layout">
                    <div className="min-w-0 flex-1 api-keys-provider-info">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-ink-900 dark:text-ink-50">{p.provider}</span>
                        <span className="font-mono text-2xs text-ink-400 dark:text-ink-500">({p.provider_code})</span>
                        {p.configured
                          ? <StatusPill tone="success" dot>✅ Configured</StatusPill>
                          : <StatusPill tone="danger"  dot>❌ Not Configured</StatusPill>}
                        <span className={`inline-flex items-center px-2 py-0.5 text-2xs font-semibold rounded-full ${PRIORITY_STYLE[p.priority] || PRIORITY_STYLE.LOW}`}>{p.priority || 'LOW'}</span>
                        {p.is_free ? <StatusPill tone="success">FREE</StatusPill> : <StatusPill tone="neutral">PAID</StatusPill>}
                        <span className="text-2xs px-1.5 py-0.5 rounded bg-info-100 dark:bg-info-500/20 text-info-800 dark:text-info-300">{p.models_count} model{p.models_count !== 1 ? 's' : ''}</span>
                      </div>
                      {/* Masked key + source */}
                      {p.configured && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="font-mono text-2xs text-ink-500 dark:text-ink-400">🔑 {p.masked_value}</span>
                          <StatusPill tone={p.source === 'database' ? 'info' : 'neutral'}>{SOURCE_LABEL[p.source] || p.source}</StatusPill>
                          {p.last_updated && <span className="text-2xs text-ink-400 dark:text-ink-500">· {new Date(p.last_updated).toLocaleDateString()}</span>}
                        </div>
                      )}
                      <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1 max-w-2xl">{p.purpose}</p>
                    </div>
                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0 api-keys-provider-actions">
                      {p.signup_url && <a href={p.signup_url} target="_blank" rel="noreferrer" className="btn-secondary text-2xs inline-flex items-center gap-1"><Icon.MapPin className="w-3 h-3" /> Get Key ↗</a>}
                      {p.configured && (
                        <>
                          <button onClick={() => handleTestKey(p.provider_code)} disabled={testingProvider === p.provider_code} className="btn-secondary text-2xs inline-flex items-center gap-1.5">
                            {testingProvider === p.provider_code ? <Spinner size="sm" /> : <Icon.Activity className="w-3 h-3" />}
                            {testingProvider === p.provider_code ? 'Testing…' : 'Test'}
                          </button>
                          {/* v4.2: View/Copy API key button */}
                          <button onClick={() => handleViewProviderKey(p)} className="btn-secondary text-2xs inline-flex items-center gap-1.5"><Icon.Key className="w-3 h-3" /> View Key</button>
                          <button onClick={() => openKeySheet(p)} className="btn-secondary text-2xs inline-flex items-center gap-1.5"><Icon.Refresh className="w-3 h-3" /> Replace</button>
                          <button onClick={() => handleRemoveKey(p.provider_code, p.provider, p.models_count)} className="btn-danger text-2xs inline-flex items-center gap-1.5"><Icon.X className="w-3 h-3" /> Remove</button>
                        </>
                      )}
                      {!p.configured && (
                        <button onClick={() => openKeySheet(p)} className="btn-accent bg-admin-600 hover:bg-admin-700 text-2xs inline-flex items-center gap-1.5"><Icon.Plus className="w-3 h-3" /> Enter Key</button>
                      )}
                      {/* v4.1.4: Edit/Delete buttons for custom providers */}
                      {p.is_custom && (
                        <>
                          <button onClick={() => { setEditingCustomProvider(p); setCustomProviderSheetOpen(true); }} className="btn-secondary text-2xs inline-flex items-center gap-1.5"><Icon.Settings className="w-3 h-3" /> Edit</button>
                          <button onClick={() => handleDeleteCustomProvider(p.provider_code, p.provider)} className="btn-danger text-2xs inline-flex items-center gap-1.5"><Icon.X className="w-3 h-3" /> Delete</button>
                        </>
                      )}
                      <button onClick={() => setExpandedProvider(isExpanded ? null : p.provider_code)} className="btn-ghost text-2xs inline-flex items-center gap-1">
                        {isExpanded ? '▲ Hide' : '▼ Show'} Models
                      </button>
                    </div>
                  </div>

                  {/* Test result */}
                  {result && (
                    <div className="mt-2">
                      <Alert tone={result.success ? 'success' : 'danger'} onClose={() => setTestResults(prev => { const next = { ...prev }; delete next[p.provider_code]; return next; })}>
                        <strong>{p.provider}:</strong> {result.message}
                      </Alert>
                    </div>
                  )}

                  {/* Model chips (expandable) */}
                  {isExpanded && p.models && p.models.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-ink-200 dark:border-ink-700">
                      <div className="flex items-center justify-between mb-2 api-keys-model-header">
                        <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Models that share this ONE API key ({p.models.length}):</p>
                        {/* v4.1.5: Add Model button on ALL providers (not just custom) */}
                        <button onClick={() => { setEditingCustomModel(null); setCustomModelProviderCode(p.provider_code); setCustomModelSheetOpen(true); }} className="btn-secondary text-2xs inline-flex items-center gap-1"><Icon.Plus className="w-3 h-3" /> Add Model</button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {p.models.map((m: any) => (
                          <span key={m.code}
                            className={`text-2xs px-2 py-1 rounded border ${m.is_default ? 'border-success-500/30 bg-success-50 dark:bg-success-500/10 text-success-800 dark:text-success-300' : 'border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800 text-ink-700 dark:text-ink-300'}`}
                            title={m.note}>
                            <span className="font-mono">{m.code}</span>
                            {m.is_default && <span className="ml-1">★</span>}
                            <span className="ml-1 text-ink-400 dark:text-ink-500">({m.task_type})</span>
                            {/* v4.1.5: Delete model button on ALL models (user-added ones only) */}
                            <button onClick={() => handleDeleteCustomModel(p.provider_code, m.code)} className="ml-1 text-danger-500 hover:text-danger-700" title="Delete model">
                              <Icon.X className="w-2.5 h-2.5 inline" />
                            </button>
                          </span>
                        ))}
                      </div>
                      {p.default_model && (
                        <p className="text-2xs text-ink-500 dark:text-ink-400 mt-2">★ Default model: <span className="font-mono">{p.default_model}</span></p>
                      )}
                    </div>
                  )}
                  {/* v4.1.5: Show "Add Model" even if no models yet (for any provider) */}
                  {isExpanded && (!p.models || p.models.length === 0) && (
                    <div className="mt-3 pt-3 border-t border-ink-200 dark:border-ink-700">
                      <p className="text-2xs text-ink-500 dark:text-ink-400 mb-2">No models yet. Add your first model:</p>
                      <button onClick={() => { setEditingCustomModel(null); setCustomModelProviderCode(p.provider_code); setCustomModelSheetOpen(true); }} className="btn-secondary text-2xs inline-flex items-center gap-1"><Icon.Plus className="w-3 h-3" /> Add Model</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* v4.1: ─── Quick Start Guide ─── */}
      <Card>
        <SectionHeader
          eyebrow="Quick Start"
          title="Get Running in 5 Minutes"
          subtitle="One free Gemini API key unlocks all 5 Gemini models immediately."
          icon={<Icon.Sparkles className="w-4 h-4" />}
        />
        <ol className="mt-4 space-y-2.5 text-sm text-ink-700 dark:text-ink-200 list-decimal list-inside">
          <li>Get a <strong>FREE</strong> Gemini API key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="text-admin-700 dark:text-admin-500 hover:underline">https://aistudio.google.com/app/apikey</a></li>
          <li>Click <strong>"Enter Key"</strong> next to Google</li>
          <li>Paste the key and click <strong>"Save &amp; Test"</strong></li>
          <li>All <strong>5 Gemini models</strong> will immediately share this key</li>
          <li>Add more provider keys (Grok, Groq, HuggingFace) to unlock additional models</li>
        </ol>
        <Alert tone="info" className="mt-4">
          v4.1: One key per provider. Entering a Gemini key once lights up all 5 Gemini models. Entering an OpenRouter key once lights up all 15+ free models. Keys take effect immediately — no server restart needed.
        </Alert>
      </Card>

      {/* v4.1: ─── Sheet modal for entering / replacing a key ─── */}
      <Sheet
        open={keySheetOpen}
        onClose={closeKeySheet}
        title={keySheetData ? `${keySheetData.configured ? 'Replace' : 'Enter'} Key — ${keySheetData.provider}` : 'Enter Key'}
        footer={
          <div className="flex flex-col gap-2">
            {saveSuccess && <Alert tone="success" onClose={() => setSaveSuccess(null)}>{saveSuccess}</Alert>}
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={() => handleSaveKey(false)} disabled={saving || keyInputValue.trim().length < 5} className="btn-secondary flex-1 inline-flex items-center justify-center gap-1.5">
                {saving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save
              </button>
              <button onClick={() => handleSaveKey(true)} disabled={saving || keyInputValue.trim().length < 5} className="btn-accent bg-admin-600 hover:bg-admin-700 flex-1 inline-flex items-center justify-center gap-1.5">
                {saving ? <Spinner size="sm" /> : <Icon.Activity className="w-4 h-4" />} Save &amp; Test
              </button>
              <button onClick={closeKeySheet} className="btn-ghost">Cancel</button>
            </div>
          </div>
        }
      >
        {keySheetData && (
          <div className="space-y-4">
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Provider</p>
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{keySheetData.provider}</p>
              <p className="text-2xs text-ink-500 dark:text-ink-400">{keySheetData.provider_code} · {keySheetData.priority} · {keySheetData.is_free ? 'FREE' : 'PAID'} · {keySheetData.models_count} model(s)</p>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Purpose</p>
              <p className="text-sm text-ink-700 dark:text-ink-200 text-pretty">{keySheetData.purpose}</p>
            </div>
            {/* v4.1: Show models that will share this key */}
            {keySheetData.models && keySheetData.models.length > 0 && (
              <div>
                <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1.5">Models that will share this key ({keySheetData.models.length}):</p>
                <div className="flex flex-wrap gap-1">
                  {keySheetData.models.map((m: any) => (
                    <span key={m.code} className={`text-2xs px-1.5 py-0.5 rounded font-mono ${m.is_default ? 'bg-success-100 dark:bg-success-500/20 text-success-800 dark:text-success-300' : 'bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-300'}`} title={m.note}>
                      {m.code}{m.is_default ? ' ★' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Get a key</p>
              {keySheetData.signup_url
                ? <a href={keySheetData.signup_url} target="_blank" rel="noreferrer" className="text-sm text-admin-700 dark:text-admin-500 hover:underline break-all">{keySheetData.signup_url}</a>
                : <p className="text-sm text-ink-400">No signup URL available</p>}
            </div>
            <div>
              <label className="label" htmlFor="api-key-input">API Key Value {keySheetData.key_prefix_hint && <span className="text-2xs text-ink-400 ml-1">(format: {keySheetData.key_prefix_hint})</span>}</label>
              <input
                id="api-key-input"
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={keyInputValue}
                onChange={e => setKeyInputValue(e.target.value)}
                placeholder={keySheetData.key_prefix_hint || 'Paste your API key here…'}
                className="input font-mono"
              />
              <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1.5">Min 5 characters. This ONE key will be shared by all {keySheetData.models_count} model(s) above. Takes effect immediately.</p>
            </div>
            {/* v4.1.1: Custom base URL field (for Alibaba MaaS, custom OpenAI endpoints, etc.) */}
            <div>
              <label className="label" htmlFor="api-baseurl-input">
                Custom Base URL <span className="text-2xs text-ink-400 ml-1">(optional — for Alibaba MaaS / custom endpoints)</span>
              </label>
              <input
                id="api-baseurl-input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={baseUrlInputValue}
                onChange={e => setBaseUrlInputValue(e.target.value)}
                placeholder="https://ws-xxx.ap-southeast-1.maas.aliyuncs.com/compatible-mode/v1"
                className="input font-mono"
              />
              <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1.5">
                Leave blank for standard endpoints. For Alibaba Bailian (MaaS), paste the OpenAI Compatible Endpoint URL from your workspace.
              </p>
            </div>
          </div>
        )}
      </Sheet>

      {/* v4.2: Sheet modal for viewing/copying a provider's API key */}
      <Sheet
        open={viewKeySheetOpen}
        onClose={() => { setViewKeySheetOpen(false); setViewKeyData(null); setViewKeyValue(null); setViewKeyCopied(false); }}
        title={viewKeyData ? `API Key — ${viewKeyData.provider}` : 'View Key'}
        footer={
          <div className="flex gap-2">
            <button onClick={handleCopyViewKey} disabled={!viewKeyValue || viewKeyLoading} className={`btn-primary flex-1 inline-flex items-center justify-center gap-1.5 ${viewKeyCopied ? 'text-success-600' : ''}`}>
              {viewKeyCopied ? <><Icon.Check className="w-4 h-4" /> Copied!</> : <><Icon.Key className="w-4 h-4" /> Copy to Clipboard</>}
            </button>
            <button onClick={() => { setViewKeySheetOpen(false); setViewKeyData(null); setViewKeyValue(null); }} className="btn-ghost">Close</button>
          </div>
        }
      >
        {viewKeyData && (
          <div className="space-y-3">
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Provider</p>
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{viewKeyData.provider}</p>
              <p className="text-2xs text-ink-500 dark:text-ink-400">{viewKeyData.provider_code} · {viewKeyData.models_count} model(s)</p>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">DB Key</p>
              <p className="font-mono text-2xs text-ink-600 dark:text-ink-300">{viewKeyData.db_key}</p>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Masked</p>
              <p className="font-mono text-2xs text-ink-500 dark:text-ink-400">{viewKeyData.masked_value || '—'}</p>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Full API Key</p>
              {viewKeyLoading ? (
                <div className="flex items-center gap-2 py-2"><Spinner size="sm" /> <span className="text-2xs text-ink-400">Loading...</span></div>
              ) : viewKeyValue ? (
                <div className="p-2.5 rounded-lg bg-ink-100 dark:bg-ink-800 border border-ink-200 dark:border-ink-700">
                  <p className="font-mono text-2xs text-ink-700 dark:text-ink-200 break-all">{viewKeyValue}</p>
                </div>
              ) : (
                <p className="text-2xs text-warning-700 dark:text-warning-400">Key not found in database. It may be set via environment variable only.</p>
              )}
            </div>
            {viewKeyValue && (
              <div className="p-2 rounded-lg bg-warning-50 dark:bg-warning-500/10 border border-warning-500/20">
                <p className="text-2xs text-warning-700 dark:text-warning-400">⚠ This is the full, unmasked API key. Copy it now and store it securely. Do not share it.</p>
              </div>
            )}
          </div>
        )}
      </Sheet>

      {/* v4.1.4: Sheet modal for adding/editing a custom provider */}
      <Sheet
        open={customProviderSheetOpen}
        onClose={() => { setCustomProviderSheetOpen(false); setEditingCustomProvider(null); }}
        title={editingCustomProvider ? `Edit Provider — ${editingCustomProvider.provider}` : 'Add Custom Provider'}
        footer={
          <div className="flex gap-2">
            {saveSuccess && <Alert tone="success" onClose={() => setSaveSuccess(null)}>{saveSuccess}</Alert>}
            <button onClick={handleSaveCustomProvider} disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5">
              {saving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save Provider
            </button>
            <button onClick={() => { setCustomProviderSheetOpen(false); setEditingCustomProvider(null); }} className="btn-ghost">Cancel</button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="label">Provider Code <span className="text-2xs text-ink-400">(unique identifier, e.g. my_custom_ai)</span></label>
            <input className="input font-mono" value={customProviderForm.provider_code}
              onChange={e => setCustomProviderForm({ ...customProviderForm, provider_code: e.target.value })}
              placeholder="my_custom_ai" disabled={!!editingCustomProvider} />
          </div>
          <div>
            <label className="label">Provider Name <span className="text-2xs text-ink-400">(display name)</span></label>
            <input className="input" value={customProviderForm.provider_name}
              onChange={e => setCustomProviderForm({ ...customProviderForm, provider_name: e.target.value })}
              placeholder="My Custom AI" />
          </div>
          <div>
            <label className="label">Env Var <span className="text-2xs text-ink-400">(environment variable name)</span></label>
            <input className="input font-mono" value={customProviderForm.env_var}
              onChange={e => setCustomProviderForm({ ...customProviderForm, env_var: e.target.value })}
              placeholder="MY_CUSTOM_API_KEY" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Priority</label>
              <select className="input" value={customProviderForm.priority}
                onChange={e => setCustomProviderForm({ ...customProviderForm, priority: e.target.value })}>
                <option value="CRITICAL">CRITICAL</option>
                <option value="HIGH">HIGH</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="LOW">LOW</option>
              </select>
            </div>
            <div>
              <label className="label">API Style</label>
              <select className="input" value={customProviderForm.api_style}
                onChange={e => setCustomProviderForm({ ...customProviderForm, api_style: e.target.value })}>
                <option value="openai">OpenAI-compatible</option>
                <option value="gemini">Gemini</option>
                <option value="anthropic">Anthropic</option>
                <option value="huggingface">HuggingFace</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Base URL <span className="text-2xs text-ink-400">(API endpoint)</span></label>
            <input className="input font-mono" value={customProviderForm.base_url}
              onChange={e => setCustomProviderForm({ ...customProviderForm, base_url: e.target.value })}
              placeholder="https://api.example.com/v1" />
          </div>
          <div>
            <label className="label">Signup URL <span className="text-2xs text-ink-400">(where to get a key)</span></label>
            <input className="input" value={customProviderForm.signup_url}
              onChange={e => setCustomProviderForm({ ...customProviderForm, signup_url: e.target.value })}
              placeholder="https://example.com/signup" />
          </div>
          <div>
            <label className="label">Key Prefix Hint <span className="text-2xs text-ink-400">(e.g. sk-...)</span></label>
            <input className="input" value={customProviderForm.key_prefix_hint}
              onChange={e => setCustomProviderForm({ ...customProviderForm, key_prefix_hint: e.target.value })}
              placeholder="sk-..." />
          </div>
          <div>
            <label className="label">Purpose <span className="text-2xs text-ink-400">(description)</span></label>
            <textarea className="input" rows={2} value={customProviderForm.purpose}
              onChange={e => setCustomProviderForm({ ...customProviderForm, purpose: e.target.value })}
              placeholder="What this provider is used for" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={customProviderForm.is_free}
              onChange={e => setCustomProviderForm({ ...customProviderForm, is_free: e.target.checked })} />
            Free tier
          </label>
        </div>
      </Sheet>

      {/* v4.1.4: Sheet modal for adding a custom model */}
      <Sheet
        open={customModelSheetOpen}
        onClose={() => setCustomModelSheetOpen(false)}
        title="Add Model"
        footer={
          <div className="flex gap-2">
            <button onClick={handleSaveCustomModel} disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5">
              {saving ? <Spinner size="sm" /> : <Icon.Check className="w-4 h-4" />} Save Model
            </button>
            <button onClick={() => setCustomModelSheetOpen(false)} className="btn-ghost">Cancel</button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="label">Model Code <span className="text-2xs text-ink-400">(exact model ID the API expects)</span></label>
            <input className="input font-mono" value={customModelForm.model_code}
              onChange={e => setCustomModelForm({ ...customModelForm, model_code: e.target.value })}
              placeholder="gpt-4o-mini" />
          </div>
          <div>
            <label className="label">Model Name <span className="text-2xs text-ink-400">(display name)</span></label>
            <input className="input" value={customModelForm.model_name}
              onChange={e => setCustomModelForm({ ...customModelForm, model_name: e.target.value })}
              placeholder="GPT-4o Mini" />
          </div>
          <div>
            <label className="label">Task Type</label>
            <select className="input" value={customModelForm.task_type}
              onChange={e => setCustomModelForm({ ...customModelForm, task_type: e.target.value })}>
              <option value="fast_response">Fast Response</option>
              <option value="long_reasoning">Long Reasoning</option>
              <option value="vision">Vision</option>
              <option value="embedding">Embedding</option>
              <option value="stt">Speech-to-Text</option>
              <option value="tts">Text-to-Speech</option>
              <option value="safety">Safety Guard</option>
              <option value="code">Code Helper</option>
              <option value="daily_assistant">Daily Assistant</option>
            </select>
          </div>
          <div>
            <label className="label">Note <span className="text-2xs text-ink-400">(optional description)</span></label>
            <input className="input" value={customModelForm.note}
              onChange={e => setCustomModelForm({ ...customModelForm, note: e.target.value })}
              placeholder="e.g. Best for fast text generation" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={customModelForm.is_default}
              onChange={e => setCustomModelForm({ ...customModelForm, is_default: e.target.checked })} />
            Set as default model for this provider
          </label>
        </div>
      </Sheet>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* GET /api/households?limit=50 → table of farmer household         */
/* profiles. "View Members" button opens a Sheet showing members    */
/* from GET /api/households/:id. Search filters by farmer_name.     */
/* ════════════════════════════════════════════════════════════════ */
const HouseholdsTab: React.FC = () => { // v2.8
  const [households, setHouseholds] = useState<any[]>([]); // v2.8
  const [loading, setLoading] = useState(true); // v2.8
  const [error, setError] = useState<string | null>(null); // v2.8
  const [search, setSearch] = useState(''); // v2.8
  const [selectedHousehold, setSelectedHousehold] = useState<any>(null); // v2.8
  const [members, setMembers] = useState<any[]>([]); // v2.8
  const [membersLoading, setMembersLoading] = useState(false); // v2.8

  useEffect(() => { // v2.8
    const fetchHouseholds = async () => { // v2.8
      setLoading(true); // v2.8
      setError(null); // v2.8
      try { // v2.8
        const res = await apiClient.get('/households', { params: { limit: 50 } }); // v2.8
        const list = res.data?.households ?? (Array.isArray(res.data) ? res.data : []); // v2.8
        setHouseholds(Array.isArray(list) ? list : []); // v2.8
      } catch (err: any) { // v2.8
        setError(err.response?.data?.error || 'Failed to load households'); // v2.8
      } finally { // v2.8
        setLoading(false); // v2.8
      }
    }; // v2.8
    fetchHouseholds(); // v2.8
  }, []); // v2.8

  const handleViewMembers = async (h: any) => { // v2.8
    setSelectedHousehold(h); // v2.8
    setMembers([]); // v2.8
    setMembersLoading(true); // v2.8
    try { // v2.8
      const res = await apiClient.get(`/households/${h.id}`); // v2.8
      setMembers(res.data?.members ?? []); // v2.8
    } catch { // v2.8
      // v2.8: ignore — the sheet will fall through to the EmptyState
    } finally { // v2.8
      setMembersLoading(false); // v2.8
    }
  }; // v2.8

  const filtered = households.filter(h => !search || (h.farmer_name || '').toLowerCase().includes(search.toLowerCase())); // v2.8

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>; // v2.8

  return ( // v2.8
    <div className="space-y-4"> {/* v2.8 */}
      <SectionHeader eyebrow="Business Module" title="Households" subtitle="Farmer household profiles — demographics, income, food security, education, gender." icon={<Icon.Users className="w-4 h-4" />} /> {/* v2.8 */}

      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.8 */}

      {/* v2.8: search by farmer name */}
      <div className="relative max-w-md"> {/* v2.8 */}
        <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" /> {/* v2.8 */}
        <input type="text" placeholder="Search by farmer name…" value={search} onChange={e => setSearch(e.target.value)} className="input pl-10" /> {/* v2.8 */}
      </div>

      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <span className="text-sm text-ink-500 dark:text-ink-400">{filtered.length} households</span> {/* v2.8 */}
        </div>
        {filtered.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Users className="w-5 h-5" />} title="No households registered" description="Create households via POST /api/households." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Farmer</th><th>Household Size</th><th>Income (XAF)</th><th>Food Security</th><th>Education</th><th>Women in Farming</th><th>Actions</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {filtered.map(h => ( // v2.8
                  <tr key={h.id}> {/* v2.8 */}
                    <td className="text-sm font-medium">{h.farmer_name || '—'}{h.household_name && <div className="text-2xs text-ink-500 dark:text-ink-400">{h.household_name}</div>}</td> {/* v2.8 */}
                    <td className="nums">{h.household_size ?? '—'}</td> {/* v2.8 */}
                    <td className="nums">{h.total_income_xaf ? Number(h.total_income_xaf).toLocaleString() : '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={h.food_security === 'adequate' ? 'success' : h.food_security === 'inadequate' ? 'danger' : 'warning'} dot>{h.food_security || '—'}</StatusPill></td> {/* v2.8 */}
                    <td className="text-2xs">{h.highest_education || '—'}</td> {/* v2.8 */}
                    <td className="nums">{h.women_in_farming ?? '—'}</td> {/* v2.8 */}
                    <td><button onClick={() => handleViewMembers(h)} className="btn-secondary text-2xs">View Members</button></td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: members sheet */}
      {selectedHousehold && ( // v2.8
        <Sheet // v2.8
          open={true} // v2.8
          onClose={() => setSelectedHousehold(null)} // v2.8
          title={`Members — ${selectedHousehold.farmer_name || selectedHousehold.household_name || selectedHousehold.id}`} // v2.8
          footer={<button onClick={() => setSelectedHousehold(null)} className="btn-secondary w-full">Close</button>} // v2.8
        > {/* v2.8 */}
          {membersLoading ? ( // v2.8
            <div className="flex justify-center py-6"><Spinner /></div> // v2.8
          ) : members.length === 0 ? ( // v2.8
            <EmptyState icon={<Icon.Users className="w-5 h-5" />} title="No members yet" description="Add household members via POST /api/households/:id/members." /> // v2.8
          ) : ( // v2.8
            <div className="overflow-x-auto"> {/* v2.8 */}
              <table className="data-table"> {/* v2.8 */}
                <thead><tr><th>Name</th><th>Relationship</th><th>Gender</th><th>Occupation</th><th>In Cocoa?</th><th>Dependent?</th></tr></thead> {/* v2.8 */}
                <tbody> {/* v2.8 */}
                  {members.map((m: any) => ( // v2.8
                    <tr key={m.id}> {/* v2.8 */}
                      <td className="text-sm font-medium">{m.full_name}</td> {/* v2.8 */}
                      <td className="text-xs">{m.relationship || '—'}</td> {/* v2.8 */}
                      <td className="text-2xs">{m.gender || '—'}</td> {/* v2.8 */}
                      <td className="text-2xs">{m.primary_occupation || '—'}</td> {/* v2.8 */}
                      <td>{m.participates_in_cocoa ? <StatusPill tone="success" dot>Yes</StatusPill> : <StatusPill tone="neutral">No</StatusPill>}</td> {/* v2.8 */}
                      <td>{m.is_dependent ? <StatusPill tone="info">Dependent</StatusPill> : <StatusPill tone="neutral">Independent</StatusPill>}</td> {/* v2.8 */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Sheet>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v2.8: NURSERY TAB (GAP 5b)                                       */
/* ════════════════════════════════════════════════════════════════ */
/* Three stacked tables:                                            */
/*   1. GET /api/nursery/beds?limit=50           → nursery beds     */
/*   2. GET /api/nursery/mother-trees            → mother trees     */
/*   3. GET /api/nursery/distributions           → distributions    */
/* ════════════════════════════════════════════════════════════════ */
const NurseryTab: React.FC = () => { // v2.8
  const [beds, setBeds] = useState<any[]>([]); // v2.8
  const [motherTrees, setMotherTrees] = useState<any[]>([]); // v2.8
  const [distributions, setDistributions] = useState<any[]>([]); // v2.8
  const [loading, setLoading] = useState(true); // v2.8
  const [error, setError] = useState<string | null>(null); // v2.8

  useEffect(() => { // v2.8
    const fetchAll = async () => { // v2.8
      setLoading(true); // v2.8
      setError(null); // v2.8
      try { // v2.8
        const [bedsRes, mtRes, distRes] = await Promise.all([ // v2.8
          apiClient.get('/nursery/beds', { params: { limit: 50 } }).catch(() => ({ data: { nursery_beds: [] } })), // v2.8
          apiClient.get('/nursery/mother-trees').catch(() => ({ data: { mother_trees: [] } })), // v2.8
          apiClient.get('/nursery/distributions').catch(() => ({ data: { distributions: [] } })), // v2.8
        ]); // v2.8
        setBeds(bedsRes.data?.nursery_beds ?? []); // v2.8
        setMotherTrees(mtRes.data?.mother_trees ?? []); // v2.8
        setDistributions(distRes.data?.distributions ?? []); // v2.8
      } catch (err: any) { // v2.8
        setError(err.response?.data?.error || 'Failed to load nursery data'); // v2.8
      } finally { // v2.8
        setLoading(false); // v2.8
      }
    }; // v2.8
    fetchAll(); // v2.8
  }, []); // v2.8

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>; // v2.8

  return ( // v2.8
    <div className="space-y-4"> {/* v2.8 */}
      <SectionHeader eyebrow="Business Module" title="Nursery" subtitle="Mother trees → seed batches → nursery beds → seedling distributions." icon={<Icon.Leaf className="w-4 h-4" />} /> {/* v2.8 */}

      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.8 */}

      {/* v2.8: Table 1 — Nursery Beds */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 1" title="Nursery Beds" subtitle={`${beds.length} beds`} icon={<Icon.Leaf className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {beds.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No nursery beds" description="Create beds via POST /api/nursery/beds." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Code</th><th>Farm</th><th>Variety</th><th>Capacity</th><th>Current Count</th><th>Survival Rate</th><th>Status</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {beds.map(b => ( // v2.8
                  <tr key={b.id}> {/* v2.8 */}
                    <td className="text-xs font-mono">{b.nursery_code}</td> {/* v2.8 */}
                    <td className="text-sm">{b.farm_name || '—'}</td> {/* v2.8 */}
                    <td className="text-2xs">{b.variety || '—'}</td> {/* v2.8 */}
                    <td className="nums">{b.capacity ?? '—'}</td> {/* v2.8 */}
                    <td className="nums">{b.current_count ?? 0}</td> {/* v2.8 */}
                    <td className="nums">{b.survival_rate != null ? `${Number(b.survival_rate).toFixed(1)}%` : '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={b.status === 'active' || b.status === 'healthy' ? 'success' : b.status === 'diseased' ? 'danger' : 'neutral'} dot>{b.status || '—'}</StatusPill></td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: Table 2 — Mother Trees */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 2" title="Mother Trees" subtitle={`${motherTrees.length} trees`} icon={<Icon.Leaf className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {motherTrees.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No mother trees" description="Register mother trees via POST /api/nursery/mother-trees." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Code</th><th>Farm</th><th>Variety</th><th>Age (yrs)</th><th>Health</th><th>Pod Production</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {motherTrees.map(mt => ( // v2.8
                  <tr key={mt.id}> {/* v2.8 */}
                    <td className="text-xs font-mono">{mt.tree_code}</td> {/* v2.8 */}
                    <td className="text-sm">{mt.farm_name || '—'}</td> {/* v2.8 */}
                    <td className="text-2xs">{mt.variety || '—'}</td> {/* v2.8 */}
                    <td className="nums">{mt.age_years ?? '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={mt.health_status === 'healthy' ? 'success' : mt.health_status === 'diseased' ? 'danger' : 'warning'} dot>{mt.health_status || '—'}</StatusPill></td> {/* v2.8 */}
                    <td className="nums">{mt.pod_production_avg ?? '—'}</td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: Table 3 — Seedling Distributions */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 3" title="Seedling Distributions" subtitle={`${distributions.length} distributions`} icon={<Icon.Send className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {distributions.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Send className="w-5 h-5" />} title="No distributions" description="Record distributions via POST /api/nursery/distributions." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Date</th><th>Farm</th><th>Count</th><th>Variety</th><th>Grade</th><th>Transplant Date</th><th>Survival</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {distributions.map(d => ( // v2.8
                  <tr key={d.id}> {/* v2.8 */}
                    <td className="text-2xs">{d.distribution_date ? new Date(d.distribution_date).toLocaleDateString() : '—'}</td> {/* v2.8 */}
                    <td className="text-sm">{d.farm_name || '—'}</td> {/* v2.8 */}
                    <td className="nums">{d.seedling_count ?? '—'}</td> {/* v2.8 */}
                    <td className="text-2xs">{d.variety || '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={d.quality_grade === 'A' ? 'success' : d.quality_grade === 'B' ? 'warning' : 'neutral'} dot>{d.quality_grade || '—'}</StatusPill></td> {/* v2.8 */}
                    <td className="text-2xs">{d.transplant_date ? new Date(d.transplant_date).toLocaleDateString() : '—'}</td> {/* v2.8 */}
                    <td className="nums">{d.survival_rate != null ? `${Number(d.survival_rate).toFixed(1)}%` : (d.survival_count != null && d.seedling_count ? `${Math.round(100 * d.survival_count / d.seedling_count)}%` : '—')}</td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v2.8: PLANTATION TAB (GAP 5c)                                    */
/* ════════════════════════════════════════════════════════════════ */
/* GET /api/plantation/blocks?limit=50 → blocks table with          */
/* "View Rows" button → Sheet showing GET /api/plantation/blocks/   */
/* :id/rows. Secondary table: GET /api/plantation/metrics.          */
/* ════════════════════════════════════════════════════════════════ */
const PlantationTab: React.FC = () => { // v2.8
  const [blocks, setBlocks] = useState<any[]>([]); // v2.8
  const [metrics, setMetrics] = useState<any[]>([]); // v2.8
  const [loading, setLoading] = useState(true); // v2.8
  const [error, setError] = useState<string | null>(null); // v2.8
  const [selectedBlock, setSelectedBlock] = useState<any>(null); // v2.8
  const [rows, setRows] = useState<any[]>([]); // v2.8
  const [rowsLoading, setRowsLoading] = useState(false); // v2.8

  useEffect(() => { // v2.8
    const fetchAll = async () => { // v2.8
      setLoading(true); // v2.8
      setError(null); // v2.8
      try { // v2.8
        const [blocksRes, metricsRes] = await Promise.all([ // v2.8
          apiClient.get('/plantation/blocks', { params: { limit: 50 } }).catch(() => ({ data: { blocks: [] } })), // v2.8
          apiClient.get('/plantation/metrics').catch(() => ({ data: { metrics: [] } })), // v2.8
        ]); // v2.8
        setBlocks(blocksRes.data?.blocks ?? []); // v2.8
        setMetrics(metricsRes.data?.metrics ?? []); // v2.8
      } catch (err: any) { // v2.8
        setError(err.response?.data?.error || 'Failed to load plantation data'); // v2.8
      } finally { // v2.8
        setLoading(false); // v2.8
      }
    }; // v2.8
    fetchAll(); // v2.8
  }, []); // v2.8

  const handleViewRows = async (b: any) => { // v2.8
    setSelectedBlock(b); // v2.8
    setRows([]); // v2.8
    setRowsLoading(true); // v2.8
    try { // v2.8
      const res = await apiClient.get(`/plantation/blocks/${b.id}/rows`); // v2.8
      setRows(res.data?.rows ?? []); // v2.8
    } catch { // v2.8
      // v2.8: ignore — sheet will fall through to EmptyState
    } finally { // v2.8
      setRowsLoading(false); // v2.8
    }
  }; // v2.8

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>; // v2.8

  return ( // v2.8
    <div className="space-y-4"> {/* v2.8 */}
      <SectionHeader eyebrow="Business Module" title="Plantation" subtitle="Plantation blocks (with rows) + tree-metrics history." icon={<Icon.Map className="w-4 h-4" />} /> {/* v2.8 */}

      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.8 */}

      {/* v2.8: Table 1 — Blocks */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 1" title="Plantation Blocks" subtitle={`${blocks.length} blocks`} icon={<Icon.Map className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {blocks.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Map className="w-5 h-5" />} title="No plantation blocks" description="Create blocks via POST /api/plantation/blocks." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Farm</th><th>Block</th><th>Area (ha)</th><th>Trees</th><th>Variety</th><th>Planting Date</th><th>Health</th><th>Actions</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {blocks.map(b => ( // v2.8
                  <tr key={b.id}> {/* v2.8 */}
                    <td className="text-sm">{b.farm_name || '—'}</td> {/* v2.8 */}
                    <td className="text-xs font-mono">{b.block_code}{b.block_name && <div className="text-2xs text-ink-500">{b.block_name}</div>}</td> {/* v2.8 */}
                    <td className="nums">{b.area_hectares != null ? Number(b.area_hectares).toFixed(2) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{b.tree_count ?? '—'}</td> {/* v2.8 */}
                    <td className="text-2xs">{b.variety || '—'}</td> {/* v2.8 */}
                    <td className="text-2xs">{b.planting_date ? new Date(b.planting_date).toLocaleDateString() : '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={b.health_status === 'healthy' ? 'success' : b.health_status === 'diseased' ? 'danger' : 'warning'} dot>{b.health_status || '—'}</StatusPill></td> {/* v2.8 */}
                    <td><button onClick={() => handleViewRows(b)} className="btn-secondary text-2xs">View Rows</button></td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: Table 2 — Tree Metrics */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 2" title="Tree Metrics" subtitle={`${metrics.length} measurements`} icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {metrics.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Activity className="w-5 h-5" />} title="No metrics recorded" description="Record metrics via POST /api/plantation/metrics." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Date</th><th>Block</th><th>Tree Count</th><th>Avg Height (m)</th><th>DBH (cm)</th><th>Canopy (m)</th><th>Health Score</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {metrics.map((m: any) => ( // v2.8
                  <tr key={m.id}> {/* v2.8 */}
                    <td className="text-2xs">{m.measurement_date ? new Date(m.measurement_date).toLocaleDateString() : '—'}</td> {/* v2.8 */}
                    <td className="text-xs font-mono">{m.block_code || '—'}</td> {/* v2.8 */}
                    <td className="nums">{m.tree_count ?? '—'}</td> {/* v2.8 */}
                    <td className="nums">{m.avg_height_m != null ? Number(m.avg_height_m).toFixed(2) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{m.avg_dbh_cm != null ? Number(m.avg_dbh_cm).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{m.avg_canopy_diameter_m != null ? Number(m.avg_canopy_diameter_m).toFixed(2) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{m.health_score != null ? Number(m.health_score).toFixed(1) : '—'}</td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: rows sheet */}
      {selectedBlock && ( // v2.8
        <Sheet // v2.8
          open={true} // v2.8
          onClose={() => setSelectedBlock(null)} // v2.8
          title={`Rows — ${selectedBlock.block_code}`} // v2.8
          footer={<button onClick={() => setSelectedBlock(null)} className="btn-secondary w-full">Close</button>} // v2.8
        > {/* v2.8 */}
          {rowsLoading ? ( // v2.8
            <div className="flex justify-center py-6"><Spinner /></div> // v2.8
          ) : rows.length === 0 ? ( // v2.8
            <EmptyState icon={<Icon.Map className="w-5 h-5" />} title="No rows in this block" description="Add rows via POST /api/plantation/blocks/:id/rows." /> // v2.8
          ) : ( // v2.8
            <div className="overflow-x-auto"> {/* v2.8 */}
              <table className="data-table"> {/* v2.8 */}
                <thead><tr><th>Row #</th><th>Trees</th><th>Length (m)</th><th>Direction</th><th>Notes</th></tr></thead> {/* v2.8 */}
                <tbody> {/* v2.8 */}
                  {rows.map((r: any) => ( // v2.8
                    <tr key={r.id}> {/* v2.8 */}
                      <td className="text-xs font-mono">#{r.row_number}</td> {/* v2.8 */}
                      <td className="nums">{r.tree_count ?? '—'}</td> {/* v2.8 */}
                      <td className="nums">{r.length_m != null ? Number(r.length_m).toFixed(1) : '—'}</td> {/* v2.8 */}
                      <td className="text-2xs">{r.direction || '—'}</td> {/* v2.8 */}
                      <td className="text-2xs text-ink-500 max-w-xs truncate" title={r.notes || ''}>{r.notes || '—'}</td> {/* v2.8 */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Sheet>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v2.8: FACTORY TAB (GAP 5d)                                       */
/* ════════════════════════════════════════════════════════════════ */
/* Three stacked tables:                                            */
/*   1. GET /api/factory/processing?limit=50  → processing batches  */
/*   2. GET /api/factory/products?limit=50     → finished products  */
/*   3. GET /api/factory/waste?limit=50       → waste conversions   */
/* ════════════════════════════════════════════════════════════════ */
const FactoryTab: React.FC = () => { // v2.8
  const [processing, setProcessing] = useState<any[]>([]); // v2.8
  const [products, setProducts] = useState<any[]>([]); // v2.8
  const [waste, setWaste] = useState<any[]>([]); // v2.8
  const [loading, setLoading] = useState(true); // v2.8
  const [error, setError] = useState<string | null>(null); // v2.8

  useEffect(() => { // v2.8
    const fetchAll = async () => { // v2.8
      setLoading(true); // v2.8
      setError(null); // v2.8
      try { // v2.8
        const [procRes, prodRes, wasteRes] = await Promise.all([ // v2.8
          apiClient.get('/factory/processing', { params: { limit: 50 } }).catch(() => ({ data: { processing_batches: [] } })), // v2.8
          apiClient.get('/factory/products', { params: { limit: 50 } }).catch(() => ({ data: { products: [] } })), // v2.8
          apiClient.get('/factory/waste', { params: { limit: 50 } }).catch(() => ({ data: { waste_conversions: [] } })), // v2.8
        ]); // v2.8
        setProcessing(procRes.data?.processing_batches ?? []); // v2.8
        setProducts(prodRes.data?.products ?? []); // v2.8
        setWaste(wasteRes.data?.waste_conversions ?? []); // v2.8
      } catch (err: any) { // v2.8
        setError(err.response?.data?.error || 'Failed to load factory data'); // v2.8
      } finally { // v2.8
        setLoading(false); // v2.8
      }
    }; // v2.8
    fetchAll(); // v2.8
  }, []); // v2.8

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>; // v2.8

  return ( // v2.8
    <div className="space-y-4"> {/* v2.8 */}
      <SectionHeader eyebrow="Business Module" title="Factory" subtitle="Bean-to-product transformation: processing batches, finished products, waste conversion." icon={<Icon.Package className="w-4 h-4" />} /> {/* v2.8 */}

      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.8 */}

      {/* v2.8: Table 1 — Processing Batches */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 1" title="Processing Batches" subtitle={`${processing.length} batches`} icon={<Icon.Package className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {processing.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No processing batches" description="Create batches via POST /api/factory/processing." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Batch #</th><th>Type</th><th>Input (kg)</th><th>Output (kg)</th><th>Yield %</th><th>Status</th><th>Grade</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {processing.map(p => ( // v2.8
                  <tr key={p.id}> {/* v2.8 */}
                    <td className="text-xs font-mono">{p.batch_number}</td> {/* v2.8 */}
                    <td className="text-2xs">{p.processing_type || '—'}</td> {/* v2.8 */}
                    <td className="nums">{p.input_weight_kg != null ? Number(p.input_weight_kg).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{p.output_weight_kg != null ? Number(p.output_weight_kg).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{p.yield_pct != null ? Number(p.yield_pct).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={p.status === 'completed' ? 'success' : p.status === 'in_progress' ? 'info' : 'warning'} dot>{p.status || '—'}</StatusPill></td> {/* v2.8 */}
                    <td><StatusPill tone={p.quality_grade === 'A' ? 'success' : p.quality_grade === 'B' ? 'warning' : 'neutral'}>{p.quality_grade || '—'}</StatusPill></td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: Table 2 — Finished Products */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 2" title="Finished Products" subtitle={`${products.length} products`} icon={<Icon.Award className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {products.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Award className="w-5 h-5" />} title="No finished products" description="Create products via POST /api/factory/products." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Qty (kg)</th><th>Grade</th><th>Price (XAF)</th><th>Sold?</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {products.map(p => ( // v2.8
                  <tr key={p.id}> {/* v2.8 */}
                    <td className="text-xs font-mono">{p.product_code}</td> {/* v2.8 */}
                    <td className="text-sm font-medium">{p.product_name}</td> {/* v2.8 */}
                    <td className="text-2xs">{p.product_type || '—'}</td> {/* v2.8 */}
                    <td className="nums">{p.quantity_kg != null ? Number(p.quantity_kg).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td><StatusPill tone={p.quality_grade === 'A' ? 'success' : p.quality_grade === 'B' ? 'warning' : 'neutral'}>{p.quality_grade || '—'}</StatusPill></td> {/* v2.8 */}
                    <td className="nums">{p.unit_price_xaf ? Number(p.unit_price_xaf).toLocaleString() : '—'}</td> {/* v2.8 */}
                    <td>{p.sold ? <StatusPill tone="success" dot>Sold</StatusPill> : <StatusPill tone="neutral">In stock</StatusPill>}</td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.8: Table 3 — Waste Conversions */}
      <Card padding="none" className="overflow-hidden"> {/* v2.8 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.8 */}
          <SectionHeader eyebrow="Layer 3" title="Waste Conversions" subtitle={`${waste.length} conversions`} icon={<Icon.Leaf className="w-4 h-4" />} /> {/* v2.8 */}
        </div>
        {waste.length === 0 ? ( // v2.8
          <div className="p-8"><EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No waste conversions" description="Record waste via POST /api/factory/waste." /></div> // v2.8
        ) : ( // v2.8
          <div className="overflow-x-auto"> {/* v2.8 */}
            <table className="data-table"> {/* v2.8 */}
              <thead><tr><th>Date</th><th>Waste Type</th><th>Input (kg)</th><th>Output Product</th><th>Output Qty</th><th>Conversion Rate</th><th>Carbon Credits</th></tr></thead> {/* v2.8 */}
              <tbody> {/* v2.8 */}
                {waste.map(w => ( // v2.8
                  <tr key={w.id}> {/* v2.8 */}
                    <td className="text-2xs">{w.conversion_date ? new Date(w.conversion_date).toLocaleDateString() : '—'}</td> {/* v2.8 */}
                    <td className="text-2xs">{w.waste_type || '—'}</td> {/* v2.8 */}
                    <td className="nums">{w.input_weight_kg != null ? Number(w.input_weight_kg).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td className="text-sm">{w.output_product || '—'}</td> {/* v2.8 */}
                    <td className="nums">{w.output_weight_kg != null ? Number(w.output_weight_kg).toFixed(1) : '—'}</td> {/* v2.8 */}
                    <td className="nums">{w.conversion_rate != null ? `${Number(w.conversion_rate).toFixed(1)}%` : '—'}</td> {/* v2.8 */}
                    <td className="nums">{w.carbon_credits_earned != null ? Number(w.carbon_credits_earned).toFixed(2) : '—'}</td> {/* v2.8 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v2.9: COGNITIVE MEMORY TAB (Intelligence group)                  */
/* ════════════════════════════════════════════════════════════════ */
/* Wires 5 endpoints:                                               */
/*   GET  /api/memory/dashboard  → 6 KPI tiles                       */
/*   GET  /api/memory/farms?limit=20 → farm profiles table          */
/*   GET  /api/memory/knowledge?limit=10 → ecosystem knowledge tbl  */
/*   POST /api/memory/backfill  → "Import Existing Data" button     */
/*   POST /api/memory/discover  → "Discover Patterns" button        */
/*   GET  /api/memory/snapshots?limit=10 → snapshots table          */
/* ════════════════════════════════════════════════════════════════ */
const CognitiveMemoryTab: React.FC = () => { // v2.9
  const [dashboard, setDashboard] = useState<any>(null); // v2.9
  const [farms, setFarms] = useState<any[]>([]); // v2.9
  const [knowledge, setKnowledge] = useState<any[]>([]); // v2.9
  const [snapshots, setSnapshots] = useState<any[]>([]); // v2.9
  const [loading, setLoading] = useState(true); // v2.9
  const [error, setError] = useState<string | null>(null); // v2.9
  const [busy, setBusy] = useState<'backfill' | 'discover' | null>(null); // v2.9
  const [busyMsg, setBusyMsg] = useState<string | null>(null); // v2.9

  const fetchAll = useCallback(async () => { // v2.9
    setLoading(true); // v2.9
    setError(null); // v2.9
    try { // v2.9
      const [dashRes, farmsRes, knowRes, snapRes] = await Promise.all([ // v2.9
        apiClient.get('/memory/dashboard').catch(() => ({ data: null })), // v2.9
        apiClient.get('/memory/farms', { params: { limit: 20 } }).catch(() => ({ data: { farms: [] } })), // v2.9
        apiClient.get('/memory/knowledge', { params: { limit: 10 } }).catch(() => ({ data: { knowledge: [] } })), // v2.9
        apiClient.get('/memory/snapshots', { params: { limit: 10 } }).catch(() => ({ data: { snapshots: [] } })), // v2.9
      ]);
      setDashboard(dashRes.data || null); // v2.9
      setFarms(farmsRes.data?.farms ?? (Array.isArray(farmsRes.data) ? farmsRes.data : [])); // v2.9
      setKnowledge(knowRes.data?.knowledge ?? (Array.isArray(knowRes.data) ? knowRes.data : [])); // v2.9
      setSnapshots(snapRes.data?.snapshots ?? (Array.isArray(snapRes.data) ? snapRes.data : [])); // v2.9
    } catch (err: any) { // v2.9
      setError(err.response?.data?.error || 'Failed to load cognitive memory data'); // v2.9
    } finally { // v2.9
      setLoading(false); // v2.9
    }
  }, []); // v2.9

  useEffect(() => { fetchAll(); }, [fetchAll]); // v2.9

  const handleBackfill = async () => { // v2.9
    setBusy('backfill'); // v2.9
    setBusyMsg(null); // v2.9
    try { // v2.9
      const res = await apiClient.post('/memory/backfill'); // v2.9
      setBusyMsg(`Import complete — ${res.data?.imported ?? 0} events imported.`); // v2.9
      fetchAll(); // v2.9
    } catch (err: any) { // v2.9
      setBusyMsg(err.response?.data?.error || 'Backfill failed'); // v2.9
    } finally { // v2.9
      setBusy(null); // v2.9
    }
  }; // v2.9

  const handleDiscover = async () => { // v2.9
    setBusy('discover'); // v2.9
    setBusyMsg(null); // v2.9
    try { // v2.9
      const res = await apiClient.post('/memory/discover'); // v2.9
      const n = Array.isArray(res.data?.patterns) ? res.data.patterns.length : (res.data?.patterns_discovered ?? 0); // v2.9
      setBusyMsg(`Pattern discovery complete — ${n} pattern(s) found.`); // v2.9
      fetchAll(); // v2.9
    } catch (err: any) { // v2.9
      setBusyMsg(err.response?.data?.error || 'Discovery failed'); // v2.9
    } finally { // v2.9
      setBusy(null); // v2.9
    }
  }; // v2.9

  const totals: any = dashboard?.totals || {}; // v2.9

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>; // v2.9

  return ( // v2.9
    <div className="space-y-4"> {/* v2.9 */}
      <SectionHeader eyebrow="National Agricultural Memory System" title="Cognitive Memory" subtitle="Longitudinal knowledge accumulation across every farm — events, treatments, AI outcomes, and cross-farm patterns." icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.9 */}

      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.9 */}
      {busyMsg && <Alert tone="info" onClose={() => setBusyMsg(null)}>{busyMsg}</Alert>} {/* v2.9 */}

      {/* v2.9: 6 KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"> {/* v2.9 */}
        <MetricTile label="Total Events"           value={Number(totals.total_events || 0).toLocaleString()} icon={<Icon.Database className="w-4 h-4" />} accent="analytics" /> {/* v2.9 */}
        <MetricTile label="Farms w/ Memory"        value={Number(totals.farms_with_memory || 0)} icon={<Icon.Map className="w-4 h-4" />} /> {/* v2.9 */}
        <MetricTile label="Treatments Tracked"     value={Number(totals.total_treatments || 0)} icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.9 */}
        <MetricTile label="AI Outcomes Verified"   value={Number(totals.verified_ai_recs || 0)} icon={<Icon.Check className="w-4 h-4" />} /> {/* v2.9 */}
        <MetricTile label="Ecosystem Patterns"     value={Number(totals.ecosystem_patterns || 0)} icon={<Icon.Sparkles className="w-4 h-4" />} /> {/* v2.9 */}
        <MetricTile label="Avg AI Accuracy"        value={`${Number(totals.avg_ai_accuracy || 0).toFixed(1)}%`} icon={<Icon.TrendingUp className="w-4 h-4" />} /> {/* v2.9 */}
      </div>

      {/* v2.9: action buttons */}
      <div className="flex flex-wrap gap-2"> {/* v2.9 */}
        <button onClick={handleBackfill} disabled={busy !== null} className="btn-secondary text-sm"> {/* v2.9 */}
          {busy === 'backfill' ? <><Spinner size="sm" /> Importing…</> : <><Icon.Download className="w-4 h-4" /> Import Existing Data</>} {/* v2.9 */}
        </button>
        <button onClick={handleDiscover} disabled={busy !== null} className="btn-secondary text-sm"> {/* v2.9 */}
          {busy === 'discover' ? <><Spinner size="sm" /> Discovering…</> : <><Icon.Sparkles className="w-4 h-4" /> Discover Patterns</>} {/* v2.9 */}
        </button>
      </div>

      {/* v2.9: primary table — farm memory profiles */}
      <Card padding="none" className="overflow-hidden"> {/* v2.9 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.9 */}
          <SectionHeader eyebrow="Layer 1" title="Farm Memory Profiles" subtitle={`${farms.length} farm profiles — ranked by memory richness`} icon={<Icon.Map className="w-4 h-4" />} /> {/* v2.9 */}
        </div>
        {farms.length === 0 ? ( // v2.9
          <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No farm memory profiles" description={'Click "Import Existing Data" to backfill the memory from historical batches, weather, and pest reports.'} /></div> // v2.9
        ) : ( // v2.9
          <div className="overflow-x-auto"> {/* v2.9 */}
            <table className="data-table"> {/* v2.9 */}
              <thead><tr><th>Farm</th><th>Farmer</th><th>Events</th><th>Memory Richness</th><th>AI Accuracy</th><th>Treatment Success</th></tr></thead> {/* v2.9 */}
              <tbody> {/* v2.9 */}
                {farms.map((f: any) => ( // v2.9
                  <tr key={f.farm_id || f.id}> {/* v2.9 */}
                    <td className="text-sm">{f.farm_name || f.farm_id || '—'}</td> {/* v2.9 */}
                    <td className="text-sm">{f.farmer_name || f.village || '—'}</td> {/* v2.9 */}
                    <td className="nums">{Number(f.total_events || 0).toLocaleString()}</td> {/* v2.9 */}
                    <td className="nums">{Number(f.memory_richness || 0).toFixed(1)}</td> {/* v2.9 */}
                    <td className="nums">{f.ai_accuracy_score != null ? `${Number(f.ai_accuracy_score).toFixed(1)}%` : '—'}</td> {/* v2.9 */}
                    <td className="nums">{f.treatment_success_rate != null ? `${Number(f.treatment_success_rate).toFixed(1)}%` : '—'}</td> {/* v2.9 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.9: secondary table — ecosystem knowledge */}
      <Card padding="none" className="overflow-hidden"> {/* v2.9 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.9 */}
          <SectionHeader eyebrow="Layer 2" title="Ecosystem Knowledge" subtitle={`${knowledge.length} validated cross-farm patterns`} icon={<Icon.Sparkles className="w-4 h-4" />} /> {/* v2.9 */}
        </div>
        {knowledge.length === 0 ? ( // v2.9
          <div className="p-8"><EmptyState icon={<Icon.Sparkles className="w-5 h-5" />} title="No ecosystem patterns yet" description={'Click "Discover Patterns" to mine cross-farm insights from accumulated events.'} /></div> // v2.9
        ) : ( // v2.9
          <div className="overflow-x-auto"> {/* v2.9 */}
            <table className="data-table"> {/* v2.9 */}
              <thead><tr><th>Type</th><th>Title</th><th>Evidence Count</th><th>Confidence</th><th>AI Enabled</th></tr></thead> {/* v2.9 */}
              <tbody> {/* v2.9 */}
                {knowledge.map((k: any) => ( // v2.9
                  <tr key={k.id}> {/* v2.9 */}
                    <td className="text-sm">{k.pattern_type || k.knowledge_type || k.type || '—'}</td> {/* v2.9 */}
                    <td className="text-sm">{k.title || k.pattern_summary || k.description || '—'}</td> {/* v2.9 */}
                    <td className="nums">{Number(k.evidence_count || 0).toLocaleString()}</td> {/* v2.9 */}
                    <td className="nums">{k.confidence_score != null ? `${Number(k.confidence_score).toFixed(1)}%` : '—'}</td> {/* v2.9 */}
                    <td>{k.ai_enabled ? <StatusPill tone="success" dot>Yes</StatusPill> : <StatusPill tone="neutral" dot>No</StatusPill>}</td> {/* v2.9 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* v2.9: tertiary table — memory snapshots */}
      <Card padding="none" className="overflow-hidden"> {/* v2.9 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.9 */}
          <SectionHeader eyebrow="Layer 3" title="Memory Snapshots" subtitle={`${snapshots.length} historical snapshots`} icon={<Icon.Clock className="w-4 h-4" />} /> {/* v2.9 */}
        </div>
        {snapshots.length === 0 ? ( // v2.9
          <div className="p-8"><EmptyState icon={<Icon.Clock className="w-5 h-5" />} title="No snapshots yet" description="Snapshots are taken periodically by the autonomous monitor and capture the memory system state at a point in time." /></div> // v2.9
        ) : ( // v2.9
          <div className="overflow-x-auto"> {/* v2.9 */}
            <table className="data-table"> {/* v2.9 */}
              <thead><tr><th>Date</th><th>Farms</th><th>Events</th><th>Treatments</th><th>Patterns</th><th>AI Accuracy</th></tr></thead> {/* v2.9 */}
              <tbody> {/* v2.9 */}
                {snapshots.map((s: any) => ( // v2.9
                  <tr key={s.id}> {/* v2.9 */}
                    <td className="text-2xs">{s.snapshot_date ? new Date(s.snapshot_date).toLocaleDateString() : '—'}</td> {/* v2.9 */}
                    <td className="nums">{Number(s.farm_profiles_count || s.total_farms || 0).toLocaleString()}</td> {/* v2.9 */}
                    <td className="nums">{Number(s.total_events || 0).toLocaleString()}</td> {/* v2.9 */}
                    <td className="nums">{Number(s.total_treatments || 0).toLocaleString()}</td> {/* v2.9 */}
                    <td className="nums">{Number(s.ecosystem_patterns || s.patterns_count || 0).toLocaleString()}</td> {/* v2.9 */}
                    <td className="nums">{s.avg_ai_accuracy != null ? `${Number(s.avg_ai_accuracy).toFixed(1)}%` : '—'}</td> {/* v2.9 */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}; // v2.9

/* ════════════════════════════════════════════════════════════════ */
/* v2.9: ORCHESTRATOR TAB (Intelligence group — EAOE)               */
/* ════════════════════════════════════════════════════════════════ */
/* Wires 3 endpoints:                                               */
/*   GET  /api/orchestrator/latest  → latest oversight report      */
/*   GET  /api/orchestrator/history?limit=20 → past reports table  */
/*   POST /api/orchestrator/run     → "Run Oversight Check" button  */
/* Color-coded severity badges:                                     */
/*   green=healthy, yellow=watch, orange=action needed, red=critical*/
/* ════════════════════════════════════════════════════════════════ */
const OrchestratorTab: React.FC = () => { // v2.9
  const [latest, setLatest] = useState<any>(null); // v2.9
  const [history, setHistory] = useState<any[]>([]); // v2.9
  const [loading, setLoading] = useState(true); // v2.9
  const [error, setError] = useState<string | null>(null); // v2.9
  const [running, setRunning] = useState(false); // v2.9

  const fetchAll = useCallback(async () => { // v2.9
    setLoading(true); // v2.9
    setError(null); // v2.9
    try { // v2.9
      const [latestRes, histRes] = await Promise.all([ // v2.9
        apiClient.get('/orchestrator/latest').catch(() => ({ data: null })), // v2.9
        apiClient.get('/orchestrator/history', { params: { limit: 20 } }).catch(() => ({ data: { reports: [] } })), // v2.9
      ]);
      setLatest(latestRes.data || null); // v2.9
      setHistory(histRes.data?.reports ?? (Array.isArray(histRes.data) ? histRes.data : [])); // v2.9
    } catch (err: any) { // v2.9
      setError(err.response?.data?.error || 'Failed to load orchestrator reports'); // v2.9
    } finally { // v2.9
      setLoading(false); // v2.9
    }
  }, []); // v2.9

  useEffect(() => { fetchAll(); }, [fetchAll]); // v2.9

  const handleRun = async () => { // v2.9
    setRunning(true); // v2.9
    setError(null); // v2.9
    try { // v2.9
      const res = await apiClient.post('/orchestrator/run'); // v2.9
      setLatest(res.data || null); // v2.9
      fetchAll(); // v2.9
    } catch (err: any) { // v2.9
      setError(err.response?.data?.error || 'Oversight run failed'); // v2.9
    } finally { // v2.9
      setRunning(false); // v2.9
    }
  }; // v2.9

  // v2.9: severity badge color mapping
  const severityTone: Record<string, 'success' | 'warning' | 'danger' | 'info'> = { // v2.9
    green: 'success',   // healthy // v2.9
    yellow: 'warning',  // watch // v2.9
    orange: 'danger',   // action needed (rendered with danger tone — orange-700 unavailable) // v2.9
    red: 'danger',      // critical // v2.9
  }; // v2.9
  const severityLabel: Record<string, string> = { // v2.9
    green: 'Healthy', // v2.9
    yellow: 'Watch', // v2.9
    orange: 'Action Needed', // v2.9
    red: 'Critical', // v2.9
  }; // v2.9

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>; // v2.9

  return ( // v2.9
    <div className="space-y-4"> {/* v2.9 */}
      <div className="flex flex-wrap items-start justify-between gap-3"> {/* v2.9 */}
        <SectionHeader eyebrow="Enterprise AI Orchestration Engine" title="Orchestrator" subtitle="Cross-cutting oversight of the entire AI agent fleet — health, circuit breakers, pending approvals, and failed pipelines." icon={<Icon.Cpu className="w-4 h-4" />} /> {/* v2.9 */}
        <button onClick={handleRun} disabled={running} className="btn-primary text-sm"> {/* v2.9 */}
          {running ? <><Spinner size="sm" /> Running oversight…</> : <><Icon.Activity className="w-4 h-4" /> Run Oversight Check</>} {/* v2.9 */}
        </button>
      </div>

      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.9 */}

      {/* v2.9: latest oversight report */}
      {!latest ? (
        <Card padding="lg"> {/* v2.9 */}
          <EmptyState icon={<Icon.Cpu className="w-5 h-5" />} title="No oversight reports yet" description={'Click "Run Oversight Check" to perform the first cross-cutting review of the AI agent fleet.'} /> {/* v2.9 */}
        </Card>
      ) : (
        <Card padding="lg"> {/* v2.9 */}
          <SectionHeader eyebrow="Latest Oversight Report" title={`Run ${(latest.runStartedAt || latest.run_started_at || latest.created_at) ? new Date(latest.runStartedAt || latest.run_started_at || latest.created_at).toLocaleString() : ''}`} icon={<Icon.Activity className="w-4 h-4" />} /> {/* v2.9 */}
          <div className="mt-4 space-y-4"> {/* v2.9 */}
            {/* v2.9: severity banner */}
            <div className={`p-3 rounded-lg border ${ // v2.9
              (latest.severity === 'red' || latest.severity === 'orange') ? 'bg-danger-50 dark:bg-danger-500/10 border-danger-500/20'
              : latest.severity === 'yellow' ? 'bg-warning-50 dark:bg-warning-500/10 border-warning-500/20'
              : 'bg-success-50 dark:bg-success-500/10 border-success-500/20'
            }`}>
              <div className="flex items-center justify-between gap-2"> {/* v2.9 */}
                <p className="text-sm font-semibold">Severity</p> {/* v2.9 */}
                <StatusPill tone={severityTone[latest.severity] || 'info'} dot>{severityLabel[latest.severity] || latest.severity || 'unknown'}</StatusPill> {/* v2.9 */}
              </div>
              <p className="text-xs text-ink-500 dark:text-ink-400 mt-1">Issues found: {Number(latest.issuesFound ?? latest.issues_found ?? 0)} · Duration: {Number(latest.durationMs ?? latest.duration_ms ?? 0).toLocaleString()} ms · Triggered by: {latest.triggeredBy || latest.triggered_by || 'cron'}</p> {/* v2.9 */}
            </div>

            {/* v2.9: summary */}
            {latest.summary && ( // v2.9
              <div className="p-3 surface-inset rounded-lg"> {/* v2.9 */}
                <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Summary</p> {/* v2.9 */}
                <p className="text-sm text-ink-700 dark:text-ink-200 text-pretty">{latest.summary}</p> {/* v2.9 */}
              </div>
            )}

            {/* v2.9: findings detail */}
            {Array.isArray(latest.findings) && latest.findings.length > 0 && ( // v2.9
              <div> {/* v2.9 */}
                <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">Issues Found ({latest.findings.length})</p> {/* v2.9 */}
                <div className="space-y-1.5"> {/* v2.9 */}
                  {latest.findings.map((f: any, i: number) => ( // v2.9
                    <div key={i} className="flex items-start gap-2 p-2 surface-inset rounded-lg"> {/* v2.9 */}
                      <StatusPill tone={severityTone[f.severity] || 'info'} dot>{severityLabel[f.severity] || f.severity}</StatusPill> {/* v2.9 */}
                      <div className="flex-1 min-w-0"> {/* v2.9 */}
                        <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{f.check || 'Check'}</p> {/* v2.9 */}
                        <p className="text-2xs text-ink-500 dark:text-ink-400">{f.count ?? 0} item(s)</p> {/* v2.9 */}
                        {f.detail && <p className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5 break-words">{typeof f.detail === 'string' ? f.detail : JSON.stringify(f.detail).slice(0, 240)}</p>} {/* v2.9 */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* v2.9: auto-resolutions */}
            {Array.isArray(latest.autoResolutions ?? latest.auto_resolutions) && (latest.autoResolutions || latest.auto_resolutions).length > 0 && ( // v2.9
              <div> {/* v2.9 */}
                <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">Auto-Resolutions Applied ({(latest.autoResolutions || latest.auto_resolutions).length})</p> {/* v2.9 */}
                <div className="space-y-1.5"> {/* v2.9 */}
                  {(latest.autoResolutions || latest.auto_resolutions).map((r: any, i: number) => ( // v2.9
                    <div key={i} className="p-2 surface-inset rounded-lg text-xs text-ink-700 dark:text-ink-200"> {/* v2.9 */}
                      {typeof r === 'string' ? r : (r.action || r.description || JSON.stringify(r))} {/* v2.9 */}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* v2.9: recommended actions */}
            {(() => { // v2.9
              const rec = latest.recommendedActions || latest.recommended_actions; // v2.9
              if (!rec) return null; // v2.9
              const arr = Array.isArray(rec) ? rec : (typeof rec === 'string' ? [rec] : []); // v2.9
              if (arr.length === 0) return null; // v2.9
              return ( // v2.9
                <div> {/* v2.9 */}
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-2">Recommended Actions ({arr.length})</p> {/* v2.9 */}
                  <div className="space-y-1.5"> {/* v2.9 */}
                    {arr.map((r: any, i: number) => ( // v2.9
                      <div key={i} className="p-2 surface-inset rounded-lg text-sm text-ink-700 dark:text-ink-200 text-pretty"> {/* v2.9 */}
                        {typeof r === 'string' ? r : (r.action || r.title || JSON.stringify(r))} {/* v2.9 */}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </Card>
      )}

      {/* v2.9: history table */}
      <Card padding="none" className="overflow-hidden"> {/* v2.9 */}
        <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.9 */}
          <SectionHeader eyebrow="Oversight History" title="Past Reports" subtitle={`${history.length} reports`} icon={<Icon.Clock className="w-4 h-4" />} /> {/* v2.9 */}
        </div>
        {history.length === 0 ? ( // v2.9
          <div className="p-8"><EmptyState icon={<Icon.Clock className="w-5 h-5" />} title="No oversight history" description="Reports will accumulate here each time the orchestrator runs (every 5 min via cron, or on manual trigger)." /></div> // v2.9
        ) : ( // v2.9
          <div className="overflow-x-auto"> {/* v2.9 */}
            <table className="data-table"> {/* v2.9 */}
              <thead><tr><th>Time</th><th>Severity</th><th>Issues</th><th>Auto-Resolved</th><th>Summary</th></tr></thead> {/* v2.9 */}
              <tbody> {/* v2.9 */}
                {history.map((r: any) => { // v2.9
                  const autoCount = Array.isArray(r.autoResolutions ?? r.auto_resolutions) ? (r.autoResolutions || r.auto_resolutions).length : 0; // v2.9
                  return ( // v2.9
                    <tr key={r.id}> {/* v2.9 */}
                      <td className="text-2xs">{(r.runStartedAt || r.run_started_at || r.created_at) ? new Date(r.runStartedAt || r.run_started_at || r.created_at).toLocaleString() : '—'}</td> {/* v2.9 */}
                      <td><StatusPill tone={severityTone[r.severity] || 'info'} dot>{severityLabel[r.severity] || r.severity || 'unknown'}</StatusPill></td> {/* v2.9 */}
                      <td className="nums">{Number(r.issuesFound ?? r.issues_found ?? 0)}</td> {/* v2.9 */}
                      <td className="nums">{autoCount}</td> {/* v2.9 */}
                      <td className="text-xs text-ink-700 dark:text-ink-300 truncate max-w-xs">{r.summary ? (r.summary.length > 100 ? r.summary.slice(0, 100) + '…' : r.summary) : '—'}</td> {/* v2.9 */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}; // v2.9

// ════════════════════════════════════════════════════════════════
// v3.2: SatelliteTab — Gemini Vision satellite imagery analysis
// ════════════════════════════════════════════════════════════════
// Surfaces the SATELLITE agent (SVC-009) through the Admin UI. Admin can
// paste a satellite image URL (from Copernicus / EarthData / NICFI / any
// public JPEG) and run vision analysis to assess vegetation health, NDVI
// proxy, deforestation, water stress, and disease risk.
// Routes through the Model Router vision task: Florence-2 -> Gemini -> GPT-4o -> Claude.
const SatelliteTab: React.FC = () => {
  const [imageUrl, setImageUrl] = useState('');
  const [farmId, setFarmId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runAnalysis = async () => {
    if (!imageUrl) {
      setError('Image URL is required');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiClient.post('/ai-agents/SATELLITE/run', {
        imageUrl,
        farmId: farmId || undefined,
      });
      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Satellite analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        eyebrow="v3.2"
        title={'Satellite Imagery Analysis (SATELLITE SVC-009)'}
        subtitle={'Analyze satellite imagery of cocoa farms via the admin-configured Model Router vision task. Supports JPEG/PNG/WebP up to 20MB. Routes through Florence-2 -> Gemini Vision -> GPT-4o Vision -> Claude Vision.'}
        icon={<Icon.MapPin className="w-4 h-4" />}
      />

      <Card padding="lg" className="space-y-3">
        <div>
          <label className="label">Satellite Image URL *</label>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/satellite-image.jpg"
            className="input"
          />
          <p className="text-2xs text-ink-500 mt-1">
            Paste a public URL to a satellite image (JPEG, PNG, WebP, or GIF). Max 20MB.
            Sources: Copernicus Sentinel-2, NASA EarthData Landsat, Planet NICFI, or any public satellite JPEG.
          </p>
        </div>
        <div>
          <label className="label">Farm ID (optional)</label>
          <input
            type="text"
            value={farmId}
            onChange={(e) => setFarmId(e.target.value)}
            placeholder="UUID of the farm to link the analysis to"
            className="input"
          />
        </div>
        <button
          onClick={runAnalysis}
          disabled={loading || !imageUrl}
          className="btn-primary text-sm inline-flex items-center gap-2"
        >
          {loading ? 'Analyzing satellite image...' : 'Analyze with AI Vision'}
        </button>
      </Card>

      {error && (
        <Card padding="lg">
          <Alert tone="danger">{error}</Alert>
        </Card>
      )}

      {result?.success && result.output && (
        <Card padding="lg" className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-green-700 dark:text-green-400">Analysis Complete</h3>
            <span className="text-2xs text-ink-500">Model: {result.model || 'unknown'}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {result.output.vegetation_health && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500">Vegetation Health</p>
                <p className="text-sm font-semibold">{result.output.vegetation_health}</p>
              </div>
            )}
            {typeof result.output.ndvi_estimate === 'number' && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500">NDVI Estimate</p>
                <p className="text-sm font-semibold nums">{result.output.ndvi_estimate.toFixed(2)}</p>
              </div>
            )}
            {result.output.deforestation_detected !== undefined && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500">Deforestation</p>
                <p className="text-sm font-semibold">{result.output.deforestation_detected ? 'Warning: Detected' : 'None'}</p>
              </div>
            )}
            {result.output.water_stress && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500">Water Stress</p>
                <p className="text-sm font-semibold">{result.output.water_stress}</p>
              </div>
            )}
            {result.output.disease_risk && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500">Disease Risk</p>
                <p className="text-sm font-semibold">{result.output.disease_risk}</p>
              </div>
            )}
            {typeof result.output.confidence === 'number' && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500">Confidence</p>
                <p className="text-sm font-semibold nums">{result.output.confidence.toFixed(1)}%</p>
              </div>
            )}
          </div>
          {Array.isArray(result.output.recommendations) && result.output.recommendations.length > 0 && (
            <div>
              <p className="text-2xs text-ink-500 mb-2">Recommendations</p>
              <ul className="space-y-1">
                {result.output.recommendations.map((r: string, i: number) => (
                  <li key={i} className="text-xs text-ink-700 dark:text-ink-300">- {r}</li>
                ))}
              </ul>
            </div>
          )}
          {result.output.image_id && (
            <p className="text-2xs text-ink-500">Persisted to satellite_imagery table. Image ID: {result.output.image_id}</p>
          )}
          {result.output.memory_event_id && (
            <p className="text-2xs text-ink-500">Cognitive memory event recorded. Event ID: {result.output.memory_event_id}</p>
          )}
        </Card>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// v3.2.8: ProducerManagementTab — Manage cocoa producers (farmers + cooperatives)
// ════════════════════════════════════════════════════════════════
// The Copilot was directing users to a "Producer Management" module that
// didn't exist. This tab provides a comprehensive view of all producers
// in the system: farmers, their cooperatives, production volumes, quality
// grades, payment status, and farm details. Also shows AI-powered producer
// performance analysis via the EXECUTIVE agent.
const ProducerManagementTab: React.FC = () => {
  const [producers, setProducers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCoop, setFilterCoop] = useState('');
  const [cooperatives, setCooperatives] = useState<any[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, active: 0, totalHectares: 0, totalProductionKg: 0 });

  useEffect(() => {
    fetchProducers();
    fetchCooperatives();
  }, []);

  const fetchProducers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/farmers?limit=200');
      const farmerList = Array.isArray(res.data) ? res.data : (res.data?.farmers || res.data?.rows || []); // v3.2.11: was missing Array.isArray check
      setProducers(farmerList);
      // Calculate stats
      const totalHectares = farmerList.reduce((sum: number, f: any) => sum + (parseFloat(f.total_hectares) || 0), 0);
      const totalProductionKg = farmerList.reduce((sum: number, f: any) => sum + (parseFloat(f.total_production_kg) || parseFloat(f.annual_production_kg) || 0), 0);
      const activeCount = farmerList.filter((f: any) => f.status !== 'INACTIVE' && f.deleted_at === null).length;
      setStats({ total: farmerList.length, active: activeCount, totalHectares, totalProductionKg });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load producers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCooperatives = async () => {
    try {
      const res = await apiClient.get('/farmers/cooperatives');
      setCooperatives(res.data?.cooperatives || []);
    } catch {
      // Non-fatal — cooperatives list is optional
    }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);
    try {
      const res = await apiClient.post('/ai-agents/EXECUTIVE/run', {
        region: 'Cameroon',
        kpi: 'producer_performance',
      });
      setAiAnalysis(res.data);
    } catch (err: any) {
      setAiError(err.response?.data?.message || err.response?.data?.error || 'AI analysis failed');
    } finally {
      setAiLoading(false);
    }
  };

  const filteredProducers = producers.filter((p: any) => {
    const matchesSearch = !searchQuery ||
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.phone?.includes(searchQuery) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.village?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCoop = !filterCoop || p.cooperative_name === filterCoop || p.cooperative_id === filterCoop;
    return matchesSearch && matchesCoop;
  });

  return (
    <div className="space-y-4">
      <SectionHeader
        eyebrow="Producer Management"
        title="Cocoa Producers Dashboard"
        subtitle="Manage all cocoa producers (farmers and cooperatives) in the CoConnect system. View production volumes, quality grades, farm sizes, and AI-powered performance analysis."
        icon={<Icon.Users className="w-4 h-4" />}
      />

      {/* Stats tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Producers" value={stats.total.toLocaleString()} accent="cargo" />
        <MetricTile label="Active Producers" value={stats.active.toLocaleString()} accent="admin" />
        <MetricTile label="Total Hectares" value={stats.totalHectares > 0 ? `${stats.totalHectares.toFixed(1)} ha` : '—'} accent="analytics" />
        <MetricTile label="Total Production" value={stats.totalProductionKg > 0 ? `${Math.round(stats.totalProductionKg).toLocaleString()} kg` : '—'} accent="cocoa" />
      </div>

      {/* Filters + Search */}
      <Card padding="lg" className="space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="label">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, email, or village..."
              className="input"
            />
          </div>
          <div className="min-w-[180px]">
            <label className="label">Filter by Cooperative</label>
            <select value={filterCoop} onChange={e => setFilterCoop(e.target.value)} className="input">
              <option value="">All Cooperatives</option>
              {cooperatives.map((c, i) => (
                <option key={i} value={c.name || c.id}>{c.name || c.cooperative_name || 'Unknown'}</option>
              ))}
            </select>
          </div>
          <button onClick={fetchProducers} disabled={loading} className="btn-secondary text-xs">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}

        {/* Producers table */}
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : filteredProducers.length === 0 ? (
          <EmptyState icon={<Icon.Users className="w-5 h-5" />} title="No producers found" description={searchQuery || filterCoop ? 'Try adjusting your search filters.' : 'No producers registered yet. Use the User Management tab to create farmer accounts.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Village</th>
                  <th>Cooperative</th>
                  <th>Hectares</th>
                  <th>Production (kg)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducers.slice(0, 50).map((p: any, i: number) => (
                  <tr key={p.id || i}>
                    <td className="text-sm font-medium">{p.name || '—'}</td>
                    <td className="text-2xs">{p.phone || '—'}</td>
                    <td className="text-2xs">{p.village || '—'}</td>
                    <td className="text-2xs">{p.cooperative_name || '—'}</td>
                    <td className="text-2xs nums">{parseFloat(p.total_hectares || 0).toFixed(1)}</td>
                    <td className="text-2xs nums">{parseFloat(p.total_production_kg || p.annual_production_kg || 0).toLocaleString()}</td>
                    <td>
                      <StatusPill tone={p.status === 'ACTIVE' || !p.status ? 'success' : 'neutral'} dot>
                        {p.status || 'ACTIVE'}
                      </StatusPill>
                    </td>
                    <td>
                      <a href={`/farmer-dashboard`} className="text-2xs text-admin-600 hover:underline">View Profile</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredProducers.length > 50 && (
              <p className="text-2xs text-ink-500 mt-2">Showing 50 of {filteredProducers.length} producers. Refine your search to see more.</p>
            )}
          </div>
        )}
      </Card>

      {/* AI Performance Analysis */}
      <Card padding="lg" className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">AI Producer Performance Analysis</h3>
            <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">Get AI-powered insights on producer performance, trends, and recommendations</p>
          </div>
          <button onClick={runAiAnalysis} disabled={aiLoading} className="btn-primary text-xs inline-flex items-center gap-1.5">
            {aiLoading ? <Spinner size="sm" /> : <Icon.Activity className="w-3 h-3" />}
            {aiLoading ? 'Analyzing...' : 'Analyze with AI'}
          </button>
        </div>

        {aiError && <Alert tone="danger">{aiError}</Alert>}

        {aiAnalysis?.success && aiAnalysis.output && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-2xs text-ink-500">Model: {aiAnalysis.model || 'unknown'}</span>
              {aiAnalysis.output.confidence && (
                <StatusPill tone={aiAnalysis.output.confidence > 70 ? 'success' : 'warning'} dot>
                  Confidence: {aiAnalysis.output.confidence}%
                </StatusPill>
              )}
            </div>
            {aiAnalysis.output.executive_dashboard && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500 mb-1">Executive Dashboard</p>
                <p className="text-xs">{JSON.stringify(aiAnalysis.output.executive_dashboard)}</p>
              </div>
            )}
            {aiAnalysis.output.strategic_insight && (
              <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/30 border-l-4 border-green-500">
                <p className="text-2xs text-ink-500 mb-1">Strategic Insight</p>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">{aiAnalysis.output.strategic_insight}</p>
              </div>
            )}
            {aiAnalysis.output.decision_support && Array.isArray(aiAnalysis.output.decision_support) && (
              <div>
                <p className="text-2xs text-ink-500 mb-1">Decision Support</p>
                <ul className="space-y-1">
                  {aiAnalysis.output.decision_support.map((d: string, i: number) => (
                    <li key={i} className="text-xs text-ink-700 dark:text-ink-300">• {d}</li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.output.reasoning && (
              <details className="text-xs">
                <summary className="cursor-pointer text-ink-500">AI Reasoning</summary>
                <p className="mt-2 text-ink-700 dark:text-ink-300">{aiAnalysis.output.reasoning}</p>
              </details>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
